import React, { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import icons from "./data/icons";
import { getHome, updateHome } from "../components/api/homeService";
import { Turnstile } from "@marsidev/react-turnstile";
import BlogAdmin from "./BlogAdmin";
import ServiceAdmin from "./ServiceAdmin";
import {
  getFooter,
  getFooterById,
  createFooter,
  updateFooter,
  deleteFooter,
} from "../components/api/footerService";
import {
  getHeader,
  getHeaderTree,
  createHeader,
  updateHeader,
  deleteHeader,
} from "../components/api/headerService";
import {
  getYoutube,
  getYoutubeById,
  createYoutube,
  updateYoutube,
  deleteYoutube,
  toggleYoutubeStatus,
} from "./api/youtubeApi";
import {
  getBlogs,
  createBlog,
  updateBlog,
  deleteBlog,
  moveBlogUp,
  moveBlogDown,
} from "./api/blogApi";
import { getServices } from "./api/serviceApi";
import {
  getContact,
  updateContact,
} from "./api/contactService";
import {
  getAbout,
  createAbout,
  updateAbout,
  deleteAbout,
  getSkills,
  getEducation,
  createSkill,
  updateSkill,
  deleteSkill,
  createEducation,
  updateEducation,
  deleteEducation,
} from "./api/aboutService";
import {
  User,
  FolderKanban,
  Sparkles,
  Briefcase,
  GraduationCap,
  Mail,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  ImageIcon,
  Plus,
  Pencil,
  Trash2,
  Search,
  ChevronRight,
  Upload,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Lock,
  FileText,
  Building2,
  BookOpen,
  Users,
  ShieldCheck,
  ShieldOff,
  Settings,
  Smartphone,
  Check,
  Loader2,
  ArrowUp,
  ArrowDown,
  Edit,
  GripVertical,
  Copy,
  ChevronUp,
  ChevronDown,
  Save,
  Monitor,
  MoreVertical,
  UserCircle2,
  ArrowLeft,
} from "lucide-react";
import {
  login,
  register,
  getUsers,
  updateUser,
  deleteUser,
  changePassword,
  unlockUser,
  setup2FA,
  enable2FA,
  disable2FA,
  getTrustedSession,
  getCurrentDevice,
  removeTrustedDevice,
  continueTrustedSession,
  verifyLoginTwoFactor,
  sendPasswordResetOtp,
  verifyPasswordResetOtp,
  resetPassword,

  // 2FA reset
  sendTwoFactorResetOtp,
  resetTwoFactor,
} from "./api/auth.service";
import {
  getExperiences,
  createExperience,
  updateExperience,
  deleteExperience,
  moveExperienceUp,
  moveExperienceDown,
} from "./api/experienceService";
import {
  getPortfolio,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
  movePortfolioUp,
  movePortfolioDown,
  getPublications,
  createPublication,
  updatePublication,
  deletePublication,
  movePublicationUp,
  movePublicationDown,
  getCommunity,
  createCommunity,
  updateCommunity,
  deleteCommunity,
  moveCommunityUp,
  moveCommunityDown,
} from "./api/portfolio.service";
import api from "./api/api";

/* ============================================================================
   FONTS
============================================================================ */
const FontStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
    .font-display { font-family: 'Space Grotesk', sans-serif; }
    .font-body { font-family: 'Inter', sans-serif; }
    * { scrollbar-width: thin; }
    ::-webkit-scrollbar { width: 8px; height: 8px; }
    ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.35); border-radius: 8px; }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes scaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
    .anim-fadeUp { animation: fadeUp 0.5s ease both; }
    .anim-fadeIn { animation: fadeIn 0.3s ease both; }
    .anim-scaleIn { animation: scaleIn 0.2s ease both; }
    @media (prefers-reduced-motion: reduce) {
      .anim-fadeUp, .anim-fadeIn, .anim-scaleIn { animation: none !important; }
    }
  `}</style>
);

/* ============================================================================
   THEME TOKENS
============================================================================ */
function useTheme() {
  return useMemo(() => ({
    bg: "bg-[#0A0D12]",
    bgAlt: "bg-[#0E1218]",
    surface: "bg-[#131822]",
    surfaceAlt: "bg-[#171D28]",
    border: "border-[#232B38]",
    text: "text-[#EDEEF0]",
    subtext: "text-[#8B93A0]",
    faint: "text-[#5C6472]",
    accent: "#2563EB",
    ring: "ring-white/10",
    shadow: "shadow-[0_8px_30px_rgb(0,0,0,0.45)]",
  }), []);
}

const cx = (...a) => a.filter(Boolean).join(" ");

/* ============================================================================
   MOCK DATA (for About, Contact, etc.)
============================================================================ */
const initialAbout = {
  aboutTitle: "",
  aboutHighlight: "",
  profileImage: "",
  profileImageAlt: "Profile photo",
  objectPosition: "center 20%",
  bio: "",
  skillLabels: [],
  academicTitle: "",
  academicHighlight: "",
  highlightWords: [],
  order: 0,
  isFeatured: false,
  isActive: true,
};

const initialContact = {

};

/* ============================================================================
   SMALL UI PRIMITIVES (Toast, ConfirmDialog, Field, TextInput, etc.)
============================================================================ */
function Toast({ toast, onClose }) {
  const theme = useTheme();
  useEffect(() => {
    const t = setTimeout(() => onClose(toast.id), 3200);
    return () => clearTimeout(t);
  }, [toast.id, onClose]);
  const isErr = toast.type === "error";
  return (
    <div
      className={cx(
        "flex items-start gap-3 px-4 py-3 rounded-xl anim-fadeUp shadow-lg min-w-[260px] max-w-[360px] border",
        "bg-[#161C26] border-[#26303F] text-white"
      )}
    >
      {isErr ? <AlertCircle size={18} className="mt-0.5 shrink-0" style={{ color: "#D65C4F" }} /> : <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: "#2563EB" }} />}
      <p className="text-sm font-body leading-snug">{toast.message}</p>
      <button onClick={() => onClose(toast.id)} className="ml-auto opacity-50 hover:opacity-100 shrink-0">
        <X size={14} />
      </button>
    </div>
  );
}

function ToastStack({ toasts, onClose }) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={onClose} />
      ))}
    </div>
  );
}

function ConfirmDialog({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 anim-fadeIn" style={{ background: "rgba(10,12,14,0.55)" }}>
      <div className={cx("w-full max-w-sm rounded-2xl p-6 anim-scaleIn border", "bg-[#151B24] border-[#26303F]")}>
        <div className="w-11 h-11 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(214,92,79,0.12)" }}>
          <Trash2 size={20} style={{ color: "#D65C4F" }} />
        </div>
        <h3 className={cx("font-display font-semibold text-lg mb-1.5", "text-white")}>{title}</h3>
        <p className={cx("text-sm font-body mb-6", "text-[#8B93A0]")}>{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className={cx("flex-1 py-2.5 rounded-xl text-sm font-medium font-body border transition", "border-[#2A3341] text-gray-200 hover:bg-white/5")}>
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 rounded-xl text-sm font-medium font-body text-white transition hover:opacity-90" style={{ background: "#D65C4F" }}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, required, children, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium font-body flex items-center gap-1 text-[#EDEEF0]">
        {label}
        {required && <span style={{ color: "#D65C4F" }}>*</span>}
      </label>
      {children}
      {hint && !error && <span className="text-xs font-body opacity-60 text-[#8B93A0]">{hint}</span>}
      {error && (
        <span className="text-xs font-body flex items-center gap-1" style={{ color: "#D65C4F" }}>
          <AlertCircle size={12} /> {error}
        </span>
      )}
    </div>
  );
}

function TextInput({ error, disabled, ...props }) {
  return (
    <input
      {...props}
      disabled={disabled}
      className={cx(
        "w-full px-3.5 py-2.5 rounded-xl text-sm font-body outline-none transition border",

        disabled
          ? "bg-[#1A202B] text-[#7C8798] border-[#303847] cursor-not-allowed"
          : "bg-[#0F141C] text-white placeholder:text-[#4E5766]",

        error
          ? "border-[#D65C4F]"
          : !disabled
            ? "border-[#232B38] focus:border-[#2563EB]"
            : ""
      )}
    />
  );
}

function TextArea({ error, ...props }) {
  return (
    <textarea
      {...props}
      rows={props.rows || 4}
      className={cx(
        "w-full px-3.5 py-2.5 rounded-xl text-sm font-body outline-none transition border resize-none",
        "bg-[#0F141C] text-white placeholder:text-[#4E5766]",
        error ? "border-[#D65C4F]" : "border-[#232B38] focus:border-[#2563EB]"
      )}
    />
  );
}

function Select({ error, children, ...props }) {
  return (
    <select
      {...props}
      className={cx(
        "w-full px-3.5 py-2.5 rounded-xl text-sm font-body outline-none transition border appearance-none",
        "bg-[#0F141C] text-white",
        error ? "border-[#D65C4F]" : "border-[#232B38] focus:border-[#2563EB]"
      )}
    >
      {children}
    </select>
  );
}

function PrimaryButton({ children, className, ...props }) {
  return (
    <button
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold font-body text-white transition hover:opacity-90 active:scale-[0.98]",
        className
      )}
      style={{ background: "#2563EB" }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, className, ...props }) {
  return (
    <button
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium font-body border transition active:scale-[0.98]",
        "border-[#232B38] text-gray-200 hover:bg-white/5",
        className
      )}
    >
      {children}
    </button>
  );
}

function ProgressRing({ pct, size = 132 }) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1E2733" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#2563EB" strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.8s ease" }}
      />
    </svg>
  );
}

/* ============================================================================
   VALIDATION
============================================================================ */
const isUrl = (v) => !v || /^https?:\/\/.+\..+/.test(v.trim());
const isEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

/* ============================================================================
   GENERIC CRUD MANAGER (Projects / Publications / Community)
============================================================================ */
function GenericModal({ config, initial, onCancel, onSave }) {
  const [form, setForm] = useState(() => ({
    ...initial,

    techStack: Array.isArray(initial.techStack)
      ? initial.techStack.map((tech) => {
        if (typeof tech === "string") {
          return {
            icon: "",
            label: tech,
            color: "#64748B",
          };
        }

        return {
          icon: tech?.icon || "",
          label: tech?.label || tech?.icon || "",
          color: tech?.color || "#64748B",
        };
      })
      : [],
  }));
  const [errors, setErrors] = useState({});
  const [iconSearch, setIconSearch] = useState("");
  const [activeIconIndex, setActiveIconIndex] = useState(null);
  const filteredIconNames = Object.keys(icons)
    .filter((name) =>
      name.toLowerCase().includes(iconSearch.toLowerCase())
    )
    .slice(0, 20);
  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const validate = () => {
    const errs = {};
    config.fields.forEach((f) => {
      const val = (form[f.key] ?? "").toString().trim();
      if (f.required && !val) errs[f.key] = `${f.label} is required`;
      else if (f.type === "url" && !isUrl(val)) errs[f.key] = "Enter a valid URL (https://...)";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = () => {
    if (!validate()) return;

    const payload = { ...form };
    if (typeof payload.features === "string") {
      payload.features = payload.features
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (typeof payload.metrics === "string") {
      const parts = payload.metrics
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      payload.metrics = [];

      for (let i = 0; i < parts.length; i += 2) {
        payload.metrics.push({
          value: parts[i] || "",
          label: parts[i + 1] || "",
          size: "default",
        });
      }
    }

    onSave(payload);
  };

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center overflow-y-auto p-6"
      style={{ background: "rgba(10,12,14,.65)" }}
    >
      <div className={cx("w-full max-w-lg rounded-2xl p-6 anim-scaleIn border max-h-[88vh] overflow-y-auto", "bg-[#131822] border-[#232B38]")}>
        <div className="flex items-center justify-between mb-5">
          <h3 className={cx("font-display font-semibold text-lg", "text-white")}>
            {initial._isNew ? `Add ${config.itemLabel}` : `Edit ${config.itemLabel}`}
          </h3>
          <button onClick={onCancel} className="opacity-50 hover:opacity-100 text-white"><X size={18} /></button>
        </div>
        <div className="flex flex-col gap-4">
          {config.fields.map((f) => {
            if (f.type === "checkbox") {
              return (
                <Field key={f.key} label={f.label} error={errors[f.key]}>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={!!form[f.key]}
                      onChange={(e) => update(f.key, e.target.checked)}
                      className="w-4 h-4 rounded border-[#232B38] bg-[#0F141C] text-[#2563EB] focus:ring-[#2563EB] focus:ring-offset-0"
                    />
                    <span className="text-sm font-body text-gray-300">{f.placeholder || f.label}</span>
                  </div>
                </Field>
              );
            }

            return (
              <Field
                key={f.key}
                label={f.label}
                required={f.required}
                error={errors[f.key]}
                hint={f.hint}
              >
                {f.type === "featureList" ? (
                  <div className="space-y-3">
                    {(form.features || []).map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <Select
                          value={feature.icon || "star"}
                          onChange={(e) => {
                            const arr = [...form.features];

                            if (typeof arr[index] === "string") {
                              arr[index] = {
                                icon: "star",
                                text: arr[index],
                              };
                            }

                            arr[index].icon = e.target.value;

                            update("features", arr);
                          }}
                        >
                          <option value="user">👤 User</option>
                          <option value="users">👥 Users</option>
                          <option value="award">🏆 Award</option>
                          <option value="target">🎯 Target</option>
                          <option value="sparkles">✨ Sparkles</option>
                          <option value="star">⭐ Star</option>
                          <option value="book">📖 Book</option>
                          <option value="message">💬 Message</option>
                          <option value="code">💻 Code</option>
                          <option value="wrench">🔧 Wrench</option>
                          <option value="laptop">💻 Laptop</option>
                          <option value="lightbulb">💡 Lightbulb</option>
                          <option value="zap">⚡ Zap</option>
                          <option value="plane">✈️ Plane</option>
                        </Select>

                        <TextInput
                          value={typeof feature === "string" ? feature : feature.text}
                          onChange={(e) => {
                            const arr = [...form.features];

                            if (typeof arr[index] === "string") {
                              arr[index] = {
                                icon: "star",
                                text: arr[index],
                              };
                            }

                            arr[index].text = e.target.value;

                            update("features", arr);
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => {
                            const arr = [...(form.features || [])];
                            arr.splice(index, 1);
                            update("features", arr);
                          }}
                          className="px-2 rounded bg-red-500 text-white"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() =>
                        update("features", [
                          ...(form.features || []),
                          {
                            icon: "star",
                            text: "",
                          },
                        ])
                      }
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white"
                    >
                      + Add Feature
                    </button>
                  </div>
                ) : f.type === "techStack" ? (
                  <div className="space-y-3">
                    {(form.techStack || []).map((tech, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 rounded-lg bg-[#0F141C] border border-[#232B38]"
                      >
                        <div className="relative flex-1">
                          <TextInput
                            value={iconSearch || tech.icon || ""}
                            placeholder="Search icon e.g. React"
                            onFocus={() => {
                              setActiveIconIndex(index);
                              setIconSearch(tech.icon || "");
                            }}
                            onChange={(e) => {
                              setIconSearch(e.target.value);
                              setActiveIconIndex(index);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && filteredIconNames[0]) {
                                e.preventDefault();

                                const arr = [...(form.techStack || [])];

                                arr[index] = {
                                  ...arr[index],
                                  icon: filteredIconNames[0],
                                };

                                update("techStack", arr);
                                setIconSearch("");
                                setActiveIconIndex(null);
                              }

                              if (e.key === "Escape") {
                                setIconSearch("");
                                setActiveIconIndex(null);
                              }
                            }}
                          />

                          {activeIconIndex === index && iconSearch.trim() && (
                            <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 shadow-xl">
                              {filteredIconNames.map((iconName) => {
                                const IconComponent = icons[iconName];

                                return (
                                  <button
                                    key={iconName}
                                    type="button"
                                    onClick={() => {
                                      const arr = [...(form.techStack || [])];

                                      arr[index] = {
                                        ...arr[index],
                                        icon: iconName,
                                      };

                                      update("techStack", arr);
                                      setIconSearch("");
                                      setActiveIconIndex(null);
                                    }}
                                    className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-300 hover:bg-slate-800 hover:text-white"
                                  >
                                    {IconComponent && <IconComponent size={18} />}

                                    <span>{iconName}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <TextInput
                            value={tech.label || ""}
                            placeholder="Label e.g. React"
                            onChange={(e) => {
                              const arr = [...(form.techStack || [])];

                              arr[index] = {
                                ...arr[index],
                                label: e.target.value,
                              };

                              update("techStack", arr);
                            }}
                          />
                        </div>

                        <input
                          type="color"
                          value={tech.color || "#64748B"}
                          onChange={(e) => {
                            const arr = [...(form.techStack || [])];

                            arr[index] = {
                              ...arr[index],
                              color: e.target.value,
                            };

                            update("techStack", arr);
                          }}
                          className="w-10 h-10 p-1 rounded-lg bg-[#0F141C] border border-[#232B38] cursor-pointer"
                          title="Icon color"
                        />

                        <button
                          type="button"
                          onClick={() => {
                            const arr = [...(form.techStack || [])];
                            arr.splice(index, 1);
                            update("techStack", arr);
                          }}
                          className="px-2 py-2 rounded-lg bg-red-500 text-white"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => {
                        update("techStack", [
                          ...(form.techStack || []),
                          {
                            icon: "",
                            label: "",
                            color: "#64748B",
                          },
                        ]);
                      }}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm"
                    >
                      + Add Technology
                    </button>
                  </div>
                ) : f.type === "textarea" ? (
                  <TextArea
                    error={errors[f.key]}
                    value={
                      f.key === "summary" && typeof form[f.key] === "object"
                        ? `${form[f.key]?.title || ""},${form[f.key]?.value || ""},${form[f.key]?.description || ""}`
                        : Array.isArray(form[f.key])
                          ? form[f.key]
                            .map((v) => {
                              if (typeof v !== "object") return v;

                              if (f.key === "metrics") {
                                return `${v.value},${v.label}`;
                              }

                              return v.text || "";
                            })
                            .join("\n")
                          : form[f.key] || ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (f.key === "features") {
                        update(f.key, value);
                      } else if (f.key === "summary") {
                        const [title, value1, description] = value
                          .split(",")
                          .map((x) => x.trim());

                        update(f.key, {
                          title: title || "",
                          value: value1 || "",
                          description: description || "",
                        });
                      } else if (f.key === "metrics") {
                        update(f.key, value);
                      }
                      else if (
                        [
                          "status",
                          "authors",
                          "keywords",
                          "media",
                        ].includes(f.key)
                      ) {
                        update(
                          f.key,
                          value
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean)
                        );
                      } else {
                        update(f.key, value);
                      }
                    }}
                    placeholder={f.placeholder}
                  />
                ) : f.type === "select" ? (
                  <Select
                    error={errors[f.key]}
                    value={form[f.key] || f.options[0]}
                    onChange={(e) => update(f.key, e.target.value)}
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                ) : f.type === "multifile" ? (
                  <div className="space-y-3">
                    <label className="flex items-center justify-center p-4 rounded-xl border border-dashed border-[#232B38] bg-[#0F141C] cursor-pointer text-gray-300">
                      <Upload size={20} />
                      <span className="ml-2">Upload Images</span>

                      <input
                        type="file"
                        multiple
                        className="hidden"
                        accept={f.accept}
                        onChange={(e) => {
                          const files = Array.from(e.target.files);

                          Promise.all(
                            files.map(
                              (file) =>
                                new Promise((resolve) => {
                                  const reader = new FileReader();

                                  reader.onload = () => resolve(reader.result);

                                  reader.readAsDataURL(file);
                                })
                            )
                          ).then((images) => update(f.key, images));
                        }}
                      />
                    </label>

                    {Array.isArray(form[f.key]) && (
                      <div className="grid grid-cols-3 gap-2">
                        {form[f.key].map((img, i) => (
                          <div key={i} className="relative">
                            <img
                              src={img}
                              alt=""
                              className="w-full h-24 object-cover rounded-lg border border-[#232B38]"
                            />

                            <button
                              type="button"
                              onClick={() =>
                                update(
                                  f.key,
                                  form[f.key].filter((_, index) => index !== i)
                                )
                              }
                              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : f.type === "file" ? (
                  <label className="flex flex-col items-center gap-3 px-3.5 py-4 rounded-xl border cursor-pointer bg-[#0F141C] border-[#232B38] text-gray-300">
                    {form[f.key] ? (
                      <img
                        src={form[f.key]}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border border-[#232B38]"
                      />
                    ) : (
                      <>
                        <Upload size={22} />
                        <span>Upload {f.label}</span>
                      </>
                    )}

                    <input
                      type="file"
                      className="hidden"
                      accept={f.accept}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const reader = new FileReader();

                        reader.onload = () => {
                          update(f.key, reader.result);
                        };

                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                ) : (
                  <TextInput
                    error={errors[f.key]}
                    value={form[f.key] || ""}
                    onChange={(e) => update(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    type={f.type === "url" ? "text" : f.type || "text"}
                  />
                )}
              </Field>
            );
          })}
        </div>
        <div className="flex gap-3 mt-6">
          <GhostButton onClick={onCancel} className="flex-1">Cancel</GhostButton>
          <PrimaryButton onClick={submit} className="flex-1">Save {config.itemLabel}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function GenericManager({
  config,
  data,
  setData,
  notify,
  createItem,
  updateItem,
  deleteItem,
  loadData,
  moveUp,
  moveDown,
}) {
  const [modal, setModal] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [query, setQuery] = useState("");
  const [filterVal, setFilterVal] = useState("All");

  const filtered = useMemo(() => {
    let rows = data;
    if (config.filterKey && filterVal !== "All") rows = rows.filter((r) => r[config.filterKey] === filterVal);
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter((r) => config.searchKeys.some((k) => (r[k] || "").toString().toLowerCase().includes(q)));
    }
    return rows;
  }, [data, query, filterVal, config]);

  const openNew = () => {
    const newItem = { _isNew: true };

    const arrayFields = [
      "techStack",
      "features",
      "status",
      "authors",
      "keywords",
      "metrics",
      "media",
    ];

    config.fields.forEach((f) => {
      if (f.type === "checkbox") {
        newItem[f.key] = false;
      } else if (f.type === "select") {
        newItem[f.key] = f.options ? f.options[0] : "";
      } else if (f.type === "file") {
        newItem[f.key] = null;
      } else if (arrayFields.includes(f.key)) {
        newItem[f.key] = [];
      } else {
        newItem[f.key] = "";
      }
    });

    setModal(newItem);
  };

  const openEdit = (row) => {
    const features = Array.isArray(row.features)
      ? row.features.map((f) =>
        typeof f === "string"
          ? { icon: "star", text: f }
          : f
      )
      : [];

    setModal({
      ...row,
      features,
      id: row._id || row.id,
      _isNew: false,
    });
  };

  const save = async (form) => {
    try {
      if (form._isNew) {
        const { _isNew, ...payload } = form;
        await createItem(payload);
        notify("success", `${config.itemLabel} added successfully.`);
      } else {
        await updateItem(form._id ?? form.id, form);
        notify("success", `${config.itemLabel} updated successfully.`);
      }
      const res = await loadData();
      setData(res.data.data || []);
      setModal(null);
    } catch (err) {
      console.error(err);
      notify("error", `Failed to save ${config.itemLabel}.`);
    }
  };

  const remove = async () => {
    try {
      await deleteItem(confirmId);
      const res = await loadData();
      setData(res.data.data || []);
      notify("success", `${config.itemLabel} deleted.`);
      setConfirmId(null);
    } catch (err) {
      console.error(err);
      notify("error", `Failed to delete ${config.itemLabel}.`);
    }
  };

  return (
    <div className="anim-fadeUp">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-6 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 opacity-40 text-[#8B93A0]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${config.itemLabel.toLowerCase()}s...`}
            className={cx("w-full pl-9 pr-3 py-2.5 rounded-xl text-sm font-body outline-none border", "bg-[#131822] border-[#232B38] text-white placeholder:text-[#4E5766]")}
          />
        </div>
        <div className="flex items-center gap-2">
          {config.filterKey && (
            <Select value={filterVal} onChange={(e) => setFilterVal(e.target.value)} className="!w-auto">
              <option>All</option>
              {config.filterOptions.map((o) => <option key={o}>{o}</option>)}
            </Select>
          )}
          <PrimaryButton onClick={openNew}>
            <Plus size={16} /> Add {config.itemLabel}
          </PrimaryButton>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={config.icon} label={config.itemLabel} onAdd={openNew} />
      ) : (
        <div className={cx("grid gap-4", config.grid || "grid-cols-1 md:grid-cols-2 xl:grid-cols-3")}>
          {filtered.map((row) => (
            <div key={row._id || row.id} className={cx("rounded-2xl border p-5 flex flex-col gap-3 transition hover:-translate-y-0.5", "bg-[#131822] border-[#232B38]")}>
              {config.renderCard(row)}
              <div className="flex gap-2 mt-auto pt-2">
                <button
                  onClick={async () => {
                    await moveUp(row._id || row.id);
                    const res = await loadData();
                    setData(res.data.data || []);
                  }}
                  className="px-3 py-2 rounded-lg border border-[#232B38] hover:bg-white/5 text-xs text-white"
                >
                  ⬆
                </button>

                <button
                  onClick={async () => {
                    await moveDown(row._id || row.id);
                    const res = await loadData();
                    setData(res.data.data || []);
                  }}
                  className="px-3 py-2 rounded-lg border border-[#232B38] hover:bg-white/5 text-xs text-white"
                >
                  ⬇
                </button>
                <button
                  onClick={() => openEdit(row)}
                  className={cx("flex-1 py-2 rounded-lg text-xs font-medium font-body flex items-center justify-center gap-1.5 border transition", "border-[#232B38] hover:bg-white/5 text-gray-200")}
                >
                  <Pencil size={13} /> Edit
                </button>
                <button
                  onClick={() => setConfirmId(row._id || row.id)}
                  className={cx("flex-1 py-2 rounded-lg text-xs font-medium font-body flex items-center justify-center gap-1.5 border transition", "border-[#2E1E1C] hover:bg-[#D65C4F]/10 text-[#E08A80]")}
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal &&
        createPortal(
          <GenericModal
            config={config}
            initial={modal}
            onCancel={() => setModal(null)}
            onSave={save}
          />,
          document.body
        )}
      <ConfirmDialog
        open={!!confirmId}
        title={`Delete ${config.itemLabel}?`}
        message="This action can't be undone. This record will be permanently removed."
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </div>
  );
}

function EmptyState({ icon: Icon, label, onAdd }) {
  return (
    <div className={cx("rounded-2xl border border-dashed py-16 flex flex-col items-center justify-center text-center gap-3", "border-[#232B38]")}>
      <div className={cx("w-12 h-12 rounded-full flex items-center justify-center", "bg-white/5")}>
        <Icon size={20} className="opacity-50 text-[#8B93A0]" />
      </div>
      <p className={cx("font-body text-sm", "text-gray-400")}>No {label.toLowerCase()}s yet. Add your first one.</p>
      <PrimaryButton onClick={onAdd}><Plus size={15} /> Add {label}</PrimaryButton>
    </div>
  );
}
function TrustedDeviceLogin({
  home,
  onLogin,
  onBackToLogin,
}) {
  const theme = useTheme();

  const [profiles, setProfiles] = useState([]);
  const [deviceName, setDeviceName] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");

  const [currentDevice, setCurrentDevice] = useState(null);
  const [settingsProfile, setSettingsProfile] = useState(null);

  // =====================================================
  // LOAD TRUSTED PROFILES + CURRENT DEVICE
  // =====================================================

  const loadTrustedDevice = async () => {
    try {
      setLoading(true);
      setError("");

      const [deviceRes, trustedRes] = await Promise.all([
        getCurrentDevice(),
        getTrustedSession(),
      ]);

      if (deviceRes.data?.success) {
        setCurrentDevice(deviceRes.data);
        setDeviceName(deviceRes.data.deviceName || "Unknown Device");
      }

      if (
        !trustedRes.data?.success ||
        !Array.isArray(trustedRes.data?.profiles) ||
        trustedRes.data.profiles.length === 0
      ) {
        setProfiles([]);
        setLoading(false);
        onBackToLogin();
        return;
      }

      setProfiles(trustedRes.data.profiles);
    } catch (err) {
      console.error("TRUSTED DEVICE ERROR:", err?.response?.data || err);
      setProfiles([]);
      setError("");
      onBackToLogin();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrustedDevice();
  }, []);

  // =====================================================
  // SELECT PROFILE
  // =====================================================

  const handleSelect = (profile) => {
    setSelectedId(profile.id);
    setError("");
  };

  // =====================================================
  // CONTINUE TRUSTED SESSION
  // =====================================================

  const handleContinue = async () => {
    if (!selectedId) {
      setError("Please select an account to continue.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await continueTrustedSession(selectedId);

      if (!res.data?.token) {
        setError(res.data?.message || "Unable to continue session.");
        return;
      }

      localStorage.setItem("adminToken", res.data.token);

      if (res.data.user) {
        localStorage.setItem("adminUser", JSON.stringify(res.data.user));
      }

      onLogin();
    } catch (err) {
      console.error("CONTINUE TRUSTED SESSION ERROR:", err);
      setError(
        err?.response?.data?.message ||
        "Trusted session expired. Please login again."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // REMOVE TRUSTED PROFILE
  // =====================================================

  const handleRemove = async (e, profile) => {
    if (e) e.stopPropagation();

    try {
      setError("");
      setLoading(true);

      await removeTrustedDevice(profile.id);

      setSettingsProfile(null);
      setSelectedId(null);

      // Re-check the device after removing the trusted account
      await loadTrustedDevice();
    } catch (err) {
      console.error("REMOVE TRUSTED DEVICE ERROR:", err);
      setError(
        err?.response?.data?.message || "Unable to remove this account."
      );
      setLoading(false);
    }
  };

  const handleOpenSettings = (e, profile) => {
    e.stopPropagation();
    setSettingsProfile(profile);
    setError("");
  };

  const handleCloseSettings = () => {
    setSettingsProfile(null);
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading && !profiles.length) {
    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[#0C121D]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-indigo-500/30 blur-xl" />

            <div className="relative h-14 w-14 animate-spin rounded-full border-[6px] border-slate-700 border-t-indigo-500" />
          </div>

          <div className="font-mono text-xs text-slate-400">
            Checking this device...
          </div>
        </div>
      </div>
    );
  }

  if (!profiles.length) {
    return null;
  }

  // =====================================================
  // TRUSTED DEVICE SCREEN
  // =====================================================

  return (
    <div className={cx("min-h-screen flex items-center justify-center px-5", theme.bg)}>
      <div className="w-full max-w-sm anim-fadeUp">

        {/* LOGO + TITLE — matches LoginScreen */}
        <div className="flex flex-col items-center text-center mb-8">
          <span className="w-20 h-auto rounded-2xl flex items-center justify-center mb-4 p-2">
            <img
              src={home?.headerLogo}
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </span>

          <h1 className={cx("font-display font-bold text-2xl", theme.text)}>
            Welcome back
          </h1>

          <p className={cx("font-body text-sm mt-1", theme.subtext)}>
            Choose an account to continue
          </p>
        </div>

        {/* CARD — same padding/border/shadow tokens as LoginScreen's form */}
        <div
          className={cx(
            "rounded-2xl border p-6 flex flex-col gap-3",
            theme.surface,
            theme.border,
            theme.shadow
          )}
        >
          {profiles.map((profile) => {
            const selected = selectedId === profile.id;

            return (
              <div
                key={profile.id}
                onClick={() => handleSelect(profile)}
                className={cx(
                  "relative flex items-center gap-4 p-4 rounded-2xl border transition-all cursor-pointer",
                  selected
                    ? "border-[#00FF9C] bg-[#00FF9C]/5"
                    : "border-[#252E3B] bg-[#151B25] hover:border-[#344052]"
                )}
              >
                <div className="w-12 h-12 rounded-full overflow-hidden bg-[#202938] shrink-0 flex items-center justify-center border border-[#303A4A]">
                  {profile.image ? (
                    <img
                      src={profile.image}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserCircle2 size={28} className="text-[#657083]" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {profile.name}
                  </p>
                  <p className="text-xs text-[#8993A3] truncate mt-0.5">
                    {profile.email}
                  </p>

                  <span className="flex items-center gap-1.5 text-[11px] font-mono text-[#00FF9C]">
                    <Monitor size={13} className="text-[#00FF9C]" />
                    {deviceName}
                  </span>
                </div>

                <div
                  className={cx(
                    "w-5 h-5 rounded-full border flex items-center justify-center shrink-0",
                    selected ? "border-[#00FF9C]" : "border-[#303A4A]"
                  )}
                >
                  {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#00FF9C]" />}
                </div>

                <button
                  type="button"
                  onClick={(e) => handleOpenSettings(e, profile)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[#596476] hover:text-white hover:bg-white/5 transition"
                  title="Device settings"
                >
                  <Settings size={14} />
                </button>
              </div>
            );
          })}

          {/* ERROR — same style block as LoginScreen */}
          {error && (
            <div
              className="flex items-center gap-2 text-xs font-body px-3 py-2.5 rounded-lg"
              style={{ background: "rgba(214,92,79,0.1)", color: "#D65C4F" }}
            >
              <AlertCircle size={13} />
              <span>{error}</span>
            </div>
          )}

          {/* CONTINUE — PrimaryButton, matching LoginScreen's sign-in button */}
          <PrimaryButton
            type="button"
            className={cx(
              "w-full mt-1",
              !selectedId || loading
                ? "opacity-50 cursor-not-allowed"
                : ""
            )}
            disabled={!selectedId || loading}
            onClick={handleContinue}
          >
            {loading ? (
              <>
                <Loader2
                  size={14}
                  className="animate-spin"
                />
                Continuing...
              </>
            ) : (
              <>
                <Lock size={14} />
                Continue
              </>
            )}
          </PrimaryButton>

          <button
            type="button"
            onClick={onBackToLogin}
            className="text-xs text-gray-500 hover:text-white transition text-center"
          >
            Use another account
          </button>
        </div>

        {/* FOOTER — identical to LoginScreen */}
        <p className={cx("text-xs font-body text-center mt-10", theme.faint)}>
          Authorized personnel only
        </p>
      </div>

      {/* =====================================================
          TRUSTED ACCOUNT SETTINGS MODAL — restyled to card tokens
      ===================================================== */}

      {settingsProfile && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-5 bg-black/70 backdrop-blur-sm"
          onClick={handleCloseSettings}
        >
          <div
            className={cx(
              "w-full max-w-sm rounded-2xl border p-6",
              theme.surface,
              theme.border,
              theme.shadow
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <h2 className={cx("font-display font-bold text-xl", theme.text)}>
                Trusted account
              </h2>

              <button
                type="button"
                onClick={handleCloseSettings}
                className="w-9 h-9 rounded-xl border border-[#252E3B] flex items-center justify-center text-[#8993A3] hover:text-white hover:border-[#344052] transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-2xl border border-[#252E3B] bg-[#151B25]">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-[#202938] shrink-0 flex items-center justify-center border border-[#303A4A]">
                {settingsProfile.image ? (
                  <img
                    src={settingsProfile.image}
                    alt={settingsProfile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserCircle2 size={30} className="text-[#657083]" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">
                  {settingsProfile.name}
                </p>
                <p className="text-xs text-[#8993A3] truncate mt-1">
                  {settingsProfile.email}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-[#00FF9C]/20 bg-[#00FF9C]/5 p-4">
              <p className="text-xs font-mono text-[#00FF9C]">✓ Trusted device</p>
              <p className="text-xs font-mono text-[#8993A3] mt-2">
                This account is trusted on this device for 30 days.
              </p>
            </div>

            <button
              type="button"
              disabled={loading}
              onClick={() => handleRemove(null, settingsProfile)}
              className="w-full mt-5 py-3.5 rounded-xl border border-[#FF453A] text-[#FF453A] font-display font-semibold text-sm hover:bg-[#FF453A]/10 transition disabled:opacity-50"
            >
              Remove trusted device
            </button>

            <button
              type="button"
              onClick={handleCloseSettings}
              className="w-full mt-4 text-sm text-[#8993A3] hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
/* ============================================================================
   LOGIN
============================================================================ */
function LoginScreen({ onLogin, home }) {
  const theme = useTheme();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  // ==========================================
  // 2FA STATES
  // ==========================================
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpTimeLeft, setOtpTimeLeft] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [loading, setLoading] = useState(false);
  // ==========================================
  // PASSWORD / 2FA RECOVERY
  // ==========================================
  const [recoveryMode, setRecoveryMode] = useState(null);
  // null | "password" | "2fa"

  const [recoveryStep, setRecoveryStep] = useState("email");
  // email | otp | password

  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryOtp, setRecoveryOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  // ==========================================
  // TRUSTED DEVICE STATE
  // ==========================================

  const [trustedProfiles, setTrustedProfiles] =
    useState([]);

  const [currentDevice, setCurrentDevice] =
    useState(null);

  const [showTrustedProfiles, setShowTrustedProfiles] =
    useState(true);

  const [trustedLoading, setTrustedLoading] =
    useState(true);

  const [trustedError, setTrustedError] =
    useState("");

  // ==========================================
  // LOAD TRUSTED DEVICE
  // ==========================================
  useEffect(() => {
    const loadCurrentDevice = async () => {
      try {
        const res = await getCurrentDevice();

        console.log(
          "CURRENT DEVICE:",
          res.data
        );

        if (res.data?.success) {
          setCurrentDevice(res.data);
        }
      } catch (error) {
        console.error(
          "CURRENT DEVICE ERROR:",
          error?.response?.data || error
        );
      }
    };

    loadCurrentDevice();
  }, []);
  useEffect(() => {
    if (recoveryStep !== "otp" || !otpExpiresAt) return;

    const updateOtpTimer = () => {
      const remaining = Math.max(
        0,
        Math.ceil((otpExpiresAt - Date.now()) / 1000)
      );

      setOtpTimeLeft(remaining);
    };

    updateOtpTimer();

    const timer = setInterval(updateOtpTimer, 1000);

    return () => clearInterval(timer);
  }, [recoveryStep, otpExpiresAt]);
  // ==========================================
  // OPEN PASSWORD RESET
  // ==========================================
  const openPasswordReset = () => {
    setRecoveryMode("password");
    setRecoveryStep("email");
    setRecoveryEmail(email);
    setRecoveryOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
    setRecoveryError("");
    setRecoveryMessage("");
  };


  // ==========================================
  // OPEN 2FA RESET
  // ==========================================
  // ==========================================
  // OPEN 2FA RESET
  // ==========================================

  const openTwoFactorReset = async () => {
    if (!email.trim()) {
      setError(
        "Login email is missing. Please go back and enter your email."
      );
      return;
    }

    setRecoveryLoading(true);
    setRecoveryError("");
    setRecoveryMessage("");

    try {
      // Send OTP to the SAME email entered on login
      await sendTwoFactorResetOtp(email.trim());

      // Store email for OTP verification
      setRecoveryEmail(email.trim());
      setRecoveryOtp("");
      setNewPassword("");
      setConfirmNewPassword("");

      // Reset normal 2FA login state
      setRequiresTwoFactor(false);
      setTwoFactorToken("");
      setAuthenticatorCode("");

      // START NEW 10-MINUTE OTP TIMER
      setOtpExpiresAt(Date.now() + 10 * 60 * 1000);

      // Open recovery screen directly at OTP
      setRecoveryMode("2fa");
      setRecoveryStep("otp");

      // Success message
      setRecoveryMessage(
        "A verification code has been sent to your registered email."
      );
    } catch (err) {
      console.error(
        "2FA RECOVERY OTP ERROR:",
        err?.response?.data || err
      );

      setRecoveryError(
        err?.response?.data?.message ||
        "Unable to send verification code."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  // ==========================================
  // CLOSE RECOVERY
  // ==========================================
  const closeRecovery = () => {
    setRecoveryMode(null);
    setRecoveryStep("email");
    setRecoveryEmail("");
    setRecoveryOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
    setRecoveryError("");
    setRecoveryMessage("");
    setOtpExpiresAt(null);
    setOtpTimeLeft(0);
  };


  // ==========================================
  // SEND RECOVERY OTP
  // ==========================================
  const handleSendRecoveryOtp = async () => {
    if (!recoveryEmail.trim()) {
      setRecoveryError("Enter your registered email address.");
      return;
    }

    setRecoveryLoading(true);
    setRecoveryError("");
    setRecoveryMessage("");

    try {
      if (recoveryMode === "password") {
        await sendPasswordResetOtp(recoveryEmail.trim());
      } else if (recoveryMode === "2fa") {
        await sendTwoFactorResetOtp(recoveryEmail.trim());
      }

      // START NEW 10-MINUTE OTP TIMER
      setOtpExpiresAt(Date.now() + 10 * 60 * 1000);

      // Reset OTP input
      setRecoveryOtp("");

      // Move to OTP screen
      setRecoveryStep("otp");

      // Success message
      setRecoveryMessage(
        "A verification code has been sent to your registered email."
      );
    } catch (err) {
      console.error("RECOVERY OTP ERROR:", err);

      setRecoveryError(
        err?.response?.data?.message ||
        "Unable to send verification code."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleResendRecoveryOtp = async () => {
    if (!recoveryEmail.trim()) return;

    setRecoveryLoading(true);
    setRecoveryError("");
    setRecoveryMessage("");

    try {
      if (recoveryMode === "2fa") {
        await sendTwoFactorResetOtp(recoveryEmail.trim());
      } else if (recoveryMode === "password") {
        await sendPasswordResetOtp(recoveryEmail.trim());
      }

      // Start a completely new 10-minute timer
      setOtpExpiresAt(Date.now() + 10 * 60 * 1000);

      // Clear previous OTP
      setRecoveryOtp("");

      setRecoveryMessage(
        "A new verification code has been sent to your email."
      );
    } catch (err) {
      console.error(
        "RESEND OTP ERROR:",
        err?.response?.data || err
      );

      setRecoveryError(
        err?.response?.data?.message ||
        "Unable to resend verification code."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };
  // ==========================================
  // VERIFY RECOVERY OTP
  // ==========================================
  const handleVerifyRecoveryOtp = async () => {
    if (!/^\d{6}$/.test(recoveryOtp)) {
      setRecoveryError("Enter the 6-digit verification code.");
      return;
    }

    setRecoveryLoading(true);
    setRecoveryError("");
    setRecoveryMessage("");

    try {
      if (recoveryMode === "password") {
        await verifyPasswordResetOtp(
          recoveryEmail.trim(),
          recoveryOtp
        );

        setRecoveryStep("password");
        setRecoveryMessage("OTP verified. Set your new password.");
      } else {
        await resetTwoFactor(
          recoveryEmail.trim(),
          recoveryOtp
        );

        setRecoveryMessage(
          "Two-factor authentication has been reset successfully."
        );

        setTimeout(() => {
          closeRecovery();
        }, 1500);
      }
    } catch (err) {
      console.error("RECOVERY OTP VERIFY ERROR:", err);

      setRecoveryError(
        err?.response?.data?.message ||
        "Invalid or expired verification code."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };


  // ==========================================
  // RESET PASSWORD
  // ==========================================
  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setRecoveryError(
        "Password must contain at least 8 characters."
      );
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setRecoveryError("Passwords do not match.");
      return;
    }

    setRecoveryLoading(true);
    setRecoveryError("");
    setRecoveryMessage("");

    try {
      await resetPassword(
        recoveryEmail.trim(),
        recoveryOtp,
        newPassword,
        confirmNewPassword
      );

      setRecoveryMessage(
        "Password reset successfully. You can now login."
      );

      setTimeout(() => {
        closeRecovery();
        setEmail(recoveryEmail);
        setPassword("");
      }, 1500);
    } catch (err) {
      console.error("PASSWORD RESET ERROR:", err);

      setRecoveryError(
        err?.response?.data?.message ||
        "Failed to reset password."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };
  // ==========================================
  // NORMAL LOGIN
  // ==========================================
  const submit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      const res = await login({
        email,
        password,
        turnstileToken,
        trustDevice
      });

      // ==========================================
      // 2FA REQUIRED
      // ==========================================
      if (res.data?.requiresTwoFactor) {
        if (!res.data?.twoFactorToken) {
          setError("2FA token was not received from server.");
          return;
        }

        setTwoFactorToken(res.data.twoFactorToken);
        setRequiresTwoFactor(true);
        setAuthenticatorCode("");

        return;
      }

      // ==========================================
      // NORMAL LOGIN WITHOUT 2FA
      // ==========================================
      if (!res.data?.token) {
        setError("Login token was not received.");
        return;
      }

      localStorage.setItem(
        "adminToken",
        res.data.token
      );

      localStorage.setItem(
        "adminUser",
        JSON.stringify(res.data.user)
      );

      onLogin();

    } catch (err) {
      console.error("LOGIN ERROR:", err);

      setError(
        err?.response?.data?.message ||
        "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // VERIFY AUTHENTICATOR CODE
  // ==========================================
  const verifyTwoFactor = async (e) => {
    e.preventDefault();

    setError("");

    if (!authenticatorCode) {
      setError("Enter the authenticator code.");
      return;
    }

    if (authenticatorCode.length !== 6) {
      setError(
        "Authenticator code must contain 6 digits."
      );
      return;
    }

    if (!twoFactorToken) {
      setError(
        "2FA session expired. Please login again."
      );
      return;
    }

    setLoading(true);

    try {
      // ==========================================
      // VERIFY 2FA
      // ==========================================

      const res = await verifyLoginTwoFactor(
        twoFactorToken,
        authenticatorCode,
        trustDevice
      );

      // ==========================================
      // BACKEND ERROR
      // ==========================================

      if (!res.data?.success || !res.data?.token) {
        setError(
          res.data?.message ||
          "Two-factor authentication failed."
        );
        return;
      }

      // ==========================================
      // SAVE FINAL JWT
      // ==========================================

      localStorage.setItem(
        "adminToken",
        res.data.token
      );

      // ==========================================
      // SAVE USER
      // ==========================================

      if (res.data.user) {
        localStorage.setItem(
          "adminUser",
          JSON.stringify(res.data.user)
        );
      }

      // ==========================================
      // CLEAR 2FA STATE
      // ==========================================

      setTwoFactorToken("");
      setAuthenticatorCode("");
      setRequiresTwoFactor(false);

      // ==========================================
      // LOGIN COMPLETE
      // ==========================================

      onLogin();

    } catch (err) {
      console.error(
        "2FA LOGIN ERROR:",
        err
      );

      setError(
        err?.response?.data?.message ||
        "Invalid authenticator code."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // BACK TO LOGIN
  // ==========================================
  const backToLogin = () => {
    setRequiresTwoFactor(false);
    setTwoFactorToken("");
    setAuthenticatorCode("");
    setError("");
  };

  // ==========================================
  // 2FA SCREEN
  // ==========================================
  // ==========================================
  // TWO-FACTOR AUTHENTICATION SCREEN
  // ==========================================
  if (requiresTwoFactor) {
    return (
      <div
        className={cx(
          "min-h-screen flex items-center justify-center px-5",
          theme.bg
        )}
      >
        <div className="w-full max-w-sm anim-fadeUp">

          {/* LOGO + TITLE */}
          <div className="flex flex-col items-center text-center mb-8">

            <span className="w-20 h-auto rounded-2xl flex items-center justify-center mb-4 p-2">
              <img
                src={home?.headerLogo}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </span>

            <h1
              className={cx(
                "font-display font-bold text-2xl",
                theme.text
              )}
            >
              Two-Factor Authentication
            </h1>

            <p
              className={cx(
                "font-body text-sm mt-2",
                theme.subtext
              )}
            >
              Enter the 6-digit code from your
              Google Authenticator or Microsoft Authenticator.
            </p>

            {/* TRUST DEVICE STATUS */}
            {trustDevice && (
              <div className="flex items-center gap-2 mt-4 px-3 py-2 rounded-lg border border-[#00FF9C]/20 bg-[#00FF9C]/5">
                <Monitor
                  size={13}
                  className="text-[#00FF9C]"
                />

                <span className="text-[11px] font-mono text-[#00FF9C]">
                  This device will be trusted for 30 days
                </span>
              </div>
            )}

          </div>

          {/* 2FA FORM */}
          <form
            onSubmit={verifyTwoFactor}
            className={cx(
              "rounded-2xl border p-6 flex flex-col gap-4",
              theme.surface,
              theme.border,
              theme.shadow
            )}
          >

            {/* AUTHENTICATOR CODE */}
            <Field
              label="Authenticator Code"
              required
              hint="Open your authenticator app and enter the current 6-digit code."
            >
              <TextInput
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={authenticatorCode}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 6);

                  setAuthenticatorCode(value);
                  setError("");
                }}
                className="text-center tracking-[0.5em] text-lg"
                autoFocus
              />
            </Field>

            {/* ERROR */}
            {error && (
              <div
                className="flex items-center gap-2 text-xs font-body px-3 py-2.5 rounded-lg"
                style={{
                  background: "rgba(214,92,79,0.1)",
                  color: "#D65C4F",
                }}
              >
                <AlertCircle size={13} />
                <span>{error}</span>
              </div>
            )}

            {/* VERIFY */}
            <PrimaryButton
              type="submit"
              className="w-full mt-1"
              disabled={
                loading ||
                authenticatorCode.length !== 6
              }
            >
              {loading ? (
                <>
                  <Loader2
                    size={14}
                    className="animate-spin"
                  />
                  Verifying...
                </>
              ) : (
                <>
                  <ShieldCheck size={14} />
                  Verify & Sign In
                </>
              )}
            </PrimaryButton>

            {/* BACK */}
            <div className="flex items-center justify-between mt-2">
              <button
                type="button"
                onClick={backToLogin}
                disabled={loading}
                className="
    group inline-flex items-center gap-1.5
    text-xs font-medium
    text-slate-500
    hover:text-blue-400
    transition-all duration-200
    disabled:opacity-50
    disabled:cursor-not-allowed
  "
              >
                <ArrowLeft
                  size={14}
                  className="
      transition-transform duration-200
      group-hover:-translate-x-0.5
    "
                />

                <span className="relative">
                  Back to login
                  <span
                    className="
absolute left-0 -bottom-0.5
        h-px w-0
        bg-blue-400
        group-hover:w-full
        transition-all duration-200
      "
                  />
                </span>
              </button>

              <button
                type="button"
                onClick={openTwoFactorReset}
                disabled={recoveryLoading}
                className="
    group inline-flex items-center gap-1.5
    text-xs font-medium
    text-slate-500
    hover:text-blue-400
    transition-all duration-200
    disabled:opacity-50
    disabled:cursor-not-allowed
  "
              >
                <icons.ShieldAlert
                  size={14}
                  className="
      text-slate-500
      group-hover:text-blue-400
      transition-colors duration-200
    "
                />

                <span className="relative">
                  {recoveryLoading ? "Sending..." : "Lost authenticator?"}
                </span>
              </button>
            </div>

          </form>

          {/* FOOTER */}
          <p
            className={cx(
              "text-xs font-body text-center mt-10",
              theme.faint
            )}
          >
            © 2026 Vinayak V Thayil —
            Authorized personnel only
          </p>

        </div>
      </div>
    );
  }

  // ==========================================
  // PASSWORD / 2FA RECOVERY SCREEN
  // ==========================================
  if (recoveryMode) {
    return (
      <div
        className={cx(
          "min-h-screen flex items-center justify-center px-5",
          theme.bg
        )}
      >
        <div className="w-full max-w-sm anim-fadeUp">

          {/* HEADER */}
          <div className="flex flex-col items-center text-center mb-8">

            <span className="w-20 h-auto rounded-2xl flex items-center justify-center mb-4 p-2">
              <img
                src={home?.headerLogo}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </span>

            <h1
              className={cx(
                "font-display font-bold text-2xl",
                theme.text
              )}
            >
              {recoveryMode === "password"
                ? "Reset Password"
                : "Reset Two-Factor Authentication"}
            </h1>

            <p
              className={cx(
                "font-body text-sm mt-2",
                theme.subtext
              )}
            >
              {recoveryStep === "email" &&
                "Enter your registered email address."}

              {recoveryStep === "otp" &&
                "Enter the verification code sent to your email."}

              {recoveryStep === "password" &&
                "Create a new password for your account."}
            </p>

          </div>


          {/* CARD */}
          <div
            className={cx(
              "rounded-2xl border p-6",
              theme.surface,
              theme.border,
              theme.shadow
            )}
          >

            {/* EMAIL STEP */}
            {recoveryStep === "email" && (
              <div className="flex flex-col gap-4">

                <Field
                  label="Registered Email"
                  required
                >
                  <TextInput
                    type="email"
                    placeholder="admin@example.com"
                    value={recoveryEmail}
                    onChange={(e) => {
                      setRecoveryEmail(e.target.value);
                      setRecoveryError("");
                    }}
                    autoFocus
                  />
                </Field>

                {recoveryError && (
                  <div className="text-xs text-red-400">
                    {recoveryError}
                  </div>
                )}

                {recoveryMessage && (
                  <div className="text-xs text-green-400">
                    {recoveryMessage}
                  </div>
                )}

                <PrimaryButton
                  type="button"
                  className="w-full"
                  disabled={recoveryLoading}
                  onClick={handleSendRecoveryOtp}
                >
                  {recoveryLoading ? (
                    <>
                      <Loader2
                        size={15}
                        className="animate-spin"
                      />
                      Sending...
                    </>
                  ) : (
                    "Send Verification Code"
                  )}
                </PrimaryButton>

              </div>
            )}


            {/* OTP STEP */}
            {recoveryStep === "otp" && (
              <div className="flex flex-col gap-4">

                <Field
                  label="Verification Code"
                  required
                  hint="Enter the 6-digit code sent to your email."
                >
                  <TextInput
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={recoveryOtp}
                    onChange={(e) => {
                      setRecoveryOtp(
                        e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6)
                      );
                      setRecoveryError("");
                    }}
                    className="text-center tracking-[0.5em] text-lg"
                    autoFocus
                  />
                </Field>
                <p
                  className={cx(
                    "text-xs text-center -mt-2",
                    otpTimeLeft > 60
                      ? "text-slate-500"
                      : "text-red-400"
                  )}
                >
                  {otpTimeLeft > 0
                    ? `OTP expires in ${String(
                      Math.floor(otpTimeLeft / 60)
                    ).padStart(2, "0")}:${String(
                      otpTimeLeft % 60
                    ).padStart(2, "0")}`
                    : "OTP has expired."}
                </p>

                {otpTimeLeft === 0 && (
                  <button
                    type="button"
                    onClick={handleResendRecoveryOtp}
                    disabled={recoveryLoading}
                    className="
      text-xs font-medium
      text-blue-400
      hover:text-blue-300
      transition
      disabled:opacity-50
      disabled:cursor-not-allowed
    "
                  >
                    {recoveryLoading ? "Sending..." : "Resend Code"}
                  </button>
                )}
                {recoveryError && (
                  <div className="text-xs text-red-400">
                    {recoveryError}
                  </div>
                )}

                {recoveryMessage && (
                  <div className="text-xs text-green-400">
                    {recoveryMessage}
                  </div>
                )}

                <PrimaryButton
                  type="button"
                  className="w-full"
                  disabled={
                    recoveryLoading ||
                    recoveryOtp.length !== 6
                  }
                  onClick={handleVerifyRecoveryOtp}
                >
                  {recoveryLoading ? (
                    <>
                      <Loader2
                        size={15}
                        className="animate-spin"
                      />
                      Verifying...
                    </>
                  ) : (
                    "Verify Code"
                  )}
                </PrimaryButton>

                {recoveryMode === "password" && (
                  <button
                    type="button"
                    onClick={() => {
                      setRecoveryStep("email");
                      setRecoveryOtp("");
                      setRecoveryError("");
                      setRecoveryMessage("");
                    }}
                    className="text-xs text-gray-500 hover:text-white transition"
                  >
                    Change email
                  </button>
                )}

              </div>
            )}


            {/* NEW PASSWORD STEP */}
            {recoveryStep === "password" && (
              <div className="flex flex-col gap-4">

                <Field label="New Password" required>
                  <div className="relative">
                    <TextInput
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setRecoveryError("");
                      }}
                      autoComplete="new-password"
                      autoFocus
                      className="pr-10"
                    />

                    <button
                      type="button"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      className="
            absolute right-3 top-1/2 -translate-y-1/2
            text-slate-500
            hover:text-blue-400
            transition-colors
          "
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? (
                        <EyeOff size={17} />
                      ) : (
                        <Eye size={17} />
                      )}
                    </button>
                  </div>
                </Field>

                <Field label="Confirm New Password" required>
                  <div className="relative">
                    <TextInput
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => {
                        setConfirmNewPassword(e.target.value);
                        setRecoveryError("");
                      }}
                      autoComplete="new-password"
                      className="pr-10"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword((prev) => !prev)
                      }
                      className="
            absolute right-3 top-1/2 -translate-y-1/2
            text-slate-500
            hover:text-blue-400
            transition-colors
          "
                      aria-label={
                        showConfirmPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={17} />
                      ) : (
                        <Eye size={17} />
                      )}
                    </button>
                  </div>
                </Field>

                {recoveryError && (
                  <div className="text-xs text-red-400">
                    {recoveryError}
                  </div>
                )}

                {recoveryMessage && (
                  <div className="text-xs text-green-400">
                    {recoveryMessage}
                  </div>
                )}

                <PrimaryButton
                  type="button"
                  className="w-full"
                  disabled={recoveryLoading}
                  onClick={handleResetPassword}
                >
                  {recoveryLoading ? (
                    <>
                      <Loader2
                        size={15}
                        className="animate-spin"
                      />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Lock size={15} />
                      Reset Password
                    </>
                  )}
                </PrimaryButton>

              </div>
            )}


            {/* BACK */}
            <button
              type="button"
              onClick={closeRecovery}
              disabled={recoveryLoading}
              className="w-full mt-5 text-xs text-gray-500 hover:text-white transition"
            >
              ← Back to login
            </button>

          </div>

          <p
            className={cx(
              "text-xs font-body text-center mt-10",
              theme.faint
            )}
          >
            Authorized personnel only
          </p>

        </div>
      </div>
    );
  }
  // ==========================================
  // NORMAL LOGIN SCREEN
  // ==========================================
  return (
    <div
      className={cx(
        "min-h-screen flex items-center justify-center px-5",
        theme.bg
      )}
    >
      <div className="w-full max-w-sm anim-fadeUp">

        {/* LOGO + TITLE */}
        <div className="flex flex-col items-center text-center mb-8">

          <span className="w-20 h-auto rounded-2xl flex items-center justify-center mb-4 p-2">
            <img
              src={home?.headerLogo}
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </span>

          <h1
            className={cx(
              "font-display font-bold text-2xl",
              theme.text
            )}
          >
            Welcome back
          </h1>

          <p
            className={cx(
              "font-body text-sm mt-1",
              theme.subtext
            )}
          >
            Sign in to manage your portfolio
          </p>

        </div>


        {/* =====================================================
          LOGIN FORM
      ===================================================== */}

        <form
          onSubmit={submit}
          className={cx(
            "rounded-2xl border p-6 flex flex-col gap-4",
            theme.surface,
            theme.border,
            theme.shadow
          )}
        >

          {/* EMAIL */}
          <Field
            label="Email"
            required
          >
            <TextInput
              type="email"
              placeholder="admin@syncpulsesignal.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              autoComplete="username"
            />
          </Field>


          {/* PASSWORD */}
          <Field
            label="Password"
            required
          >
            <div className="relative">

              <TextInput
                type={
                  showPw
                    ? "text"
                    : "password"
                }
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(
                    e.target.value
                  );
                  setError("");
                }}
                autoComplete="current-password"
              />

              <button
                type="button"
                onClick={() =>
                  setShowPw(!showPw)
                }
                className="absolute right-3.5 top-1/2 -translate-y-1/2 opacity-50 text-[#8B93A0]"
              >
                {showPw ? (
                  <EyeOff size={15} />
                ) : (
                  <Eye size={15} />
                )}
              </button>

            </div>
          </Field>


          {/* =====================================================
            TRUST THIS DEVICE
        ===================================================== */}

          <label
            className={cx(
              "flex items-center gap-4 p-4 rounded-2xl border cursor-pointer transition-all",
              trustDevice
                ? "border-[#00FF9C] bg-[#00FF9C]/5"
                : "border-[#252E3B] bg-[#151B25] hover:border-[#344052]"
            )}
          >

            {/* DEVICE ICON */}
            <div className="w-12 h-12 rounded-full bg-[#FF4B35] flex items-center justify-center shrink-0">
              <Monitor
                size={22}
                className="text-white"
              />
            </div>


            {/* DEVICE INFORMATION */}
            <div className="flex-1 min-w-0">

              <p className="text-sm font-semibold text-white">
                TRUST THIS DEVICE
              </p>

              <p className="text-xs font-mono text-[#8993A3] mt-1 truncate">
                {currentDevice?.deviceName}
              </p>

              <p className="text-[11px] font-mono text-[#8993A3] mt-1">
                DON'T ASK AGAIN FOR 30 DAYS
              </p>

            </div>


            {/* CHECKBOX */}
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) =>
                setTrustDevice(
                  e.target.checked
                )
              }
              onClick={(e) =>
                e.stopPropagation()
              }
              className="w-6 h-6 rounded border-[#596476] bg-transparent accent-[#00FF9C] cursor-pointer shrink-0"
            />

          </label>


          {/* ERROR */}
          {error && (
            <div
              className="flex items-center gap-2 text-xs font-body px-3 py-2.5 rounded-lg"
              style={{
                background:
                  "rgba(214,92,79,0.1)",
                color: "#D65C4F",
              }}
            >
              <AlertCircle size={13} />
              <span>{error}</span>
            </div>
          )}


          {/* TURNSTILE */}
          <div className="flex justify-center items-center w-full mt-2 mb-2">

            <Turnstile
              siteKey={
                process.env
                  .REACT_APP_TURNSTILE_SITE_KEY
              }
              onSuccess={(token) => {
                setTurnstileToken(
                  token
                );
                setError("");
              }}
              onExpire={() => {
                setTurnstileToken("");
              }}
              onError={() => {
                setTurnstileToken("");
              }}
            />

          </div>


          {/* SIGN IN */}
          <PrimaryButton
            type="submit"
            className={cx(
              "w-full mt-1",
              (!turnstileToken || loading) &&
              "opacity-50 cursor-not-allowed"
            )}
            disabled={
              !turnstileToken ||
              loading
            }
          >
            {loading ? (
              <>
                <Loader2
                  size={14}
                  className="animate-spin"
                />
                Signing In...
              </>
            ) : (
              <>
                <Lock size={14} />
                Sign In
              </>
            )}
          </PrimaryButton>
          <div className="flex items-center justify-center mt-3">
            <button
              type="button"
              onClick={openPasswordReset}
              className="
      group
      inline-flex items-center gap-2
      px-3 py-1.5
      rounded-lg
      text-xs font-semibold
      text-slate-400
      bg-slate-900/70
      border border-slate-700/70
      hover:bg-blue-500/10
      hover:border-blue-500/40
      hover:text-blue-400
      transition-all duration-200
    "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                className="w-3.5 h-3.5 transition-transform duration-200 group-hover:-rotate-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 5.25a3.75 3.75 0 0 0-7.5 0V9h7.5V5.25Z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 9h10.5A1.5 1.5 0 0 1 18.75 10.5v7.5a1.5 1.5 0 0 1-1.5 1.5h-10.5a1.5 1.5 0 0 1-1.5-1.5v-7.5A1.5 1.5 0 0 1 6.75 9Z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 13v2"
                />
              </svg>

              <span>Forgot password?</span>
            </button>
          </div>
        </form>


        {/* FOOTER */}
        <p
          className={cx(
            "text-xs font-body text-center mt-10",
            theme.faint
          )}
        >
          © 2026 Vinayak V Thayil —
          Authorized personnel only
        </p>

      </div>
    </div>
  );
}

/* ============================================================================
   ADMIN SHELL: sidebar + header + footer (fixed)
============================================================================ */
const NAV_ITEMS = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["header", "Header", Menu],
  ["home", "Home", icons.FaHome],
  ["about", "About", User],
  ["blog", "Blog", icons.FaBlog],
  ["experience", "Experience", Briefcase],
  ["projects", "Projects", FolderKanban],
  ["publications", "Publications", BookOpen],
  ["community", "Community", Users],
  ["service", "Services", icons.FaTools],
  ["youtube", "YouTube", icons.FaYoutube],
  ["contact", "Contact", Mail],
  ["footer", "Footer", Edit],
  ["users", "Users", Users],
];

function Sidebar({
  page,
  setPage,
  open,
  setOpen,
  onLogout,
  home,
  currentUser
}) {
  const theme = useTheme();

  const [sidebarSearch, setSidebarSearch] = useState("");

  const userRole = currentUser?.role || "User";

  const userPermissions = Array.isArray(currentUser?.permissions)
    ? currentUser.permissions
    : [];

  const allowedNavItems =
    userRole === "Admin"
      ? NAV_ITEMS
      : NAV_ITEMS.filter(([id]) =>
        userPermissions.includes(id)
      );

  const filteredNavItems = allowedNavItems.filter(
    ([id, label]) =>
      label.toLowerCase().includes(sidebarSearch.toLowerCase())
  );

  return (
    <>
      {/* Mobile Overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cx(
          "fixed top-0 left-0 h-screen w-64 border-r flex flex-col z-40 transition-transform duration-300",
          theme.surface,
          theme.border,
          open
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* =========================
            LOGO HEADER
        ========================== */}
        <div
          className={cx(
            "relative h-20 flex items-center justify-center px-5 border-b shrink-0",
            "bg-gradient-to-r from-[#0F172A] via-[#111827] to-[#1E293B]",
            "border-[#232B38]"
          )}
        >
          <img
            src={home?.headerLogo}
            alt="Logo"
            className="h-20 w-auto object-contain drop-shadow-lg"
          />

          {/* Mobile Close */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="
              absolute right-5 lg:hidden
              w-10 h-10 rounded-full
              bg-white/10 backdrop-blur-md
              border border-white/10
              flex items-center justify-center
              text-gray-300 hover:text-white
              hover:bg-blue-600 hover:scale-110
              transition-all duration-300
              shadow-lg
            "
          >
            <X size={18} />
          </button>
        </div>

        {/* =========================
            SEARCH
        ========================== */}
        <div className="px-3 pt-3 pb-2 shrink-0">
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              placeholder="Search menu..."
              className={cx(
                "w-full pl-9 pr-3 py-2.5 rounded-xl border",
                "bg-white/[0.03] text-sm outline-none",
                "placeholder:text-gray-500",
                "focus:border-blue-500/50",
                theme.border,
                theme.text
              )}
            />
          </div>
        </div>

        {/* =========================
            MENU TITLE + COUNT
        ========================== */}
        <div className="px-4 pb-1 flex items-center justify-between shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">
            Menu
          </span>

          <span
            className="
              min-w-6 h-5 px-1.5
              rounded-full
              bg-white/10
              flex items-center justify-center
              text-[10px] font-semibold
              text-gray-400
            "
          >
            {allowedNavItems.length}
          </span>
        </div>

        {/* =========================
            NAVIGATION
        ========================== */}
        <nav className="flex-1 overflow-y-auto py-2 px-3 flex flex-col gap-1">
          {filteredNavItems.length > 0 ? (
            filteredNavItems.map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setPage(id);
                  setOpen(false);
                }}
                className={cx(
                  "flex items-center gap-3 px-3.5 py-2.5 rounded-xl",
                  "text-sm font-medium font-body transition text-left",
                  page === id
                    ? "text-white"
                    : "text-gray-300 hover:bg-white/5"
                )}
                style={
                  page === id
                    ? { background: "#2563EB" }
                    : {}
                }
              >
                <Icon size={16} />
                <span className="truncate">{label}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-6 text-center">
              <Search
                size={20}
                className="mx-auto mb-2 text-gray-600"
              />

              <p className="text-xs text-gray-500">
                No menu found
              </p>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
}
function AdminHeader({
  page,
  setOpen,
  about,
  home,
  onLogout,
  onProfileClick,
  setPage
}) {
  const theme = useTheme();

  const label =
    NAV_ITEMS.find(([id]) => id === page)?.[1] || "Dashboard";

  const [currentUser, setCurrentUser] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  // ==========================================
  // LOAD CURRENT USER
  // ==========================================

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("adminUser");

      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error("Failed to load admin user:", error);
    }
  }, []);

  // ==========================================
  // CLOSE PROFILE WHEN CLICKING OUTSIDE
  // ==========================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileOpen &&
        !event.target.closest(".admin-profile-menu")
      ) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [profileOpen]);

  // ==========================================
  // USER DETAILS
  // ==========================================

  const userName =
    currentUser?.name ||
    currentUser?.username ||
    "Administrator";

  const userEmail =
    currentUser?.email ||
    "Administrator";

  const userRole =
    currentUser?.role ||
    "Administrator";

  // ==========================================
  // AVATAR
  // ==========================================

  const userAvatar =
    currentUser?.avatar ||
    currentUser?.avatarUrl ||
    currentUser?.profileImage ||
    currentUser?.profileImageUrl ||
    currentUser?.profilePicture ||
    currentUser?.photo ||
    currentUser?.image ||
    "";

  // ==========================================
  // INITIALS
  // ==========================================

  const initials = userName
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // ==========================================
  // AVATAR COMPONENT
  // ==========================================

  const Avatar = ({
    size = "w-9 h-9",
    textSize = "text-[11px]",
  }) => (
    <div
      className={cx(
        size,
        "rounded-full overflow-hidden",
        "flex items-center justify-center shrink-0",
        "bg-gradient-to-br",
        "from-[#2563EB]",
        "via-[#3B82F6]",
        "to-[#06B6D4]"
      )}
    >
      {userAvatar ? (
        <img
          src={userAvatar}
          alt={userName}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span
          className={cx(
            "text-white font-bold font-display",
            textSize
          )}
        >
          {initials}
        </span>
      )}
    </div>
  );

  return (
    <header
      className={cx(
        "fixed top-0 left-0 lg:left-64 right-0",
        "h-[72px]",
        "flex items-center justify-between",
        "px-4 sm:px-7",
        "z-30",
        "border-b",
        "backdrop-blur-xl",
        theme.surface,
        theme.border
      )}
    >

      {/* =====================================================
          LEFT SIDE
      ===================================================== */}

      <div className="flex items-center gap-3.5 min-w-0">

        {/* MOBILE MENU */}

        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cx(
            "lg:hidden",
            "w-9 h-9",
            "rounded-xl",
            "flex items-center justify-center",
            "border",
            "transition-all duration-200",
            theme.border,
            theme.text,
            "hover:bg-[#172554]",
            "hover:text-[#60A5FA]",
            "hover:border-[#2563EB]"
          )}
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>


        {/* PAGE TITLE */}

        <div className="min-w-0">

          <div
            className={cx(
              "flex items-center gap-1.5",
              "text-[10px]",
              "font-medium",
              "uppercase",
              "tracking-[0.14em]",
              theme.faint
            )}
          >
            <span>Admin</span>

            <ChevronRight
              size={11}
              className="opacity-40"
            />

            <span
              className={cx(
                "truncate",
                theme.subtext
              )}
            >
              {label}
            </span>
          </div>


          <h1
            className={cx(
              "font-display",
              "font-semibold",
              "text-[18px]",
              "tracking-tight",
              "leading-tight",
              theme.text
            )}
          >
            {label}
          </h1>

        </div>

      </div>


      {/* =====================================================
          RIGHT SIDE
      ===================================================== */}

      <div className="relative admin-profile-menu">

        {/* ===================================================
            PROFILE BUTTON
        =================================================== */}

        <button
          type="button"
          onClick={() =>
            setProfileOpen((prev) => !prev)
          }
          className={cx(
            "group",
            "flex items-center gap-2.5",
            "px-2.5 py-1.5",
            "rounded-xl",

            // ------------------------------------------
            // GLASS / BORDER
            // ------------------------------------------

            "border",
            "border-white/[0.08]",
            "bg-white/[0.025]",
            "backdrop-blur-xl",
            "backdrop-saturate-150",

            // ------------------------------------------
            // SMOOTH TRANSITION
            // ------------------------------------------

            "transition-all",
            "duration-300",
            "ease-out",
            "cursor-pointer",

            // ------------------------------------------
            // HOVER
            // ------------------------------------------

            "hover:bg-[#2563EB]/[0.10]",
            "hover:border-[#3B82F6]/70",
            "hover:shadow-[0_0_0_1px_rgba(59,130,246,0.12),0_8px_25px_rgba(37,99,235,0.16)]",

            // ------------------------------------------
            // ACTIVE / OPEN
            // ------------------------------------------

            profileOpen
              ? [
                "bg-[#2563EB]/[0.12]",
                "border-[#3B82F6]/80",
                "shadow-[0_0_0_1px_rgba(59,130,246,0.15),0_8px_25px_rgba(37,99,235,0.20)]",
              ]
              : []
          )}
          aria-label="Open profile menu"
          aria-expanded={profileOpen}
        >

          {/* AVATAR */}

          <div className="relative shrink-0">

            <div
              className="
                rounded-full
                p-[1.5px]
                bg-gradient-to-br
                from-[#2563EB]
                via-[#3B82F6]
                to-[#06B6D4]
                shadow-[0_2px_8px_rgba(37,99,235,0.25)]
              "
            >

              <div
                className="
                  rounded-full
                  p-[1px]
                  bg-[#0F172A]
                "
              >

                <Avatar
                  size="w-9 h-9"
                  textSize="text-[11px]"
                />

              </div>

            </div>


            {/* ONLINE STATUS */}

            <span
              className="
                absolute
                bottom-0
                right-0
                w-2.5
                h-2.5
                rounded-full
                bg-[#10B981]
                border-2
                border-[#0F172A]
              "
            />

          </div>


          {/* USER INFORMATION */}

          <div className="hidden sm:block text-left w-[170px]">

            {/* NAME */}

            <div
              className="
                text-[13px]
                font-semibold
                truncate
                leading-tight
                text-[#F8FAFC]
              "
            >
              {userName}
            </div>


            {/* EMAIL */}

            <div
              className="
                text-[10px]
                truncate
                mt-0.5
                text-[#94A3B8]
              "
            >
              {userEmail}
            </div>


            {/* ROLE + STATUS */}

            <div className="flex items-center gap-1.5 mt-1">

              <span
                className="
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.12em]
                  text-[#5EEAD4]
                  truncate
                  max-w-[90px]
                "
              >
                {userRole}
              </span>
            </div>

          </div>


          {/* ARROW */}

          <ChevronDown
            size={14}
            className={cx(
              "shrink-0",
              "transition-transform duration-200",

              profileOpen
                ? "rotate-180 text-[#60A5FA]"
                : "text-[#94A3B8]"
            )}
          />

        </button>


        {/* ===================================================
            PROFILE DROPDOWN
        =================================================== */}

        {profileOpen && (

          <div
            className="
              absolute
              right-0
              top-[calc(100%+9px)]
              w-[320px]
              max-w-[calc(100vw-1rem)]

              rounded-[18px]
              overflow-hidden
              z-50

              bg-[#0F172A]
              border
              border-[#334155]

              shadow-[0_20px_55px_rgba(0,0,0,0.40)]

              animate-in
              fade-in
              slide-in-from-top-2
              duration-200
            "
          >

            {/* =================================================
                PROFILE HEADER
            ================================================= */}

            <div
              className="
                relative
                px-4
                py-4
                overflow-hidden

                bg-gradient-to-br
                from-[#172554]
                via-[#0F2A43]
                to-[#0F172A]

                border-b
                border-[#1E3A5F]
              "
            >

              {/* BLUE GLOW */}

              <div
                className="
                  absolute
                  -top-10
                  -right-8
                  w-28
                  h-28
                  rounded-full
                  bg-[#2563EB]/20
                  blur-2xl
                  pointer-events-none
                "
              />


              {/* CYAN GLOW */}

              <div
                className="
                  absolute
                  -bottom-12
                  left-10
                  w-24
                  h-24
                  rounded-full
                  bg-[#06B6D4]/10
                  blur-2xl
                  pointer-events-none
                "
              />


              {/* DECORATIVE DOT */}

              <div
                className="
                  absolute
                  top-5
                  right-7
                  w-1.5
                  h-1.5
                  rounded-full
                  bg-[#22D3EE]/60
                "
              />


              {/* PROFILE CONTENT */}

              <div className="relative flex items-center gap-3">

                {/* AVATAR */}

                <div className="relative shrink-0">

                  <div
                    className="
                      rounded-full
                      p-[2px]

                      bg-gradient-to-br
                      from-[#2563EB]
                      via-[#3B82F6]
                      to-[#06B6D4]

                      shadow-[0_4px_15px_rgba(37,99,235,0.30)]
                    "
                  >

                    <div
                      className="
                        rounded-full
                        bg-[#0F172A]
                        p-[2px]
                      "
                    >

                      <Avatar
                        size="w-12 h-12"
                        textSize="text-sm"
                      />

                    </div>

                  </div>


                  {/* ACTIVE */}

                  <span
                    className="
                      absolute
                      bottom-0
                      right-0
                      w-3
                      h-3
                      rounded-full
                      bg-[#10B981]
                      border-2
                      border-[#0F172A]
                    "
                  />

                </div>


                {/* DETAILS */}

                <div className="min-w-0 flex-1">

                  <div
                    className="
                      text-[15px]
                      font-bold
                      truncate
                      text-[#F8FAFC]
                    "
                  >
                    {userName}
                  </div>


                  <div
                    className="
                      text-[10px]
                      truncate
                      mt-1
                      text-[#94A3B8]
                    "
                  >
                    {userEmail}
                  </div>


                  <div className="flex items-center gap-2 mt-1.5">

                    <span
                      className="
                        inline-flex
                        items-center
                        px-2
                        py-0.5
                        rounded-md

                        bg-[#0C4A6E]
                        text-[#67E8F9]

                        border
                        border-[#155E75]

                        text-[8px]
                        font-bold
                        uppercase
                        tracking-wider
                      "
                    >
                      {userRole}
                    </span>


                    <span
                      className="
                        flex
                        items-center
                        gap-1
                        text-[9px]
                        font-semibold
                        text-[#34D399]
                      "
                    >

                      <span
                        className="
                          w-1.5
                          h-1.5
                          rounded-full
                          bg-[#34D399]
                        "
                      />

                      Active

                    </span>

                  </div>

                </div>

              </div>

            </div>


            {/* =================================================
                MENU
            ================================================= */}

            <div
              className="
                p-1.5
                bg-[#0F172A]
              "
            >

              {/* =================================================
                  EDIT PROFILE
              ================================================= */}

              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false);

                  if (onProfileClick) {
                    onProfileClick();
                  }
                }}
                className="
                  group
                  w-full
                  flex
                  items-center
                  gap-3
                  px-3
                  py-2.5
                  rounded-xl
                  text-left

                  bg-[#0F172A]

                  border
                  border-transparent

                  transition-all
                  duration-200

                  hover:bg-[#172554]
                  hover:border-[#1D4ED8]

                  hover:shadow-[0_4px_16px_rgba(37,99,235,0.16)]
                "
              >

                {/* ICON */}

                <div
                  className="
                    w-9
                    h-9
                    rounded-lg
                    flex
                    items-center
                    justify-center

                    bg-[#172554]
                    text-[#60A5FA]

                    border
                    border-[#1E40AF]

                    transition-all
                    duration-200

                    group-hover:bg-[#2563EB]
                    group-hover:text-white
                    group-hover:border-[#3B82F6]

                    group-hover:scale-105

                    group-hover:shadow-[0_4px_12px_rgba(37,99,235,0.30)]
                  "
                >
                  <icons.UserRound
                    size={16}
                    strokeWidth={1.8}
                  />
                </div>


                {/* TEXT */}

                <div className="flex-1 min-w-0">

                  <div
                    className="
                      text-[13px]
                      font-semibold

                      text-[#E2E8F0]

                      transition-colors
                      duration-200

                      group-hover:text-[#60A5FA]
                    "
                  >
                    Edit Profile
                  </div>


                  <div
                    className="
                      text-[10px]
                      mt-0.5

                      text-[#94A3B8]

                      transition-colors
                      duration-200

                      group-hover:text-[#CBD5E1]
                    "
                  >
                    Update your account details
                  </div>

                </div>


                {/* ARROW */}

                <ChevronRight
                  size={15}
                  className="
                    text-[#64748B]

                    transition-all
                    duration-200

                    group-hover:text-[#60A5FA]
                    group-hover:translate-x-1
                  "
                />

              </button>
              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false);
                  setPage("settings");
                }}
                className="
    group
    w-full
    flex
    items-center
    gap-3
    px-3
    py-2.5
    rounded-xl
    text-left

    bg-[#0F172A]
    border
    border-transparent

    transition-all
    duration-200

    hover:bg-[#172554]
    hover:border-[#1D4ED8]

    hover:shadow-[0_4px_16px_rgba(37,99,235,0.16)]
  "
              >
                <div
                  className="
      w-9 h-9 rounded-lg
      flex items-center justify-center

      bg-[#172554]
      text-[#60A5FA]

      border border-[#1E40AF]

      transition-all duration-200

      group-hover:bg-[#2563EB]
      group-hover:text-white
      group-hover:border-[#3B82F6]
      group-hover:scale-105
      group-hover:shadow-[0_4px_12px_rgba(37,99,235,0.30)]
    "
                >
                  <icons.Settings
                    size={16}
                    strokeWidth={1.8}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className="
        text-[13px]
        font-semibold
        text-[#E2E8F0]

        transition-colors
        duration-200

        group-hover:text-[#60A5FA]
      "
                  >
                    Settings
                  </div>

                  <div
                    className="
        text-[10px]
        mt-0.5
        text-[#94A3B8]

        transition-colors
        duration-200

        group-hover:text-[#CBD5E1]
      "
                  >
                    Manage application settings
                  </div>
                </div>

                <ChevronRight
                  size={15}
                  className="
      text-[#64748B]

      transition-all
      duration-200

      group-hover:text-[#60A5FA]
      group-hover:translate-x-1
    "
                />
              </button>

              {/* =================================================
                  DIVIDER
              ================================================= */}

              <div
                className="
                  my-1
                  border-t
                  border-[#1E293B]
                "
              />


              {/* =================================================
                  LOGOUT
              ================================================= */}

              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false);
                  onLogout();
                }}
                className="
                  group
                  w-full
                  flex
                  items-center
                  gap-3
                  px-3
                  py-2.5
                  rounded-xl
                  text-left

                  bg-[#0F172A]

                  border
                  border-transparent

                  transition-all
                  duration-200

                  hover:bg-[#3F1D24]
                  hover:border-[#7F1D1D]

                  hover:shadow-[0_4px_16px_rgba(239,68,68,0.12)]
                "
              >

                {/* ICON */}

                <div
                  className="
                    w-9
                    h-9
                    rounded-lg
                    flex
                    items-center
                    justify-center

                    bg-[#2A171B]
                    text-[#F87171]

                    border
                    border-[#4C1D22]

                    transition-all
                    duration-200

                    group-hover:bg-[#DC2626]
                    group-hover:text-white
                    group-hover:border-[#EF4444]

                    group-hover:scale-105

                    group-hover:shadow-[0_4px_12px_rgba(239,68,68,0.25)]
                  "
                >
                  <LogOut
                    size={16}
                    strokeWidth={1.8}
                  />
                </div>


                {/* TEXT */}

                <div className="flex-1">

                  <div
                    className="
                      text-[13px]
                      font-semibold

                      text-[#F87171]

                      transition-colors
                      duration-200

                      group-hover:text-[#FCA5A5]
                    "
                  >
                    Log Out
                  </div>
                </div>

              </button>

            </div>

          </div>

        )}

      </div>

    </header>
  );
}
function AdminFooter({ home }) {
  const theme = useTheme();

  return (
    <footer
      className={cx(
        "bottom-0 left-0 lg:left-64 right-0 min-h-12 py-2 px-3 sm:px-4 border-t backdrop-blur z-30",
        "flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-1 sm:gap-3",
        theme.surface,
        theme.border
      )}
    >
      {/* Copyright */}
      <p
        className={cx(
          "text-[10px] sm:text-xs font-body text-center sm:text-left leading-tight",
          theme.faint
        )}
      >
        © {new Date().getFullYear()} Vinayak V Thayil · All rights reserved.
      </p>

      {/* Designed By */}
      <div className="flex items-center justify-center gap-2 shrink-0">
        <span
          className={cx(
            "text-[10px] sm:text-xs font-body",
            theme.faint
          )}
        >
          Designed &amp; Developed by
        </span>

        <a
          href="https://syncpulsesignal.vercel.app"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={home?.footerLogo}
            alt="Vinayak Logo"
            className="h-8 sm:h-8 w-auto object-contain hover:opacity-80 transition-opacity duration-200"
          />
        </a>
      </div>
    </footer>
  );
}

/* ============================================================================
   ADMIN: DASHBOARD
============================================================================ */
// ============================================================
// PREMIUM STAT CARD
// ============================================================

function LocalTimeCard() {
  const [now, setNow] = useState(new Date());
  const [timeZone, setTimeZone] = useState("");
  const [offset, setOffset] = useState("");

  useEffect(() => {
    const syncClock = () => {
      const currentDate = new Date();

      const detectedZone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

      setNow(currentDate);
      setTimeZone(detectedZone);

      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: detectedZone,
        timeZoneName: "longOffset",
      }).formatToParts(currentDate);

      const timezonePart = parts.find(
        (part) => part.type === "timeZoneName"
      );

      setOffset(
        timezonePart?.value?.replace("UTC", "GMT") || "GMT"
      );
    };

    syncClock();

    const interval = setInterval(syncClock, 1000);

    return () => clearInterval(interval);
  }, []);

  // ==========================================================
  // TIME
  // ==========================================================

  const formattedTime = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone || "UTC",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  // ==========================================================
  // DATE
  // ==========================================================

  const dateParts = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || "UTC",
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).formatToParts(now);

  const weekday =
    dateParts.find((p) => p.type === "weekday")?.value || "";

  const day =
    dateParts.find((p) => p.type === "day")?.value || "";

  const month =
    dateParts.find((p) => p.type === "month")?.value || "";

  const year =
    dateParts.find((p) => p.type === "year")?.value || "";

  const displayTimezone = timeZone || "UTC";

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div
      className="
        group
        relative
        w-full
        max-w-[400px]
        min-h-[160px]
        h-auto
        overflow-hidden
        rounded-[26px]
        border border-white/[0.12]
        bg-[#070D1A]/95
        backdrop-blur-2xl
        shadow-[0_22px_70px_rgba(0,0,0,0.40)]
        transition-all
        duration-300
        hover:border-cyan-400/25
        hover:shadow-[0_28px_85px_rgba(0,0,0,0.50)]
      "
    >

      {/* ======================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-20
          -top-20
          h-56
          w-56
          rounded-full
          bg-cyan-400/[0.10]
          blur-3xl
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -left-20
          -bottom-20
          h-52
          w-52
          rounded-full
          bg-blue-500/[0.10]
          blur-3xl
        "
      />

      {/* ======================================================
          TOP GLASS HIGHLIGHT
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-4
          right-4
          sm:left-5
          sm:right-5
          md:left-7
          md:right-7
          top-0
          h-px
          bg-gradient-to-r
          from-transparent
          via-white/30
          to-transparent
        "
      />

      {/* ======================================================
          CENTER LINE
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-0
          right-0
          top-[76px]
          h-px
          bg-gradient-to-r
          from-transparent
          via-cyan-400/60
          to-transparent
          animate-line-move
        "
      />

      {/* ======================================================
          LEFT CONTENT
      ====================================================== */}

      <div
        className="
          absolute
          left-4
          sm:left-5
          md:left-7
          top-5
        "
      >

        {/* LOCAL TIME LABEL */}

        <div
          className="
            flex
            items-center
            gap-1.5
            sm:gap-2
            font-mono
            text-[8px]
            sm:text-[9px]
            md:text-[10px]
            font-bold
            uppercase
            tracking-[0.18em]
            sm:tracking-[0.22em]
            md:tracking-[0.25em]
            text-slate-400
          "
        >
          <span
            className="
              h-1.5
              w-1.5
              sm:h-2
              sm:w-2
              rounded-full
              bg-cyan-400
              shadow-[0_0_10px_rgba(34,211,238,0.9)]
            "
          />

          LOCAL TIME
        </div>

        {/* ==================================================
            BIG CLOCK
        ================================================== */}

        <div
          className="
            mt-3
            font-mono
            text-[24px]
            sm:text-[28px]
            md:text-[34px]
            font-bold
            leading-none
            tracking-[0.04em]
            sm:tracking-[0.06em]
            md:tracking-[0.08em]
            text-white
            drop-shadow-[0_0_18px_rgba(255,255,255,0.10)]
          "
        >
          {formattedTime}
        </div>

        {/* ==================================================
            DATE
        ================================================== */}

        <div
          className="
            mt-4
            font-mono
            text-[8px]
            sm:text-[9px]
            md:text-[10px]
            font-semibold
            uppercase
            tracking-[0.10em]
            sm:tracking-[0.13em]
            md:tracking-[0.16em]
            text-slate-400
          "
        >
          {weekday.toUpperCase()}, {day}
        </div>

        <div
          className="
            mt-1
            font-mono
            text-[8px]
            sm:text-[9px]
            md:text-[10px]
            font-semibold
            uppercase
            tracking-[0.12em]
            sm:tracking-[0.15em]
            md:tracking-[0.18em]
            text-slate-500
          "
        >
          {month.toUpperCase()} {year}
        </div>

      </div>

      {/* ======================================================
          RIGHT CONTENT
      ====================================================== */}

      <div
        className="
          absolute
          right-4
          sm:right-5
          md:right-7
          top-[37px]
          flex
          flex-col
          items-end
        "
      >

        {/* ==================================================
            TIMEZONE
        ================================================== */}

        <div
          title={timeZone}
          className="
            max-w-[105px]
            sm:max-w-[135px]
            md:max-w-[175px]
            truncate
            font-mono
            text-[10px]
            sm:text-[12px]
            md:text-[14px]
            font-bold
            tracking-[0.02em]
            md:tracking-[0.04em]
            text-emerald-400
            drop-shadow-[0_0_10px_rgba(52,211,153,0.22)]
          "
        >
          {displayTimezone}
        </div>

        {/* ==================================================
            GMT BADGE
        ================================================== */}

        <div
          className="
            mt-2
            sm:mt-3
            rounded-lg
            border
            border-white/[0.08]
            bg-white/[0.035]
            px-2
            sm:px-3
            py-1
            sm:py-1.5
            font-mono
            text-[8px]
            sm:text-[9px]
            md:text-[10px]
            font-bold
            tracking-[0.10em]
            sm:tracking-[0.12em]
            md:tracking-[0.14em]
            text-slate-300
            shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
          "
        >
          {offset}
        </div>

        {/* ==================================================
            LIVE STATUS
        ================================================== */}

        <div
          className="
            mt-2
            sm:mt-3
            flex
            items-center
            gap-1.5
            sm:gap-2
          "
        >

          <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">

            <span
              className="
                absolute
                inset-0
                rounded-full
                bg-emerald-400
                animate-ping
                opacity-50
              "
            />

            <span
              className="
                relative
                h-1.5
                w-1.5
                sm:h-2
                sm:w-2
                rounded-full
                bg-emerald-400
                shadow-[0_0_10px_rgba(52,211,153,0.95)]
              "
            />

          </span>

          <span
            className="
              font-mono
              text-[7px]
              sm:text-[8px]
              md:text-[9px]
              font-bold
              uppercase
              tracking-[0.10em]
              sm:tracking-[0.13em]
              md:tracking-[0.16em]
              text-emerald-400
            "
          >
            LIVE • AUTO SYNC
          </span>

        </div>

      </div>

      {/* ======================================================
          CORNER DESIGN
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          right-0
          top-0
          h-16
          w-16
          rounded-bl-[26px]
          border-b
          border-l
          border-cyan-400/[0.12]
        "
      />

      {/* Corner dot */}

      <div
        className="
          pointer-events-none
          absolute
          right-3
          sm:right-4
          top-3
          sm:top-4
          h-1.5
          w-1.5
          rounded-full
          bg-cyan-400/60
          shadow-[0_0_9px_rgba(34,211,238,0.8)]
        "
      />

      {/* Bottom accent */}

      <div
        className="
          pointer-events-none
          absolute
          bottom-0
          left-4
          right-4
          sm:left-5
          sm:right-5
          md:left-7
          md:right-7
          h-px
          bg-gradient-to-r
          from-transparent
          via-cyan-400/20
          to-transparent
        "
      />

    </div>
  );
}

// ============================================================
// PREMIUM DASHBOARD
// ============================================================

function AdminDashboard({
  about,
  projects,
  publications,
  community,
  experience,
  contact,
  skillCount,
  educationCount,
  setPage,
  home,
  blog,
  service
}) {
  const theme = useTheme();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [expandedEducation, setExpandedEducation] = useState({});
  const [education, setEducation] = useState([]);
  const [expandedPositions, setExpandedPositions] = useState({});
  const loadEducation = async () => {
    try {
      const response = await getEducation();

      if (response.data?.success) {
        setEducation(response.data.data || []);
      } else {
        setEducation([]);
      }
    } catch (error) {
      console.error(
        "Failed to load education:",
        error?.response?.data || error
      );

      setEducation([]);
    }
  };

  useEffect(() => {
    loadEducation();
  }, []);
  // ==========================================================
  // COMPLETION
  // ==========================================================

  const missingSections = useMemo(() => {
    const missing = [];

    if (
      !about?.bio?.trim() ||
      !about?.profileImage?.trim()
    ) {
      missing.push("About");
    }

    if (skillCount === 0) {
      missing.push("Skills");
    }

    if (educationCount === 0) {
      missing.push("Education");
    }

    if (projects.length === 0) {
      missing.push("Projects");
    }

    if (publications.length === 0) {
      missing.push("Publications");
    }

    if (community.length === 0) {
      missing.push("Community");
    }
    if (!Array.isArray(blog) || blog.length === 0) {
      missing.push("Blog");
    }

    if (experience.length === 0) {
      missing.push("Experience");
    }
    if (service.length === 0) {
      missing.push("Services");
    }

    if (!(contact?.email || contact?.phone)) {
      missing.push("Contact");
    }

    return missing;
  }, [
    about,
    skillCount,
    educationCount,
    projects,
    publications,
    community,
    experience,
    contact,
    blog,
    service
  ]);

  const totalSections = 10;

  const completedSections =
    totalSections - missingSections.length;

  const completion = Math.round(
    (completedSections / totalSections) * 100
  );

  const aboutComplete = !!(
    about?.bio?.trim() &&
    about?.profileImage?.trim()
  );

  // ==========================================================
  // METRICS
  // ==========================================================

  const metrics = [
    {
      label: "Projects",
      value: projects.length,
      icon: FolderKanban,
      page: "projects",
      gradient: "from-blue-500/20 to-cyan-400/5",
      iconColor: "text-blue-400",
    },
    {
      label: "Publications",
      value: publications.length,
      icon: BookOpen,
      page: "publications",
      gradient: "from-violet-500/20 to-fuchsia-400/5",
      iconColor: "text-violet-400",
    },
    {
      label: "Experience",
      value: experience.length,
      icon: Briefcase,
      page: "experience",
      gradient: "from-amber-500/20 to-orange-400/5",
      iconColor: "text-amber-400",
    },
    {
      label: "Community",
      value: community.length,
      icon: Users,
      page: "community",
      gradient: "from-pink-500/20 to-rose-400/5",
      iconColor: "text-pink-400",
    },
    {
      label: "Skills",
      value: skillCount,
      icon: Sparkles,
      page: "skills",
      gradient: "from-emerald-500/20 to-teal-400/5",
      iconColor: "text-emerald-400",
    },
    {
      label: "Education",
      value: educationCount,
      icon: GraduationCap,
      page: "education",
      gradient: "from-indigo-500/20 to-blue-400/5",
      iconColor: "text-indigo-400",
    }, {
      label: "Blog",
      value: Array.isArray(blog) ? blog.length : 0,
      icon: icons.FaBlog,
      page: "blog",
      gradient: "from-indigo-500/20 to-blue-400/5",
      iconColor: "text-indigo-400",
    },
    {
      label: "Services",
      value: service.length,
      icon: icons.FaTools,
      page: "service",
      gradient: "from-indigo-500/20 to-blue-400/5",
      iconColor: "text-indigo-400",
    },

  ];

  // ==========================================================
  // CTA
  // ==========================================================

  const primaryAction =
    missingSections.length > 0
      ? missingSections[0]
      : "projects";

  const primaryActionMap = {
    About: "about",
    Skills: "skills",
    Education: "education",
    Projects: "projects",
    Publications: "publications",
    Community: "community",
    Experience: "experience",
    Contact: "contact",
    Blog: "blog",
    Services: "service"
  };

  // ==========================================================
  // QUICK ACTIONS
  // ==========================================================

  const quickActions = [
    {
      id: "projects",
      icon: FolderKanban,
      title: "Create Project",
      description: "Showcase new work",
    },
    {
      id: "experience",
      icon: Briefcase,
      title: "Add Experience",
      description: "Update career history",
    },
    {
      id: "publications",
      icon: BookOpen,
      title: "Add Publication",
      description: "Publish research",
    },
    {
      id: "community",
      icon: Users,
      title: "Add Activity",
      description: "Share contribution",
    },
    {
      id: "blog",
      icon: icons.FaBlog,
      title: "Add Blog",
      description: "Share contribution",
    }, {
      id: "service",
      icon: icons.FaTools,
      title: "Add Services",
      description: "Share contribution",
    },
  ];

  return (
    <div className="anim-fadeUp flex flex-col gap-7 pb-10">

      {/* ======================================================
          HERO
      ====================================================== */}

      <section
        className="
    relative
    overflow-hidden
    rounded-[24px]
    sm:rounded-[28px]
    xl:rounded-[32px]
    border
    border-white/[0.08]
    bg-[#090D14]
    p-5
    sm:p-6
    md:p-7
    xl:p-9
  "
      >
        {/* Background lighting */}

        <div
          className="
      pointer-events-none
      absolute
      -right-24
      -top-24
      h-56
      w-56
      sm:h-72
      sm:w-72
      xl:h-80
      xl:w-80
      rounded-full
      bg-cyan-500/[0.09]
      blur-[80px]
      xl:blur-[100px]
    "
        />

        <div
          className="
      pointer-events-none
      absolute
      left-1/3
      -bottom-32
      h-56
      w-56
      sm:h-64
      sm:w-64
      xl:h-72
      xl:w-72
      rounded-full
      bg-blue-500/[0.07]
      blur-[80px]
      xl:blur-[100px]
    "
        />

        {/* Grid */}

        <div
          className="
      pointer-events-none
      absolute
      inset-0
      opacity-[0.025]
      bg-[linear-gradient(rgba(255,255,255,0.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.8)_1px,transparent_1px)]
      bg-[size:24px_24px]
      sm:bg-[size:28px_28px]
      xl:bg-[size:32px_32px]
    "
        />

        {/* ======================================================
      CONTENT
  ====================================================== */}

        <div
          className="
      relative
      flex
      flex-col
      gap-8
      md:gap-10
      xl:grid
      xl:grid-cols-[minmax(0,1fr)_400px]
      xl:gap-10
      xl:items-center
    "
        >

          {/* ====================================================
        LEFT
    ==================================================== */}

          <div className="min-w-0">

            {/* Eyebrow */}

            <div
              className="
          flex
          items-center
          gap-2
          sm:gap-3
          text-[8px]
          sm:text-[9px]
          font-mono
          font-bold
          uppercase
          tracking-[0.22em]
          sm:tracking-[0.28em]
          text-cyan-400
        "
            >
              <span
                className="
            h-px
            w-5
            sm:w-8
            shrink-0
            bg-cyan-400
          "
              />

              ADMIN WORKSPACE
            </div>

            {/* Heading */}

            <h1
              className="
          mt-4
          sm:mt-5
          max-w-2xl
          font-display
          text-[2rem]
          leading-[0.98]
          tracking-[-0.045em]
          sm:text-4xl
          md:text-5xl
          xl:text-6xl
          font-bold
          text-white
        "
            >
              Build your
              <br />

              <span
                className="
            bg-gradient-to-r
            from-white
            via-cyan-200
            to-cyan-400
            bg-clip-text
            text-transparent
          "
              >
                digital presence.
              </span>
            </h1>

            {/* Description */}

            <p
              className="
          mt-4
          sm:mt-5
          max-w-xl
          text-[13px]
          sm:text-sm
          leading-relaxed
          text-slate-500
        "
            >
              Manage your projects, experience, publications,
              skills and professional profile from one workspace.
            </p>

            {/* ==================================================
          CTA
      ================================================== */}

            <div
              className="
          mt-6
          sm:mt-7
          flex
          flex-col
          xs:flex-row
          sm:flex-row
          flex-wrap
          items-stretch
          sm:items-center
          gap-3
        "
            >

              {/* Primary */}

              <button
                type="button"
                onClick={() =>
                  setPage(
                    primaryActionMap[primaryAction] ||
                    primaryAction
                  )
                }
                className="
            group
            inline-flex
            min-h-[44px]
            items-center
            justify-center
            gap-3
            rounded-xl
            bg-cyan-400
            px-5
            py-3
            text-[9px]
            sm:text-[10px]
            font-mono
            font-bold
            uppercase
            tracking-[0.14em]
            sm:tracking-[0.16em]
            text-[#061016]
            shadow-[0_10px_35px_rgba(34,211,238,0.18)]
            transition-all
            duration-300
            hover:-translate-y-0.5
            hover:bg-cyan-300
            hover:shadow-[0_15px_45px_rgba(34,211,238,0.28)]
          "
              >
                {missingSections.length > 0
                  ? `Complete ${primaryAction}`
                  : "Manage Portfolio"}

                <span
                  className="
              transition-transform
              duration-300
              group-hover:translate-x-1
            "
                >
                  →
                </span>
              </button>

              {/* Secondary */}

              <button
                type="button"
                onClick={() => setShowProfileModal(true)}
                className="
            inline-flex
            min-h-[44px]
            items-center
            justify-center
            gap-2
            rounded-xl
            border
            border-white/[0.08]
            bg-white/[0.025]
            px-5
            py-3
            text-[9px]
            sm:text-[10px]
            font-mono
            font-bold
            uppercase
            tracking-[0.14em]
            sm:tracking-[0.16em]
            text-slate-400
            transition-all
            duration-300
            hover:border-white/[0.16]
            hover:bg-white/[0.05]
            hover:text-white
          "
              >
                View Profile
              </button>
              {showProfileModal &&
                createPortal(
                  <div
                    className="
        fixed
        inset-0
        z-[9999]
        flex
        items-center
        justify-center
        bg-black/75
        p-4
        backdrop-blur-md
        animate-in
        fade-in
        duration-200
      "
                    onClick={() => setShowProfileModal(false)}
                  >

                    <div
                      className="
          relative
          max-h-[90vh]
          w-full
          max-w-3xl
          overflow-y-auto
          rounded-2xl
          border
          border-white/[0.08]
          bg-[#080d16]
          shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_24px_80px_-12px_rgba(0,0,0,0.8)]
          [scrollbar-width:thin]
          [scrollbar-color:rgba(34,211,238,0.25)_transparent]
          animate-in
          zoom-in-95
          slide-in-from-bottom-2
          duration-200
        "
                      onClick={(e) => e.stopPropagation()}
                    >

                      {/* Close button */}
                      <button
                        type="button"
                        onClick={() => setShowProfileModal(false)}
                        className="
            group
            absolute
            right-4
            top-4
            z-20
            flex
            h-9
            w-9
            items-center
            justify-center
            rounded-full
            border
            border-white/[0.08]
            bg-white/[0.04]
            text-lg
            text-slate-400
            backdrop-blur-sm
            transition-all
            duration-200
            hover:rotate-90
            hover:border-cyan-400/30
            hover:bg-cyan-400/10
            hover:text-cyan-300
          "
                      >
                        <span className="transition-transform">×</span>
                      </button>


                      {/* Profile Header */}
                      <div
                        className="
            relative
            overflow-hidden
            border-b
            border-white/[0.06]
            bg-gradient-to-br
            from-cyan-400/[0.06]
            via-transparent
            to-transparent
            p-6
            sm:p-8
          "
                      >
                        {/* ambient glow */}
                        <div className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />

                        <div className="relative flex items-center gap-5">

                          {about?.profileImage ? (
                            <div className="relative shrink-0">
                              <div className="absolute inset-0 -z-10 rounded-2xl bg-cyan-400/20 blur-xl" />
                              <img
                                src={about.profileImage}
                                alt={about?.name || "Profile"}
                                className="
                    h-24
                    w-24
                    rounded-2xl
                    border
                    border-white/[0.1]
                    object-cover
                    shadow-lg
                  "
                              />
                            </div>
                          ) : (
                            <div
                              className="
                  relative
                  flex
                  h-24
                  w-24
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  border
                  border-white/[0.08]
                  bg-gradient-to-br
                  from-cyan-400/20
                  to-cyan-600/5
                  text-2xl
                  font-bold
                  text-cyan-300
                  shadow-lg
                "
                            >
                              {about?.name?.charAt(0)?.toUpperCase() || "P"}
                            </div>
                          )}

                          <div className="min-w-0">

                            <div
                              className="
                  flex
                  items-center
                  gap-1.5
                  text-[9px]
                  font-mono
                  uppercase
                  tracking-[0.2em]
                  text-cyan-400
                "
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_2px_rgba(34,211,238,0.6)]" />
                              Profile
                            </div>

                            <h2 className="mt-2 truncate text-2xl font-semibold text-white">
                              {home?.name || "Your Name"}
                            </h2>

                            <p className="mt-1 truncate text-sm text-slate-400">
                              {Array.isArray(home?.roles)
                                ? home.roles.join(" • ")
                                : home?.roles ||
                                about?.designation ||
                                "Professional"}
                            </p>

                          </div>

                        </div>

                      </div>


                      {/* Content */}
                      <div className="space-y-8 p-6 sm:p-8">


                        {/* ABOUT */}
                        <section>

                          <h3
                            className="
                mb-3
                flex
                items-center
                gap-2
                text-[9px]
                font-mono
                font-bold
                uppercase
                tracking-[0.2em]
                text-cyan-400
              "
                          >
                            <span className="h-3 w-1 rounded-full bg-gradient-to-b from-cyan-400 to-cyan-400/20" />
                            About
                          </h3>

                          <div
                            className="
                rounded-xl
                border
                border-white/[0.06]
                bg-gradient-to-br
                from-white/[0.03]
                to-white/[0.01]
                p-5
                transition-colors
                hover:border-white/[0.1]
              "
                          >
                            <p className="whitespace-pre-line text-sm leading-7 text-slate-300">
                              {about?.bio || "No about information available."}
                            </p>
                          </div>

                        </section>


                        {/* EXPERIENCE */}

                        <section>
                          <h3
                            className="
                mb-3
                flex
                items-center
                gap-2
                text-[9px]
                font-mono
                font-bold
                uppercase
                tracking-[0.2em]
                text-cyan-400
              "
                          >
                            <span className="h-3 w-1 rounded-full bg-gradient-to-b from-cyan-400 to-cyan-400/20" />
                            Experience
                          </h3>

                          {(() => {
                            if (!Array.isArray(experience) || experience.length === 0) {
                              return (
                                <div
                                  className="
                      rounded-xl
                      border
                      border-dashed
                      border-white/[0.08]
                      bg-white/[0.015]
                      p-5
                      text-xs
                      text-slate-600
                    "
                                >
                                  No experience added yet.
                                </div>
                              );
                            }

                            // ---------------------------------------------
                            // 1. Find active/current experiences
                            // ---------------------------------------------
                            const activeExperiences = experience.filter(
                              (item) =>
                                item.isActive === true &&
                                (
                                  String(item.endDate || "").toLowerCase() === "present" ||
                                  !item.endDate
                                )
                            );

                            // ---------------------------------------------
                            // 2. Use active experiences if available,
                            //    otherwise use all experiences
                            // ---------------------------------------------
                            const source =
                              activeExperiences.length > 0
                                ? activeExperiences
                                : experience;

                            // ---------------------------------------------
                            // 3. Get latest experience/company
                            // ---------------------------------------------
                            const latestExperience = [...source].sort(
                              (a, b) =>
                                new Date(b.startDate || b.createdAt || 0) -
                                new Date(a.startDate || a.createdAt || 0)
                            )[0];

                            if (!latestExperience) {
                              return (
                                <div
                                  className="
                      rounded-xl
                      border
                      border-dashed
                      border-white/[0.08]
                      bg-white/[0.015]
                      p-5
                      text-xs
                      text-slate-600
                    "
                                >
                                  No experience added yet.
                                </div>
                              );
                            }

                            // ---------------------------------------------
                            // 4. Get ALL positions for this company
                            // ---------------------------------------------
                            const positions = Array.isArray(latestExperience.positions)
                              ? latestExperience.positions
                              : [];

                            const companyName =
                              latestExperience.company || "";

                            const isCurrent =
                              latestExperience.isActive === true &&
                              (
                                String(latestExperience.endDate || "").toLowerCase() ===
                                "present" ||
                                !latestExperience.endDate
                              );

                            return (
                              <div
                                className="
                    rounded-xl
                    border
                    border-white/[0.06]
                    bg-gradient-to-br
                    from-white/[0.03]
                    to-white/[0.01]
                    p-5
                  "
                              >
                                {/* COMPANY HEADER */}
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    {companyName && (
                                      <h4 className="flex items-center gap-2 text-sm font-semibold text-white">
                                        {companyName}
                                        {isCurrent && (
                                          <span
                                            className="
                                inline-flex
                                items-center
                                rounded-full
                                border
                                border-emerald-400/20
                                bg-emerald-400/10
                                px-2
                                py-0.5
                                text-[8px]
                                font-mono
                                uppercase
                                tracking-wider
                                text-emerald-400
                              "
                                          >
                                            Current
                                          </span>
                                        )}
                                      </h4>
                                    )}
                                  </div>
                                </div>

                                {/* ALL POSITIONS FOR SAME COMPANY */}
                                {positions.length > 0 && (
                                  <div className="mt-4 space-y-2">
                                    {positions.map((position, index) => {
                                      const positionKey = `${latestExperience.id || "experience"}-${index}`;

                                      const isExpanded =
                                        expandedPositions[positionKey] ?? index === 0;

                                      return (
                                        <div
                                          key={positionKey}
                                          className="
                              overflow-hidden
                              rounded-lg
                              border
                              border-white/[0.06]
                              bg-white/[0.015]
                              transition-colors
                              hover:border-white/[0.1]
                            "
                                        >
                                          {/* POSITION HEADER */}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setExpandedPositions((prev) => ({
                                                ...prev,
                                                [positionKey]: !isExpanded,
                                              }))
                                            }
                                            className="
                                flex
                                w-full
                                items-center
                                justify-between
                                gap-3
                                px-4
                                py-3
                                text-left
                                transition-colors
                                hover:bg-white/[0.03]
                              "
                                          >
                                            <div className="min-w-0">
                                              <h5 className="text-sm font-semibold text-cyan-300">
                                                {position.title || "Position"}
                                              </h5>

                                              {position.duration && (
                                                <p className="mt-1 text-[9px] font-mono text-slate-600">
                                                  {position.duration}
                                                </p>
                                              )}
                                            </div>

                                            {/* EXPAND / COLLAPSE ICON */}
                                            <span
                                              className="
                                  flex
                                  h-6
                                  w-6
                                  shrink-0
                                  items-center
                                  justify-center
                                  rounded-md
                                  border
                                  border-white/[0.06]
                                  bg-white/[0.03]
                                  text-xs
                                  text-slate-400
                                  transition-transform
                                  duration-200
                                "
                                              style={{
                                                transform: isExpanded
                                                  ? "rotate(180deg)"
                                                  : "rotate(0deg)",
                                              }}
                                            >
                                              {isExpanded ? "−" : "+"}
                                            </span>
                                          </button>

                                          {/* POSITION CONTENT */}
                                          {isExpanded && (
                                            <div
                                              className="
                                  border-t
                                  border-white/[0.06]
                                  bg-black/10
                                  px-4
                                  pb-4
                                  pt-3
                                "
                                            >
                                              {/* TYPE + LOCATION */}
                                              <div className="space-y-1">
                                                {position.type && (
                                                  <p className="text-[9px] font-mono text-slate-500">
                                                    {position.type}
                                                  </p>
                                                )}

                                                {position.location && (
                                                  <p className="text-[9px] font-mono text-slate-500">
                                                    {position.location}
                                                  </p>
                                                )}
                                              </div>

                                              {/* SKILLS */}
                                              {Array.isArray(position.skills) &&
                                                position.skills.length > 0 && (
                                                  <div className="mt-3 flex flex-wrap gap-1.5">
                                                    {position.skills.map((skill, skillIndex) => {
                                                      if (!skill) return null;

                                                      const label =
                                                        typeof skill === "string"
                                                          ? skill
                                                          : skill.label || "";

                                                      const color =
                                                        typeof skill === "object"
                                                          ? skill.color
                                                          : undefined;

                                                      const Icon =
                                                        typeof skill === "object" &&
                                                          typeof skill.icon === "function"
                                                          ? skill.icon
                                                          : null;

                                                      if (!label) return null;

                                                      return (
                                                        <span
                                                          key={skillIndex}
                                                          className="
                                              inline-flex
                                              items-center
                                              gap-1.5
                                              rounded-md
                                              border
                                              border-white/[0.06]
                                              bg-white/[0.03]
                                              px-2
                                              py-1
                                              text-[8px]
                                              font-mono
                                              text-slate-400
                                              transition-colors
                                              hover:border-white/[0.12]
                                              hover:bg-white/[0.05]
                                            "
                                                          style={{
                                                            color: color || undefined,
                                                          }}
                                                        >
                                                          {Icon && <Icon size={11} />}

                                                          <span>{label}</span>
                                                        </span>
                                                      );
                                                    })}
                                                  </div>
                                                )}

                                              {/* DESCRIPTION */}
                                              {Array.isArray(position.description) &&
                                                position.description.length > 0 && (
                                                  <ul className="mt-3 space-y-2">
                                                    {position.description.map(
                                                      (description, descriptionIndex) => (
                                                        <li
                                                          key={descriptionIndex}
                                                          className="
                                              flex
                                              items-start
                                              gap-2
                                              text-xs
                                              leading-6
                                              text-slate-400
                                            "
                                                        >
                                                          <span
                                                            className="
                                                mt-[9px]
                                                h-1
                                                w-1
                                                shrink-0
                                                rounded-full
                                                bg-cyan-400
                                              "
                                                          />

                                                          <span>{description}</span>
                                                        </li>
                                                      )
                                                    )}
                                                  </ul>
                                                )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* NO POSITIONS */}
                                {positions.length === 0 && (
                                  <p className="mt-3 text-xs text-slate-600">
                                    No positions added.
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </section>


                        {/* EDUCATION */}
                        <section>
                          <h3
                            className="
                mb-3
                flex
                items-center
                gap-2
                text-[9px]
                font-mono
                font-bold
                uppercase
                tracking-[0.2em]
                text-cyan-400
              "
                          >
                            <span className="h-3 w-1 rounded-full bg-gradient-to-b from-cyan-400 to-cyan-400/20" />
                            Education
                          </h3>

                          {Array.isArray(education) && education.length > 0 ? (
                            <div className="space-y-2">
                              {education.map((item, index) => {
                                const educationKey =
                                  item._id || item.id || `education-${index}`;

                                const isExpanded =
                                  expandedEducation[educationKey] ?? index === 0;

                                return (
                                  <div
                                    key={educationKey}
                                    className="
                        overflow-hidden
                        rounded-xl
                        border
                        border-white/[0.06]
                        bg-gradient-to-br
                        from-white/[0.03]
                        to-white/[0.01]
                        transition-colors
                        hover:border-white/[0.1]
                      "
                                  >
                                    {/* HEADER */}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedEducation((prev) => ({
                                          ...prev,
                                          [educationKey]: !isExpanded,
                                        }))
                                      }
                                      className="
                          flex
                          w-full
                          items-center
                          justify-between
                          gap-3
                          px-4
                          py-3
                          text-left
                          transition-colors
                          hover:bg-white/[0.03]
                        "
                                    >
                                      <div className="min-w-0">
                                        <h4 className="text-sm font-semibold text-white">
                                          {item.degree || "Education"}
                                        </h4>

                                        {item.institution && (
                                          <p className="mt-1 text-xs text-cyan-400">
                                            {item.institution}
                                          </p>
                                        )}
                                      </div>

                                      <span
                                        className="
                            flex
                            h-6
                            w-6
                            shrink-0
                            items-center
                            justify-center
                            rounded-md
                            border
                            border-white/[0.06]
                            bg-white/[0.03]
                            text-xs
                            text-slate-400
                            transition-transform
                            duration-200
                          "
                                        style={{
                                          transform: isExpanded
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      >
                                        {isExpanded ? "−" : "+"}
                                      </span>
                                    </button>

                                    {/* DETAILS */}
                                    {isExpanded && (
                                      <div
                                        className="
                            border-t
                            border-white/[0.06]
                            bg-black/10
                            px-4
                            pb-4
                            pt-3
                          "
                                      >
                                        {item.location && (
                                          <p className="text-xs text-slate-500">
                                            {item.location}
                                          </p>
                                        )}

                                        {item.period && (
                                          <p className="mt-2 text-[9px] font-mono text-slate-600">
                                            {item.period}
                                          </p>
                                        )}

                                        {item.isActive === false && (
                                          <span
                                            className="
                                mt-3
                                inline-flex
                                rounded-full
                                border
                                border-red-400/20
                                bg-red-400/10
                                px-2
                                py-1
                                text-[8px]
                                font-mono
                                uppercase
                                text-red-400
                              "
                                          >
                                            Inactive
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div
                              className="
                  rounded-xl
                  border
                  border-dashed
                  border-white/[0.08]
                  bg-white/[0.015]
                  p-5
                  text-xs
                  text-slate-600
                "
                            >
                              No education added yet.
                            </div>
                          )}
                        </section>
                        {/* ==========================================================
    CONTENT COUNTS
========================================================== */}
                        <section
                          className="
              relative
              overflow-hidden
              rounded-[24px]
              border
              border-white/[0.06]
              bg-gradient-to-br
              from-white/[0.03]
              to-white/[0.01]
              p-5
              sm:p-6
            "
                        >
                          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-cyan-400/5 blur-3xl" />

                          <div className="relative mb-5">
                            <p
                              className="
                  text-[9px]
                  font-mono
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  text-cyan-400
                "
                            >
                              Portfolio Overview
                            </p>

                            <h3 className="mt-1 text-lg font-semibold text-white">
                              Content Summary
                            </h3>
                          </div>

                          <div className="relative grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">

                            {[
                              { label: "Skills", value: skillCount || 0, color: "emerald" },
                              { label: "Projects", value: Array.isArray(projects) ? projects.length : 0, color: "blue" },
                              { label: "Community", value: Array.isArray(community) ? community.length : 0, color: "pink" },
                              { label: "Services", value: Array.isArray(service) ? service.length : 0, color: "orange" },
                              { label: "Blog", value: Array.isArray(blog) ? blog.length : 0, color: "violet" },
                              { label: "Publications", value: Array.isArray(publications) ? publications.length : 0, color: "cyan" },
                            ].map((stat) => (
                              <div
                                key={stat.label}
                                className={`
                    group
                    rounded-xl
                    border
                    border-white/[0.06]
                    bg-white/[0.02]
                    p-4
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:border-${stat.color}-400/30
                    hover:bg-${stat.color}-400/[0.06]
                    hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]
                  `}
                              >
                                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">
                                  {stat.label}
                                </div>

                                <div className={`mt-2 text-2xl font-bold text-${stat.color}-400`}>
                                  {stat.value}
                                </div>
                              </div>
                            ))}

                          </div>
                        </section>

                      </div>

                    </div>

                  </div>,

                  document.body
                )}

            </div>

          </div>

          {/* ====================================================
        CLOCK
    ==================================================== */}

          <div
            className="
        w-full
        flex
        justify-center
        xl:justify-end
      "
          >
            <div
              className="
          w-full
          max-w-[400px]
          xl:max-w-none
        "
            >
              <LocalTimeCard />
            </div>
          </div>

        </div>
      </section>


      {/* ======================================================
          METRIC CARDS
      ====================================================== */}

      <section>

        <div className="mb-4 flex items-center justify-between">

          <div>

            <div
              className="
                text-[9px]
                font-mono
                font-bold
                uppercase
                tracking-[0.24em]
                text-slate-600
              "
            >
              Overview
            </div>

            <h2
              className="
                mt-1
                font-display
                text-lg
                font-semibold
                text-white
              "
            >
              Portfolio metrics
            </h2>

          </div>


          <div
            className="
              hidden
              sm:flex
              items-center
              gap-2
              rounded-full
              border
              border-white/[0.06]
              bg-white/[0.02]
              px-3
              py-1.5
              text-[8px]
              font-mono
              uppercase
              tracking-[0.16em]
              text-slate-600
            "
          >
            LIVE DATA
          </div>

        </div>


        <div
          className="
            grid
            grid-cols-2
            md:grid-cols-3
            xl:grid-cols-6
            gap-3
          "
        >

          {metrics.map((item) => {

            const Icon = item.icon;

            return (
              <button
                key={item.label}
                type="button"
                onClick={() => setPage(item.page)}
                className="
                  group
                  relative
                  overflow-hidden
                  min-h-[175px]
                  rounded-[24px]
                  border
                  border-white/[0.07]
                  bg-[#0B1018]
                  p-5
                  text-left
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:border-white/[0.14]
                  hover:shadow-[0_20px_50px_rgba(0,0,0,0.25)]
                "
              >

                {/* Gradient */}

                <div
                  className={`
                    absolute
                    inset-0
                    bg-gradient-to-br
                    ${item.gradient}
                    opacity-40
                    transition-opacity
                    duration-300
                    group-hover:opacity-100
                  `}
                />


                <div className="relative">

                  <div className="flex items-center justify-between">

                    <div
                      className="
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-xl
                        border
                        border-white/[0.07]
                        bg-black/20
                      "
                    >

                      <Icon
                        size={17}
                        strokeWidth={1.8}
                        className={item.iconColor}
                      />

                    </div>


                    <span
                      className="
                        text-[8px]
                        font-mono
                        text-slate-700
                        transition-colors
                        group-hover:text-slate-400
                      "
                    >
                      VIEW →
                    </span>

                  </div>


                  <div className="mt-8">

                    <div
                      className="
                        font-display
                        text-4xl
                        font-bold
                        leading-none
                        tracking-[-0.05em]
                        text-white
                      "
                    >
                      {item.value}
                    </div>


                    <div
                      className="
                        mt-3
                        text-[9px]
                        font-mono
                        font-bold
                        uppercase
                        tracking-[0.16em]
                        text-slate-400
                      "
                    >
                      {item.label}
                    </div>

                  </div>

                </div>


                {/* Bottom glow */}

                <div
                  className="
                    absolute
                    bottom-0
                    left-5
                    right-5
                    h-px
                    bg-gradient-to-r
                    from-transparent
                    via-white/10
                    to-transparent
                    transition-all
                    duration-300
                    group-hover:via-cyan-400/60
                  "
                />

              </button>
            );
          })}

        </div>

      </section>


      {/* ======================================================
          MAIN CONTENT
      ====================================================== */}

      <section
        className="
          grid
          xl:grid-cols-[1.1fr_0.9fr]
          gap-5
        "
      >

        {/* ====================================================
            PORTFOLIO HEALTH
        ==================================================== */}

        <div
          className="
            relative
            overflow-hidden
            rounded-[28px]
            border
            border-white/[0.07]
            bg-[#0A0F17]
            p-7
          "
        >

          <div
            className="
              absolute
              -right-20
              -top-20
              h-60
              w-60
              rounded-full
              bg-cyan-500/[0.06]
              blur-[90px]
            "
          />


          <div className="relative">

            {/* Header */}
            <div className="flex items-start justify-between">

              <div>
                <div
                  className="
          text-[9px]
          font-mono
          font-bold
          uppercase
          tracking-[0.24em]
          text-cyan-400
        "
                >
                  Portfolio Health
                </div>

                <h2
                  className="
          mt-2
          font-display
          text-2xl
          font-semibold
          tracking-tight
          text-white
        "
                >
                  Profile readiness
                </h2>

                <p className="mt-2 max-w-xl text-xs leading-relaxed text-slate-500">
                  Keep your portfolio information complete and up to date.
                  Each section contributes to your overall profile readiness.
                </p>
              </div>

              {/* Section counter */}
              <div
                className="
        rounded-xl
        border
        border-white/[0.07]
        bg-white/[0.025]
        px-4
        py-3
      "
              >
                <div
                  className="
          text-[8px]
          font-mono
          uppercase
          tracking-[0.16em]
          text-slate-600
        "
                >
                  Sections
                </div>

                <div
                  className="
          mt-1
          text-right
          text-sm
          font-mono
          font-semibold
          text-white
        "
                >
                  {completedSections}/{totalSections}
                </div>

                <div className="mt-2 h-1 w-20 overflow-hidden rounded-full bg-white/[0.05]">
                  <div
                    className="h-full rounded-full bg-cyan-400 transition-all duration-700"
                    style={{
                      width: `${completion}%`,
                    }}
                  />
                </div>
              </div>

            </div>


            {/* Main readiness area */}
            <div
              className="
      mt-8
      grid
      gap-8
      lg:grid-cols-[auto_1fr]
      lg:items-center
    "
            >

              {/* Circular progress */}
              <div className="flex justify-center lg:justify-start">

                <div className="relative h-36 w-36 shrink-0">

                  <svg
                    className="h-full w-full -rotate-90"
                    viewBox="0 0 120 120"
                  >

                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="rgba(255,255,255,0.05)"
                      strokeWidth="7"
                    />

                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray={314}
                      strokeDashoffset={
                        314 - (314 * completion) / 100
                      }
                      className="
              text-cyan-400
              transition-all
              duration-1000
            "
                    />

                  </svg>

                  <div
                    className="
            absolute
            inset-0
            flex
            flex-col
            items-center
            justify-center
          "
                  >

                    <span
                      className="
              font-display
              text-3xl
              font-bold
              tracking-[-0.05em]
              text-white
            "
                    >
                      {completion}%
                    </span>

                    <span
                      className="
              mt-1
              text-[7px]
              font-mono
              uppercase
              tracking-[0.16em]
              text-slate-600
            "
                    >
                      Ready
                    </span>

                  </div>

                </div>

              </div>


              {/* Readiness information */}
              <div className="min-w-0">

                <div
                  className="
          text-sm
          font-medium
          leading-relaxed
          text-slate-300
        "
                >
                  {missingSections.length === 0
                    ? "Your portfolio is completely configured."
                    : `${missingSections.length} section${missingSections.length > 1 ? "s" : ""
                    } still need${missingSections.length === 1 ? "s" : ""
                    } attention.`}
                </div>


                {/* Progress bar */}
                <div className="mt-5">

                  <div className="mb-2 flex items-center justify-between">

                    <span
                      className="
              text-[8px]
              font-mono
              uppercase
              tracking-[0.16em]
              text-slate-600
            "
                    >
                      Overall completion
                    </span>

                    <span
                      className="
              text-[9px]
              font-mono
              font-semibold
              text-cyan-400
            "
                    >
                      {completedSections} of {totalSections}
                    </span>

                  </div>

                  <div
                    className="
            h-2
            overflow-hidden
            rounded-full
            bg-white/[0.05]
          "
                  >
                    <div
                      className="
              h-full
              rounded-full
              bg-gradient-to-r
              from-cyan-500
              to-blue-500
              transition-all
              duration-1000
            "
                      style={{
                        width: `${completion}%`,
                      }}
                    />
                  </div>

                </div>


                {/* Status summary */}
                <div className="mt-5 grid grid-cols-3 gap-2">

                  <div
                    className="
            rounded-lg
            border
            border-emerald-400/10
            bg-emerald-400/[0.025]
            px-3
            py-2
          "
                  >
                    <div className="text-[7px] font-mono uppercase tracking-wider text-slate-600">
                      Complete
                    </div>

                    <div className="mt-1 text-sm font-semibold text-emerald-400">
                      {completedSections}
                    </div>
                  </div>


                  <div
                    className="
            rounded-lg
            border
            border-amber-400/10
            bg-amber-400/[0.025]
            px-3
            py-2
          "
                  >
                    <div className="text-[7px] font-mono uppercase tracking-wider text-slate-600">
                      Pending
                    </div>

                    <div className="mt-1 text-sm font-semibold text-amber-400">
                      {missingSections.length}
                    </div>
                  </div>


                  <div
                    className="
            rounded-lg
            border
            border-cyan-400/10
            bg-cyan-400/[0.025]
            px-3
            py-2
          "
                  >
                    <div className="text-[7px] font-mono uppercase tracking-wider text-slate-600">
                      Progress
                    </div>

                    <div className="mt-1 text-sm font-semibold text-cyan-400">
                      {completion}%
                    </div>
                  </div>

                </div>

              </div>

            </div>


            {/* All 8 sections */}
            <div className="mt-10">

              <div className="mb-4 flex items-center justify-between">

                <div>
                  <div
                    className="
            text-[8px]
            font-mono
            font-bold
            uppercase
            tracking-[0.2em]
            text-slate-600
          "
                  >
                    Portfolio sections
                  </div>

                  <div className="mt-1 text-xs text-slate-500">
                    Configuration status
                  </div>
                </div>

                <div
                  className="
          rounded-md
          border
          border-white/[0.06]
          bg-white/[0.02]
          px-2
          py-1
          text-[8px]
          font-mono
          uppercase
          tracking-wider
          text-slate-500
        "
                >
                  {completedSections}/{totalSections} ready
                </div>

              </div>


              {/* Section grid */}
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">

                {[
                  {
                    name: "About",
                    key: "about",
                    complete:
                      !!about?.bio?.trim() &&
                      !!about?.profileImage?.trim(),
                    detail:
                      about?.bio?.trim() && about?.profileImage?.trim()
                        ? "Profile configured"
                        : "Profile information required",
                  },

                  {
                    name: "Skills",
                    key: "skills",
                    complete: skillCount > 0,
                    detail:
                      skillCount > 0
                        ? `${skillCount} skill${skillCount > 1 ? "s" : ""}`
                        : "No skills added",
                  },

                  {
                    name: "Education",
                    key: "education",
                    complete: educationCount > 0,
                    detail:
                      educationCount > 0
                        ? `${educationCount} record${educationCount > 1 ? "s" : ""
                        }`
                        : "No education added",
                  },

                  {
                    name: "Projects",
                    key: "projects",
                    complete: projects.length > 0,
                    detail:
                      projects.length > 0
                        ? `${projects.length} project${projects.length > 1 ? "s" : ""
                        }`
                        : "No projects added",
                  },

                  {
                    name: "Publications",
                    key: "publications",
                    complete: publications.length > 0,
                    detail:
                      publications.length > 0
                        ? `${publications.length} publication${publications.length > 1 ? "s" : ""
                        }`
                        : "No publications added",
                  },

                  {
                    name: "Community",
                    key: "community",
                    complete: community.length > 0,
                    detail:
                      community.length > 0
                        ? `${community.length} entr${community.length > 1 ? "ies" : "y"
                        }`
                        : "No community activity",
                  },

                  {
                    name: "Experience",
                    key: "experience",
                    complete: experience.length > 0,
                    detail:
                      experience.length > 0
                        ? `${experience.length} position${experience.length > 1 ? "s" : ""
                        }`
                        : "No experience added",
                  },

                  {
                    name: "Contact",
                    key: "contact",
                    complete: !!(contact?.email || contact?.phone),
                    detail:
                      contact?.email || contact?.phone
                        ? "Contact configured"
                        : "Contact information required",
                  },
                  {
                    name: "Blog",
                    key: "blog",
                    complete: blog.length > 0,
                    detail:
                      blog.length > 0
                        ? `${blog.length} position${blog.length > 1 ? "s" : ""
                        }`
                        : "No Blog added",
                  }, {
                    name: "Services",
                    key: "service",
                    complete: service.length > 0,
                    detail:
                      service.length > 0
                        ? `${service.length} position${service.length > 1 ? "s" : ""
                        }`
                        : "No Services added",
                  },
                ].map((section) => (

                  <button
                    key={section.name}
                    type="button"
                    onClick={() =>
                      setPage(
                        primaryActionMap[section.name] ||
                        section.key
                      )
                    }
                    className={`
            group
            relative
            overflow-hidden
            rounded-xl
            border
            px-3
            py-3
            text-left
            transition-all
            ${section.complete
                        ? `
                  border-emerald-400/10
                  bg-emerald-400/[0.025]
                  hover:border-emerald-400/20
                  hover:bg-emerald-400/[0.05]
                `
                        : `
                  border-amber-400/10
                  bg-amber-400/[0.02]
                  hover:border-amber-400/20
                  hover:bg-amber-400/[0.04]
                `
                      }
          `}
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div className="flex min-w-0 items-center gap-2">

                        <span
                          className={`
                  h-1.5
                  w-1.5
                  shrink-0
                  rounded-full
                  ${section.complete
                              ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]"
                              : "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]"
                            }
                `}
                        />

                        <span
                          className="
                  truncate
                  text-[9px]
                  font-mono
                  font-bold
                  uppercase
                  tracking-[0.12em]
                  text-slate-300
                  group-hover:text-white
                "
                        >
                          {section.name}
                        </span>

                      </div>


                      <span
                        className={`
                shrink-0
                text-[7px]
                font-mono
                uppercase
                tracking-wider
                ${section.complete
                            ? "text-emerald-400"
                            : "text-amber-400"
                          }
              `}
                      >
                        {section.complete ? "Ready" : "Pending"}
                      </span>

                    </div>


                    <div className="mt-2 text-[8px] text-slate-600">
                      {section.detail}
                    </div>


                    {/* Tiny status line */}
                    <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/[0.04]">

                      <div
                        className={`
                h-full
                rounded-full
                transition-all
                duration-500
                ${section.complete
                            ? "w-full bg-emerald-400/70"
                            : "w-1/3 bg-amber-400/60"
                          }
              `}
                      />

                    </div>

                  </button>

                ))}

              </div>

            </div>


            {/* Attention section */}
            {missingSections.length > 0 && (

              <div className="mt-8">

                <div className="mb-3 flex items-center justify-between">

                  <div
                    className="
            text-[8px]
            font-mono
            font-bold
            uppercase
            tracking-[0.2em]
            text-slate-600
          "
                  >
                    Complete next
                  </div>

                  <div className="text-[8px] font-mono text-amber-400">
                    {missingSections.length} pending
                  </div>

                </div>


                <div className="flex flex-wrap gap-2">

                  {missingSections.map((section) => (

                    <button
                      key={section}
                      type="button"
                      onClick={() =>
                        setPage(
                          primaryActionMap[section] ||
                          section.toLowerCase()
                        )
                      }
                      className="
              group
              inline-flex
              items-center
              gap-2
              rounded-lg
              border
              border-white/[0.07]
              bg-white/[0.02]
              px-3
              py-2
              transition-all
              hover:border-cyan-400/20
              hover:bg-cyan-400/[0.04]
            "
                    >

                      <span
                        className="
                h-1.5
                w-1.5
                rounded-full
                bg-amber-400
                shadow-[0_0_8px_rgba(251,191,36,0.6)]
              "
                      />

                      <span
                        className="
                text-[8px]
                font-mono
                uppercase
                tracking-[0.12em]
                text-slate-400
                group-hover:text-white
              "
                      >
                        {section}
                      </span>

                    </button>

                  ))}

                </div>

              </div>

            )}


            {/* Footer status */}
            <div
              className="
      mt-8
      flex
      flex-col
      gap-2
      border-t
      border-white/[0.05]
      pt-5
      sm:flex-row
      sm:items-center
      sm:justify-between
    "
            >

              <div className="flex items-center gap-2">

                <span
                  className={`
          h-1.5
          w-1.5
          rounded-full
          ${missingSections.length === 0
                      ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]"
                      : "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]"
                    }
        `}
                />

                <span
                  className="
          text-[8px]
          font-mono
          uppercase
          tracking-[0.16em]
          text-slate-500
        "
                >
                  {missingSections.length === 0
                    ? "Portfolio fully configured"
                    : "Portfolio configuration in progress"}
                </span>

              </div>

              <div className="text-[8px] font-mono text-slate-700">
                Profile Health · {completion}% complete
              </div>

            </div>

          </div>

        </div>


        {/* ====================================================
            QUICK ACTIONS
        ==================================================== */}

        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-[#080D15] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">

          {/* Subtle ambient glow */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-violet-500/[0.06] blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-20 h-48 w-48 rounded-full bg-cyan-400/[0.04] blur-3xl" />

          {/* Header */}
          <div className="relative flex items-start justify-between">

            <div>
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400 shadow-[0_0_10px_rgba(167,139,250,0.9)]" />

                <span className="
          text-[9px]
          font-mono
          font-bold
          uppercase
          tracking-[0.25em]
          text-violet-400
        ">
                  Shortcuts
                </span>
              </div>

              <h2 className="
        mt-2
        font-display
        text-[25px]
        font-semibold
        tracking-[-0.02em]
        text-white
      ">
                Create something
              </h2>

              <p className="
        mt-1
        text-[11px]
        text-slate-500
      ">
                Jump into your most used actions
              </p>
            </div>
          </div>


          {/* Quick actions */}
          <div className="relative mt-6 grid gap-3 sm:grid-cols-2">

            {quickActions.map((action) => {

              const Icon = action.icon;

              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => setPage(action.id)}
                  className="
            group
            relative
            overflow-hidden
            rounded-2xl
            border
            border-white/[0.07]
            bg-white/[0.018]
            p-4
            text-left
            transition-all
            duration-300
            hover:-translate-y-1
            hover:border-violet-400/[0.22]
            hover:bg-violet-400/[0.035]
            hover:shadow-[0_12px_30px_rgba(139,92,246,0.08)]
          "
                >

                  {/* Hover gradient */}
                  <div className="
            pointer-events-none
            absolute
            inset-0
            bg-gradient-to-br
            from-violet-400/[0.06]
            via-transparent
            to-cyan-400/[0.025]
            opacity-0
            transition-opacity
            duration-300
            group-hover:opacity-100
          " />

                  <div className="relative">

                    {/* Icon + arrow */}
                    <div className="flex items-center justify-between">

                      <div className="
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-xl
                border
                border-violet-400/[0.10]
                bg-violet-500/[0.08]
                transition-all
                duration-300
                group-hover:scale-105
                group-hover:border-violet-400/[0.2]
                group-hover:bg-violet-500/[0.13]
                group-hover:shadow-[0_0_20px_rgba(139,92,246,0.12)]
              ">
                        <Icon
                          size={17}
                          strokeWidth={1.8}
                          className="
                    text-violet-400
                    transition-transform
                    duration-300
                    group-hover:scale-110
                  "
                        />
                      </div>

                      <span className="
                text-[16px]
                text-slate-700
                transition-all
                duration-300
                group-hover:translate-x-1
                group-hover:text-violet-400
              ">
                        →
                      </span>

                    </div>


                    {/* Content */}
                    <div className="
              mt-5
              text-[13px]
              font-semibold
              tracking-[-0.01em]
              text-slate-200
            ">
                      {action.title}
                    </div>

                    <div className="
              mt-1.5
              max-w-[90%]
              text-[10px]
              leading-relaxed
              text-slate-500
            ">
                      {action.description}
                    </div>

                  </div>

                </button>
              );
            })}

          </div>


          {/* Profile CTA */}
          <button
            type="button"
            onClick={() => setPage("about")}
            className="
      group
      relative
      mt-4
      flex
      w-full
      items-center
      justify-between
      overflow-hidden
      rounded-2xl
      border
      border-cyan-400/[0.10]
      bg-gradient-to-r
      from-cyan-400/[0.045]
      to-transparent
      px-4
      py-3.5
      transition-all
      duration-300
      hover:border-cyan-400/[0.25]
      hover:from-cyan-400/[0.07]
      hover:to-cyan-400/[0.02]
    "
          >

            <div className="flex items-center gap-3">

              <div className="
        flex
        h-7
        w-7
        items-center
        justify-center
        rounded-lg
        border
        border-cyan-400/[0.12]
        bg-cyan-400/[0.06]
      ">
                <span className="
          h-1.5
          w-1.5
          rounded-full
          bg-cyan-400
          shadow-[0_0_10px_rgba(34,211,238,0.9)]
        " />
              </div>

              <div className="text-left">
                <div className="
          text-[9px]
          font-mono
          font-bold
          uppercase
          tracking-[0.16em]
          text-cyan-400
        ">
                  Edit your profile
                </div>

                <div className="
          mt-0.5
          text-[9px]
          text-slate-600
        ">
                  Keep your information up to date
                </div>
              </div>

            </div>


            <span className="
      text-cyan-400/70
      transition-all
      duration-300
      group-hover:translate-x-1
      group-hover:text-cyan-300
    ">
              →
            </span>

          </button>

        </div>

      </section>


      {/* ======================================================
          FOOTER
      ====================================================== */}

      <div
        className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-3
          px-1
        "
      >

        <div className="flex items-center gap-2">

          <span
            className="
              h-1.5
              w-1.5
              rounded-full
              bg-emerald-400
              shadow-[0_0_8px_rgba(52,211,153,0.7)]
            "
          />

          <span
            className="
              text-[8px]
              font-mono
              uppercase
              tracking-[0.18em]
              text-slate-600
            "
          >
            System operational
          </span>

        </div>


        <span
          className="
            text-[8px]
            font-mono
            uppercase
            tracking-[0.18em]
            text-slate-700
          "
        >
          Portfolio Control Center
        </span>

      </div>

    </div>
  );
}

/* ============================================================================
   ADMIN: EDIT ABOUT (keeps skills and education inside)
============================================================================ */
function AdminAbout({ about, setAbout, notify }) {
  const theme = useTheme();
  const [iconSearch, setIconSearch] = useState("");
  const [showIconSuggestions, setShowIconSuggestions] = useState(false);
  const iconNames = Object.keys(icons);

  const filteredIconNames = iconNames
    .filter((name) =>
      name.toLowerCase().includes(iconSearch.toLowerCase())
    )
    .slice(0, 30);
  const getIconComponent = (iconName) => {
    if (!iconName) return null;

    return icons[iconName] || null;
  };

  // =====================================================
  // ABOUT FORM
  // =====================================================

  const buildAboutForm = (source) => ({
    aboutTitle: source?.aboutTitle || "",
    aboutHighlight: source?.aboutHighlight || "",

    profileImage: source?.profileImage || "",
    profileImageAlt: source?.profileImageAlt || "Profile photo",
    objectPosition: source?.objectPosition || "center 20%",

    bio: source?.bio || "",

    skillLabels: source?.skillLabels || [],

    academicTitle: source?.academicTitle || "",
    academicHighlight: source?.academicHighlight || "",

    highlightWords: source?.highlightWords || [],

    order: source?.order || 0,
    isFeatured: source?.isFeatured || false,
    isActive: source?.isActive ?? true,
  });

  const [form, setForm] = useState(buildAboutForm(about));
  const [errors, setErrors] = useState({});
  const [aboutModal, setAboutModal] = useState(false);

  // =====================================================
  // HIGHLIGHT WORDS
  // =====================================================

  const [newHighlight, setNewHighlight] = useState("");

  // =====================================================
  // SKILLS
  // =====================================================

  const [skills, setSkills] = useState([]);
  const [skillModal, setSkillModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [savingSkill, setSavingSkill] = useState(false);
  const [loadingSkills, setLoadingSkills] = useState(false);

  const [skillForm, setSkillForm] = useState({
    name: "",
    category: "",
    icon: "",
    color: "#FFFFFF",
    order: 0,
    isFeatured: false,
    isActive: true,
  });

  const [customCategoryMode, setCustomCategoryMode] = useState(false);
  const [skillCategoryFilter, setSkillCategoryFilter] = useState("");
  const [previewSkillCategory, setPreviewSkillCategory] = useState("");
  // =====================================================
  // EDUCATION
  // =====================================================
  const [education, setEducation] = useState([]);
  const [educationModal, setEducationModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [savingEducation, setSavingEducation] = useState(false);
  const [loadingEducation, setLoadingEducation] = useState(false);

  const [educationForm, setEducationForm] = useState({
    degree: "",
    institution: "",
    location: "",
    period: "",
    gradient: "from-indigo-500 to-purple-500",
    order: 0,
    isActive: true,
  });

  // =====================================================
  // GENERIC UPDATE (About form)
  // =====================================================

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // =====================================================
  // LOAD SKILLS
  // =====================================================

  const loadSkills = async () => {
    try {
      setLoadingSkills(true);

      const response = await api.get("/about/skills");

      if (response.data?.success) {
        setSkills(response.data.data || []);
      }
    } catch (error) {
      console.error("Load skills error:", error);
      notify("error", "Failed to load skills");
    } finally {
      setLoadingSkills(false);
    }
  };
  const loadAllData = async () => {
    try {
      const aboutRes = await getAbout();

      const aboutData = aboutRes.data?.data?.about?.[0];

      setAbout(aboutData || initialAbout);
    } catch (err) {
      console.error("Failed to load About data:", err);
    }
  };
  // =====================================================
  // LOAD EDUCATION
  // =====================================================

  const loadEducation = async () => {
    try {
      setLoadingEducation(true);

      const response = await api.get("/about/education");

      if (response.data?.success) {
        setEducation(response.data.data || []);
      }
    } catch (error) {
      console.error("Load education error:", error);
      notify("error", "Failed to load education");
    } finally {
      setLoadingEducation(false);
    }
  };

  // Load skills + education when component mounts
  useEffect(() => {
    loadSkills();
    loadEducation();
    loadAllData();
  }, []);

  // Keep the About form in sync if the `about` prop changes externally
  useEffect(() => {
    setForm(buildAboutForm(about));
  }, [about]);

  // =====================================================
  // HIGHLIGHT WORDS
  // =====================================================

  const addHighlight = () => {
    const value = newHighlight.trim();

    if (!value) return;

    const exists = form.highlightWords.some(
      (item) => item.toLowerCase() === value.toLowerCase()
    );

    if (exists) {
      notify("error", "Highlight already exists");
      return;
    }

    update("highlightWords", [...form.highlightWords, value]);
    setNewHighlight("");
  };

  const removeHighlight = (index) => {
    update(
      "highlightWords",
      form.highlightWords.filter((_, i) => i !== index)
    );
  };

  // =====================================================
  // SKILL CATEGORY LABELS
  // =====================================================

  const updateSkillLabel = (index, value) => {
    const updated = [...form.skillLabels];

    updated[index] = {
      ...updated[index],
      label: value,
    };

    update("skillLabels", updated);
  };

  const addCategoryLabel = () => {
    const key = window.prompt("Enter category key");

    if (!key) return;

    const cleanKey = key.trim().toLowerCase().replace(/\s+/g, "");

    if (!cleanKey) return;

    const exists = form.skillLabels.some((item) => item.key === cleanKey);

    if (exists) {
      notify("error", "Category already exists");
      return;
    }

    const label = window.prompt("Enter category label", key);

    if (!label) return;

    update("skillLabels", [
      ...form.skillLabels,
      { key: cleanKey, label: label.trim() },
    ]);
  };

  // =====================================================
  // SKILL FORM HELPERS
  // =====================================================

  const updateSkillForm = (key, value) => {
    setSkillForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openAddSkill = () => {
    setEditingSkill(null);
    setCustomCategoryMode(false);

    setSkillForm({
      name: "",
      category: "",
      icon: "",
      color: "#FFFFFF",
      order: skills.length + 1,
      isFeatured: false,
      isActive: true,
    });

    setSkillModal(true);
  };

  const openEditSkill = (skill) => {
    setEditingSkill(skill);
    setCustomCategoryMode(false);

    setSkillForm({
      name: skill.name || "",
      category: skill.category || "",
      icon: skill.icon || "",
      color: skill.color || "#FFFFFF",
      order: skill.order || 0,
      isFeatured: skill.isFeatured || false,
      isActive: skill.isActive ?? true,
    });

    setSkillModal(true);
  };

  const saveSkill = async () => {
    if (!skillForm.name.trim()) {
      notify("error", "Skill name is required");
      return;
    }

    if (!skillForm.category.trim()) {
      notify("error", "Category is required");
      return;
    }

    if (!skillForm.icon.trim()) {
      notify("error", "Icon is required");
      return;
    }

    try {
      setSavingSkill(true);

      const rawCategory = skillForm.category.trim();

      // If this category already matches a known label key exactly (picked
      // from the dropdown), keep it as-is. Otherwise it's freshly typed, so
      // normalize it into a key the same way skillLabels keys are built.
      const knownKeys = form.skillLabels.map((l) => l.key);
      const categoryKey = knownKeys.includes(rawCategory)
        ? rawCategory
        : rawCategory.toLowerCase().replace(/\s+/g, "");

      const payload = {
        name: skillForm.name.trim(),
        category: categoryKey,
        icon: skillForm.icon.trim(),
        color: skillForm.color,
        order: skillForm.order,
        isFeatured: skillForm.isFeatured,
        isActive: skillForm.isActive,
      };

      const response = editingSkill
        ? await api.put(`/about/skills/${editingSkill.id}`, payload)
        : await api.post("/about/skills", payload);

      if (response.data?.success) {
        notify(
          "success",
          editingSkill ? "Skill updated successfully" : "Skill added successfully"
        );

        // Automatically create a category label if it's new
        const categoryExists = form.skillLabels.some(
          (item) => item.key === categoryKey
        );

        if (!categoryExists) {
          const newLabel = {
            key: categoryKey,
            label: rawCategory,
          };

          setForm((prev) => ({
            ...prev,
            skillLabels: [...prev.skillLabels, newLabel],
          }));
        }

        await loadSkills();

        setSkillModal(false);
        setEditingSkill(null);
        setCustomCategoryMode(false);
      }
    } catch (error) {
      console.error("Save skill error:", error);
      notify("error", error.response?.data?.message || "Failed to save skill");
    } finally {
      setSavingSkill(false);
    }
  };

  const deleteSkill = async (skill) => {
    const confirmed = window.confirm(`Delete "${skill.name}"?`);

    if (!confirmed) return;

    try {
      const response = await api.delete(`/about/skills/${skill.id}`);

      if (response.data?.success) {
        notify("success", "Skill deleted successfully");
        await loadSkills();
      }
    } catch (error) {
      console.error("Delete skill error:", error);
      notify("error", "Failed to delete skill");
    }
  };

  // =====================================================
  // EDUCATION FORM HELPERS
  // =====================================================

  const updateEducationForm = (key, value) => {
    setEducationForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openAddEducation = () => {
    setEditingEducation(null);

    setEducationForm({
      degree: "",
      institution: "",
      location: "",
      period: "",
      gradient: "from-indigo-500 to-purple-500",
      order: education.length + 1,
      isActive: true,
    });

    setEducationModal(true);
  };

  const openEditEducation = (item) => {
    setEditingEducation(item);

    setEducationForm({
      degree: item.degree || "",
      institution: item.institution || "",
      location: item.location || "",
      period: item.period || "",
      gradient: item.gradient || "from-indigo-500 to-purple-500",
      order: item.order || 0,
      isActive: item.isActive ?? true,
    });

    setEducationModal(true);
  };

  const saveEducation = async () => {
    if (!educationForm.degree.trim()) {
      notify("error", "Degree is required");
      return;
    }

    if (!educationForm.institution.trim()) {
      notify("error", "Institution is required");
      return;
    }

    if (!educationForm.period.trim()) {
      notify("error", "Period is required");
      return;
    }

    try {
      setSavingEducation(true);

      const payload = {
        degree: educationForm.degree.trim(),
        institution: educationForm.institution.trim(),
        location: educationForm.location.trim(),
        period: educationForm.period.trim(),
        gradient: educationForm.gradient,
        order: educationForm.order,
        isActive: educationForm.isActive,
      };

      const response = editingEducation
        ? await api.put(`/about/education/${editingEducation.id}`, payload)
        : await api.post("/about/education", payload);

      if (response.data?.success) {
        notify(
          "success",
          editingEducation
            ? "Education updated successfully"
            : "Education added successfully"
        );

        await loadEducation();

        setEducationModal(false);
        setEditingEducation(null);
      }
    } catch (error) {
      console.error("Save education error:", error);
      notify(
        "error",
        error.response?.data?.message || "Failed to save education"
      );
    } finally {
      setSavingEducation(false);
    }
  };

  const deleteEducation = async (item) => {
    const confirmed = window.confirm(`Delete "${item.degree}"?`);

    if (!confirmed) return;

    try {
      const response = await api.delete(`/about/education/${item.id}`);

      if (response.data?.success) {
        notify("success", "Education deleted successfully");
        await loadEducation();
      }
    } catch (error) {
      console.error("Delete education error:", error);
      notify("error", "Failed to delete education");
    }
  };

  // =====================================================
  // ABOUT MODAL + SAVE
  // =====================================================

  const openAboutModal = () => {
    setForm(buildAboutForm(about));
    setErrors({});
    setAboutModal(true);
  };

  const save = async () => {
    const errs = {};

    if (!form.aboutTitle.trim())
      errs.aboutTitle = "About title is required";

    if (!form.aboutHighlight.trim())
      errs.aboutHighlight = "About highlight is required";

    if (!form.bio.trim())
      errs.bio = "Bio is required";

    if (!form.academicTitle.trim())
      errs.academicTitle = "Academic title is required";

    if (!form.academicHighlight.trim())
      errs.academicHighlight = "Academic highlight is required";

    setErrors(errs);

    if (Object.keys(errs).length) return;

    try {
      let response;

      const aboutId = about?._id || about?.id;

      if (aboutId) {
        // Existing About → UPDATE
        response = await updateAbout(aboutId, form);
      } else {
        // No About yet → CREATE
        response = await createAbout(form);
      }

      const savedAbout = response.data?.data || form;

      setAbout(savedAbout);

      notify("success", "About section saved successfully.");
      setAboutModal(false);
    } catch (error) {
      console.error("Save About error:", error);

      notify(
        "error",
        error.response?.data?.message || "Failed to save About section"
      );
    }
  };

  // =====================================================
  // RENDER HELPERS FOR LIVE PREVIEW
  // =====================================================

  const renderBio = (bio, highlightWords) => {
    const resolved = (bio || "").replace(
      /{{experience}}/g,
    );

    if (!highlightWords?.length) return resolved;

    // Escape regex special characters
    const escaped = highlightWords
      .filter(Boolean)
      .map((w) =>
        w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      );

    if (!escaped.length) return resolved;

    const pattern = new RegExp(
      `(${escaped.join("|")})`,
      "gi"
    );

    const parts = resolved.split(pattern);

    return parts.map((part, i) => {
      const isHighlight = highlightWords.some(
        (word) =>
          word &&
          word.toLowerCase() === part.toLowerCase()
      );

      return isHighlight ? (
        <span
          key={i}
          className="text-indigo-400 font-semibold"
        >
          {part}
        </span>
      ) : (
        <React.Fragment key={i}>
          {part}
        </React.Fragment>
      );
    });
  };

  const groupedSkills = (() => {
    const map = new Map();

    // Existing category labels
    form.skillLabels.forEach((label) => {
      map.set(label.key, {
        key: label.key,
        label: label.label,
        items: [],
      });
    });

    // Add categories that exist in skills but
    // don't exist in skillLabels
    skills.forEach((skill) => {
      if (!skill.category || !skill.isActive) return;

      if (!map.has(skill.category)) {
        map.set(skill.category, {
          key: skill.category,
          label: skill.category,
          items: [],
        });
      }

      map.get(skill.category).items.push(skill);
    });

    return Array.from(map.values());
  })();

  // Dropdown options for the skill Category field: known skillLabels first,
  // plus any category already used on a skill that doesn't have a label yet.
  const categoryOptions = (() => {
    const map = new Map();

    form.skillLabels.forEach((l) => map.set(l.key, l.label));

    skills.forEach((s) => {
      if (s.category && !map.has(s.category)) {
        map.set(s.category, s.category);
      }
    });

    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  })();
  useEffect(() => {
    if (!skillCategoryFilter && categoryOptions.length > 0) {
      setSkillCategoryFilter(categoryOptions[0].key);
    }

    if (!previewSkillCategory) {
      const firstGroup = groupedSkills.find(
        (group) => group.items.length > 0
      );

      if (firstGroup) {
        setPreviewSkillCategory(firstGroup.key);
      }
    }
  }, [
    categoryOptions,
    groupedSkills,
    skillCategoryFilter,
    previewSkillCategory,
  ]);
  // Skills shown on the main admin page, narrowed to the selected category
  const visibleSkills =
    skillCategoryFilter === "all"
      ? skills
      : skills.filter((s) => s.category === skillCategoryFilter);

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 items-start w-full">        {/* ================================================= */}
        {/* LEFT COLUMN — MANAGEMENT */}
        {/* ================================================= */}

        <div className="flex flex-col gap-6">
          {/* HEADER */}

          <div>
            <h2 className={cx("text-xl font-bold font-display", theme.text)}>
              About Section
            </h2>

            <p className="text-sm text-gray-400 mt-1">
              Manage your portfolio About section, biography, highlights,
              skills and education.
            </p>
          </div>

          {/* ============================================= */}
          {/* ABOUT SUMMARY CARD */}
          {/* ============================================= */}

          <div className={cx("rounded-2xl border p-6", theme.surface, theme.border)}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-4 min-w-0">
                <div className="w-14 h-16 rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                  {form.profileImage ? (
                    <img
                      src={form.profileImage}
                      alt={form.profileImageAlt}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: form.objectPosition }}
                    />
                  ) : (
                    <ImageIcon size={18} className="text-gray-600" />
                  )}
                </div>

                <div className="min-w-0">
                  <h3 className={cx("font-semibold truncate", theme.text)}>
                    {form.aboutTitle || "About"}{" "}
                    <span className="text-indigo-400">
                      {form.aboutHighlight || "Me"}
                    </span>
                  </h3>

                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {form.bio || "No biography written yet."}
                  </p>

                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={
                        form.isActive
                          ? "text-xs text-emerald-400"
                          : "text-xs text-red-400"
                      }
                    >
                      {form.isActive ? "Active" : "Inactive"}
                    </span>

                    {form.isFeatured && (
                      <span className="text-xs text-yellow-400">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={openAboutModal}
                className="px-3 py-2 rounded-lg border border-slate-700 text-sm text-gray-300 hover:bg-slate-800 shrink-0 flex items-center gap-1.5"
              >
                <Pencil size={14} />
                Edit About
              </button>
            </div>
          </div>

          {/* ============================================= */}
          {/* SKILLS CARD */}
          {/* ============================================= */}

          <div className={cx("rounded-2xl border p-6", theme.surface, theme.border)}>
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <div>
                <h3 className={cx("text-sm font-semibold", theme.text)}>
                  Skills
                </h3>

                <p className="text-xs text-gray-500 mt-1">
                  Add, edit or remove skills from the database.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={skillCategoryFilter}
                  onChange={(e) => setSkillCategoryFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat.key} value={cat.key}>
                      {cat.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={openAddSkill}
                  className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5 shrink-0"
                >
                  <Plus size={14} />
                  Add Skill
                </button>
              </div>
            </div>

            {loadingSkills ? (
              <div className="py-10 text-center text-gray-500">
                Loading skills...
              </div>
            ) : visibleSkills.length === 0 ? (
              <div className="py-10 text-center text-gray-500">
                {skillCategoryFilter === "all"
                  ? "No skills found."
                  : "No skills in this category."}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {visibleSkills.map((skill) => (
                  <div
                    key={skill.id}
                    className="rounded-xl border border-slate-700 bg-slate-900/40 p-3 flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-xs font-bold border border-slate-700 shrink-0"
                        style={{ color: skill.color || "#FFFFFF" }}
                      >
                        {(() => {
                          const IconComponent = getIconComponent(skill.icon);

                          return IconComponent ? (
                            <IconComponent size={20} />
                          ) : (
                            skill.icon || "?"
                          );
                        })()}
                      </div>

                      <div className="min-w-0">
                        <p className="font-medium text-white text-sm truncate">
                          {skill.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {skill.category}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => openEditSkill(skill)}
                        className="w-8 h-8 rounded-lg border border-slate-700 text-gray-300 hover:bg-slate-800 flex items-center justify-center"
                      >
                        <Pencil size={13} />
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteSkill(skill)}
                        className="w-8 h-8 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ============================================= */}
          {/* EDUCATION CARD */}
          {/* ============================================= */}

          <div className={cx("rounded-2xl border p-6", theme.surface, theme.border)}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className={cx("text-sm font-semibold", theme.text)}>
                  Education
                </h3>

                <p className="text-xs text-gray-500 mt-1">
                  Add, edit or remove entries in your academic journey.
                </p>
              </div>

              <button
                type="button"
                onClick={openAddEducation}
                className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5"
              >
                <Plus size={14} />
                Add Education
              </button>
            </div>

            {loadingEducation ? (
              <div className="py-10 text-center text-gray-500">
                Loading education...
              </div>
            ) : education.length === 0 ? (
              <div className="py-10 text-center text-gray-500">
                No education entries found.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {education.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={cx(
                          "w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center shrink-0",
                          item.gradient || "from-indigo-500 to-purple-500"
                        )}
                      >
                        <GraduationCap size={16} className="text-white" />
                      </div>

                      <div className="min-w-0">
                        <p className="font-medium text-white text-sm truncate">
                          {item.degree}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {item.institution}
                          {item.location ? ` · ${item.location}` : ""}
                        </p>
                        <p className="text-xs text-gray-600">{item.period}</p>
                      </div>
                    </div>

                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => openEditEducation(item)}
                        className="w-8 h-8 rounded-lg border border-slate-700 text-gray-300 hover:bg-slate-800 flex items-center justify-center"
                      >
                        <Pencil size={13} />
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteEducation(item)}
                        className="w-8 h-8 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ================================================= */}
        {/* RIGHT COLUMN — LIVE FRONTEND PREVIEW */}
        {/* ================================================= */}

        <div className="rounded-2xl border border-slate-700 bg-slate-950/60 p-7">

          {/* HEADING */}
          <h2 className="text-2xl font-bold text-white">
            {form.aboutTitle || "About"}{" "}
            <span className="text-indigo-400">
              {form.aboutHighlight || "Me"}
            </span>
          </h2>

          {/* PROFILE + BIO */}
          <div className="flex flex-col sm:flex-row gap-5 mt-6">
            <div className="w-28 h-36 rounded-xl overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
              {form.profileImage ? (
                <img
                  src={form.profileImage}
                  alt={form.profileImageAlt}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: form.objectPosition }}
                />
              ) : (
                <span className="text-gray-600 text-xs">
                  No Image
                </span>
              )}
            </div>

            <p className="text-sm text-gray-300 leading-relaxed">
              {renderBio(form.bio, form.highlightWords) ||
                "Your biography will appear here."}
            </p>
          </div>

          {/* ================================================= */}
          {/* SKILLS */}
          {/* ================================================= */}

          {/* SKILLS */}
          {groupedSkills.some((g) => g.items.length > 0) && (
            <div className="mt-8">

              {/* SKILLS HEADER + DROPDOWN */}
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Skills
                </p>

                <select
                  value={previewSkillCategory}
                  onChange={(e) => setPreviewSkillCategory(e.target.value)}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-gray-300 focus:outline-none focus:border-indigo-500"
                >
                  {groupedSkills
                    .filter((group) => group.items.length > 0)
                    .map((group) => (
                      <option key={group.key} value={group.key}>
                        {group.label}
                      </option>
                    ))}
                </select>
              </div>

              {/* SKILL GROUPS */}
              {groupedSkills
                .filter(
                  (group) =>
                    group.items.length > 0 &&
                    (previewSkillCategory === "all" ||
                      previewSkillCategory === group.key)
                )
                .map((group) => (
                  <div key={group.key} className="mb-5">

                    {/* CATEGORY TITLE */}
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-2.5">
                      {group.label}
                    </p>

                    {/* SKILLS */}
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((skill) => {
                        const IconComponent = getIconComponent(skill.icon);

                        return (
                          <span
                            key={skill.id}
                            className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs font-medium inline-flex items-center gap-1.5"
                            style={{
                              color: skill.color || "#FFFFFF",
                            }}
                          >
                            {IconComponent ? (
                              <IconComponent size={14} />
                            ) : (
                              <span>
                                {skill.icon || "?"}
                              </span>
                            )}

                            <span>{skill.name}</span>
                          </span>
                        );
                      })}
                    </div>

                  </div>
                ))}
            </div>
          )}

          {/* ================================================= */}
          {/* ACADEMIC / EDUCATION */}
          {/* ================================================= */}

          <div className="mt-8">
            <h3 className="text-lg font-bold text-white">
              {form.academicTitle || "Academic"}{" "}
              <span className="text-indigo-400">
                {form.academicHighlight || "Journey"}
              </span>
            </h3>

            {education.length === 0 ? (
              <p className="text-xs text-gray-600 mt-3">
                No education entries yet.
              </p>
            ) : (
              <div className="flex flex-col gap-3 mt-4">
                {education
                  .filter((e) => e.isActive)
                  .map((item) => (
                    <div
                      key={item.id}
                      className={cx(
                        "rounded-xl p-4 bg-gradient-to-br",
                        item.gradient ||
                        "from-indigo-500 to-purple-500"
                      )}
                    >
                      <p className="font-semibold text-white text-sm">
                        {item.degree}
                      </p>

                      <p className="text-xs text-white/80 mt-0.5">
                        {item.institution}
                        {item.location
                          ? ` · ${item.location}`
                          : ""}
                      </p>

                      <p className="text-xs text-white/70 mt-0.5">
                        {item.period}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ================================================= */}
      {/* ABOUT EDIT MODAL */}
      {/* ================================================= */}

      {aboutModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setAboutModal(false)}
          />

          <div
            className={cx(
              "relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border p-6 shadow-2xl",
              theme.surface,
              theme.border
            )}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={cx("text-lg font-bold", theme.text)}>
                  Edit About
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Update your About section and preview the changes live.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setAboutModal(false)}
                className="w-9 h-9 rounded-lg border border-slate-700 text-gray-400 hover:text-white hover:bg-slate-800 flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* FORM */}
              <div className="space-y-5">
                {/* SECTION HEADING */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Main Title" required error={errors.aboutTitle}>
                    <TextInput
                      value={form.aboutTitle}
                      onChange={(e) => update("aboutTitle", e.target.value)}
                      error={errors.aboutTitle}
                      placeholder="About"
                    />
                  </Field>

                  <Field
                    label="Highlighted Title"
                    required
                    error={errors.aboutHighlight}
                  >
                    <TextInput
                      value={form.aboutHighlight}
                      onChange={(e) =>
                        update("aboutHighlight", e.target.value)
                      }
                      error={errors.aboutHighlight}
                      placeholder="Me"
                    />
                  </Field>
                </div>

                <Field label="Profile Image">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];

                      if (!file) return;

                      const reader = new FileReader();

                      reader.onloadend = () => {
                        update("profileImage", reader.result);
                      };

                      reader.readAsDataURL(file);
                    }}
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-500 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
                  />

                  {form.profileImage && (
                    <div className="mt-3">
                      <img
                        src={form.profileImage}
                        alt="Profile preview"
                        className="h-24 w-20 rounded-lg object-cover border border-slate-700"
                        style={{
                          objectPosition: form.objectPosition,
                        }}
                      />
                    </div>
                  )}
                </Field>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Image Alt Text">
                    <TextInput
                      value={form.profileImageAlt}
                      onChange={(e) =>
                        update("profileImageAlt", e.target.value)
                      }
                      placeholder="Profile photo"
                    />
                  </Field>

                  <Field label="Object Position">
                    <TextInput
                      value={form.objectPosition}
                      onChange={(e) =>
                        update("objectPosition", e.target.value)
                      }
                      placeholder="center 20%"
                    />
                  </Field>
                </div>

                {/* BIO */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-300">
                      Biography
                    </span>
                    <span className="text-xs text-gray-500">
                      {"{{experience}}"} is automatic
                    </span>
                  </div>

                  <Field required error={errors.bio}>
                    <TextArea
                      value={form.bio}
                      onChange={(e) => update("bio", e.target.value)}
                      error={errors.bio}
                      rows={6}
                      placeholder="Write your biography..."
                    />
                  </Field>

                  <p className="text-xs text-gray-500 mt-2">
                    Use <strong>{"{{experience}}"}</strong> in the bio. It
                    will automatically display your calculated professional
                    experience.
                  </p>
                </div>

                {/* HIGHLIGHT WORDS */}
                <div>
                  <p className="text-sm font-medium text-gray-300 mb-1">
                    Highlight Words
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Words added here will receive the highlight color in your
                    biography.
                  </p>

                  <div className="flex gap-2">
                    <TextInput
                      value={newHighlight}
                      onChange={(e) => setNewHighlight(e.target.value)}
                      placeholder="Digital Creator"
                    />

                    <button
                      type="button"
                      onClick={addHighlight}
                      className="px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold"
                    >
                      Add
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.highlightWords.map((word, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-sm"
                      >
                        <span>{word}</span>
                        <button
                          type="button"
                          onClick={() => removeHighlight(index)}
                          className="text-gray-400 hover:text-red-400"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SKILL CATEGORY LABELS */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-gray-300">
                        Skill Category Labels
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Labels are stored in the About database record.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addCategoryLabel}
                      className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold"
                    >
                      + Category
                    </button>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {form.skillLabels.map((category, index) => (
                      <Field key={category.key} label={category.key}>
                        <TextInput
                          value={category.label}
                          onChange={(e) =>
                            updateSkillLabel(index, e.target.value)
                          }
                          placeholder="Category label"
                        />
                      </Field>
                    ))}
                  </div>
                </div>

                {/* ACADEMIC */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Academic Title"
                    required
                    error={errors.academicTitle}
                  >
                    <TextInput
                      value={form.academicTitle}
                      onChange={(e) =>
                        update("academicTitle", e.target.value)
                      }
                      error={errors.academicTitle}
                      placeholder="Academic"
                    />
                  </Field>

                  <Field
                    label="Academic Highlight"
                    required
                    error={errors.academicHighlight}
                  >
                    <TextInput
                      value={form.academicHighlight}
                      onChange={(e) =>
                        update("academicHighlight", e.target.value)
                      }
                      error={errors.academicHighlight}
                      placeholder="Journey"
                    />
                  </Field>
                </div>

                {/* SETTINGS */}
                <div className="grid sm:grid-cols-3 gap-4 pt-2">
                  <Field label="Display Order">
                    <TextInput
                      type="number"
                      value={form.order}
                      onChange={(e) =>
                        update("order", Number(e.target.value))
                      }
                    />
                  </Field>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isFeatured}
                      onChange={(e) =>
                        update("isFeatured", e.target.checked)
                      }
                      className="w-4 h-4 accent-indigo-600"
                    />
                    <span className="text-sm text-gray-300">Featured</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => update("isActive", e.target.checked)}
                      className="w-4 h-4 accent-indigo-600"
                    />
                    <span className="text-sm text-gray-300">Active</span>
                  </label>
                </div>
              </div>

              {/* LIVE PREVIEW */}
              <div
                className={cx("rounded-2xl border p-6 flex flex-col", theme.border)}
              >
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-5">
                  Live Preview
                </p>

                <h2 className="text-xl font-bold text-white">
                  {form.aboutTitle || "About"}{" "}
                  <span className="text-indigo-400">
                    {form.aboutHighlight || "Me"}
                  </span>
                </h2>

                <div className="flex gap-4 mt-5">
                  <div className="w-20 h-24 rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                    {form.profileImage ? (
                      <img
                        src={form.profileImage}
                        alt={form.profileImageAlt}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: form.objectPosition }}
                      />
                    ) : (
                      <span className="text-gray-600 text-xs">No Image</span>
                    )}
                  </div>

                  <p className="text-xs text-gray-300 leading-relaxed">
                    {renderBio(form.bio, form.highlightWords) ||
                      "Your biography will appear here."}
                  </p>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-bold text-white">
                    {form.academicTitle || "Academic"}{" "}
                    <span className="text-indigo-400">
                      {form.academicHighlight || "Journey"}
                    </span>
                  </h3>

                  {education.length === 0 ? (
                    <p className="text-xs text-gray-600 mt-3">
                      No education entries yet.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3 mt-4">
                      {education
                        .filter((e) => e.isActive)
                        .map((item) => (
                          <div
                            key={item.id}
                            className={cx(
                              "rounded-xl p-4 bg-gradient-to-br",
                              item.gradient ||
                              "from-indigo-500 to-purple-500"
                            )}
                          >
                            <p className="font-semibold text-white text-sm">
                              {item.degree}
                            </p>

                            <p className="text-xs text-white/80 mt-0.5">
                              {item.institution}
                              {item.location
                                ? ` · ${item.location}`
                                : ""}
                            </p>

                            <p className="text-xs text-white/70 mt-0.5">
                              {item.period}
                            </p>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

              </div>
            </div>

            <div className="flex justify-end gap-3 mt-7 pt-5 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setAboutModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-700 text-gray-300 hover:bg-slate-800"
              >
                Cancel
              </button>

              <PrimaryButton onClick={save}>
                <CheckCircle2 size={15} />
                Save Changes
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* SKILL ADD / EDIT MODAL */}
      {/* ================================================= */}

      {skillModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setSkillModal(false)}
          />

          <div
            className={cx(
              "relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border p-6 shadow-2xl",
              theme.surface,
              theme.border
            )}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={cx("text-lg font-bold", theme.text)}>
                  {editingSkill ? "Edit Skill" : "Add New Skill"}
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  {editingSkill
                    ? "Edit the selected skill and preview the changes."
                    : "Create a skill and preview it before saving."}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSkillModal(false)}
                className="w-9 h-9 rounded-lg border border-slate-700 text-gray-400 hover:text-white hover:bg-slate-800 flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* FORM */}
              <div className="space-y-4">
                <Field label="Skill Name">
                  <TextInput
                    value={skillForm.name}
                    onChange={(e) => updateSkillForm("name", e.target.value)}
                    placeholder="React.js"
                  />
                </Field>

                <Field label="Category">
                  {customCategoryMode ? (
                    <div className="flex gap-2">
                      <TextInput
                        value={skillForm.category}
                        onChange={(e) =>
                          updateSkillForm("category", e.target.value)
                        }
                        placeholder="e.g. frontend"
                        autoFocus
                      />

                      <button
                        type="button"
                        onClick={() => {
                          setCustomCategoryMode(false);
                          updateSkillForm("category", "");
                        }}
                        className="px-3 rounded-xl border border-slate-700 text-gray-400 hover:text-white hover:bg-slate-800 text-sm shrink-0"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <select
                      value={skillForm.category}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          setCustomCategoryMode(true);
                          updateSkillForm("category", "");
                        } else {
                          updateSkillForm("category", e.target.value);
                        }
                      }}
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-sm text-gray-200 focus:outline-none focus:border-indigo-500"
                    >
                      <option value="" disabled>
                        Select a category
                      </option>

                      {categoryOptions.map((cat) => (
                        <option key={cat.key} value={cat.key}>
                          {cat.label}
                        </option>
                      ))}

                      <option value="__new__">+ Add new category</option>
                    </select>
                  )}

                  <p className="text-xs text-gray-500 mt-1">
                    Categories are pulled from your existing skill labels.
                    Pick "Add new category" to create one.
                  </p>
                </Field>

                <Field label="Icon Name">
                  <div className="relative">
                    <input
                      type="text"
                      value={skillForm.icon}
                      onChange={(e) => {
                        const value = e.target.value;

                        setSkillForm((prev) => ({
                          ...prev,
                          icon: value,
                        }));

                        setIconSearch(value);
                        setShowIconSuggestions(true);
                      }}
                      onFocus={() => {
                        setIconSearch(skillForm.icon);
                        setShowIconSuggestions(true);
                      }}
                      placeholder="e.g. FaReact"
                      className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-indigo-500"
                    />

                    {showIconSuggestions && iconSearch && (
                      <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 shadow-xl">

                        {filteredIconNames.length > 0 ? (
                          filteredIconNames.map((iconName) => {
                            const IconComponent = icons[iconName];

                            return (
                              <button
                                key={iconName}
                                type="button"
                                onClick={() => {
                                  setSkillForm((prev) => ({
                                    ...prev,
                                    icon: iconName,
                                  }));

                                  setIconSearch(iconName);
                                  setShowIconSuggestions(false);
                                }}
                                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-300 hover:bg-slate-800 hover:text-white"
                              >
                                {IconComponent && (
                                  <IconComponent
                                    size={18}
                                    className="shrink-0"
                                  />
                                )}

                                <span>{iconName}</span>
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-3 text-xs text-gray-500">
                            No icons found
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                </Field>

                <Field label="Color">
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={skillForm.color}
                      onChange={(e) =>
                        updateSkillForm("color", e.target.value)
                      }
                      className="w-12 h-10 rounded-lg cursor-pointer bg-transparent"
                    />

                    <TextInput
                      value={skillForm.color}
                      onChange={(e) =>
                        updateSkillForm("color", e.target.value)
                      }
                      placeholder="#61DAFB"
                    />
                  </div>
                </Field>

                <Field label="Display Order">
                  <TextInput
                    type="number"
                    value={skillForm.order}
                    onChange={(e) =>
                      updateSkillForm("order", Number(e.target.value))
                    }
                  />
                </Field>

                <div className="flex gap-6 pt-2">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skillForm.isFeatured}
                      onChange={(e) =>
                        updateSkillForm("isFeatured", e.target.checked)
                      }
                      className="accent-indigo-600"
                    />
                    Featured
                  </label>

                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skillForm.isActive}
                      onChange={(e) =>
                        updateSkillForm("isActive", e.target.checked)
                      }
                      className="accent-indigo-600"
                    />
                    Active
                  </label>
                </div>
              </div>

              {/* LIVE PREVIEW */}
              <div
                className={cx("rounded-2xl border p-6 flex flex-col", theme.border)}
              >
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-5">
                  Live Preview
                </p>

                <div className="flex-1 flex items-center justify-center">
                  <div className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900/70 p-7 text-center">
                    <div
                      className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center border border-slate-700 bg-slate-800 text-lg font-bold"
                      style={{ color: skillForm.color || "#FFFFFF" }}
                    >
                      {(() => {
                        const IconComponent = getIconComponent(skillForm.icon);

                        return IconComponent ? (
                          <IconComponent size={40} />
                        ) : (
                          <span className="text-sm text-gray-500">
                            ?
                          </span>
                        );
                      })()}
                    </div>

                    <h3 className="text-xl font-bold text-white mt-5">
                      {skillForm.name || "Skill Name"}
                    </h3>

                    <p className="text-sm text-gray-500 mt-2">
                      {skillForm.category || "category"}
                    </p>

                    <div className="flex items-center justify-center gap-2 mt-5">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: skillForm.color }}
                      />
                      <span className="text-xs text-gray-500">
                        {skillForm.color}
                      </span>
                    </div>

                    <div className="flex justify-center gap-2 mt-5">
                      {skillForm.isFeatured && (
                        <span className="px-2.5 py-1 rounded-full bg-yellow-500/10 text-yellow-400 text-xs">
                          Featured
                        </span>
                      )}

                      <span
                        className={
                          skillForm.isActive
                            ? "px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs"
                            : "px-2.5 py-1 rounded-full bg-red-500/10 text-red-400 text-xs"
                        }
                      >
                        {skillForm.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-7 pt-5 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setSkillModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-700 text-gray-300 hover:bg-slate-800"
              >
                Cancel
              </button>

              <PrimaryButton onClick={saveSkill} disabled={savingSkill}>
                {savingSkill
                  ? "Saving..."
                  : editingSkill
                    ? "Update Skill"
                    : "Add Skill"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* EDUCATION ADD / EDIT MODAL */}
      {/* ================================================= */}

      {educationModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setEducationModal(false)}
          />

          <div
            className={cx(
              "relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border p-6 shadow-2xl",
              theme.surface,
              theme.border
            )}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={cx("text-lg font-bold", theme.text)}>
                  {editingEducation ? "Edit Education" : "Add Education"}
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  {editingEducation
                    ? "Edit this entry and preview the changes."
                    : "Add an entry to your academic journey."}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setEducationModal(false)}
                className="w-9 h-9 rounded-lg border border-slate-700 text-gray-400 hover:text-white hover:bg-slate-800 flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* FORM */}
              <div className="space-y-4">
                <Field label="Degree">
                  <TextInput
                    value={educationForm.degree}
                    onChange={(e) =>
                      updateEducationForm("degree", e.target.value)
                    }
                    placeholder="B.Tech - CSE"
                  />
                </Field>

                <Field label="Institution">
                  <TextInput
                    value={educationForm.institution}
                    onChange={(e) =>
                      updateEducationForm("institution", e.target.value)
                    }
                    placeholder="Amrita Vishwa Vidyapeetham"
                  />
                </Field>

                <Field label="Location">
                  <TextInput
                    value={educationForm.location}
                    onChange={(e) =>
                      updateEducationForm("location", e.target.value)
                    }
                    placeholder="Amritapuri, Kerala"
                  />
                </Field>

                <Field label="Period">
                  <TextInput
                    value={educationForm.period}
                    onChange={(e) =>
                      updateEducationForm("period", e.target.value)
                    }
                    placeholder="2021 - 2025"
                  />
                </Field>

                <Field label="Gradient (Tailwind classes)">
                  <TextInput
                    value={educationForm.gradient}
                    onChange={(e) =>
                      updateEducationForm("gradient", e.target.value)
                    }
                    placeholder="from-indigo-500 to-purple-500"
                  />
                </Field>

                <Field label="Display Order">
                  <TextInput
                    type="number"
                    value={educationForm.order}
                    onChange={(e) =>
                      updateEducationForm("order", Number(e.target.value))
                    }
                  />
                </Field>

                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={educationForm.isActive}
                    onChange={(e) =>
                      updateEducationForm("isActive", e.target.checked)
                    }
                    className="accent-indigo-600"
                  />
                  Active
                </label>
              </div>

              {/* LIVE PREVIEW */}
              <div
                className={cx("rounded-2xl border p-6 flex flex-col", theme.border)}
              >
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-5">
                  Live Preview
                </p>

                <div className="flex-1 flex items-center justify-center">
                  <div
                    className={cx(
                      "w-full max-w-sm rounded-2xl p-6 bg-gradient-to-br",
                      educationForm.gradient || "from-indigo-500 to-purple-500"
                    )}
                  >
                    <GraduationCap size={22} className="text-white/90" />

                    <p className="font-bold text-white mt-4">
                      {educationForm.degree || "Degree name"}
                    </p>

                    <p className="text-sm text-white/80 mt-1">
                      {educationForm.institution || "Institution"}
                      {educationForm.location
                        ? ` · ${educationForm.location}`
                        : ""}
                    </p>

                    <p className="text-xs text-white/70 mt-2">
                      {educationForm.period || "Period"}
                    </p>

                    <span
                      className={
                        educationForm.isActive
                          ? "inline-block mt-4 px-2.5 py-1 rounded-full bg-white/15 text-white text-xs"
                          : "inline-block mt-4 px-2.5 py-1 rounded-full bg-black/20 text-white/60 text-xs"
                      }
                    >
                      {educationForm.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-7 pt-5 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setEducationModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-700 text-gray-300 hover:bg-slate-800"
              >
                Cancel
              </button>

              <PrimaryButton onClick={saveEducation} disabled={savingEducation}>
                {savingEducation
                  ? "Saving..."
                  : editingEducation
                    ? "Update Education"
                    : "Add Education"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================================================
   ADMIN: CONTACT
============================================================================ */
function AdminContact({ contact, setContact, notify }) {
  const theme = useTheme();
  const getSocialIcon = (iconName) => {
    if (!iconName) return null;

    // Exact match
    if (icons[iconName]) {
      return icons[iconName];
    }

    // Case-insensitive match
    const matchedKey = Object.keys(icons).find(
      (key) => key.toLowerCase() === iconName.toLowerCase()
    );

    return matchedKey ? icons[matchedKey] : null;
  };
  const [showEdit, setShowEdit] = useState(false);

  if (!contact) {
    return (
      <div className={cx("p-6 rounded-2xl border", theme.surface, theme.border)}>
        <p className="text-slate-400">No contact information available.</p>
      </div>
    );
  }

  const visibleSocialLinks = (contact.socialLinks || []).filter(
    (link) => link && link.show
  );

  return (
    <>
      {/* ================================================= */}
      {/* MAIN PREVIEW */}
      {/* ================================================= */}

      <div className="anim-fadeUp max-w-5xl">
        <div
          className={cx(
            "rounded-2xl border overflow-hidden",
            theme.surface,
            theme.border
          )}
        >
          {/* Header */}
          <div
            className={cx(
              "p-6 border-b flex items-center justify-between gap-4",
              "bg-gradient-to-r from-indigo-500/10 via-transparent to-transparent",
              theme.border
            )}
          >
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                  <Mail size={14} className="text-indigo-400" />
                </div>
                <p className="text-sm font-medium text-indigo-400">Contact Section</p>
              </div>

              <h2 className={cx("text-2xl font-bold", theme.text)}>
                {contact.contactTitle || "Get In Touch"}
              </h2>

              <p className="text-sm mt-1 text-slate-400">
                {contact.contactSubtitle || ""}
              </p>

              {contact.getInTouchTitle && (
                <p className="text-xs mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-500/10 text-slate-400">
                  {contact.getInTouchTitle}
                </p>
              )}
            </div>

            <PrimaryButton onClick={() => setShowEdit(true)}>
              <Pencil size={15} />
              Edit
            </PrimaryButton>
          </div>

          {/* Preview grid */}
          <div className="p-6 grid md:grid-cols-2 gap-4">

            {/* Email */}
            <div
              className={cx(
                "rounded-xl border p-5 border-l-4 border-l-indigo-500/60 transition-colors hover:border-indigo-500/40",
                theme.border,
                theme.surface
              )}
            >
              <div className="flex items-center gap-2 mb-3">
                <Mail size={14} className="text-indigo-400" />
                <p className="text-xs uppercase tracking-wide font-semibold text-indigo-400">
                  Email
                </p>
              </div>

              <p className={cx("font-medium", theme.text)}>
                {contact.email || "Not configured"}
              </p>

              {contact.emailDescription && (
                <p className="text-sm mt-2 text-slate-400">
                  {contact.emailDescription}
                </p>
              )}

              {contact.sendEmailText && (
                <p className="text-xs mt-2 text-slate-500">
                  Send text: {contact.sendEmailText}
                </p>
              )}
            </div>

            {/* Location */}
            <div
              className={cx(
                "rounded-xl border p-5 border-l-4 border-l-purple-500/60 transition-colors hover:border-purple-500/40",
                theme.border,
                theme.surface
              )}
            >
              <div className="flex items-center gap-2 mb-3">
                <icons.MapPin size={14} className="text-purple-400" />
                <p className="text-xs uppercase tracking-wide font-semibold text-purple-400">
                  Location
                </p>
              </div>

              <p className={cx("font-medium", theme.text)}>
                {contact.location || "Not configured"}
              </p>

              <span
                className={cx(
                  "inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full text-xs font-medium",
                  contact.showLocation
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-slate-500/10 text-slate-400"
                )}
              >
                <span
                  className={cx(
                    "w-1.5 h-1.5 rounded-full",
                    contact.showLocation ? "bg-emerald-400" : "bg-slate-500"
                  )}
                />
                {contact.showLocation ? "Visible" : "Hidden"}
              </span>
            </div>

            {/* Contact Form */}
            <div
              className={cx(
                "rounded-xl border p-5 border-l-4 border-l-sky-500/60 transition-colors hover:border-sky-500/40",
                theme.border,
                theme.surface
              )}
            >
              <div className="flex items-center gap-2 mb-3">
                <icons.MessageSquare size={14} className="text-sky-400" />
                <p className="text-xs uppercase tracking-wide font-semibold text-sky-400">
                  Contact Form
                </p>
              </div>

              <p className={cx("font-medium", theme.text)}>
                {contact.sendMessageTitle || "Send a Message"}
              </p>

              <p className="text-sm mt-2 text-slate-400">
                Button: <span className="text-slate-300">{contact.sendButtonText || "Send Message"}</span>
              </p>

              <p className="text-xs mt-1 text-slate-500">
                Sending: {contact.sendingButtonText || "Sending..."}
              </p>
            </div>

            {/* Status */}
            <div
              className={cx(
                "rounded-xl border p-5 border-l-4 transition-colors",
                contact.isActive ? "border-l-emerald-500/60" : "border-l-rose-500/60",
                theme.border,
                theme.surface
              )}
            >
              <div className="flex items-center gap-2 mb-3">
                <icons.CircleCheck
                  size={14}
                  className={contact.isActive ? "text-emerald-400" : "text-rose-400"}
                />
                <p className="text-xs uppercase tracking-wide font-semibold text-slate-400">
                  Status
                </p>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <span
                  className={cx(
                    "px-3 py-1 rounded-full text-xs font-medium",
                    contact.isActive
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-rose-500/10 text-rose-400"
                  )}
                >
                  {contact.isActive ? "Active" : "Inactive"}
                </span>

                {contact.isFeatured && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 flex items-center gap-1">
                    <icons.Star size={11} className="fill-amber-400 text-amber-400" />
                    Featured
                  </span>
                )}

                {typeof contact.order !== "undefined" && contact.order !== null && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-400">
                    Order: {contact.order}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className={cx("p-6 border-t", theme.border)}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <icons.Link2 size={16} className="text-cyan-400" />
                <h3 className={cx("font-semibold", theme.text)}>Social Links</h3>
              </div>

              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400">
                {visibleSocialLinks.length} visible
              </span>
            </div>

            {visibleSocialLinks.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No visible social links.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleSocialLinks.map((link, i) => {
                  const Icon = getSocialIcon(link.icon);

                  return (
                    <div
                      key={link.id || i}
                      className={cx(
                        "rounded-xl border p-4 transition-colors hover:border-cyan-500/40",
                        theme.border
                      )}
                    >
                      <div className="flex items-start gap-3">
                        {Icon && (
                          <div
                            className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center border"
                            style={{
                              color: link.iconColor || "#22d3ee",
                            }}
                          >
                            <Icon size={20} />
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <p className={cx("font-medium", theme.text)}>
                            {link.title || link.id || "Untitled"}
                          </p>

                          {link.description && (
                            <p className="text-xs mt-1 text-slate-400">
                              {link.description}
                            </p>
                          )}

                          <p className="text-xs mt-1 truncate text-cyan-400/80">
                            {link.href || "No URL"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================================================= */}
      {/* EDIT MODAL */}
      {/* ================================================= */}

      {showEdit && (
        <ContactEditModal
          contact={contact}
          setContact={setContact}
          notify={notify}
          onClose={() => setShowEdit(false)}
        />
      )}
    </>
  );
}

function ContactEditModal({ contact, setContact, notify, onClose }) {
  const theme = useTheme();
  const [iconSearch, setIconSearch] = useState("");
  const [activeIconIndex, setActiveIconIndex] = useState(null);

  const filteredIconNames = Object.keys(icons)
    .filter((name) =>
      name.toLowerCase().includes(iconSearch.toLowerCase())
    )
    .slice(0, 20);
  const [form, setForm] = useState({
    ...contact,
    socialLinks: contact?.socialLinks || [],
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAccessKey, setShowAccessKey] = useState(false);

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateSocialLink = (index, key, value) => {
    setForm((prev) => {
      const links = [...(prev.socialLinks || [])];

      links[index] = {
        ...links[index],
        [key]: value,
      };

      return {
        ...prev,
        socialLinks: links,
      };
    });
  };

  const addSocialLink = () => {
    setForm((prev) => ({
      ...prev,
      socialLinks: [
        ...(prev.socialLinks || []),
        {
          id: `link-${Date.now()}`,
          title: "",
          description: "",
          actionText: "",
          actionColor: "",
          href: "",
          show: true,
          icon: "",
          iconColor: "",
          boxClass: "",
        },
      ],
    }));
  };

  const removeSocialLink = (index) => {
    setForm((prev) => {
      const links = [...(prev.socialLinks || [])];
      links.splice(index, 1);

      return {
        ...prev,
        socialLinks: links,
      };
    });
  };

  const save = async () => {
    const errs = {};

    if (!form.contactTitle?.trim()) {
      errs.contactTitle = "Contact title is required";
    }

    if (!form.email?.trim()) {
      errs.email = "Email is required";
    } else if (!isEmail(form.email)) {
      errs.email = "Enter a valid email";
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      return;
    }

    try {
      setSaving(true);

      const response = await api.put(`/contact/${form.id}`, form);

      if (response.data.success) {
        setContact(response.data.data);
        notify("success", "Contact information updated successfully.");
        onClose();
      }
    } catch (error) {
      console.error("Update contact error:", error);

      notify(
        "error",
        error.response?.data?.message ||
        "Failed to update contact information."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cx(
          "relative z-10 w-full max-w-5xl",
          "max-h-[90vh] rounded-2xl border",
          "shadow-2xl overflow-hidden flex flex-col",
          theme.surface,
          theme.border
        )}
      >

        {/* Header (sticky) */}
        <div
          className={cx(
            "flex items-center justify-between",
            "p-5 border-b shrink-0",
            "bg-gradient-to-r from-indigo-500/10 via-transparent to-transparent",
            theme.surface,
            theme.border
          )}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center">
              <Pencil size={16} className="text-indigo-400" />
            </div>

            <div>
              <h2 className={cx("text-xl font-bold", theme.text)}>Edit Contact</h2>
              <p className="text-sm text-slate-400">
                Update your contact section information
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={cx(
              "w-9 h-9 rounded-lg",
              "flex items-center justify-center",
              "hover:bg-white/10 transition-colors",
              theme.text
            )}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="overflow-y-auto flex-1 p-6">
          <div className="space-y-8">

            {/* 1. Contact Header */}
            <section>
              <h3 className={cx("text-lg font-semibold mb-4 flex items-center gap-2", theme.text)}>
                <span className="w-7 h-7 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                  <icons.Type size={14} className="text-indigo-400" />
                </span>
                Contact Header
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Contact Title" required error={errors.contactTitle}>
                  <TextInput
                    value={form.contactTitle || ""}
                    onChange={(e) => update("contactTitle", e.target.value)}
                    error={errors.contactTitle}
                  />
                </Field>

                <Field label="Get In Touch Title">
                  <TextInput
                    value={form.getInTouchTitle || ""}
                    onChange={(e) => update("getInTouchTitle", e.target.value)}
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Contact Subtitle">
                    <textarea
                      value={form.contactSubtitle || ""}
                      onChange={(e) => update("contactSubtitle", e.target.value)}
                      rows={3}
                      className={cx(
                        "w-full rounded-xl border",
                        "p-3 bg-transparent",
                        "outline-none resize-none focus:ring-2 focus:ring-indigo-500/40",
                        theme.border,
                        theme.text
                      )}
                    />
                  </Field>
                </div>
              </div>
            </section>

            {/* 2. Email & Location */}
            <section>
              <h3 className={cx("text-lg font-semibold mb-4 flex items-center gap-2", theme.text)}>
                <span className="w-7 h-7 rounded-lg bg-purple-500/15 flex items-center justify-center">
                  <icons.MapPin size={14} className="text-purple-400" />
                </span>
                Email & Location
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" required error={errors.email}>
                  <TextInput
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => update("email", e.target.value)}
                    error={errors.email}
                  />
                </Field>

                <Field label="Email Description">
                  <TextInput
                    value={form.emailDescription || ""}
                    onChange={(e) => update("emailDescription", e.target.value)}
                  />
                </Field>

                <Field label="Location">
                  <TextInput
                    value={form.location || ""}
                    onChange={(e) => update("location", e.target.value)}
                  />
                </Field>

                <Field label="Send Email Text">
                  <TextInput
                    value={form.sendEmailText || ""}
                    onChange={(e) => update("sendEmailText", e.target.value)}
                  />
                </Field>

                <label className={cx("flex items-center gap-3 cursor-pointer", theme.text)}>
                  <input
                    type="checkbox"
                    checked={!!form.showLocation}
                    onChange={(e) => update("showLocation", e.target.checked)}
                    className="accent-purple-500"
                  />
                  Show Location
                </label>
                <Field label="Phone">
                  <TextInput
                    value={form.phone || ""}
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </Field>

                <Field label="Phone Description">
                  <TextInput
                    value={form.phoneDescription || ""}
                    onChange={(e) => update("phoneDescription", e.target.value)}
                  />
                </Field>

                <Field label="Phone Text">
                  <TextInput
                    value={form.phoneText || ""}
                    onChange={(e) => update("phoneText", e.target.value)}
                  />
                </Field>

                <label className={cx(
                  "flex items-center gap-3 cursor-pointer",
                  theme.text
                )}>
                  <input
                    type="checkbox"
                    checked={!!form.showPhone}
                    onChange={(e) => update("showPhone", e.target.checked)}
                    className="accent-cyan-500"
                  />
                  Show Phone
                </label>
              </div>
            </section>

            {/* 3. Contact Form */}
            <section>
              <h3 className={cx("text-lg font-semibold mb-4 flex items-center gap-2", theme.text)}>
                <span className="w-7 h-7 rounded-lg bg-sky-500/15 flex items-center justify-center">
                  <icons.MessageSquare size={14} className="text-sky-400" />
                </span>
                Contact Form
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Send Message Title">
                  <TextInput
                    value={form.sendMessageTitle || ""}
                    onChange={(e) => update("sendMessageTitle", e.target.value)}
                  />
                </Field>

                <Field label="Name Label">
                  <TextInput
                    value={form.nameLabel || ""}
                    onChange={(e) => update("nameLabel", e.target.value)}
                  />
                </Field>

                <Field label="Email Label">
                  <TextInput
                    value={form.emailLabel || ""}
                    onChange={(e) => update("emailLabel", e.target.value)}
                  />
                </Field>

                <Field label="Message Label">
                  <TextInput
                    value={form.messageLabel || ""}
                    onChange={(e) => update("messageLabel", e.target.value)}
                  />
                </Field>

                <Field label="Name Placeholder">
                  <TextInput
                    value={form.namePlaceholder || ""}
                    onChange={(e) => update("namePlaceholder", e.target.value)}
                  />
                </Field>

                <Field label="Email Placeholder">
                  <TextInput
                    value={form.emailPlaceholder || ""}
                    onChange={(e) => update("emailPlaceholder", e.target.value)}
                  />
                </Field>

                <Field label="Message Placeholder">
                  <TextInput
                    value={form.messagePlaceholder || ""}
                    onChange={(e) => update("messagePlaceholder", e.target.value)}
                  />
                </Field>

                <Field label="Send Button Text">
                  <TextInput
                    value={form.sendButtonText || ""}
                    onChange={(e) => update("sendButtonText", e.target.value)}
                  />
                </Field>

                <Field label="Sending Button Text">
                  <TextInput
                    value={form.sendingButtonText || ""}
                    onChange={(e) => update("sendingButtonText", e.target.value)}
                  />
                </Field>
              </div>
            </section>

            {/* 4. Form Messages */}
            <section>
              <h3 className={cx("text-lg font-semibold mb-4 flex items-center gap-2", theme.text)}>
                <span className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <icons.CircleCheck size={14} className="text-emerald-400" />
                </span>
                Form Messages
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Success Title">
                  <TextInput
                    value={form.successTitle || ""}
                    onChange={(e) => update("successTitle", e.target.value)}
                  />
                </Field>

                <Field label="Fill Details Message">
                  <TextInput
                    value={form.fillDetailsMessage || ""}
                    onChange={(e) => update("fillDetailsMessage", e.target.value)}
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Success Message">
                    <textarea
                      value={form.successMessage || ""}
                      onChange={(e) => update("successMessage", e.target.value)}
                      rows={3}
                      className={cx(
                        "w-full rounded-xl border",
                        "p-3 bg-transparent",
                        "outline-none resize-none focus:ring-2 focus:ring-emerald-500/40",
                        theme.border,
                        theme.text
                      )}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Failure Message">
                    <textarea
                      value={form.failMessage || ""}
                      onChange={(e) => update("failMessage", e.target.value)}
                      rows={3}
                      className={cx(
                        "w-full rounded-xl border",
                        "p-3 bg-transparent",
                        "outline-none resize-none focus:ring-2 focus:ring-rose-500/40",
                        theme.border,
                        theme.text
                      )}
                    />
                  </Field>
                </div>
              </div>
            </section>

            {/* 5. Web3Forms */}
            <section>
              <h3 className={cx("text-lg font-semibold mb-4 flex items-center gap-2", theme.text)}>
                <span className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <icons.KeyRound size={14} className="text-amber-400" />
                </span>
                Web3Forms
              </h3>

              <div
                className={cx(
                  "rounded-xl border p-5 border-l-4 border-l-amber-500/60",
                  theme.border
                )}
              >
                <Field label="Access Key">
                  <div className="flex gap-2">
                    <TextInput
                      type={showAccessKey ? "text" : "password"}
                      value={form.web3formsAccessKey || ""}
                      onChange={(e) => update("web3formsAccessKey", e.target.value)}
                      className="flex-1"
                    />

                    <button
                      type="button"
                      onClick={() => setShowAccessKey((s) => !s)}
                      className={cx(
                        "px-3 rounded-xl border text-sm shrink-0 transition-colors hover:bg-white/10",
                        theme.border,
                        theme.text
                      )}
                    >
                      {showAccessKey ? "Hide" : "Show"}
                    </button>
                  </div>
                </Field>

                <p className="text-xs mt-2 text-amber-400/80">
                  The access key is used to submit contact messages through Web3Forms.
                </p>
              </div>
            </section>

            {/* 6. Social Links */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className={cx("text-lg font-semibold flex items-center gap-2", theme.text)}>
                  <span className="w-7 h-7 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                    <icons.Link2 size={14} className="text-cyan-400" />
                  </span>
                  Social Links
                </h3>

                <button
                  type="button"
                  onClick={addSocialLink}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-cyan-500/40 text-sm text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                >
                  <icons.Plus size={14} />
                  Add Social Link
                </button>
              </div>

              {(form.socialLinks || []).length === 0 ? (
                <p className="text-sm text-slate-500 italic">No social links yet.</p>
              ) : (
                <div className="space-y-4">
                  {(form.socialLinks || []).map((link, index) => (
                    <div
                      key={link.id || index}
                      className={cx(
                        "rounded-xl border p-4 border-l-4 border-l-cyan-500/40",
                        theme.border
                      )}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <p className={cx("text-sm font-medium flex items-center gap-2", theme.text)}>
                          <span className="w-5 h-5 rounded-full bg-cyan-500/15 text-cyan-400 text-[10px] flex items-center justify-center font-semibold">
                            {index + 1}
                          </span>
                          {link.title || `Link ${index + 1}`}
                        </p>

                        <button
                          type="button"
                          onClick={() => removeSocialLink(index)}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-rose-400 hover:bg-rose-500/10 transition-colors"
                        >
                          <icons.Trash2 size={13} />
                          Remove
                        </button>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Title">
                          <TextInput
                            value={link.title || ""}
                            onChange={(e) =>
                              updateSocialLink(index, "title", e.target.value)
                            }
                          />
                        </Field>

                        <Field label="URL">
                          <TextInput
                            value={link.href || ""}
                            onChange={(e) =>
                              updateSocialLink(index, "href", e.target.value)
                            }
                          />
                        </Field>

                        <div className="sm:col-span-2">
                          <Field label="Description">
                            <TextInput
                              value={link.description || ""}
                              onChange={(e) =>
                                updateSocialLink(index, "description", e.target.value)
                              }
                            />
                          </Field>
                        </div>

                        <Field label="Action Text">
                          <TextInput
                            value={link.actionText || ""}
                            onChange={(e) =>
                              updateSocialLink(index, "actionText", e.target.value)
                            }
                          />
                        </Field>

                        <Field label="Icon">
                          <div className="relative">
                            <TextInput
                              value={iconSearch || link.icon || ""}
                              placeholder="Search icon e.g. Github"
                              onFocus={() => {
                                setActiveIconIndex(index);
                                setIconSearch(link.icon || "");
                              }}
                              onChange={(e) => {
                                setIconSearch(e.target.value);
                                setActiveIconIndex(index);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && filteredIconNames[0]) {
                                  e.preventDefault();

                                  updateSocialLink(
                                    index,
                                    "icon",
                                    filteredIconNames[0]
                                  );

                                  setIconSearch("");
                                  setActiveIconIndex(null);
                                }

                                if (e.key === "Escape") {
                                  setIconSearch("");
                                  setActiveIconIndex(null);
                                }
                              }}
                            />

                            {activeIconIndex === index && iconSearch.trim() && (
                              <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto rounded-xl border border-[#232B38] bg-[#0F141C] shadow-xl">
                                {filteredIconNames.length > 0 ? (
                                  filteredIconNames.map((iconName) => {
                                    const IconComponent = icons[iconName];

                                    return (
                                      <button
                                        key={iconName}
                                        type="button"
                                        onClick={() => {
                                          updateSocialLink(
                                            index,
                                            "icon",
                                            iconName
                                          );

                                          setIconSearch("");
                                          setActiveIconIndex(null);
                                        }}
                                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-white"
                                      >
                                        {IconComponent && (
                                          <IconComponent size={18} />
                                        )}

                                        <span>{iconName}</span>
                                      </button>
                                    );
                                  })
                                ) : (
                                  <div className="px-3 py-3 text-sm text-[#8B93A0]">
                                    No icons found
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </Field>

                        <Field label="Icon Color">
                          <input
                            type="color"
                            value={link.iconColor || "#60A5FA"}
                            onChange={(e) =>
                              updateSocialLink(index, "iconColor", e.target.value)
                            }
                            className="w-full h-11 p-1 rounded-xl bg-[#0F141C] border border-[#232B38] cursor-pointer"
                          />
                        </Field>

                        <Field label="Action Color">
                          <input
                            type="color"
                            value={link.actionColor || "#2563EB"}
                            onChange={(e) =>
                              updateSocialLink(index, "actionColor", e.target.value)
                            }
                            className="w-full h-11 p-1 rounded-xl bg-[#0F141C] border border-[#232B38] cursor-pointer"
                          />
                        </Field>

                        <Select
                          value={
                            link.boxClass ||
                            "bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30"
                          }
                          onChange={(e) =>
                            updateSocialLink(index, "boxClass", e.target.value)
                          }
                        >
                          <option value="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30">
                            Default Blue Gradient
                          </option>

                          <option value="bg-blue-500/10 border-blue-500/20">
                            Blue
                          </option>

                          <option value="bg-cyan-500/10 border-cyan-500/20">
                            Cyan
                          </option>

                          <option value="bg-green-500/10 border-green-500/20">
                            Green
                          </option>

                          <option value="bg-purple-500/10 border-purple-500/20">
                            Purple
                          </option>

                          <option value="bg-pink-500/10 border-pink-500/20">
                            Pink
                          </option>

                          <option value="bg-yellow-500/10 border-yellow-500/20">
                            Yellow
                          </option>

                          <option value="bg-red-500/10 border-red-500/20">
                            Red
                          </option>

                          <option value="bg-white/5 border-white/10 backdrop-blur">
                            Glass
                          </option>
                        </Select>

                        <label
                          className={cx(
                            "flex items-center gap-2 sm:col-span-2 cursor-pointer",
                            theme.text
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={!!link.show}
                            onChange={(e) =>
                              updateSocialLink(index, "show", e.target.checked)
                            }
                            className="accent-cyan-500"
                          />
                          Show this link
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* 7. Display Settings */}
            <section>
              <h3 className={cx("text-lg font-semibold mb-4 flex items-center gap-2", theme.text)}>
                <span className="w-7 h-7 rounded-lg bg-fuchsia-500/15 flex items-center justify-center">
                  <icons.Settings2 size={14} className="text-fuchsia-400" />
                </span>
                Display Settings
              </h3>

              <div className="grid sm:grid-cols-3 gap-4 items-end">
                <Field label="Order">
                  <TextInput
                    type="number"
                    value={form.order ?? ""}
                    onChange={(e) =>
                      update(
                        "order",
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                  />
                </Field>

                <label className={cx("flex items-center gap-2 cursor-pointer", theme.text)}>
                  <input
                    type="checkbox"
                    checked={!!form.isFeatured}
                    onChange={(e) => update("isFeatured", e.target.checked)}
                    className="accent-amber-500"
                  />
                  <icons.Star size={13} className="text-amber-400" />
                  Featured
                </label>

                <label className={cx("flex items-center gap-2 cursor-pointer", theme.text)}>
                  <input
                    type="checkbox"
                    checked={form.isActive !== false}
                    onChange={(e) => update("isActive", e.target.checked)}
                    className="accent-emerald-500"
                  />
                  <icons.CircleCheck size={13} className="text-emerald-400" />
                  Active
                </label>
              </div>
            </section>

          </div>
        </div>

        {/* Footer (sticky) */}
        <div
          className={cx(
            "p-5 border-t flex justify-end gap-3 shrink-0",
            theme.surface,
            theme.border
          )}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={cx(
              "px-4 py-2 rounded-xl border hover:bg-white/10 transition-colors",
              theme.border,
              theme.text
            )}
          >
            Cancel
          </button>

          <PrimaryButton onClick={save} disabled={saving}>
            <CheckCircle2 size={15} />
            {saving ? "Saving..." : "Save Changes"}
          </PrimaryButton>
        </div>

      </div>
    </div>
  );
}
/* ============================================================================
   EXPERIENCE MANAGER (custom component, no changes needed)
============================================================================ */
function ExperienceManager({ notify }) {
  // ---------- State (unchanged) ----------
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    company: '',
    link: '',
    logo: '',
    isFeatured: false,
    isActive: true,
    positions: []
  });

  const [draggedPositionIndex, setDraggedPositionIndex] = useState(null);

  const [iconSearch, setIconSearch] = useState("");
  const [activeIconInput, setActiveIconInput] = useState(null);
  const [iconDropdownOpen, setIconDropdownOpen] = useState(false);
  const [skillLabel, setSkillLabel] = useState("");
  const [skillColor, setSkillColor] = useState("#60A5FA");
  const [editingSkillKey, setEditingSkillKey] = useState(null);
  const [editingDescription, setEditingDescription] = useState(null);
  const [editDescText, setEditDescText] = useState("");
  const [expandedPositions, setExpandedPositions] = useState({});

  // Mobile: toggle between edit form and live preview inside the modal,
  // since they can't sit side-by-side below `lg`.
  const [mobileTab, setMobileTab] = useState('form'); // 'form' | 'preview'

  const iconNames = Object.keys(icons);

  const filteredIconNames = iconNames
    .filter((name) => name.toLowerCase().includes(iconSearch.toLowerCase()))
    .slice(0, 15);

  // ---------- Icon helpers (unchanged) ----------
  const cleanIconName = (name) =>
    name.replace(/^Fa/, "").replace(/^Si/, "").replace(/^Md/, "")
      .replace(/^Hi/, "").replace(/^Ri/, "").replace(/^Io/, "").replace(/^Lu/, "");

  const getSkillIconComponent = (iconName) => {
    if (!iconName) return null;
    if (icons[iconName]) return icons[iconName];
    const prefixes = ["Fa", "Si", "Md", "Hi", "Ri", "Io", "Lu"];
    for (const prefix of prefixes) {
      const Component = icons[`${prefix}${iconName}`];
      if (Component) return Component;
    }
    return null;
  };

  const getSkillDisplay = (skill) => {
    if (typeof skill === 'string') return { icon: skill, label: skill, color: null };
    return {
      icon: skill?.icon || '',
      label: skill?.label || skill?.icon || '',
      color: skill?.color || null
    };
  };

  // ---------- API calls (unchanged) ----------
  const fetchExperiences = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getExperiences();
      setExperiences(res.data.data || []);
    } catch (error) {
      notify('Failed to load experiences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  // ---------- Handlers (unchanged) ----------
  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      company: '', link: '', logo: '', isFeatured: false, isActive: true,
      positions: [{ title: '', type: '', duration: '', location: '', countExperience: false, skills: [], description: [] }]
    });
    setExpandedPositions({ 0: true });
    setMobileTab('form');
    setModalOpen(true);
  };

  const handleEdit = (exp) => {
    setEditingId(exp._id || exp.id);
    setFormData({
      company: exp.company || '',
      link: exp.link || '',
      logo: exp.logo || '',
      isFeatured: exp.isFeatured || false,
      isActive: exp.isActive !== undefined ? exp.isActive : true,
      positions: exp.positions ? exp.positions.map(p => ({
        ...p, skills: p.skills || [], description: p.description || []
      })) : []
    });
    const expandState = {};
    (exp.positions || []).forEach((_, idx) => expandState[idx] = true);
    setExpandedPositions(expandState);
    setMobileTab('form');
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteExperience(id);
      notify('Experience deleted');
      fetchExperiences();
    } catch (error) {
      notify('Failed to delete');
    }
  };

  const handleMoveUp = async (id) => {
    try { await moveExperienceUp(id); fetchExperiences(); }
    catch (error) { notify('Failed to move up'); }
  };

  const handleMoveDown = async (id) => {
    try { await moveExperienceDown(id); fetchExperiences(); }
    catch (error) { notify('Failed to move down'); }
  };

  // ---------- Modal form handlers (unchanged) ----------
  const updateFormField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const updatePosition = (index, field, value) => {
    setFormData(prev => {
      const newPositions = [...prev.positions];
      newPositions[index] = { ...newPositions[index], [field]: value };
      return { ...prev, positions: newPositions };
    });
  };

  const addPosition = () => {
    setFormData(prev => ({
      ...prev,
      positions: [...prev.positions, { title: '', type: '', duration: '', location: '', countExperience: false, skills: [], description: [] }]
    }));
    const newIndex = formData.positions.length;
    setExpandedPositions(prev => ({ ...prev, [newIndex]: true }));
  };

  const duplicatePosition = (index) => {
    setFormData(prev => {
      const posToCopy = prev.positions[index];
      const newPos = { ...posToCopy, skills: [...posToCopy.skills], description: [...posToCopy.description] };
      const newPositions = [...prev.positions];
      newPositions.splice(index + 1, 0, newPos);
      return { ...prev, positions: newPositions };
    });
    const newIndex = index + 1;
    setExpandedPositions(prev => ({ ...prev, [newIndex]: true }));
  };

  const deletePosition = (index) => {
    setFormData(prev => ({ ...prev, positions: prev.positions.filter((_, i) => i !== index) }));
    setExpandedPositions(prev => {
      const newState = { ...prev };
      delete newState[index];
      const keys = Object.keys(newState).map(Number).sort();
      const reindexed = {};
      keys.forEach((key, i) => { reindexed[i] = newState[key]; });
      return reindexed;
    });
  };

  const movePositionUp = (index) => {
    if (index === 0) return;
    setFormData(prev => {
      const newPositions = [...prev.positions];
      [newPositions[index - 1], newPositions[index]] = [newPositions[index], newPositions[index - 1]];
      return { ...prev, positions: newPositions };
    });
    setExpandedPositions(prev => {
      const newExpanded = {};
      formData.positions.forEach((_, idx) => {
        if (idx === index - 1) newExpanded[idx] = prev[index] || false;
        else if (idx === index) newExpanded[idx] = prev[index - 1] || false;
        else newExpanded[idx] = prev[idx] || false;
      });
      return newExpanded;
    });
  };

  const movePositionDown = (index) => {
    if (index === formData.positions.length - 1) return;
    setFormData(prev => {
      const newPositions = [...prev.positions];
      [newPositions[index], newPositions[index + 1]] = [newPositions[index + 1], newPositions[index]];
      return { ...prev, positions: newPositions };
    });
    setExpandedPositions(prev => {
      const newExpanded = {};
      formData.positions.forEach((_, idx) => {
        if (idx === index) newExpanded[idx] = prev[index + 1] || false;
        else if (idx === index + 1) newExpanded[idx] = prev[index] || false;
        else newExpanded[idx] = prev[idx] || false;
      });
      return newExpanded;
    });
  };

  // Drag handlers (unchanged — desktop only, see note below)
  const onDragStartPosition = (e, index) => {
    setDraggedPositionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOverPosition = (e) => e.preventDefault();
  const onDropPosition = (e, targetIndex) => {
    e.preventDefault();
    if (draggedPositionIndex === null || draggedPositionIndex === targetIndex) return;
    setFormData(prev => {
      const newPositions = [...prev.positions];
      const [removed] = newPositions.splice(draggedPositionIndex, 1);
      newPositions.splice(targetIndex, 0, removed);
      return { ...prev, positions: newPositions };
    });
    setExpandedPositions(prev => {
      const newExpanded = {};
      formData.positions.forEach((_, idx) => {
        if (idx === draggedPositionIndex) newExpanded[targetIndex] = prev[draggedPositionIndex] || false;
        else if (idx === targetIndex && targetIndex > draggedPositionIndex) newExpanded[targetIndex - 1] = prev[targetIndex] || false;
        else if (idx === targetIndex && targetIndex < draggedPositionIndex) newExpanded[targetIndex + 1] = prev[targetIndex] || false;
        else newExpanded[idx] = prev[idx] || false;
      });
      return newExpanded;
    });
    setDraggedPositionIndex(null);
  };

  // ---------- Skills helpers (unchanged) ----------
  const startEditSkill = (positionIndex, skill) => {
    const { icon, label, color } = getSkillDisplay(skill);
    setActiveIconInput(positionIndex);
    setIconSearch(icon);
    setSkillLabel(label);
    setSkillColor(color || "#60A5FA");
    setEditingSkillKey({ positionIndex, icon });
    setIconDropdownOpen(false);
  };

  const cancelEditSkill = () => {
    setEditingSkillKey(null);
    setIconSearch("");
    setSkillLabel("");
    setSkillColor("#60A5FA");
    setActiveIconInput(null);
    setIconDropdownOpen(false);
  };

  const saveSkill = (positionIndex) => {
    const icon = iconSearch.trim();
    const label = skillLabel.trim();
    const color = skillColor || null;
    if (!icon || !label) return;

    const isEditing = editingSkillKey && editingSkillKey.positionIndex === positionIndex;
    const originalIcon = isEditing ? editingSkillKey.icon : null;
    let blocked = false;

    setFormData(prev => {
      const newPositions = [...prev.positions];
      const existingSkills = newPositions[positionIndex].skills || [];
      const duplicate = existingSkills.some((s) => {
        const sIcon = typeof s === 'string' ? s : s.icon;
        if (isEditing && sIcon === originalIcon) return false;
        return sIcon === icon;
      });
      if (duplicate) { blocked = true; return prev; }

      let newSkills;
      if (isEditing) {
        newSkills = existingSkills.map((s) => {
          const sIcon = typeof s === 'string' ? s : s.icon;
          return sIcon === originalIcon ? { icon, label, color } : s;
        });
      } else {
        newSkills = [...existingSkills, { icon, label, color }];
      }
      newPositions[positionIndex] = { ...newPositions[positionIndex], skills: newSkills };
      return { ...prev, positions: newPositions };
    });

    if (blocked) { notify('That skill icon is already added'); return; }

    setIconSearch("");
    setSkillLabel("");
    setSkillColor("#60A5FA");
    setActiveIconInput(null);
    setIconDropdownOpen(false);
    setEditingSkillKey(null);
  };

  const removeSkill = (index, iconToRemove) => {
    setFormData(prev => {
      const newPositions = [...prev.positions];
      newPositions[index] = {
        ...newPositions[index],
        skills: newPositions[index].skills.filter((s) => (typeof s === 'string' ? s : s.icon) !== iconToRemove)
      };
      return { ...prev, positions: newPositions };
    });
    if (editingSkillKey && editingSkillKey.positionIndex === index && editingSkillKey.icon === iconToRemove) {
      cancelEditSkill();
    }
  };

  // ---------- Description helpers (unchanged) ----------
  const addDescription = (index, text) => {
    if (!text.trim()) return;
    setFormData(prev => ({
      ...prev,
      positions: prev.positions.map((position, i) =>
        i === index ? { ...position, description: [...position.description, text.trim()] } : position
      ),
    }));
  };

  const removeDescription = (index, descIndex) => {
    setFormData(prev => {
      const newPositions = [...prev.positions];
      newPositions[index].description.splice(descIndex, 1);
      return { ...prev, positions: newPositions };
    });
    if (editingDescription && editingDescription.positionIndex === index && editingDescription.descIndex === descIndex) {
      setEditingDescription(null);
      setEditDescText("");
    }
  };

  const startEditDescription = (positionIndex, descIndex, text) => {
    setEditingDescription({ positionIndex, descIndex });
    setEditDescText(text);
  };

  const cancelEditDescription = () => {
    setEditingDescription(null);
    setEditDescText("");
  };

  const saveEditDescription = () => {
    if (!editingDescription) return;
    const { positionIndex, descIndex } = editingDescription;
    const text = editDescText.trim();
    if (!text) { removeDescription(positionIndex, descIndex); return; }
    setFormData(prev => {
      const newPositions = [...prev.positions];
      const newDescription = [...newPositions[positionIndex].description];
      newDescription[descIndex] = text;
      newPositions[positionIndex] = { ...newPositions[positionIndex], description: newDescription };
      return { ...prev, positions: newPositions };
    });
    setEditingDescription(null);
    setEditDescText("");
  };

  // Logo upload (unchanged)
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updateFormField('logo', ev.target.result);
    reader.readAsDataURL(file);
  };

  // Submit (unchanged)
  const handleSubmit = async () => {
    if (formData.positions.length === 0) { notify('At least one position is required'); return; }
    for (let i = 0; i < formData.positions.length; i++) {
      if (!formData.positions[i].title.trim()) { notify(`Position ${i + 1} must have a title`); return; }
    }
    try {
      if (editingId) { await updateExperience(editingId, formData); notify('Experience updated'); }
      else { await createExperience(formData); notify('Experience created'); }
      setModalOpen(false);
      fetchExperiences();
    } catch (error) {
      notify('Failed to save experience');
    }
  };

  const filtered = experiences.filter(exp =>
    exp.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.positions?.some(p => p.title?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // ---------- Render ----------
  return (
    <div className="p-4 sm:p-6">

      {/* =========================================================
          TOOLBAR — search full-width on mobile, side-by-side on sm+
      ========================================================== */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search experiences..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-4 py-2.5 sm:py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute right-3 top-3 sm:top-2.5 text-gray-400" size={18} />
        </div>
        <button
          onClick={handleAdd}
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 sm:py-2 rounded-lg flex items-center justify-center gap-2 transition"
        >
          <Plus size={18} />
          Add Experience
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-blue-500" size={32} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 px-4 text-gray-400 text-sm">
          No experiences found. Tap "Add Experience" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {filtered.map((exp) => (
            <div
              key={exp._id || exp.id}
              className="rounded-2xl border border-[#232B38] bg-[#111827] p-4 sm:p-5 hover:border-blue-500 transition-all"
            >
              <div className="flex gap-3 sm:gap-4">
                {exp.logo ? (
                  <img src={exp.logo} alt={exp.company} className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <Building2 className="text-blue-500" size={22} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2 flex-wrap">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white text-base sm:text-lg truncate">
                        {exp.company}
                      </h3>
                      {exp.link && (
                        <a href={exp.link} target="_blank" rel="noreferrer" className="text-xs text-blue-400 truncate block">
                          {exp.link}
                        </a>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {exp.isFeatured && (
                        <span className="text-[10px] sm:text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">Featured</span>
                      )}
                      {exp.isActive && (
                        <span className="text-[10px] sm:text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Active</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {exp.positions?.map((pos, idx) => (
                      <div key={idx} className="border-l-2 border-blue-500 pl-3 sm:pl-4">
                        <h4 className="font-medium text-white text-sm sm:text-base">{pos.title}</h4>
                        <p className="text-xs text-yellow-500">
                          {pos.duration}{pos.location && ` • ${pos.location}`}
                        </p>
                        {pos.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {pos.skills.slice(0, 3).map((skill, si) => {
                              const { icon, label, color } = getSkillDisplay(skill);
                              const IconComponent = getSkillIconComponent(icon);
                              return (
                                <span key={`${icon}-${si}`} className="px-2 py-0.5 rounded-full text-xs bg-blue-500/10 text-blue-300 flex items-center gap-1">
                                  {IconComponent && <IconComponent size={10} style={color ? { color } : undefined} />}
                                  {label}
                                </span>
                              );
                            })}
                            {pos.skills.length > 3 && (
                              <span className="text-xs text-gray-400">+{pos.skills.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-1 sm:gap-2 mt-4 pt-3 border-t border-[#232B38]">
                    <button onClick={() => handleMoveUp(exp._id || exp.id)} className="p-2 sm:p-1.5 rounded hover:bg-[#1E293B] text-gray-400 hover:text-white transition" title="Move Up">
                      <ArrowUp size={16} />
                    </button>
                    <button onClick={() => handleMoveDown(exp._id || exp.id)} className="p-2 sm:p-1.5 rounded hover:bg-[#1E293B] text-gray-400 hover:text-white transition" title="Move Down">
                      <ArrowDown size={16} />
                    </button>
                    <button onClick={() => handleEdit(exp)} className="p-2 sm:p-1.5 rounded hover:bg-[#1E293B] text-gray-400 hover:text-white transition" title="Edit">
                      <Edit size={16} />
                    </button>
                    <button onClick={() => handleDelete(exp._id || exp.id)} className="p-2 sm:p-1.5 rounded hover:bg-[#1E293B] text-red-400 hover:text-red-300 transition" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* =========================================================
          MODAL — full-screen sheet on mobile, centered dialog on lg+
      ========================================================== */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4">
          <div className="bg-[#0F172A] border border-[#232B38] w-full h-[92vh] sm:h-auto sm:max-h-[90vh] sm:max-w-6xl rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl">

            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-[#232B38] shrink-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                {editingId ? 'Edit Experience' : 'Add Experience'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-white transition p-1">
                <X size={22} />
              </button>
            </div>

            {/* Form/Preview tab switcher — lg screens show both side by side, so hide this */}
            <div className="lg:hidden flex border-b border-[#232B38] shrink-0">
              <button
                onClick={() => setMobileTab('form')}
                className={cx(
                  "flex-1 py-3 text-sm font-medium transition",
                  mobileTab === 'form' ? "text-blue-400 border-b-2 border-blue-500" : "text-gray-500"
                )}
              >
                Edit Details
              </button>
              <button
                onClick={() => setMobileTab('preview')}
                className={cx(
                  "flex-1 py-3 text-sm font-medium transition",
                  mobileTab === 'preview' ? "text-blue-400 border-b-2 border-blue-500" : "text-gray-500"
                )}
              >
                Live Preview
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">

                {/* ---------- FORM COLUMN ---------- */}
                <div className={cx("space-y-6", mobileTab !== 'form' && "hidden lg:block")}>
                  <div className="bg-[#111827] rounded-xl p-4 border border-[#232B38]">
                    <h3 className="text-sm font-medium text-gray-400 mb-4">Company Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Company Name *</label>
                        <input
                          type="text"
                          value={formData.company}
                          onChange={(e) => updateFormField('company', e.target.value)}
                          placeholder="SyncPulse Signal"
                          className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Website</label>
                        <input
                          type="url"
                          value={formData.link}
                          onChange={(e) => updateFormField('link', e.target.value)}
                          placeholder="https://company.com"
                          className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Company Logo</label>
                        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                          {formData.logo && (
                            <img src={formData.logo} alt="Logo preview" className="w-12 h-12 rounded-lg object-cover border border-[#334155]" />
                          )}
                          <label className="cursor-pointer bg-[#1E293B] hover:bg-[#2A3A4F] border border-[#334155] rounded-lg px-4 py-2 text-sm text-gray-300 transition">
                            Upload Logo
                            <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                          </label>
                          {formData.logo && (
                            <button onClick={() => updateFormField('logo', '')} className="text-red-400 hover:text-red-300 text-sm">
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-6 flex-wrap">
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input type="checkbox" checked={formData.isFeatured} onChange={(e) => updateFormField('isFeatured', e.target.checked)}
                            className="rounded border-[#334155] bg-[#1E293B] text-blue-500 focus:ring-blue-500" />
                          Featured
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input type="checkbox" checked={formData.isActive} onChange={(e) => updateFormField('isActive', e.target.checked)}
                            className="rounded border-[#334155] bg-[#1E293B] text-blue-500 focus:ring-blue-500" />
                          Active
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-medium text-gray-400">Positions</h3>
                      <button onClick={addPosition} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                        <Plus size={16} /> Add Position
                      </button>
                    </div>
                    <div className="space-y-4">
                      {formData.positions.map((pos, idx) => (
                        <div
                          key={idx}
                          draggable
                          onDragStart={(e) => onDragStartPosition(e, idx)}
                          onDragOver={onDragOverPosition}
                          onDrop={(e) => onDropPosition(e, idx)}
                          className="bg-[#111827] rounded-xl border border-[#232B38] overflow-hidden transition"
                        >
                          <div
                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#1E293B]"
                            onClick={() => setExpandedPositions(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {/* Drag handle hidden on touch devices — reorder via Up/Down buttons instead */}
                              <span className="hidden sm:inline text-gray-500 cursor-grab" title="Drag to reorder">
                                <GripVertical size={16} />
                              </span>
                              <span className="font-medium text-white truncate">
                                {pos.title || `Position ${idx + 1}`}
                              </span>
                              <span className="text-xs text-gray-400 shrink-0">
                                {pos.duration ? `(${pos.duration})` : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button onClick={(e) => { e.stopPropagation(); duplicatePosition(idx); }} className="text-gray-400 hover:text-blue-400 transition p-1.5" title="Duplicate">
                                <Copy size={16} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); deletePosition(idx); }} className="text-gray-400 hover:text-red-400 transition p-1.5" title="Delete">
                                <X size={16} />
                              </button>
                              <button className="text-gray-400 p-1.5">
                                {expandedPositions[idx] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </button>
                            </div>
                          </div>

                          {expandedPositions[idx] && (
                            <div className="p-4 border-t border-[#232B38] space-y-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Job Title *</label>
                                  <input type="text" value={pos.title} onChange={(e) => updatePosition(idx, 'title', e.target.value)}
                                    placeholder="Software Engineer"
                                    className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Employment Type</label>
                                  <input type="text" value={pos.type} onChange={(e) => updatePosition(idx, 'type', e.target.value)}
                                    placeholder="Full Time / Internship"
                                    className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Duration</label>
                                  <input type="text" value={pos.duration} onChange={(e) => updatePosition(idx, 'duration', e.target.value)}
                                    placeholder="June 2026 - Present"
                                    className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Location</label>
                                  <input type="text" value={pos.location} onChange={(e) => updatePosition(idx, 'location', e.target.value)}
                                    placeholder="Kerala, India"
                                    className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </div>
                              </div>

                              <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-sm text-gray-300">
                                  <input type="checkbox" checked={pos.countExperience} onChange={(e) => updatePosition(idx, 'countExperience', e.target.checked)}
                                    className="rounded border-[#334155] bg-[#1E293B] text-blue-500 focus:ring-blue-500" />
                                  Count Experience
                                </label>
                              </div>

                              {/* ---------- Skills ---------- */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <label className="block text-xs text-gray-400">Skills</label>
                                  <span className="text-[11px] text-gray-500 hidden sm:inline">Click a skill to edit it</span>
                                </div>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {pos.skills.map((skill, si) => {
                                    const { icon, label, color } = getSkillDisplay(skill);
                                    const IconComponent = getSkillIconComponent(icon);
                                    const isBeingEdited = editingSkillKey && editingSkillKey.positionIndex === idx && editingSkillKey.icon === icon;
                                    return (
                                      <span
                                        key={`${icon}-${si}`}
                                        onClick={() => startEditSkill(idx, skill)}
                                        className={`group cursor-pointer select-none px-2 py-1 rounded-full text-xs flex items-center gap-1.5 transition-all hover:-translate-y-0.5 hover:shadow-md ${isBeingEdited ? 'bg-blue-500/40 text-white ring-2 ring-blue-400' : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'}`}
                                        style={color ? { boxShadow: `inset 0 0 0 1px ${color}55` } : undefined}
                                        title="Click to edit"
                                      >
                                        {IconComponent && <IconComponent size={12} style={color ? { color } : undefined} />}
                                        {label}
                                        <Pencil size={10} className="opacity-0 group-hover:opacity-70 transition-opacity" />
                                        <button onClick={(e) => { e.stopPropagation(); removeSkill(idx, icon); }} className="text-blue-300 hover:text-white" title="Remove">
                                          <X size={12} />
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>

                                {/* Input row: wraps into 2 rows on mobile instead of overflowing */}
                                <div className="flex flex-wrap gap-2 items-start">
                                  <div className="relative flex-1 min-w-[140px]">
                                    <input
                                      type="text"
                                      placeholder="Icon e.g. Instagram"
                                      value={activeIconInput === idx ? iconSearch : ""}
                                      onFocus={() => { setActiveIconInput(idx); setIconDropdownOpen(true); }}
                                      onChange={(e) => { setActiveIconInput(idx); setIconSearch(e.target.value); setIconDropdownOpen(true); }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Escape") setIconDropdownOpen(false);
                                        if (e.key === "Enter") { e.preventDefault(); saveSkill(idx); }
                                      }}
                                      className={`w-full bg-[#1E293B] border rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition ${editingSkillKey && editingSkillKey.positionIndex === idx ? 'border-blue-500' : 'border-[#334155]'}`}
                                    />
                                    {activeIconInput === idx && iconDropdownOpen && iconSearch.trim() && (
                                      <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 shadow-xl">
                                        {filteredIconNames.length === 0 && (
                                          <div className="px-3 py-2 text-sm text-gray-500">No icons found</div>
                                        )}
                                        {filteredIconNames.map((iconName) => {
                                          const IconComponent = icons[iconName];
                                          return (
                                            <button
                                              key={iconName}
                                              type="button"
                                              onClick={() => {
                                                const cleanName = cleanIconName(iconName);
                                                setIconSearch(cleanName);
                                                setIconDropdownOpen(false);
                                              }}
                                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-300 hover:bg-slate-800 hover:text-white"
                                            >
                                              {IconComponent && <IconComponent size={18} style={skillColor ? { color: skillColor } : undefined} />}
                                              <span>{iconName}</span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>

                                  <input
                                    type="text"
                                    placeholder="Display label"
                                    value={skillLabel}
                                    onChange={(e) => setSkillLabel(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveSkill(idx); } }}
                                    className="flex-1 min-w-[140px] bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />

                                  <input
                                    type="color"
                                    value={skillColor || "#60A5FA"}
                                    onChange={(e) => setSkillColor(e.target.value)}
                                    title="Icon color"
                                    className="w-10 h-9 p-1 bg-[#1E293B] border border-[#334155] rounded-lg cursor-pointer"
                                  />

                                  <button type="button" onClick={() => saveSkill(idx)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:py-1.5 rounded-lg text-sm transition">
                                    {editingSkillKey && editingSkillKey.positionIndex === idx ? 'Update' : 'Add'}
                                  </button>

                                  {editingSkillKey && editingSkillKey.positionIndex === idx && (
                                    <button type="button" onClick={cancelEditSkill} className="text-gray-400 hover:text-white text-sm px-2 py-2 sm:py-1.5">
                                      Cancel
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* ---------- Responsibilities ---------- */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <label className="block text-xs text-gray-400">Responsibilities</label>
                                  <span className="text-[11px] text-gray-500 hidden sm:inline">Click a line to edit it</span>
                                </div>
                                <ul className="list-disc ml-5 mb-2 space-y-1">
                                  {pos.description.map((desc, di) => {
                                    const isEditingThis = editingDescription && editingDescription.positionIndex === idx && editingDescription.descIndex === di;
                                    if (isEditingThis) {
                                      return (
                                        <li key={di} className="text-sm -ml-5 list-none">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <input
                                              autoFocus
                                              type="text"
                                              value={editDescText}
                                              onChange={(e) => setEditDescText(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); saveEditDescription(); }
                                                if (e.key === 'Escape') cancelEditDescription();
                                              }}
                                              onBlur={saveEditDescription}
                                              className="flex-1 min-w-[160px] bg-[#1E293B] border border-blue-500 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                            <button onMouseDown={(e) => e.preventDefault()} onClick={saveEditDescription} className="text-blue-400 hover:text-blue-300 text-xs">Save</button>
                                            <button onMouseDown={(e) => e.preventDefault()} onClick={cancelEditDescription} className="text-gray-400 hover:text-white text-xs">Cancel</button>
                                          </div>
                                        </li>
                                      );
                                    }
                                    return (
                                      <li
                                        key={di}
                                        onClick={() => startEditDescription(idx, di, desc)}
                                        className="group text-sm text-gray-300 flex items-start gap-2 cursor-pointer rounded px-1 -ml-1 hover:bg-[#1E293B] transition"
                                        title="Click to edit"
                                      >
                                        <span className="flex-1">{desc}</span>
                                        <Pencil size={12} className="opacity-0 group-hover:opacity-60 transition-opacity mt-0.5" />
                                        <button onClick={(e) => { e.stopPropagation(); removeDescription(idx, di); }} className="text-red-400 hover:text-red-300">
                                          <X size={14} />
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    placeholder="Add responsibility"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); addDescription(idx, e.target.value); e.target.value = ''; }
                                    }}
                                    className="flex-1 bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2 sm:py-1.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                  <button
                                    onClick={(e) => {
                                      const input = e.currentTarget.previousSibling;
                                      addDescription(idx, input.value);
                                      input.value = '';
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:py-1.5 rounded-lg text-sm"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>

                              <div className="flex justify-end gap-2 pt-2 border-t border-[#232B38]">
                                <button onClick={() => movePositionUp(idx)} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 disabled:opacity-30" disabled={idx === 0}>
                                  <ArrowUp size={14} /> Up
                                </button>
                                <button onClick={() => movePositionDown(idx)} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 disabled:opacity-30" disabled={idx === formData.positions.length - 1}>
                                  <ArrowDown size={14} /> Down
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ---------- PREVIEW COLUMN — sticky only at lg+, tab-gated below ---------- */}
                <div className={cx(
                  "bg-[#111827] rounded-xl p-4 border border-[#232B38] h-fit lg:sticky lg:top-0",
                  mobileTab !== 'preview' && "hidden lg:block"
                )}>
                  <h3 className="text-sm font-medium text-gray-400 mb-4">Live Preview</h3>
                  <div className="flex gap-4">
                    {formData.logo ? (
                      <img src={formData.logo} alt="Logo" className="w-14 h-14 rounded-xl object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center">
                        <Building2 className="text-blue-500" size={24} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h4 className="font-semibold text-white text-lg truncate">
                        {formData.company || 'Your Company'}
                      </h4>
                      {formData.link && (
                        <a href={formData.link} target="_blank" rel="noreferrer" className="text-xs text-blue-400 truncate block">
                          {formData.link}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-4">
                    {formData.positions.map((pos, idx) => (
                      <div key={idx} className="border-l-2 border-blue-500 pl-4">
                        <h5 className="font-medium text-white">{pos.title || 'Job Title'}</h5>
                        <p className="text-xs text-yellow-500">
                          {pos.duration || 'Duration'}{pos.location && ` • ${pos.location}`}
                        </p>
                        {pos.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {pos.skills.map((skill, si) => {
                              const { icon, label, color } = getSkillDisplay(skill);
                              const IconComponent = getSkillIconComponent(icon);
                              return (
                                <span key={`${icon}-${si}`} className="px-2 py-0.5 rounded-full text-xs bg-blue-500/10 text-blue-300 flex items-center gap-1">
                                  {IconComponent && <IconComponent size={10} style={color ? { color } : undefined} />}
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {pos.description?.length > 0 && (
                          <ul className="mt-1 text-sm text-gray-400 list-disc ml-4">
                            {pos.description.map((d, di) => <li key={di}>{d}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-[#232B38] bg-[#0F172A] shrink-0">
              <button onClick={() => setModalOpen(false)} className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 rounded-lg border border-[#334155] text-gray-300 hover:bg-[#1E293B] transition">
                Cancel
              </button>
              <button onClick={handleSubmit} className="flex-1 sm:flex-none px-6 py-2.5 sm:py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition flex items-center justify-center gap-2">
                <Save size={18} />
                {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function UserManagement({ profileEditRequest }) {
  const theme = useTheme();
  const [notification, setNotification] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [isProfileEdit, setIsProfileEdit] = useState(false);
  const showNotification = (message, type = "success") => {
    setNotification({
      message,
      type,
    });

    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };
  const handleUnlock = async (user) => {
    if (!user?.id) {
      showNotification("error", "Invalid user ID");
      return;
    }

    try {
      setSaving(true);

      console.log("Unlocking user:", user.id);

      const res = await unlockUser(user.id);

      console.log("Unlock response:", res.data);

      // Immediately update UI
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id
            ? {
              ...u,
              isLocked: false,
              failedLoginAttempts: 0,
            }
            : u
        )
      );

      showNotification(
        "Account unlocked successfully",
        "success"
      );

      // Get latest data from backend
      await fetchUsers();
    } catch (error) {
      console.error(
        "UNLOCK USER ERROR:",
        error.response?.data || error
      );

      showNotification(
        error?.response?.data?.message ||
        "Failed to unlock account",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [modal, setModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "User",
    isActive: true,
    image: "",
    permissions: [],
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profilePreview, setProfilePreview] = useState("");
  const [profileFile, setProfileFile] = useState(null);

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError("Profile image must be less than 2MB.");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setProfileFile(file);
      setProfilePreview(reader.result);

      setForm((prev) => ({
        ...prev,
        image: reader.result,
      }));

      setError("");
    };

    reader.readAsDataURL(file);
  };
  // =====================================================
  // LOAD USERS
  // =====================================================

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await getUsers();

      setUsers(res?.data?.data || []);
    } catch (error) {
      console.error("GET USERS ERROR:", error);

      showNotification(
        "error",
        error?.response?.data?.message ||
        "Failed to load users"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // =====================================================
  // FILTER
  // =====================================================

  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search);

    const matchesRole =
      roleFilter === "all" ||
      user.role === roleFilter;

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.isActive) ||
      (statusFilter === "inactive" && !user.isActive);

    return (
      matchesSearch &&
      matchesRole &&
      matchesStatus
    );
  });

  // =====================================================
  // OPEN ADD
  // =====================================================

  const openAdd = () => {
    setSelectedUser(null);

    setForm({
      name: "",
      email: "",
      password: "",
      role: "User",
      isActive: true,
      image: "",
      permissions: [],

    });
    setProfileFile(null);
    setProfilePreview("");
    setModal("add");
  };

  // =====================================================
  // OPEN EDIT
  // =====================================================

  const openEdit = (user) => {
    setIsProfileEdit(false);
    setSelectedUser(user);

    const userImage =
      user?.image ||
      user?.avatar ||
      user?.avatarUrl ||
      user?.profileImage ||
      user?.profileImageUrl ||
      user?.profilePicture ||
      user?.photo ||
      "";

    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "User",
      isActive: user.isActive ?? true,
      image: userImage,
      permissions: user.permissions || [],
    });

    setProfileFile(null);
    setProfilePreview(userImage);

    setModal("edit");
  };
  useEffect(() => {
    if (!profileEditRequest) return;

    const storedUser = localStorage.getItem("adminUser");

    if (!storedUser) return;

    try {
      const user = JSON.parse(storedUser);
      setIsProfileEdit(true);
      const userImage =
        user?.image ||
        user?.avatar ||
        user?.avatarUrl ||
        user?.profileImage ||
        user?.profileImageUrl ||
        user?.profilePicture ||
        user?.photo ||
        "";

      setSelectedUser(user);

      setForm({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "User",
        isActive: user.isActive ?? true,
        image: userImage,
      });

      setProfileFile(null);
      setProfilePreview(userImage);

      setModal("edit");
    } catch (error) {
      console.error("Failed to open profile editor:", error);
    }
  }, [profileEditRequest]);
  // =====================================================
  // OPEN CHANGE PASSWORD
  // =====================================================

  const openChangePassword = (user) => {
    setSelectedUser(user);

    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    setModal("password");
  };

  // =====================================================
  // CLOSE
  // =====================================================

  const closeModal = () => {
    if (saving) return;

    setModal(null);
    setSelectedUser(null);

    setForm({
      name: "",
      email: "",
      password: "",
      role: "User",
      isActive: true,
    });

    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    setShowPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // =====================================================
  // CREATE / UPDATE
  // =====================================================

  const handleSave = async () => {
    if (saving) return;

    if (!form.name.trim()) {
      showNotification("error", "Name is required");
      return;
    }

    if (!form.email.trim()) {
      showNotification("error", "Email is required");
      return;
    }

    if (modal === "add" && !form.password) {
      showNotification("error", "Password is required");
      return;
    }

    if (
      modal === "add" &&
      form.password.length < 8
    ) {
      showNotification(
        "error",
        "Password must be at least 8 characters"
      );
      return;
    }

    setSaving(true);

    try {
      if (modal === "add") {
        await register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          image: form.image || null,
          permissions: form.permissions || [],

        });

        showNotification(
          "success",
          "User created successfully"
        );
      } else {
        await updateUser(selectedUser.id, {
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
          isActive: form.isActive,
          image: form.image || null,
          permissions: form.role === "Admin" ? [] : form.permissions || [],

        });

        showNotification(
          "success",
          "User updated successfully"
        );
      }

      closeModal();
      await fetchUsers();
    } catch (error) {
      console.error("SAVE USER ERROR:", error);

      showNotification(
        "error",
        error?.response?.data?.message ||
        "Failed to save user"
      );
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      `Delete user "${user.name}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteUser(user.id);

      showNotification(
        "success",
        "User deleted successfully"
      );

      await fetchUsers();
    } catch (error) {
      console.error("DELETE USER ERROR:", error);

      showNotification(
        "error",
        error?.response?.data?.message ||
        "Failed to delete user"
      );
    }
  };

  // =====================================================
  // CHANGE PASSWORD
  // =====================================================

  const handlePasswordChange = async () => {
    if (saving) return;

    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      showNotification(
        "error",
        "All password fields are required"
      );
      return;
    }

    if (
      passwordForm.newPassword.length < 8
    ) {
      showNotification(
        "error",
        "Password must be at least 8 characters"
      );
      return;
    }

    if (
      passwordForm.newPassword !==
      passwordForm.confirmPassword
    ) {
      showNotification(
        "error",
        "New passwords do not match"
      );
      return;
    }

    setSaving(true);

    try {
      /*
       * Your current backend change-password route
       * uses req.user.id, so it changes the currently
       * logged-in user's password.
       */
      await changePassword(passwordForm);

      showNotification(
        "success",
        "Password changed successfully"
      );

      closeModal();
    } catch (error) {
      console.error(
        "CHANGE PASSWORD ERROR:",
        error
      );

      showNotification(
        "error",
        error?.response?.data?.message ||
        "Failed to change password"
      );
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // INITIALS
  // =====================================================

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0])
      .join("")
      .toUpperCase();

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="anim-fadeUp">

      {/* HEADER */}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">

        <div>
          <h1
            className={cx(
              "font-display font-semibold text-2xl",
              theme.text
            )}
          >
            User Management
          </h1>

          <p
            className={cx(
              "text-sm font-body mt-1",
              theme.subtext
            )}
          >
            Manage users, roles, passwords and account status.
          </p>
        </div>
        {JSON.parse(localStorage.getItem("adminUser"))?.role === "Admin" && (
          <PrimaryButton onClick={openAdd}>
            <Plus size={16} />
            Add User
          </PrimaryButton>
        )}

      </div>

      {/* FILTERS */}

      <div
        className={cx(
          "rounded-2xl border p-4 mb-5",
          theme.surface,
          theme.border
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B93A0]"
            />

            <TextInput
              className="pl-9"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />
          </div>

          <Select
            value={roleFilter}
            onChange={(e) =>
              setRoleFilter(e.target.value)
            }
          >
            <option value="all">
              All Roles
            </option>

            <option value="Admin">
              Admin
            </option>

            <option value="User">
              User
            </option>
          </Select>

          <Select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value)
            }
          >
            <option value="all">
              All Status
            </option>

            <option value="active">
              Active
            </option>

            <option value="inactive">
              Inactive
            </option>
          </Select>

        </div>
      </div>

      {/* TABLE */}

      {/* ==========================================
    DESKTOP / TABLET TABLE
========================================== */}

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-[#232B38]">
            <tr>
              <th className="px-5 py-4 text-xs uppercase text-[#8B93A0]">
                User
              </th>

              <th className="px-5 py-4 text-xs uppercase text-[#8B93A0]">
                Email
              </th>

              <th className="px-5 py-4 text-xs uppercase text-[#8B93A0]">
                Role
              </th>

              <th className="px-5 py-4 text-xs uppercase text-[#8B93A0]">
                Status
              </th>

              <th className="px-5 py-4 text-xs uppercase text-[#8B93A0]">
                Created
              </th>

              <th className="px-5 py-4 text-xs uppercase text-[#8B93A0] text-right">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.map((user) => (
              <tr
                key={user.id}
                className="border-b border-[#232B38]/60 hover:bg-white/[0.02]"
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold overflow-hidden">
                      {(
                        user.image ||
                        user.avatar ||
                        user.avatarUrl ||
                        user.profileImage ||
                        user.profileImageUrl
                      ) ? (
                        <img
                          src={
                            user.image ||
                            user.avatar ||
                            user.avatarUrl ||
                            user.profileImage ||
                            user.profileImageUrl
                          }
                          alt={user.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitials(user.name)
                      )}
                    </div>

                    <div>
                      <div className="font-medium text-white">
                        {user.name}
                      </div>

                      <div className="text-xs text-[#5C6472]">
                        ID #{user.id}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4 text-sm text-[#8B93A0]">
                  {user.email}
                </td>

                <td className="px-5 py-4">
                  <span
                    className={cx(
                      "px-2.5 py-1 rounded-full text-xs font-medium",
                      user.role === "Admin"
                        ? "bg-purple-500/15 text-purple-400"
                        : "bg-blue-500/15 text-blue-400"
                    )}
                  >
                    {user.role}
                  </span>
                </td>

                <td className="px-5 py-4">
                  <span
                    className={
                      user.isActive
                        ? "text-emerald-400 text-xs"
                        : "text-red-400 text-xs"
                    }
                  >
                    ●{" "}
                    {user.isActive
                      ? "Active"
                      : "Inactive"}
                  </span>
                </td>

                <td className="px-5 py-4 text-xs text-[#8B93A0]">
                  {user.createdAt
                    ? new Date(
                      user.createdAt
                    ).toLocaleDateString()
                    : "-"}
                </td>

                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    {JSON.parse(localStorage.getItem("adminUser"))?.role === "Admin" && (
                      <button
                        onClick={() => openEdit(user)}
                        title="Edit User"
                        className="p-2 rounded-lg border border-[#232B38] hover:bg-white/5 text-gray-300"
                      >
                        <Pencil size={14} />
                      </button>
                    )}

                    <button
                      onClick={() =>
                        openChangePassword(user)
                      }
                      title="Change Password"
                      className="p-2 rounded-lg border border-[#232B38] hover:bg-white/5 text-yellow-400"
                    >
                      <Lock size={14} />
                    </button>
                    {JSON.parse(localStorage.getItem("adminUser"))?.role === "Admin" && (
                      <>
                        {user.isLocked && (
                          <button
                            onClick={() => handleUnlock(user)}
                            title="Unlock Account"
                            className="p-2 rounded-lg border border-green-500/20 hover:bg-green-500/10 text-green-400"
                          >
                            <icons.Unlock size={14} />
                          </button>
                        )}

                        <button
                          onClick={() => handleDelete(user)}
                          title="Delete User"
                          className="p-2 rounded-lg border border-[#2E1E1C] hover:bg-red-500/10 text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      {/* ==========================================
    MOBILE ACCOUNT CARDS
========================================== */}

      <div className="md:hidden grid grid-cols-1 gap-4">
        {filteredUsers.map((user) => (
          <div
            key={user.id}
            className="aspect-square rounded-2xl border border-[#232B38] bg-[#11151C] p-5 flex flex-col justify-between"
          >
            {/* TOP */}
            <div>
              <div className="flex items-center justify-between">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-base font-bold text-white">
                  {(
                    user.image ||
                    user.avatar ||
                    user.avatarUrl ||
                    user.profileImage ||
                    user.profileImageUrl
                  ) ? (
                    <img
                      src={
                        user.image ||
                        user.avatar ||
                        user.avatarUrl ||
                        user.profileImage ||
                        user.profileImageUrl
                      }
                      alt={user.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    getInitials(user.name)
                  )}
                </div>

                {/* Status */}
                <span
                  className={
                    user.isActive
                      ? "text-emerald-400 text-xs font-medium"
                      : "text-red-400 text-xs font-medium"
                  }
                >
                  ●{" "}
                  {user.isActive
                    ? "Active"
                    : "Inactive"}
                </span>
              </div>

              {/* NAME */}
              <div className="mt-5">
                <h3 className="text-lg font-semibold text-white truncate">
                  {user.name}
                </h3>

                <p className="text-xs text-[#5C6472] mt-1">
                  ID #{user.id}
                </p>
              </div>

              {/* EMAIL */}
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6472]">
                  Email
                </p>

                <p className="text-sm text-[#8B93A0] truncate mt-1">
                  {user.email}
                </p>
              </div>

              {/* ROLE */}
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6472]">
                  Role
                </p>

                <span
                  className={cx(
                    "inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-medium",
                    user.role === "Admin"
                      ? "bg-purple-500/15 text-purple-400"
                      : "bg-blue-500/15 text-blue-400"
                  )}
                >
                  {user.role}
                </span>
              </div>

              {/* CREATED */}
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6472]">
                  Created
                </p>

                <p className="text-xs text-[#8B93A0] mt-1">
                  {user.createdAt
                    ? new Date(
                      user.createdAt
                    ).toLocaleDateString()
                    : "-"}
                </p>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="flex gap-2 pt-4 border-t border-[#232B38]">
              <button
                onClick={() => openEdit(user)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#232B38] hover:bg-white/5 text-gray-300 text-xs"
              >
                <Pencil size={14} />
                Edit
              </button>

              <button
                onClick={() =>
                  openChangePassword(user)
                }
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#232B38] hover:bg-yellow-500/10 text-yellow-400 text-xs"
              >
                <Lock size={14} />
                Password
              </button>

              <button
                onClick={() => handleDelete(user)}
                className="w-10 flex items-center justify-center rounded-lg border border-[#2E1E1C] hover:bg-red-500/10 text-red-400"
                title="Delete User"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}

        {/* EMPTY STATE */}
        {filteredUsers.length === 0 && (
          <div className="aspect-square rounded-2xl border border-[#232B38] flex items-center justify-center text-sm text-[#5C6472]">
            No users found.
          </div>
        )}
      </div>

      {/* ================================================= */}
      {/* ADD / EDIT MODAL */}
      {/* ================================================= */}
      {(modal === "add" || modal === "edit") &&
        createPortal(
          <div
            className="fixed inset-0 z-[999999] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 pt-20 pb-10"
          >
            {/* MODAL */}
            <div
              className={cx(
                "relative w-full max-w-md rounded-2xl border p-6 mt-2",
                theme.surface,
                theme.border,
                theme.shadow
              )}
            >
              {/* HEADER */}
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h2
                    className={cx(
                      "font-display font-semibold text-lg",
                      theme.text
                    )}
                  >
                    {modal === "add"
                      ? "Add User"
                      : "Edit User"}
                  </h2>

                  <p
                    className={cx(
                      "text-xs mt-1",
                      theme.subtext
                    )}
                  >
                    {modal === "add"
                      ? "Create a new user account."
                      : "Update user account details."}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition"
                >
                  <X size={18} />
                </button>
              </div>

              {/* FORM */}
              <div className="flex flex-col gap-4">
                {/* PROFILE IMAGE */}
                <Field label="Profile Image">
                  <div className="flex items-center gap-4">

                    {/* Preview */}
                    <div className="w-16 h-16 rounded-full overflow-hidden border border-[#232B38] bg-[#11151C] flex items-center justify-center shrink-0">
                      {profilePreview ? (
                        <img
                          src={profilePreview}
                          alt="Profile preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-xs text-[#5C6472]">
                          No Image
                        </span>
                      )}
                    </div>

                    {/* Upload */}
                    <div className="flex-1">
                      <label
                        htmlFor="profile-image-upload"
                        className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg border border-[#232B38] bg-transparent hover:bg-white/5 text-sm text-[#8B93A0] hover:text-white cursor-pointer transition"
                      >
                        <Upload size={16} />
                        Upload Image
                      </label>

                      <input
                        id="profile-image-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleProfileImageChange}
                        className="hidden"
                      />

                      <p className="text-[10px] text-[#5C6472] mt-1.5">
                        JPG, PNG or WEBP • Max 2MB
                      </p>
                    </div>
                  </div>

                  {/* Remove uploaded image */}
                  {profilePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setProfileFile(null);
                        setProfilePreview("");

                        setForm((prev) => ({
                          ...prev,
                          image: "",
                        }));
                      }}
                      className="mt-2 text-xs text-red-400 hover:text-red-300"
                    >
                      Remove image
                    </button>
                  )}
                </Field>
                {/* NAME */}
                <Field label="Name" required>
                  <TextInput
                    value={form.name}
                    disabled={modal === "edit" && isProfileEdit}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        name: e.target.value,
                      })
                    }
                    placeholder="Enter name"
                  />
                </Field>

                {/* EMAIL */}
                <Field label="Email" required>
                  <TextInput
                    type="email"
                    value={form.email}
                    disabled={modal === "edit" && isProfileEdit}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        email: e.target.value,
                      })
                    }
                    placeholder="user@example.com"
                  />
                </Field>

                {/* PASSWORD - ONLY ADD */}
                {modal === "add" && (
                  <Field
                    label="Password"
                    required
                    hint="Minimum 8 characters"
                  >
                    <div className="relative">

                      <TextInput
                        type={
                          showPassword
                            ? "text"
                            : "password"
                        }
                        value={form.password}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            password: e.target.value,
                          })
                        }
                        placeholder="Enter password"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword(
                            (prev) => !prev
                          )
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B93A0] hover:text-white transition"
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>

                    </div>
                  </Field>
                )}

                {/* ROLE */}
                {!isProfileEdit && (
                  <Field label="Role">
                    <Select
                      value={form.role}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          role: e.target.value,
                        })
                      }
                    >
                      <option value="Admin">Admin</option>
                      <option value="User">User</option>
                    </Select>
                  </Field>
                )}
                {/* =====================================================
    PERMISSIONS - USER ONLY
===================================================== */}
                {JSON.parse(localStorage.getItem("adminUser"))?.role === "Admin" &&
                  form.role === "User" && (
                    <Field label="Permissions">
                      <div className="rounded-xl border border-[#232B38] bg-[#0F141C] p-3">

                        {/* Header + Select All */}
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div>
                            <p className="text-sm font-medium text-white">
                              Page Access
                            </p>

                            <p className="text-[10px] text-[#5C6472] mt-1">
                              Select the pages this user can view.
                            </p>
                          </div>
                          {/* Allow Login */}
                          <label
                            className={cx(
                              "flex items-center justify-between gap-3",
                              "px-3 py-2.5 rounded-lg border cursor-pointer transition-all",
                              form.permissions?.includes("allowLogin")
                                ? "border-green-500/50 bg-green-500/10"
                                : "border-[#232B38] bg-[#131822] hover:bg-white/5"
                            )}
                          >
                            <div className="flex items-center gap-2.5">
                              <input
                                type="checkbox"
                                checked={form.permissions?.includes("allowLogin")}
                                onChange={(e) => {
                                  setForm((prev) => {
                                    const current = prev.permissions || [];

                                    return {
                                      ...prev,
                                      permissions: e.target.checked
                                        ? [...current, "allowLogin"]
                                        : current.filter(
                                          (permission) => permission !== "allowLogin"
                                        ),
                                    };
                                  });
                                }}
                                className="
        w-4 h-4 rounded
        border-[#334155]
        bg-[#0F141C]
        text-green-600
        focus:ring-green-500
      "
                              />

                              <Lock
                                size={15}
                                className={
                                  form.permissions?.includes("allowLogin")
                                    ? "text-green-400"
                                    : "text-gray-500"
                                }
                              />

                              <span className="text-xs text-gray-300">
                                Allow Login
                              </span>
                            </div>

                            {form.permissions?.includes("allowLogin") && (
                              <span className="text-[9px] text-green-400">
                                LOGIN
                              </span>
                            )}
                          </label>
                          {/* SELECT ALL */}
                          <label className="flex items-center gap-2 cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={
                                form.permissions?.length === NAV_ITEMS.length
                              }
                              onChange={(e) => {
                                const availablePermissions = NAV_ITEMS.map(
                                  ([id]) => id
                                );

                                setForm((prev) => ({
                                  ...prev,
                                  permissions: e.target.checked
                                    ? availablePermissions
                                    : [],
                                }));
                              }}
                              className="
      w-4 h-4 rounded
      border-[#334155]
      bg-[#0F141C]
      text-blue-600
      focus:ring-blue-500
    "
                            />

                            <span className="text-xs font-medium text-blue-400">
                              Select All
                            </span>
                          </label>
                        </div>

                        {/* Permission List */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {NAV_ITEMS.map(([id, label, Icon]) => {
                            const checked =
                              form.permissions?.includes(id);

                            return (
                              <label
                                key={id}
                                className={cx(
                                  "flex items-center justify-between",
                                  "gap-3 px-3 py-2.5 rounded-lg border",
                                  "cursor-pointer transition-all",
                                  checked
                                    ? "border-blue-500/50 bg-blue-500/10"
                                    : "border-[#232B38] bg-[#131822] hover:bg-white/5"
                                )}
                              >
                                <div className="flex items-center gap-2.5 min-w-0">

                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      setForm((prev) => {
                                        const current =
                                          prev.permissions || [];

                                        return {
                                          ...prev,

                                          permissions: e.target.checked
                                            ? [...current, id]
                                            : current.filter(
                                              (permission) =>
                                                permission !== id
                                            ),
                                        };
                                      });
                                    }}
                                    className="
                      w-4 h-4 rounded
                      border-[#334155]
                      bg-[#0F141C]
                      text-blue-600
                      focus:ring-blue-500
                      shrink-0
                    "
                                  />

                                  {Icon && (
                                    <Icon
                                      size={15}
                                      className={
                                        checked
                                          ? "text-blue-400"
                                          : "text-gray-500"
                                      }
                                    />
                                  )}

                                  <span className="text-xs text-gray-300 truncate">
                                    {label}
                                  </span>
                                </div>

                                {checked && (
                                  <span className="text-[9px] text-blue-400 shrink-0">
                                    VIEW
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>

                        {/* Selected Count */}
                        <div className="mt-3 pt-3 border-t border-[#232B38] flex items-center justify-between">
                          <span className="text-[10px] text-[#5C6472]">
                            {form.permissions?.filter((id) => id !== "allowLogin").length || 0} of{" "}
                            {NAV_ITEMS.length}{" "}
                            pages selected
                          </span>
                        </div>

                      </div>
                    </Field>
                  )}
                {/* STATUS - ONLY EDIT */}
                {modal === "edit" && !isProfileEdit && (
                  <Field label="Account Status">
                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">

                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            isActive:
                              e.target.checked,
                          })
                        }
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                      />

                      <span>
                        Active account
                      </span>

                    </label>
                  </Field>
                )}

              </div>
              <div className="flex justify-center items-center w-full mt-2 mb-2">
                <Turnstile
                  siteKey={
                    process.env.REACT_APP_TURNSTILE_SITE_KEY
                  }
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                    setError("");
                  }}
                  onExpire={() => {
                    setTurnstileToken("");
                  }}
                  onError={() => {
                    setTurnstileToken("");
                  }}
                />
              </div>
              {/* BUTTONS */}
              <div className="flex gap-3 mt-6">

                <GhostButton
                  onClick={closeModal}
                  disabled={saving}
                  className="flex-1"
                >
                  Cancel
                </GhostButton>

                <PrimaryButton
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <Loader2
                        size={15}
                        className="animate-spin"
                      />

                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={15} />

                      {modal === "add"
                        ? "Create User"
                        : "Update User"}
                    </>
                  )}
                </PrimaryButton>

              </div>

            </div>
          </div>,

          // IMPORTANT: Portal target
          document.body
        )}

      {/* ================================================= */}
      {/* PASSWORD MODAL */}
      {/* ================================================= */}
      {modal === "password" &&
        createPortal(
          <div className="fixed inset-0 z-[999999] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 pt-20 pb-10">

            <div
              className={cx(
                "relative w-full max-w-md rounded-2xl border p-6 mt-2",
                theme.surface,
                theme.border,
                theme.shadow
              )}
            >

              {/* HEADER */}
              <div className="flex justify-between items-start mb-5">

                <div>
                  <h2
                    className={cx(
                      "font-display font-semibold text-lg",
                      theme.text
                    )}
                  >
                    Change Password
                  </h2>

                  <p
                    className={cx(
                      "text-xs mt-1",
                      theme.subtext
                    )}
                  >
                    Change your current account password.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition"
                >
                  <X size={18} />
                </button>

              </div>

              {/* FORM */}
              <div className="flex flex-col gap-4">

                {/* CURRENT PASSWORD */}
                <Field label="Current Password" required>
                  <div className="relative">

                    <TextInput
                      type={
                        showPassword
                          ? "text"
                          : "password"
                      }
                      value={
                        passwordForm.currentPassword
                      }
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          currentPassword:
                            e.target.value,
                        })
                      }
                      placeholder="Current password"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword(
                          (prev) => !prev
                        )
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B93A0] hover:text-white transition"
                    >
                      {showPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>

                  </div>
                </Field>

                {/* NEW PASSWORD */}
                <Field
                  label="New Password"
                  required
                  hint="Minimum 8 characters"
                >
                  <div className="relative">

                    <TextInput
                      type={
                        showNewPassword
                          ? "text"
                          : "password"
                      }
                      value={
                        passwordForm.newPassword
                      }
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          newPassword:
                            e.target.value,
                        })
                      }
                      placeholder="New password"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowNewPassword(
                          (prev) => !prev
                        )
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B93A0] hover:text-white transition"
                    >
                      {showNewPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>

                  </div>
                </Field>

                {/* CONFIRM PASSWORD */}
                <Field
                  label="Confirm Password"
                  required
                >
                  <div className="relative">

                    <TextInput
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      value={
                        passwordForm.confirmPassword
                      }
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          confirmPassword:
                            e.target.value,
                        })
                      }
                      placeholder="Confirm password"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(
                          (prev) => !prev
                        )
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B93A0] hover:text-white transition"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>

                  </div>
                </Field>

              </div>

              {/* ACTIONS */}
              <div className="flex gap-3 mt-6">

                <GhostButton
                  onClick={closeModal}
                  disabled={saving}
                  className="flex-1"
                >
                  Cancel
                </GhostButton>

                <PrimaryButton
                  onClick={handlePasswordChange}
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <Loader2
                        size={15}
                        className="animate-spin"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Lock size={15} />
                      Change Password
                    </>
                  )}
                </PrimaryButton>

              </div>

            </div>
          </div>,

          document.body
        )}

    </div>
  );
}

function SecuritySettings({ currentUser, setCurrentUser, notify }) {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [setupStarted, setSetupStarted] = useState(false);
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisablePw, setShowDisablePw] = useState(false);
  const twoFactorEnabled =
    currentUser?.twoFactorEnabled === true;

  const startSetup = async () => {
    try {
      setLoading(true);

      const res = await setup2FA();

      setQrCode(res.data.qrCode);
      setSecret(res.data.secret);
      setSetupStarted(true);

      notify(
        "success",
        "Scan the QR code with Google Authenticator or Microsoft Authenticator."
      );
    } catch (error) {
      console.error("2FA SETUP ERROR:", error);

      notify(
        "error",
        error?.response?.data?.message ||
        "Failed to setup two-factor authentication."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (!code || code.length !== 6) {
      notify("error", "Enter the 6-digit authenticator code.");
      return;
    }

    try {
      setLoading(true);

      await enable2FA(code);

      const updatedUser = {
        ...currentUser,
        twoFactorEnabled: true,
      };

      setCurrentUser(updatedUser);

      localStorage.setItem(
        "adminUser",
        JSON.stringify(updatedUser)
      );

      setQrCode("");
      setSecret("");
      setCode("");
      setSetupStarted(false);

      notify(
        "success",
        "Two-factor authentication enabled successfully."
      );
    } catch (error) {
      console.error("ENABLE 2FA ERROR:", error);

      notify(
        "error",
        error?.response?.data?.message ||
        "Invalid authenticator code."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword.trim()) {
      notify("error", "Enter your current password.");
      return;
    }

    try {
      setLoading(true);

      await disable2FA(disablePassword);

      const updatedUser = {
        ...currentUser,
        twoFactorEnabled: false,
      };

      setCurrentUser(updatedUser);

      localStorage.setItem(
        "adminUser",
        JSON.stringify(updatedUser)
      );

      setDisablePassword("");
      setShowDisablePassword(false);

      notify(
        "success",
        "Two-factor authentication disabled."
      );
    } catch (error) {
      console.error("DISABLE 2FA ERROR:", error);

      notify(
        "error",
        error?.response?.data?.message ||
        "Incorrect password or failed to disable 2FA."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="anim-fadeUp max-w-3xl">
      {/* HEADER */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600/10 flex items-center justify-center">
            <Settings
              size={21}
              className="text-blue-500"
            />
          </div>

          <div>
            <h1
              className={cx(
                "font-display font-semibold text-2xl",
                theme.text
              )}
            >
              Security Settings
            </h1>

            <p
              className={cx(
                "text-sm mt-1",
                theme.subtext
              )}
            >
              Manage your account security and
              two-factor authentication.
            </p>
          </div>
        </div>
      </div>

      {/* 2FA CARD */}
      <div
        className={cx(
          "rounded-2xl border p-5 sm:p-6",
          theme.surface,
          theme.border,
          theme.shadow
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div
              className={cx(
                "w-11 h-11 rounded-xl flex items-center justify-center shrink-0",
                twoFactorEnabled
                  ? "bg-green-500/10"
                  : "bg-white/5"
              )}
            >
              {twoFactorEnabled ? (
                <ShieldCheck
                  size={22}
                  className="text-green-500"
                />
              ) : (
                <ShieldOff
                  size={22}
                  className="text-gray-500"
                />
              )}
            </div>

            <div>
              <h2
                className={cx(
                  "font-display font-semibold text-base",
                  theme.text
                )}
              >
                Two-Factor Authentication
              </h2>

              <p
                className={cx(
                  "text-xs mt-1 leading-relaxed",
                  theme.subtext
                )}
              >
                Add an extra layer of security using
                Google Authenticator or Microsoft
                Authenticator.
              </p>

              <div className="mt-2">
                {twoFactorEnabled ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-green-400">
                    <Check size={13} />
                    Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <ShieldOff size={13} />
                    Disabled
                  </span>
                )}
              </div>
            </div>
          </div>

          {!twoFactorEnabled && !setupStarted && (
            <PrimaryButton
              onClick={startSetup}
              disabled={loading}
              className="shrink-0"
            >
              <ShieldCheck size={15} />

              {loading
                ? "Setting up..."
                : "Enable 2FA"}
            </PrimaryButton>
          )}

          {twoFactorEnabled && (
            <button
              type="button"
              onClick={() => {
                setShowDisablePassword(true);
                setDisablePassword("");
                setShowDisablePw(false);
              }}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-red-500/20 text-red-400 hover:bg-red-500/10 transition shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldOff size={15} />

              {loading
                ? "Disabling..."
                : "Disable 2FA"}
            </button>
          )}
        </div>
        {showDisablePassword &&
          createPortal(
            <div className="fixed inset-0 z-[999999] flex items-center justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 py-8">
              <div
                className={cx(
                  "relative w-full max-w-md my-auto rounded-2xl border p-6 shadow-2xl",
                  theme.surface,
                  theme.border
                )}
              >
                {/* CLOSE */}
                <button
                  type="button"
                  onClick={() => {
                    setShowDisablePassword(false);
                    setDisablePassword("");
                    setShowDisablePw(false);
                  }}
                  disabled={loading}
                  className="absolute right-4 top-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition"
                >
                  <X size={16} />
                </button>

                {/* HEADER */}
                <div className="flex items-start gap-3 mb-6 pr-8">
                  <div className="w-11 h-11 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                    <ShieldOff
                      size={20}
                      className="text-red-400"
                    />
                  </div>

                  <div>
                    <h3
                      className={cx(
                        "text-base font-semibold font-display",
                        theme.text
                      )}
                    >
                      Disable Two-Factor Authentication
                    </h3>

                    <p
                      className={cx(
                        "text-xs mt-1 leading-relaxed",
                        theme.subtext
                      )}
                    >
                      For your security, enter your current
                      password to disable 2FA.
                    </p>
                  </div>
                </div>

                {/* PASSWORD */}
                <div>
                  <label
                    className={cx(
                      "block text-xs font-medium mb-2",
                      theme.text
                    )}
                  >
                    Current Password
                  </label>

                  <div className="relative">
                    <TextInput
                      type={
                        showDisablePw
                          ? "text"
                          : "password"
                      }
                      value={disablePassword}
                      onChange={(e) =>
                        setDisablePassword(e.target.value)
                      }
                      placeholder="Enter your current password"
                      autoComplete="current-password"
                      className="pr-11 w-full"
                      autoFocus
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowDisablePw(
                          (prev) => !prev
                        )
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition"
                    >
                      {showDisablePw ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                </div>

                {/* WARNING */}
                <div className="mt-4 rounded-xl border border-red-500/15 bg-red-500/[0.06] px-3.5 py-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle
                      size={15}
                      className="text-red-400 mt-0.5 shrink-0"
                    />

                    <p className="text-[11px] leading-relaxed text-red-300/80">
                      Disabling 2FA will remove the
                      additional authenticator verification
                      from your next login.
                    </p>
                  </div>
                </div>

                {/* BUTTONS */}
                <div className="flex gap-3 mt-6">
                  <GhostButton
                    type="button"
                    onClick={() => {
                      setShowDisablePassword(false);
                      setDisablePassword("");
                      setShowDisablePw(false);
                    }}
                    disabled={loading}
                    className="flex-1"
                  >
                    Cancel
                  </GhostButton>

                  <button
                    type="button"
                    onClick={handleDisable}
                    disabled={
                      loading ||
                      !disablePassword.trim()
                    }
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2
                          size={15}
                          className="animate-spin"
                        />
                        Disabling...
                      </>
                    ) : (
                      <>
                        <ShieldOff size={15} />
                        Disable 2FA
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        {/* SETUP */}
        {setupStarted && !twoFactorEnabled && (
          <div className="mt-6 pt-6 border-t border-[#232B38]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* QR */}
              <div className="flex flex-col items-center justify-center">
                <div className="rounded-2xl bg-white p-4">
                  {qrCode && (
                    <img
                      src={qrCode}
                      alt="2FA QR Code"
                      className="w-48 h-48 object-contain"
                    />
                  )}
                </div>

                <p className="text-xs text-gray-400 text-center mt-3">
                  Scan this QR code using
                  Google Authenticator or
                  Microsoft Authenticator.
                </p>
              </div>

              {/* CODE */}
              <div className="flex flex-col justify-center">
                <Field
                  label="Authenticator Code"
                  required
                  hint="Enter the 6-digit code shown in your authenticator app."
                >
                  <TextInput
                    value={code}
                    onChange={(e) =>
                      setCode(
                        e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6)
                      )
                    }
                    placeholder="000000"
                    inputMode="numeric"
                    maxLength={6}
                    className="text-center tracking-[0.35em] text-lg"
                  />
                </Field>

                {/* SECRET */}
                {secret && (
                  <div className="mt-4">
                    <label className="text-xs text-gray-500">
                      Manual setup key
                    </label>

                    <div className="flex gap-2 mt-1">
                      <div className="flex-1 px-3 py-2.5 rounded-xl bg-[#0F141C] border border-[#232B38] text-xs text-gray-300 break-all">
                        {secret}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            secret
                          )
                        }
                        className="px-3 rounded-xl border border-[#232B38] text-gray-400 hover:text-white hover:bg-white/5"
                      >
                        <Copy size={15} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-5">
                  <GhostButton
                    onClick={() => {
                      setSetupStarted(false);
                      setQrCode("");
                      setSecret("");
                      setCode("");
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </GhostButton>

                  <PrimaryButton
                    onClick={handleEnable}
                    disabled={
                      loading || code.length !== 6
                    }
                    className="flex-1"
                  >
                    <ShieldCheck size={15} />

                    {loading
                      ? "Verifying..."
                      : "Enable 2FA"}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
/* ============================================================================
   ROOT: AdminApp
============================================================================ */
export default function AdminApp() {
  const theme = useTheme();
  const [skillCount, setSkillCount] = useState(0);
  const [educationCount, setEducationCount] = useState(0);
  const [authed, setAuthed] = useState(() => {
    const token = localStorage.getItem("adminToken");
    const storedUser = localStorage.getItem("adminUser");

    return !!token && !!storedUser;
  });
  const [showTrustedDevice, setShowTrustedDevice] =
    useState(true);
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const storedUser = localStorage.getItem("adminUser");

    if (!token || !storedUser) {
      setAuthed(false);
      setCurrentUser(null);
      return;
    }

    try {
      const user = JSON.parse(storedUser);

      if (!user || !user.id) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        setAuthed(false);
        setCurrentUser(null);
        return;
      }

      setCurrentUser(user);

      if (user.role === "Admin") {
        setAdminPage("dashboard");
      } else {
        const permissions = Array.isArray(user.permissions)
          ? user.permissions
          : [];

        // Remove login-only permission
        const pagePermissions = permissions.filter(
          (permission) => permission !== "allowLogin"
        );

        if (pagePermissions.length === 0) {
          // User can login but has no page access
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");

          setCurrentUser(null);
          setAuthed(false);

          return;
        }

        setAdminPage(pagePermissions[0]);
      }

      setAuthed(true);
    } catch (error) {
      console.error("Failed to restore login:", error);

      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");

      setCurrentUser(null);
      setAuthed(false);
    }
  }, []);
  const [adminPage, setAdminPage] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("adminUser");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Failed to load current user:", error);
      return null;
    }
  });
  const [about, setAbout] = useState(initialAbout);
  const [projects, setProjects] = useState([]);
  const [publications, setPublications] = useState([]);
  const [home, setHome] = useState(null);
  const [community, setCommunity] = useState([]);
  const [experience, setExperience] = useState([]);
  const [blog, setBlog] = useState([]);
  const [service, setService] = useState([]);
  const [contact, setContact] = useState(initialContact);
  useEffect(() => {
    const loadHome = async () => {
      try {
        const response = await getHome();
        const raw = response?.data?.data ?? response?.data ?? null;
        const homeData = Array.isArray(raw) ? raw[0] : raw;

        setHome(homeData || null);
      } catch (error) {
        console.error("Failed to load Home configuration:", error);
      }
    };

    loadHome();
  }, []);
  useEffect(() => {
    const loadContact = async () => {
      try {
        const response = await getContact();

        if (
          response.data?.success &&
          Array.isArray(response.data.data) &&
          response.data.data.length > 0
        ) {
          setContact(response.data.data[0]);
        } else {
          console.warn("No contact record found");
          setContact(null);
        }
      } catch (error) {
        console.error("Failed to load contact:", error);
        notify(
          "error",
          error.response?.data?.message ||
          "Failed to load contact information."
        );
      }
    };

    loadContact();
  }, []);
  const [toasts, setToasts] = useState([]);
  const notify = useCallback((type, message) => {
    setToasts((t) => [...t, { id: Date.now() + Math.random(), type, message }]);
  }, []);
  const closeToast = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const loadAllData = async () => {
    try {
      const [projRes, pubRes, commRes, expRes, skillsRes, educationRes, aboutRes, blogRes, serviceRes] = await Promise.all([
        getPortfolio(),
        getPublications(),
        getCommunity(),
        getExperiences(),
        getSkills(),
        getEducation(),
        getAbout(),
        getBlogs(),
        getServices(),
      ]);
      const aboutData = aboutRes.data?.data?.about?.[0] || null;
      setAbout(aboutData || initialAbout);
      setProjects(projRes.data.data || []);
      setPublications(pubRes.data.data || []);
      setCommunity(commRes.data.data || []);
      setExperience(expRes.data.data || []);
      setSkillCount(skillsRes.data.data?.length || 0);
      setEducationCount(educationRes.data.data?.length || 0);
      setBlog(blogRes.data?.data || []);
      setService(serviceRes.data?.data || []);
    } catch (err) {
      console.error("Failed to load data", err);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const handleLogin = () => {
    setAuthed(true);

    const storedUser = localStorage.getItem("adminUser");

    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);

        setCurrentUser(user);

        if (user.role === "Admin") {
          setAdminPage("dashboard");
        } else if (
          Array.isArray(user.permissions) &&
          user.permissions.length > 0
        ) {
          setAdminPage(user.permissions[0]);
        } else {
          // No permission
          handleLogout();
          return;
        }
      } catch (error) {
        console.error("Invalid stored user:", error);
        handleLogout();
        return;
      }
    }

    notify("success", "Welcome back!");
  };
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");

    setCurrentUser(null);
    setAuthed(false);
    setShowTrustedDevice(false);
    setAdminPage("dashboard");
    window.location.reload();
  };
  const [profileEditRequest, setProfileEditRequest] = useState(0);

  const handleProfileClick = () => {
    setAdminPage("users");
    setProfileEditRequest((prev) => prev + 1);
  };
  if (!authed) {
    return (
      <div className="font-body">
        <FontStyles />
        {showTrustedDevice ? (
          <TrustedDeviceLogin
            home={home}
            onLogin={handleLogin}
            onBackToLogin={() =>
              setShowTrustedDevice(false)
            }
          />
        ) : (
          <LoginScreen
            home={home}
            onLogin={handleLogin}
          />
        )}
        <ToastStack toasts={toasts} onClose={closeToast} />
      </div>
    );
  }

  // ─── Configurations for Projects, Publications, Community ─────

  const projectsConfig = {
    itemLabel: "Project",
    moveUp: async (id) => {
      await movePortfolioUp(id);
      const res = await getPortfolio();
      setProjects(res.data.data);
    },
    moveDown: async (id) => {
      await movePortfolioDown(id);
      const res = await getPortfolio();
      setProjects(res.data.data);
    },
    icon: FolderKanban,
    prefix: "p",
    searchKeys: ["title", "subtitle", "subSubtitle", "description", "tech", "category", "organizedBy"],
    grid: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    fields: [
      {
        key: "order",
        label: "Display Order",
        type: "number",
        required: false,
        placeholder: "1",
      },
      { key: "title", label: "Project Title", required: true, placeholder: "e.g. Health Chatbot" },
      { key: "subtitle", label: "Subtitle", placeholder: "e.g. AI-Powered Health Assistant" },
      { key: "subSubtitle", label: "Sub Subtitle", placeholder: "e.g. Phase 2" },
      { key: "description", label: "Description", type: "textarea", required: true, placeholder: "What does this project do?" },
      { key: "category", label: "Category", placeholder: "e.g. Mobile, Web, AI" },
      { key: "cardType", label: "Card Type", placeholder: "e.g. project, blog" },
      { key: "organizedBy", label: "Organized By", placeholder: "e.g. Team Alpha" },
      { key: "duration", label: "Duration", placeholder: "e.g. Nov 2024 – Dec 2024" },
      { key: "techStack", label: "Technologies", type: "techStack", placeholder: "Add technology" },
      { key: "features", label: "Key Features", type: "textarea", placeholder: "One per line" },
      { key: "status", label: "Status Tags", type: "textarea", placeholder: "Planning, In Progress" },
      { key: "github", label: "GitHub Link", type: "url", placeholder: "https://github.com/..." },
      { key: "demo", label: "Live Demo", type: "url", placeholder: "https://..." },
      { key: "image", label: "Project Image", type: "file", accept: "image/*" },
      { key: "isActive", label: "Active Project", type: "checkbox" },
    ],
    renderCard: (row) => {
      const techStack = Array.isArray(row.techStack)
        ? row.techStack.map((tech) => {
          if (typeof tech === "string") {
            return {
              icon: "",
              label: tech.trim(),
              color: "#94A3B8",
            };
          }
          return {
            icon: tech?.icon || "",
            label: tech?.label || tech?.icon || "",
            color: tech?.color || "#94A3B8",
          };
        })
        : typeof row.techStack === "string"
          ? (() => {
            try {
              const parsed = JSON.parse(row.techStack);
              if (Array.isArray(parsed)) {
                return parsed.map((tech) => {
                  if (typeof tech === "string") {
                    return {
                      icon: "",
                      label: tech.trim(),
                      color: "#94A3B8",
                    };
                  }
                  return {
                    icon: tech?.icon || "",
                    label: tech?.label || tech?.icon || "",
                    color: tech?.color || "#94A3B8",
                  };
                });
              }
            } catch (error) { }
            return row.techStack
              .split(",")
              .map((tech) => ({
                icon: "",
                label: tech.trim(),
                color: "#94A3B8",
              }))
              .filter((tech) => tech.label);
          })()
          : [];

      return (
        <>
          {row.image ? (
            <img
              src={row.image}
              alt={row.title}
              className="h-28 w-full rounded-xl -mx-1 -mt-1 object-cover border border-[#232B38]"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/placeholder-image.png";
              }}
            />
          ) : (
            <div className="h-28 rounded-xl -mx-1 -mt-1 flex items-center justify-center bg-[#1A2230] border border-[#232B38] text-gray-500 text-xs">
              No Image
            </div>
          )}

          <div className="mt-2">
            <div className="flex items-start flex-wrap gap-1.5">
              <h3 className="font-display font-semibold text-sm text-white flex-1 min-w-0 break-words">
                {row.title}
              </h3>
              <span className="px-2 py-1 rounded bg-[#2563EB] text-white text-xs font-semibold">
                #{row.order}
              </span>
              <div className="flex shrink-0 gap-1.5">
                {row.isActive && (
                  <span className="text-[9px] font-body bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                    Active
                  </span>
                )}
                {row.cardType && (
                  <span className="text-[9px] font-body bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/20 whitespace-nowrap">
                    {row.cardType}
                  </span>
                )}
              </div>
            </div>
            {row.subtitle && (
              <p className="text-xs text-blue-400 mt-0.5 break-words">
                {row.subtitle}
              </p>
            )}
            {row.subSubtitle && (
              <p className="text-[10px] text-gray-400 italic break-words">
                {row.subSubtitle}
              </p>
            )}
            {row.category && (
              <p className="text-[10px] text-gray-500 mt-0.5">
                {row.category}
              </p>
            )}
          </div>

          <p className="text-xs font-body line-clamp-2 text-gray-400 mt-1 break-words">
            {row.description}
          </p>

          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-500 flex-wrap">
            {row.duration && (
              <span className="flex items-center gap-1">
                📅 {row.duration}
              </span>
            )}
            {row.organizedBy && (
              <span className="flex items-center gap-1">
                👥 {row.organizedBy}
              </span>
            )}
          </div>

          {row.status &&
            Array.isArray(row.status) &&
            row.status.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {row.status.slice(0, 3).map((s, i) => (
                  <span
                    key={i}
                    className="text-[9px] font-body px-2 py-0.5 rounded-full bg-[#1A2230] border border-[#232B38] text-gray-300"
                  >
                    {s}
                  </span>
                ))}
                {row.status.length > 3 && (
                  <span className="text-[9px] font-body px-2 py-0.5 rounded-full bg-[#1A2230] border border-[#232B38] text-gray-500">
                    +{row.status.length - 3}
                  </span>
                )}
              </div>
            )}

          {row.features &&
            Array.isArray(row.features) &&
            row.features.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {row.features.slice(0, 2).map((f, i) => (
                  <span
                    key={i}
                    className="text-[9px] font-body px-2 py-0.5 rounded-full bg-[#1A2230] border border-[#232B38] text-gray-300"
                  >
                    {typeof f === "object" ? f?.text : f}
                  </span>
                ))}
                {row.features.length > 2 && (
                  <span className="text-[9px] font-body px-2 py-0.5 rounded-full bg-[#1A2230] text-gray-500">
                    +{row.features.length - 2}
                  </span>
                )}
              </div>
            )}

          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {techStack.slice(0, 4).map((tech, i) => {
              const iconName =
                typeof tech === "string"
                  ? tech.trim()
                  : tech?.icon || "";
              const label =
                typeof tech === "string"
                  ? tech.trim()
                  : tech?.label || tech?.icon || "";
              const color =
                typeof tech === "object"
                  ? tech?.color || "#94A3B8"
                  : "#94A3B8";

              let IconComponent = null;
              if (iconName && icons[iconName]) {
                IconComponent = icons[iconName];
              } else if (iconName) {
                const normalizedIconName = iconName
                  .toLowerCase()
                  .replace(/[^a-z0-9]/g, "");
                const matchedKey = Object.keys(icons).find(
                  (key) =>
                    key
                      .toLowerCase()
                      .replace(/^(fa|si|md|hi|ri|io|lu)/, "")
                      .replace(/[^a-z0-9]/g, "") ===
                    normalizedIconName
                );
                if (matchedKey) {
                  IconComponent = icons[matchedKey];
                }
              }

              return (
                <span
                  key={`${iconName}-${label}-${i}`}
                  className="inline-flex items-center gap-1.5 text-[10px] font-body px-2 py-1 rounded-md bg-[#1A2230] border border-[#232B38]"
                  style={{ color }}
                  title={label}
                >
                  {IconComponent && (
                    <IconComponent
                      size={16}
                      color={color}
                    />
                  )}
                  <span>{label}</span>
                </span>
              );
            })}
            {techStack.length > 4 && (
              <span className="text-[10px] font-body px-2 py-1 rounded-md bg-[#1A2230] text-gray-500">
                +{techStack.length - 4}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#232B38]">
            {row.github && row.github !== "#" && (
              <a
                href={row.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-gray-400 hover:text-blue-400 flex items-center gap-1"
              >
                🐙 Code
              </a>
            )}
            {row.demo && row.demo !== "#" && (
              <a
                href={row.demo}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-gray-400 hover:text-blue-400 flex items-center gap-1"
              >
                🚀 Demo
              </a>
            )}
            {(!row.github || row.github === "#") &&
              (!row.demo || row.demo === "#") && (
                <span className="text-[10px] text-gray-600">
                  No links
                </span>
              )}
          </div>
        </>
      );
    },
  };

  const publicationsConfig = {
    itemLabel: "Publication",
    icon: BookOpen,
    prefix: "pub",
    searchKeys: ["title", "subtitle", "description", "conference", "journal", "authors"],
    grid: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    fields: [
      { key: "title", label: "Title", required: true, placeholder: "e.g. A Novel AI Approach" },
      { key: "subtitle", label: "Subtitle", placeholder: "e.g. Extended Abstract" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Brief summary" },
      { key: "type", label: "Type", type: "select", options: ["Conference", "Journal", "Book Chapter", "Other"] },
      { key: "conference", label: "Conference", placeholder: "e.g. NeurIPS 2025" },
      { key: "journal", label: "Journal", placeholder: "e.g. Nature AI" },
      { key: "publisher", label: "Publisher", placeholder: "e.g. Springer" },
      { key: "publisherLink", label: "Publisher Link", type: "url", placeholder: "https://..." },
      { key: "citation", label: "Citation", type: "textarea", placeholder: "APA or BibTeX" },
      { key: "year", label: "Year", placeholder: "2025" },
      { key: "image", label: "Cover Image", type: "file", accept: "image/*" },
      { key: "techStack", label: "Technologies", type: "techStack", placeholder: "Add Technology" },
      { key: "metrics", label: "Metrics (comma-separated)", type: "textarea", placeholder: "Citations: 20, Downloads: 150" },
      { key: "authors", label: "Authors (comma-separated)", placeholder: "J. Doe, A. Smith" },
      { key: "doi", label: "DOI", placeholder: "10.1234/..." },
      { key: "keywords", label: "Keywords (comma-separated)", placeholder: "AI, NLP, Healthcare" },
      { key: "isFeatured", label: "Featured Publication", type: "checkbox" },
      { key: "isActive", label: "Active", type: "checkbox" },
    ],
    renderCard: (row) => (
      <>
        {row.image ? (
          <img src={row.image} alt={row.title} className="h-28 w-full rounded-xl -mx-1 -mt-1 object-cover border border-[#232B38]" onError={(e) => { e.target.onerror = null; e.target.src = "/placeholder-image.png"; }} />
        ) : (
          <div className="h-28 rounded-xl -mx-1 -mt-1 flex items-center justify-center bg-[#1A2230] border border-[#232B38] text-gray-500 text-xs">No Image</div>
        )}
        <div className="mt-2">
          <div className="flex items-start flex-wrap gap-1.5">
            <h3 className="font-display font-semibold text-sm text-white flex-1 min-w-0 break-words">{row.title}</h3>
            <span className="px-2 py-1 rounded bg-[#2563EB] text-white text-xs font-semibold">
              #{row.order}
            </span>
            <div className="flex shrink-0 gap-1.5">
              {row.isFeatured && <span className="text-[9px] font-body bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full whitespace-nowrap">★ Featured</span>}
              {row.isActive && <span className="text-[9px] font-body bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full whitespace-nowrap">Active</span>}
            </div>
          </div>
          {row.subtitle && <p className="text-xs text-blue-400 mt-0.5 break-words">{row.subtitle}</p>}
          <p className="text-[10px] text-gray-500 mt-0.5">{row.type} · {row.year}</p>
          {row.conference && <p className="text-[10px] text-gray-400">Conference: {row.conference}</p>}
          {row.journal && <p className="text-[10px] text-gray-400">Journal: {row.journal}</p>}
        </div>
        <p className="text-xs font-body line-clamp-2 text-gray-400 mt-1 break-words">{row.description}</p>
        {row.authors && Array.isArray(row.authors) && row.authors.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {row.authors.slice(0, 3).map((a, i) => <span key={i} className="text-[9px] font-body px-2 py-0.5 rounded-full bg-[#1A2230] border border-[#232B38] text-gray-300">{a}</span>)}
            {row.authors.length > 3 && <span className="text-[9px] font-body px-2 py-0.5 rounded-full bg-[#1A2230] border border-[#232B38] text-gray-500">+{row.authors.length - 3}</span>}
          </div>
        )}
        {row.doi && <p className="text-[10px] text-gray-500 mt-0.5">DOI: {row.doi}</p>}
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {(Array.isArray(row.techStack)
            ? row.techStack
            : typeof row.techStack === "string"
              ? row.techStack.split(",").map(t => t.trim()).filter(Boolean)
              : []
          ).slice(0, 3).map((t, index) => {
            const label = typeof t === "object" && t !== null
              ? t.label || t.name || ""
              : String(t);
            if (!label) return null;
            return (
              <span
                key={`${label}-${index}`}
                className="text-[10px] font-body px-2 py-0.5 rounded-md"
                style={{
                  background: "rgba(37,99,235,0.1)",
                  color: "#2563EB"
                }}
              >
                {label}
              </span>
            );
          })}
        </div>
        {row.publisherLink && row.publisherLink !== "#" && (
          <div className="mt-2 pt-2 border-t border-[#232B38]">
            <a href={row.publisherLink} target="_blank" rel="noopener noreferrer" className="text-[10px] text-gray-400 hover:text-blue-400 flex items-center gap-1">📄 Publisher</a>
          </div>
        )}
      </>
    ),
  };

  const communityConfig = {
    itemLabel: "Community Item",
    icon: Users,
    prefix: "comm",
    searchKeys: [
      "title",
      "subtitle",
      "category",
      "description",
      "organizedBy",
      "postername",
      "typename",
    ],
    grid: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    fields: [
      { key: "title", label: "Title", required: true, placeholder: "e.g. AI Meetup 2025" },
      { key: "subtitle", label: "Subtitle", placeholder: "e.g. Monthly Gathering" },
      { key: "category", label: "Category", placeholder: "Conference, Webinar" },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "organizedBy", label: "Organized By" },
      { key: "duration", label: "Duration" },
      { key: "postername", label: "Poster Name" },
      { key: "typename", label: "Type Name" },
      {
        key: "media",
        label: "Community Images",
        type: "multifile",
        accept: "image/*"
      },
      {
        key: "features",
        label: "Features",
        type: "featureList"
      },
      {
        key: "summary",
        label: "Summary",
        type: "textarea",
        placeholder: "Impact,+30%,Mental health awareness"
      },
      {
        key: "link",
        label: "Link",
        type: "url"
      },
      {
        key: "isActive",
        label: "Active",
        type: "checkbox"
      }
    ],
    renderCard: (row) => (
      <>
        {Array.isArray(row.media) && row.media.length > 0 ? (
          <img
            src={row.media[0]}
            alt={row.title}
            className="w-full h-36 object-cover rounded-xl -mx-1 -mt-1 mb-3 border border-[#232B38]"
          />
        ) : (
          <div className="w-full h-36 rounded-xl -mx-1 -mt-1 mb-3 bg-[#1A2230] flex items-center justify-center text-gray-500 text-sm border border-[#232B38]">
            No Image
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-display font-semibold text-sm text-white">
              {row.title}
            </h3>
            <span className="px-2 py-1 rounded bg-[#2563EB] text-white text-xs font-semibold">
              #{row.order}
            </span>
            {row.subtitle && (
              <p className="text-xs text-blue-400 mt-1">
                {row.subtitle}
              </p>
            )}
            {row.category && (
              <p className="text-[10px] text-gray-500 mt-1">
                {row.category}
              </p>
            )}
          </div>
          {row.isActive && (
            <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
              Active
            </span>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-3 line-clamp-3">
          {row.description}
        </p>

        <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-gray-500">
          {row.organizedBy && <span>👥 {row.organizedBy}</span>}
          {row.duration && <span>📅 {row.duration}</span>}
        </div>

        {(row.postername || row.typename) && (
          <div className="mt-3">
            {row.postername && (
              <div className="text-sm font-medium text-white">
                {row.postername}
              </div>
            )}
            {row.typename && (
              <div className="text-xs text-gray-400">
                {row.typename}
              </div>
            )}
          </div>
        )}

        {Array.isArray(row.features) && row.features.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {row.features.slice(0, 3).map((feature, i) => (
              <span
                key={i}
                className="text-[10px] px-2 py-1 rounded bg-[#1A2230] border border-[#232B38] text-gray-300"
              >
                {typeof feature === "object" ? feature.text : feature}
              </span>
            ))}
            {row.features.length > 3 && (
              <span className="text-[10px] px-2 py-1 rounded bg-[#1A2230] border border-[#232B38] text-gray-500">
                +{row.features.length - 3}
              </span>
            )}
          </div>
        )}

        {row.summary && typeof row.summary === "object" && (
          <div className="mt-3 p-3 rounded-xl border border-[#232B38] bg-[#0F141C]">
            <div className="text-xs text-gray-500">
              {row.summary.title}
            </div>
            <div className="text-xl font-bold text-white">
              {row.summary.value}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {row.summary.description}
            </div>
          </div>
        )}

        {Array.isArray(row.media) && row.media.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {row.media.slice(1, 5).map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Media ${i + 1}`}
                className="w-16 h-16 rounded-lg object-cover border border-[#232B38] flex-shrink-0"
              />
            ))}
          </div>
        )}

        {row.link && row.link !== "#" && (
          <a
            href={row.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center mt-4 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs transition-colors"
          >
            🔗 Open Link
          </a>
        )}
      </>
    ),
  };
  const emptyForm = {
    apiKey: "",
    channelId: "",
    channelUrl: "",
    banner: "",
    content: {
      sectionTitle: "",
      sectionSubtitle: "",
      subscribeButtonText: "Subscribe on YouTube",
      subscribeDescription: "",
    },
    socialLinks: [],
    settings: {
      showVideos: true,
      showShorts: true,
      showSocialLinks: true,
      maxVideos: 8,
      maxShorts: 8,
    },
    order: 0,
    isFeatured: false,
    isActive: true,
  };

  const emptySocialLink = {
    name: "",
    url: "",
    icon: "",
    color: "#E4405F",
    order: 1,
    isActive: true,
  };

  function YoutubeAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [formLoading, setFormLoading] = useState(false); // loading record for edit
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    // ---------------------------------------------------------------------
    // List loading
    // ---------------------------------------------------------------------
    const fetchList = useCallback(async () => {
      setLoading(true);
      try {
        const response = await getYoutube();
        const data = response?.data?.data ?? response?.data ?? [];
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load YouTube configs:", err);
        notify("Failed to load YouTube configurations");
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchList();
    }, [fetchList]);

    // ---------------------------------------------------------------------
    // Modal open helpers
    // ---------------------------------------------------------------------
    const openCreateModal = () => {
      setFormData(emptyForm);
      setEditingId(null);
      setModalMode("create");
      setShowModal(true);
    };

    const handleEdit = async (id) => {
      setFormLoading(true);
      setModalMode("edit");
      setEditingId(id);
      setShowModal(true);

      try {
        const response = await getYoutubeById(id);
        const youtube = response?.data?.data ?? response?.data ?? {};

        setFormData({
          // apiKey intentionally left blank in the form; if the backend omits
          // it from the response we must NOT overwrite the stored key on save
          // (see handleSave "preserve apiKey" logic below).
          apiKey: youtube.apiKey || "",
          channelId: youtube.channelId || "",
          channelUrl: youtube.channelUrl || "",
          banner: youtube.banner || "",

          content: {
            sectionTitle: youtube.content?.sectionTitle || "",
            sectionSubtitle: youtube.content?.sectionSubtitle || "",
            subscribeButtonText:
              youtube.content?.subscribeButtonText || "Subscribe on YouTube",
            subscribeDescription: youtube.content?.subscribeDescription || "",
          },

          socialLinks: Array.isArray(youtube.socialLinks)
            ? youtube.socialLinks
            : [],

          settings: {
            showVideos: youtube.settings?.showVideos ?? true,
            showShorts: youtube.settings?.showShorts ?? true,
            showSocialLinks: youtube.settings?.showSocialLinks ?? true,
            maxVideos: youtube.settings?.maxVideos ?? 8,
            maxShorts: youtube.settings?.maxShorts ?? 8,
          },

          order: youtube.order ?? 0,
          isFeatured: youtube.isFeatured ?? false,
          isActive: youtube.isActive ?? true,
        });
      } catch (err) {
        console.error("Failed to load YouTube record:", err);
        notify("Failed to load record for editing");
        setShowModal(false);
      } finally {
        setFormLoading(false);
      }
    };

    const closeModal = () => {
      setShowModal(false);
      setEditingId(null);
      setModalMode("create");
      setFormData(emptyForm);
    };

    // ---------------------------------------------------------------------
    // Field change helpers
    // ---------------------------------------------------------------------
    const setField = (field, value) =>
      setFormData((prev) => ({ ...prev, [field]: value }));

    const setContentField = (field, value) =>
      setFormData((prev) => ({
        ...prev,
        content: { ...prev.content, [field]: value },
      }));

    const setSettingsField = (field, value) =>
      setFormData((prev) => ({
        ...prev,
        settings: { ...prev.settings, [field]: value },
      }));

    const addSocialLink = () =>
      setFormData((prev) => ({
        ...prev,
        socialLinks: [
          ...prev.socialLinks,
          { ...emptySocialLink, order: prev.socialLinks.length + 1 },
        ],
      }));

    const updateSocialLink = (index, field, value) =>
      setFormData((prev) => ({
        ...prev,
        socialLinks: prev.socialLinks.map((link, i) =>
          i === index ? { ...link, [field]: value } : link
        ),
      }));

    const removeSocialLink = (index) =>
      setFormData((prev) => ({
        ...prev,
        socialLinks: prev.socialLinks.filter((_, i) => i !== index),
      }));

    // ---------------------------------------------------------------------
    // Save (create / update)
    // ---------------------------------------------------------------------
    const handleSave = async () => {
      if (saving) return; // prevent duplicate submissions

      if (!formData.channelId.trim() || !formData.channelUrl.trim()) {
        notify("Channel ID and Channel URL are required");
        return;
      }
      if (modalMode === "create" && !formData.apiKey.trim()) {
        notify("API Key is required");
        return;
      }

      setSaving(true);
      try {
        // If editing and the API key field was left blank (backend never
        // returned it), strip it from the payload so we don't wipe out the
        // stored key with an empty string.
        const payload = { ...formData };
        if (modalMode === "edit" && !payload.apiKey.trim()) {
          delete payload.apiKey;
        }

        if (modalMode === "edit") {
          await updateYoutube(editingId, payload);
          notify("YouTube configuration updated");
        } else {
          await createYoutube(payload);
          notify("YouTube configuration created");
        }

        closeModal();
        await fetchList();
      } catch (err) {
        console.error("Failed to save YouTube config:", err);
        notify(
          err?.response?.data?.message || "Failed to save YouTube configuration"
        );
      } finally {
        setSaving(false);
      }
    };

    // ---------------------------------------------------------------------
    // Delete
    // ---------------------------------------------------------------------
    const handleDelete = async (id) => {
      if (
        !window.confirm(
          "Delete this YouTube configuration? This action cannot be undone."
        )
      ) {
        return;
      }
      setDeletingId(id);
      try {
        await deleteYoutube(id);
        notify("YouTube configuration deleted");
        await fetchList();
      } catch (err) {
        console.error("Failed to delete YouTube config:", err);
        notify("Failed to delete YouTube configuration");
      } finally {
        setDeletingId(null);
      }
    };

    // ---------------------------------------------------------------------
    // Toggle active status
    // ---------------------------------------------------------------------
    const handleToggleStatus = async (id) => {
      setTogglingId(id);
      try {
        await toggleYoutubeStatus(id);
        notify("Status updated");
        await fetchList();
      } catch (err) {
        console.error("Failed to toggle status:", err);
        notify("Failed to update status");
      } finally {
        setTogglingId(null);
      }
    };

    // ---------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <icons.YoutubeIcon className="w-6 h-6 text-red-500" />
            YouTube Manager
          </h1>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-500 transition-colors text-white px-4 py-2 rounded-lg font-medium"
          >
            <icons.Plus className="w-4 h-4" />
            Add YouTube
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading configurations…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
            No YouTube configurations yet. Click "Add YouTube" to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col"
              >
                <div className="h-32 bg-zinc-800 relative overflow-hidden">
                  {item.banner ? (
                    <img
                      src={item.banner}
                      alt="Banner"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      No banner
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    {item.isFeatured && (
                      <span className="flex items-center gap-1 bg-amber-500/90 text-black text-xs font-medium px-2 py-0.5 rounded-full">
                        <icons.Star className="w-3 h-3" />
                        Featured
                      </span>
                    )}
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.isActive
                        ? "bg-emerald-500/90 text-black"
                        : "bg-zinc-600 text-zinc-200"
                        }`}
                    >
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="p-4 flex-1 flex flex-col gap-1.5">
                  <p className="font-medium truncate">{item.channelId}</p>
                  <a
                    href={item.channelUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-zinc-400 hover:text-red-400 truncate"
                  >
                    {item.channelUrl}
                  </a>
                  <p className="text-xs text-zinc-500">Order: {item.order}</p>
                </div>

                <div className="flex border-t border-zinc-800">
                  <button
                    onClick={() => handleEdit(item.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleStatus(item.id)}
                    disabled={togglingId === item.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors border-l border-zinc-800 disabled:opacity-50"
                  >
                    {togglingId === item.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <icons.Power className="w-3.5 h-3.5" />
                    )}
                    Toggle
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-l border-zinc-800 disabled:opacity-50"
                  >
                    {deletingId === item.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <YoutubeModal
            mode={modalMode}
            formData={formData}
            formLoading={formLoading}
            saving={saving}
            setField={setField}
            setContentField={setContentField}
            setSettingsField={setSettingsField}
            addSocialLink={addSocialLink}
            updateSocialLink={updateSocialLink}
            removeSocialLink={removeSocialLink}
            onSave={handleSave}
            onClose={closeModal}
          />
        )}
      </div>
    );
  }

  // ===========================================================================
  // Modal (create / edit) with live preview
  // ===========================================================================
  function YoutubeModal({
    mode,
    formData,
    formLoading,
    saving,
    setField,
    setContentField,
    setSettingsField,
    addSocialLink,
    updateSocialLink,
    removeSocialLink,
    onSave,
    onClose,
  }) {
    const [iconSuggestions, setIconSuggestions] = useState(null);
    const getSocialIcon = (iconName) => {
      if (!iconName) return null;

      // Exact match
      if (icons[iconName]) {
        return icons[iconName];
      }

      // Case-insensitive match
      const matchedKey = Object.keys(icons).find(
        (key) =>
          key.toLowerCase() === iconName.toLowerCase()
      );

      return matchedKey ? icons[matchedKey] : null;
    };
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
            <h2 className="text-lg font-semibold">
              {mode === "edit" ? "Edit YouTube Configuration" : "Add YouTube Configuration"}
            </h2>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {formLoading ? (
              <div className="flex items-center justify-center py-24 text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading record…
              </div>
            ) : (
              <div className="flex flex-col md:flex-row">
                {/* FORM */}
                <div className="md:w-1/2 p-6 space-y-8 border-b md:border-b-0 md:border-r border-zinc-800">
                  <Section title="YouTube Configuration">
                    <Field label="API Key">
                      <input
                        type="password"
                        value={formData.apiKey}
                        onChange={(e) => setField("apiKey", e.target.value)}
                        placeholder={
                          mode === "edit" ? "Leave blank to keep existing key" : ""
                        }
                        autoComplete="new-password"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Channel ID">
                      <input
                        type="text"
                        value={formData.channelId}
                        onChange={(e) => setField("channelId", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Channel URL">
                      <input
                        type="text"
                        value={formData.channelUrl}
                        onChange={(e) => setField("channelUrl", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Banner Image">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          const reader = new FileReader();

                          reader.onloadend = () => {
                            setField("banner", reader.result);
                          };

                          reader.readAsDataURL(file);
                        }}
                        className={inputClass}
                      />

                      {formData.banner && (
                        <img
                          src={formData.banner}
                          alt="Banner preview"
                          className="mt-3 w-full max-h-48 object-cover rounded-lg"
                        />
                      )}
                    </Field>
                  </Section>

                  <Section title="Content">
                    <Field label="Section Title">
                      <input
                        type="text"
                        value={formData.content.sectionTitle}
                        onChange={(e) => setContentField("sectionTitle", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Section Subtitle">
                      <input
                        type="text"
                        value={formData.content.sectionSubtitle}
                        onChange={(e) => setContentField("sectionSubtitle", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Subscribe Button Text">
                      <input
                        type="text"
                        value={formData.content.subscribeButtonText}
                        onChange={(e) => setContentField("subscribeButtonText", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Subscribe Description">
                      <textarea
                        rows={2}
                        value={formData.content.subscribeDescription}
                        onChange={(e) => setContentField("subscribeDescription", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                  </Section>

                  <Section
                    title="Social Links"
                    action={
                      <button
                        onClick={addSocialLink}
                        className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Social Link
                      </button>
                    }
                  >
                    {formData.socialLinks.length === 0 ? (
                      <p className="text-sm text-zinc-500">No social links yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {formData.socialLinks.map((link, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-2 gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg p-3"
                          >
                            <input
                              type="text"
                              placeholder="Name"
                              value={link.name}
                              onChange={(e) => updateSocialLink(index, "name", e.target.value)}
                              className={inputClassSm}
                            />
                            <input
                              type="text"
                              placeholder="URL"
                              value={link.url}
                              onChange={(e) => updateSocialLink(index, "url", e.target.value)}
                              className={inputClassSm}
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">

                              {/* Icon */}
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Icon (e.g. FaInstagram)"
                                  value={link.icon || ""}
                                  onFocus={() => setIconSuggestions(index)}
                                  onChange={(e) => {
                                    updateSocialLink(
                                      index,
                                      "icon",
                                      e.target.value
                                    );
                                    setIconSuggestions(index);
                                  }}
                                  className={inputClassSm}
                                />

                                {/* Selected icon preview */}
                                {getSocialIcon(link.icon) && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {(() => {
                                      const Icon = getSocialIcon(link.icon);

                                      return (
                                        <Icon
                                          size={18}
                                          color={link.color || "#ffffff"}
                                        />
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Icon suggestions */}
                                {iconSuggestions === index && (
                                  <div
                                    className="
          absolute
          left-0
          right-0
          top-full
          z-[9999]
          mt-1
          max-h-52
          overflow-y-auto
          rounded-lg
          border
          border-white/10
          bg-[#111827]
          shadow-2xl
        "
                                  >
                                    {Object.keys(icons)
                                      .filter((key) =>
                                        key
                                          .toLowerCase()
                                          .includes(
                                            (link.icon || "").toLowerCase()
                                          )
                                      )
                                      .slice(0, 15)
                                      .map((key) => {
                                        const Icon = icons[key];

                                        return (
                                          <button
                                            key={key}
                                            type="button"
                                            onMouseDown={(e) => {
                                              e.preventDefault();

                                              updateSocialLink(
                                                index,
                                                "icon",
                                                key
                                              );

                                              setIconSuggestions(null);
                                            }}
                                            className="
                  flex
                  w-full
                  items-center
                  gap-3
                  px-3
                  py-2
                  text-left
                  text-sm
                  text-gray-300
                  hover:bg-white/10
                  hover:text-white
                "
                                          >
                                            <Icon
                                              size={18}
                                              color={link.color || "#ffffff"}
                                            />

                                            <span>{key}</span>
                                          </button>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>

                              {/* Color */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={link.color || "#ffffff"}
                                  onChange={(e) =>
                                    updateSocialLink(
                                      index,
                                      "color",
                                      e.target.value
                                    )
                                  }
                                  className="
        h-10
        w-11
        shrink-0
        cursor-pointer
        rounded-lg
        border
        border-white/10
        bg-transparent
        p-1
      "
                                />

                                <input
                                  type="text"
                                  placeholder="#hex"
                                  value={link.color || ""}
                                  onChange={(e) =>
                                    updateSocialLink(
                                      index,
                                      "color",
                                      e.target.value
                                    )
                                  }
                                  className={inputClassSm}
                                />
                              </div>

                            </div>
                            <input
                              type="number"
                              placeholder="Order"
                              value={link.order}
                              onChange={(e) =>
                                updateSocialLink(index, "order", Number(e.target.value))
                              }
                              className={inputClassSm}
                            />
                            <div className="flex items-center justify-between px-1">
                              <label className="flex items-center gap-2 text-sm text-zinc-300">
                                <input
                                  type="checkbox"
                                  checked={link.isActive}
                                  onChange={(e) =>
                                    updateSocialLink(index, "isActive", e.target.checked)
                                  }
                                />
                                Active
                              </label>
                              <button
                                onClick={() => removeSocialLink(index)}
                                className="text-red-400 hover:text-red-300"
                                aria-label="Delete social link"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>

                  <Section title="Settings">
                    <ToggleRow
                      label="Show Videos"
                      checked={formData.settings.showVideos}
                      onChange={(v) => setSettingsField("showVideos", v)}
                    />
                    <ToggleRow
                      label="Show Shorts"
                      checked={formData.settings.showShorts}
                      onChange={(v) => setSettingsField("showShorts", v)}
                    />
                    <ToggleRow
                      label="Show Social Links"
                      checked={formData.settings.showSocialLinks}
                      onChange={(v) => setSettingsField("showSocialLinks", v)}
                    />
                    <Field label="Max Videos">
                      <input
                        type="number"
                        min={0}
                        value={formData.settings.maxVideos}
                        onChange={(e) =>
                          setSettingsField("maxVideos", Number(e.target.value))
                        }
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Max Shorts">
                      <input
                        type="number"
                        min={0}
                        value={formData.settings.maxShorts}
                        onChange={(e) =>
                          setSettingsField("maxShorts", Number(e.target.value))
                        }
                        className={inputClass}
                      />
                    </Field>
                  </Section>

                  <Section title="Other">
                    <Field label="Order">
                      <input
                        type="number"
                        value={formData.order}
                        onChange={(e) => setField("order", Number(e.target.value))}
                        className={inputClass}
                      />
                    </Field>
                    <ToggleRow
                      label="Featured"
                      checked={formData.isFeatured}
                      onChange={(v) => setField("isFeatured", v)}
                    />
                    <ToggleRow
                      label="Active"
                      checked={formData.isActive}
                      onChange={(v) => setField("isActive", v)}
                    />
                  </Section>
                </div>

                {/* LIVE PREVIEW */}
                <div className="md:w-1/2 p-6 bg-zinc-950/50">
                  <p className="text-xs uppercase tracking-wide text-zinc-500 mb-3">
                    Live Preview
                  </p>
                  <LivePreview formData={formData} />
                </div>
              </div>
            )}
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-6 py-4 border-t border-zinc-800 bg-zinc-900">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || formLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  function LivePreview({ formData }) {
    const activeLinks = (formData.socialLinks || []).filter((l) => l.isActive);

    return (
      <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900">
        <div className="h-36 bg-zinc-800 relative">
          {formData.banner ? (
            <img
              src={formData.banner}
              alt="Banner preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm">
              No banner set
            </div>
          )}
          <div className="absolute top-2 right-2 flex gap-1.5">
            {formData.isFeatured && (
              <span className="bg-amber-500/90 text-black text-xs font-medium px-2 py-0.5 rounded-full">
                Featured
              </span>
            )}
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${formData.isActive
                ? "bg-emerald-500/90 text-black"
                : "bg-zinc-600 text-zinc-200"
                }`}
            >
              {formData.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <h3 className="text-xl font-semibold">
            {formData.content.sectionTitle || "Section Title"}
          </h3>
          {formData.content.sectionSubtitle && (
            <p className="text-sm text-red-400">{formData.content.sectionSubtitle}</p>
          )}
          {formData.settings.showSocialLinks && activeLinks.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {activeLinks
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((link, i) => (
                  <span
                    key={i}
                    className="text-xs px-2.5 py-1 rounded-full border"
                    style={{
                      borderColor: link.color || "#666",
                      color: link.color || "#ccc",
                    }}
                  >
                    {link.name || "Link"}
                  </span>
                ))}
            </div>
          )}

          <div className="pt-3">
            <button
              type="button"
              className="bg-red-600 text-white text-sm font-medium px-4 py-2 rounded-lg pointer-events-none"
            >
              {formData.content.subscribeButtonText || "Subscribe on YouTube"}
            </button>
            {formData.content.subscribeDescription && (
              <p className="text-xs text-zinc-500 mt-2">
                {formData.content.subscribeDescription}
              </p>
            )}
          </div>

          <div className="pt-2 text-xs text-zinc-500 flex gap-3">
            {formData.settings.showVideos && <span>Videos: max {formData.settings.maxVideos}</span>}
            {formData.settings.showShorts && <span>Shorts: max {formData.settings.maxShorts}</span>}
          </div>
        </div>
      </div>
    );
  }


  const homeForm = {
    name: "",
    laptopName: "",
    description: "",
    roles: [],
    badges: [],
    buttons: {
      viewDetails: {
        action: "filter",
        filter: "",
        target: "",
        route: "",
      },
      viewExplore: {
        action: "filter",
        filter: "",
        target: "",
        route: "",
      },
    },
    laptopBg: "",
    logo: "",
    logoAlt: "",
    headerLogo: "",
    footerLogo: "",
    viewDetails: "",
    ViewExplore: "",
    opportunities: "",
    isActive: true,
  };

  const emptyBadge = { name: "", icon: "", color: "#6366F1", position: "top-left" };
  function HomeAdmin() {
    const [record, setRecord] = useState(null); // raw DB record for display card
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [formData, setFormData] = useState(homeForm);
    const [saving, setSaving] = useState(false);

    // ---------------------------------------------------------------------
    // Load the single Home record
    // ---------------------------------------------------------------------
    const fetchHome = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getHome();
        const raw = response?.data?.data ?? response?.data ?? null;
        // GET /api/home may return a single object or a list containing one item
        const home = Array.isArray(raw) ? raw[0] : raw;
        setRecord(home || null);
      } catch (err) {
        console.error("Failed to load Home config:", err);
        setError("Failed to load Home configuration");
        notify("Failed to load Home configuration");
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchHome();
    }, [fetchHome]);

    // ---------------------------------------------------------------------
    // Map DB record -> form state
    // ---------------------------------------------------------------------
    const recordToForm = (home) => ({
      name: home.name || "",
      laptopName: home.laptopName || "",
      description: home.description || "",
      roles: Array.isArray(home.roles) ? home.roles : [],
      badges: Array.isArray(home.badges) ? home.badges : [],
      buttons: {
        viewDetails: {
          action: home.buttons?.viewDetails?.action || "filter",
          filter: home.buttons?.viewDetails?.filter || "",
          target: home.buttons?.viewDetails?.target || "",
          route: home.buttons?.viewDetails?.route || "",
        },

        viewExplore: {
          action: home.buttons?.viewExplore?.action || "filter",
          filter: home.buttons?.viewExplore?.filter || "",
          target: home.buttons?.viewExplore?.target || "",
          route: home.buttons?.viewExplore?.route || "",
        },
      },
      laptopBg: home.laptopBg || "",
      logo: home.logo || "",
      logoAlt: home.logoAlt || "",
      headerLogo: home.headerLogo || "",
      footerLogo: home.footerLogo || "",
      viewDetails: home.viewDetails || "",
      ViewExplore: home.ViewExplore || "",
      opportunities: home.opportunities || "",
      isActive: home.isActive ?? true,
    });

    const openEditModal = () => {
      if (!record) return;
      setFormData(recordToForm(record));
      setShowEditModal(true);
    };

    const closeEditModal = () => setShowEditModal(false);

    // Preview uses live form data while editing, otherwise falls back to the
    // saved DB record (no static fallback content — only real data).
    const openPreview = () => {
      if (!record && !showEditModal) return;
      if (!showEditModal) setFormData(recordToForm(record));
      setShowPreviewModal(true);
    };

    // ---------------------------------------------------------------------
    // Field helpers
    // ---------------------------------------------------------------------
    const setField = (field, value) =>
      setFormData((prev) => ({ ...prev, [field]: value }));

    const setButtonFilter = (key, value) =>
      setFormData((prev) => ({
        ...prev,
        buttons: {
          ...prev.buttons,
          [key]: { ...prev.buttons[key], filter: value },
        },
      }));

    // Roles: simple string list
    const addRole = () =>
      setFormData((prev) => ({ ...prev, roles: [...prev.roles, ""] }));

    const updateRole = (index, value) =>
      setFormData((prev) => ({
        ...prev,
        roles: prev.roles.map((r, i) => (i === index ? value : r)),
      }));

    const removeRole = (index) =>
      setFormData((prev) => ({
        ...prev,
        roles: prev.roles.filter((_, i) => i !== index),
      }));

    // Badges
    const addBadge = () =>
      setFormData((prev) => ({ ...prev, badges: [...prev.badges, { ...emptyBadge }] }));

    const updateBadge = (index, field, value) =>
      setFormData((prev) => ({
        ...prev,
        badges: prev.badges.map((b, i) => (i === index ? { ...b, [field]: value } : b)),
      }));

    const removeBadge = (index) =>
      setFormData((prev) => ({
        ...prev,
        badges: prev.badges.filter((_, i) => i !== index),
      }));

    // ---------------------------------------------------------------------
    // Save
    // ---------------------------------------------------------------------
    const setButtonAction = (key, action) =>
      setFormData((prev) => ({
        ...prev,
        buttons: {
          ...prev.buttons,
          [key]: {
            action,
            filter: action === "filter" ? prev.buttons[key].filter : "",
            target: action === "scroll" ? prev.buttons[key].target : "",
            route: action === "route" ? prev.buttons[key].route : "",
          },
        },
      }));


    const setButtonTarget = (key, target) =>
      setFormData((prev) => ({
        ...prev,
        buttons: {
          ...prev.buttons,
          [key]: {
            ...prev.buttons[key],
            target,
          },
        },
      }));

    const setButtonRoute = (key, route) =>
      setFormData((prev) => ({
        ...prev,
        buttons: {
          ...prev.buttons,
          [key]: {
            ...prev.buttons[key],
            route,
          },
        },
      }));
    const handleSave = async () => {
      if (saving || !record?.id) return;

      if (!formData.name.trim()) {
        notify("Name is required");
        return;
      }

      setSaving(true);
      try {
        const payload = {
          name: formData.name,
          laptopName: formData.laptopName,
          description: formData.description,
          roles: formData.roles.filter((r) => r.trim() !== ""),
          badges: formData.badges,
          buttons: {
            viewDetails: {
              action: formData.buttons.viewDetails.action,
              filter: formData.buttons.viewDetails.filter,
              target: formData.buttons.viewDetails.target,
              route: formData.buttons.viewDetails.route,
            },
            viewExplore: {
              action: formData.buttons.viewExplore.action,
              filter: formData.buttons.viewExplore.filter,
              target: formData.buttons.viewExplore.target,
              route: formData.buttons.viewExplore.route,
            },
          },
          laptopBg: formData.laptopBg,
          logo: formData.logo,
          logoAlt: formData.logoAlt,
          headerLogo: formData.headerLogo,
          footerLogo: formData.footerLogo,
          viewDetails: formData.viewDetails,
          ViewExplore: formData.ViewExplore,
          opportunities: formData.opportunities,
          isActive: formData.isActive,
        };

        await updateHome(record.id, payload);
        notify("Home configuration updated");
        setShowEditModal(false);
        await fetchHome();
      } catch (err) {
        console.error("Failed to update Home config:", err);
        notify(err?.response?.data?.message || "Failed to update Home configuration");
      } finally {
        setSaving(false);
      }
    };

    // ---------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <icons.HomeIcon className="w-6 h-6 text-indigo-500" />
            Home Manager
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading configuration…
          </div>
        ) : error ? (
          <div className="text-center py-24 text-red-400 border border-dashed border-red-900 rounded-xl">
            {error}
          </div>
        ) : !record ? (
          <div className="text-center py-24 text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
            No Home configuration found in the database.
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden max-w-3xl">
            <div className="h-40 bg-zinc-800 relative overflow-hidden">
              {record.laptopBg ? (
                <img
                  src={record.laptopBg}
                  alt="Laptop background"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-600">
                  No background set
                </div>
              )}
              <span
                className={`absolute top-3 right-3 text-xs font-medium px-2 py-0.5 rounded-full ${record.isActive
                  ? "bg-emerald-500/90 text-black"
                  : "bg-zinc-600 text-zinc-200"
                  }`}
              >
                {record.isActive ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="p-5 space-y-3">
              <div className="flex items-center gap-3">
                {record.logo && (
                  <img
                    src={record.logo}
                    alt={record.logoAlt || "Logo"}
                    className="w-10 h-10 rounded-lg object-cover border border-zinc-800"
                  />
                )}
                <div>
                  <p className="font-semibold text-lg">{record.name}</p>
                  <p className="text-sm text-zinc-400">{record.laptopName}</p>
                </div>
              </div>

              <p className="text-sm text-zinc-400 line-clamp-2">{record.description}</p>

              <div className="flex flex-wrap gap-1.5 pt-1">
                {(record.roles || []).map((role, i) => (
                  <span
                    key={i}
                    className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700"
                  >
                    {role}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {(record.badges || []).map((badge, i) => (
                  <span
                    key={i}
                    className="text-xs px-2 py-0.5 rounded-full border"
                    style={{ borderColor: badge.color || "#666", color: badge.color || "#ccc" }}
                  >
                    {badge.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex border-t border-zinc-800">
              <button
                onClick={openEditModal}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
              <button
                onClick={openPreview}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-indigo-400 hover:bg-indigo-500/10 transition-colors border-l border-zinc-800"
              >
                <Eye className="w-3.5 h-3.5" />
                Preview
              </button>
            </div>
          </div>
        )}

        {showEditModal && (
          <HomeEditModal
            formData={formData}
            saving={saving}
            setField={setField}
            setButtonAction={setButtonAction}
            setButtonFilter={setButtonFilter}
            setButtonTarget={setButtonTarget}
            setButtonRoute={setButtonRoute}
            addRole={addRole}
            updateRole={updateRole}
            removeRole={removeRole}
            addBadge={addBadge}
            updateBadge={updateBadge}
            removeBadge={removeBadge}
            onSave={handleSave}
            onClose={closeEditModal}
            onPreview={openPreview}

          />
        )}

        {showPreviewModal && (
          <HomePreviewModal
            formData={formData}
            onClose={() => setShowPreviewModal(false)}
          />
        )}
      </div>
    );
  }

  // ===========================================================================
  // Edit modal
  // ===========================================================================
  function HomeEditModal({
    formData,
    saving,
    setField,
    setButtonFilter,
    setButtonAction,
    setButtonTarget,
    setButtonRoute,
    addRole,
    updateRole,
    removeRole,
    addBadge,
    updateBadge,
    removeBadge,
    onSave,
    onClose,
    onPreview,
  }) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
            <h2 className="text-lg font-semibold">Edit Home Configuration</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={onPreview}
                className="flex items-center gap-1.5 text-sm text-indigo-400 hover:text-indigo-300 px-2 py-1"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            <Section title="General">
              <Field label="Name">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Laptop Name">
                <input
                  type="text"
                  value={formData.laptopName}
                  onChange={(e) => setField("laptopName", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setField("description", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Opportunities">
                <input
                  type="text"
                  value={formData.opportunities}
                  onChange={(e) => setField("opportunities", e.target.value)}
                  className={inputClass}
                />
              </Field>
            </Section>

            <Section
              title="Roles"
              action={
                <button
                  onClick={addRole}
                  className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Role
                </button>
              }
            >
              {formData.roles.length === 0 ? (
                <p className="text-sm text-zinc-500">No roles yet.</p>
              ) : (
                <div className="space-y-2">
                  {formData.roles.map((role, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={role}
                        onChange={(e) => updateRole(index, e.target.value)}
                        placeholder="e.g. Full Stack Developer"
                        className={inputClassSm}
                      />
                      <button
                        onClick={() => removeRole(index)}
                        className="text-red-400 hover:text-red-300 shrink-0"
                        aria-label="Remove role"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section
              title="Badges"
              action={
                <button
                  onClick={addBadge}
                  className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Badge
                </button>
              }
            >
              {formData.badges.length === 0 ? (
                <p className="text-sm text-zinc-500">No badges yet.</p>
              ) : (
                <div className="space-y-3">
                  {formData.badges.map((badge, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-2 gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg p-3"
                    >
                      <input
                        type="text"
                        placeholder="Name"
                        value={badge.name}
                        onChange={(e) => updateBadge(index, "name", e.target.value)}
                        className={inputClassSm}
                      />
                      <input
                        type="text"
                        placeholder="Icon"
                        value={badge.icon}
                        onChange={(e) => updateBadge(index, "icon", e.target.value)}
                        className={inputClassSm}
                      />
                      <input
                        type="text"
                        placeholder="Color (#hex)"
                        value={badge.color}
                        onChange={(e) => updateBadge(index, "color", e.target.value)}
                        className={inputClassSm}
                      />
                      <select
                        value={badge.position}
                        onChange={(e) => updateBadge(index, "position", e.target.value)}
                        className={inputClassSm}
                      >
                        <option value="top-left">Top Left</option>
                        <option value="top-right">Top Right</option>
                        <option value="bottom-left">Bottom Left</option>
                        <option value="bottom-right">Bottom Right</option>
                      </select>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => removeBadge(index)}
                          className="text-red-400 hover:text-red-300 flex items-center gap-1 text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Buttons">
              {/* =========================
      VIEW DETAILS
  ========================= */}

              <Field label="View Details Button — Action">
                <select
                  value={formData.buttons.viewDetails.action || "filter"}
                  onChange={(e) =>
                    setButtonAction("viewDetails", e.target.value)
                  }
                  className={inputClass}
                >
                  <option value="filter">Filter</option>
                  <option value="scroll">Scroll</option>
                  <option value="route">Route</option>
                </select>
              </Field>

              {formData.buttons.viewDetails.action === "filter" && (
                <Field label="View Details — Filter">
                  <input
                    type="text"
                    value={formData.buttons.viewDetails.filter || ""}
                    onChange={(e) =>
                      setButtonFilter("viewDetails", e.target.value)
                    }
                    placeholder="Enter filter name"
                    className={inputClass}
                  />
                </Field>
              )}

              {formData.buttons.viewDetails.action === "scroll" && (
                <Field label="View Details — Section ID">
                  <input
                    type="text"
                    value={formData.buttons.viewDetails.target || ""}
                    onChange={(e) =>
                      setButtonTarget("viewDetails", e.target.value)
                    }
                    placeholder="Enter section ID"
                    className={inputClass}
                  />
                </Field>
              )}

              {formData.buttons.viewDetails.action === "route" && (
                <Field label="View Details — Route / Link">
                  <input
                    type="text"
                    value={formData.buttons.viewDetails.route || ""}
                    onChange={(e) =>
                      setButtonRoute("viewDetails", e.target.value)
                    }
                    placeholder="/publication-details"
                    className={inputClass}
                  />
                </Field>
              )}


              {/* =========================
      VIEW EXPLORE
  ========================= */}

              <Field label="View Explore Button — Action">
                <select
                  value={formData.buttons.viewExplore.action || "filter"}
                  onChange={(e) =>
                    setButtonAction("viewExplore", e.target.value)
                  }
                  className={inputClass}
                >
                  <option value="filter">Filter</option>
                  <option value="scroll">Scroll</option>
                  <option value="route">Route</option>
                </select>
              </Field>

              {formData.buttons.viewExplore.action === "filter" && (
                <Field label="View Explore — Filter">
                  <input
                    type="text"
                    value={formData.buttons.viewExplore.filter || ""}
                    onChange={(e) =>
                      setButtonFilter("viewExplore", e.target.value)
                    }
                    placeholder="Enter filter name"
                    className={inputClass}
                  />
                </Field>
              )}

              {formData.buttons.viewExplore.action === "scroll" && (
                <Field label="View Explore — Section ID">
                  <input
                    type="text"
                    value={formData.buttons.viewExplore.target || ""}
                    onChange={(e) =>
                      setButtonTarget("viewExplore", e.target.value)
                    }
                    placeholder="Enter section ID"
                    className={inputClass}
                  />
                </Field>
              )}

              {formData.buttons.viewExplore.action === "route" && (
                <Field label="View Explore — Route / Link">
                  <input
                    type="text"
                    value={formData.buttons.viewExplore.route || ""}
                    onChange={(e) =>
                      setButtonRoute("viewExplore", e.target.value)
                    }
                    placeholder="/community"
                    className={inputClass}
                  />
                </Field>
              )}
            </Section>
            <Section title="Links & Media">
              <Field label="View Details">
                <input
                  type="text"
                  value={formData.viewDetails}
                  onChange={(e) => setField("viewDetails", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="View Explore">
                <input
                  type="text"
                  value={formData.ViewExplore}
                  onChange={(e) => setField("ViewExplore", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Laptop Background">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const reader = new FileReader();

                    reader.onloadend = () => {
                      setField("laptopBg", reader.result);
                    };

                    reader.readAsDataURL(file);
                  }}
                  className={inputClass}
                />

                {formData.laptopBg && (
                  <img
                    src={formData.laptopBg}
                    alt="Laptop Background Preview"
                    className="mt-3 w-full h-32 object-cover rounded-lg border border-slate-700"
                  />
                )}
              </Field>

              <Field label="Logo">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const reader = new FileReader();

                    reader.onloadend = () => {
                      setField("logo", reader.result);
                    };

                    reader.readAsDataURL(file);
                  }}
                  className={inputClass}
                />

                {formData.logo && (
                  <img
                    src={formData.logo}
                    alt={formData.logoAlt || "Logo"}
                    className="mt-3 w-32 h-32 object-contain rounded-lg border border-slate-700 bg-slate-900 p-2"
                  />
                )}
              </Field>
              <Field label="Logo Alt">
                <input
                  type="text"
                  value={formData.logoAlt}
                  onChange={(e) => setField("logoAlt", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4 space-y-4">
                <h3 className="text-sm font-semibold text-zinc-300">
                  Header & Footer Logo
                </h3>

                {/* Header Logo */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">
                    Header Logo
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const reader = new FileReader();

                      reader.onloadend = () => {
                        setField("headerLogo", reader.result);
                      };

                      reader.readAsDataURL(file);
                    }}
                    className={inputClass}
                  />

                  {formData.headerLogo && (
                    <img
                      src={formData.headerLogo}
                      alt="Header Logo"
                      className="mt-3 h-14 w-full object-contain rounded-lg border border-zinc-700 bg-zinc-950 p-2"
                    />
                  )}
                </div>

                {/* Footer Logo */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">
                    Footer Logo
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const reader = new FileReader();

                      reader.onloadend = () => {
                        setField("footerLogo", reader.result);
                      };

                      reader.readAsDataURL(file);
                    }}
                    className={inputClass}
                  />

                  {formData.footerLogo && (
                    <img
                      src={formData.footerLogo}
                      alt="Footer Logo"
                      className="mt-3 h-14 w-full object-contain rounded-lg border border-zinc-700 bg-zinc-950 p-2"
                    />
                  )}
                </div>
              </div>

            </Section>

            <Section title="Options">
              <ToggleRow
                label="Active"
                checked={formData.isActive}
                onChange={(v) => setField("isActive", v)}
              />
            </Section>
          </div>

          <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-6 py-4 border-t border-zinc-800 bg-zinc-900">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // Preview modal (read-only, uses live form data, never saves)
  // ===========================================================================
  function HomePreviewModal({ formData, onClose }) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Eye className="w-4 h-4 text-indigo-400" />
              Home Preview
            </h2>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="rounded-xl overflow-hidden border border-zinc-800">
              <div className="h-44 bg-zinc-800 relative">
                {formData.laptopBg ? (
                  <img
                    src={formData.laptopBg}
                    alt="Laptop background preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm">
                    No background set
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1.5">
                  {formData.badges.map((badge, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-0.5 rounded-full border bg-black/40"
                      style={{ borderColor: badge.color || "#666", color: badge.color || "#ccc" }}
                    >
                      {badge.name}
                    </span>
                  ))}
                </div>
                <span
                  className={`absolute top-2 left-2 text-xs font-medium px-2 py-0.5 rounded-full ${formData.isActive
                    ? "bg-emerald-500/90 text-black"
                    : "bg-zinc-600 text-zinc-200"
                    }`}
                >
                  {formData.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="p-5 space-y-3 bg-zinc-900">
                <div className="flex items-center gap-3">
                  {formData.logo && (
                    <img
                      src={formData.logo}
                      alt={formData.logoAlt || "Logo"}
                      className="w-10 h-10 rounded-lg object-cover border border-zinc-800"
                    />
                  )}
                  <div>
                    <h3 className="text-xl font-semibold">{formData.name || "Name"}</h3>
                    <p className="text-sm text-indigo-400">{formData.laptopName}</p>
                  </div>
                </div>

                <p className="text-sm text-zinc-400">{formData.description}</p>

                <div className="flex flex-wrap gap-1.5">
                  {formData.roles.map((role, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700"
                    >
                      {role}
                    </span>
                  ))}
                </div>

                {formData.opportunities && (
                  <p className="text-xs text-zinc-500">{formData.opportunities}</p>
                )}

                <div className="flex gap-3 pt-3">
                  <button
                    type="button"
                    className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg pointer-events-none"
                  >
                    {formData.viewDetails || "View Details"}
                  </button>
                  <button
                    type="button"
                    className="bg-zinc-800 text-zinc-200 text-sm font-medium px-4 py-2 rounded-lg pointer-events-none border border-zinc-700"
                  >
                    {formData.ViewExplore || "View Explore"}
                  </button>
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900 mt-4">
              <div className="px-5 py-4 border-b border-zinc-800">
                <h3 className="text-sm font-semibold text-zinc-300">
                  Header & Footer Logos
                </h3>
              </div>

              <div className="p-5 space-y-5">

                {/* Header Logo */}
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Header Logo</p>

                  {formData.headerLogo ? (
                    <a
                      href={home?.headerLogo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg border border-zinc-700 bg-zinc-950 p-4 hover:border-indigo-500 transition-colors"
                    >
                      <img
                        src={formData.headerLogo}
                        alt="Header Logo"
                        className="h-16 w-full object-contain"
                      />
                    </a>
                  ) : (
                    <div className="h-20 flex items-center justify-center rounded-lg border border-dashed border-zinc-700 text-sm text-zinc-500">
                      No Header Logo
                    </div>
                  )}
                </div>

                {/* Footer Logo */}
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Footer Logo</p>

                  {formData.footerLogo ? (
                    <a
                      href={formData.footerLogo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg border border-zinc-700 bg-zinc-950 p-4 hover:border-indigo-500 transition-colors"
                    >
                      <img
                        src={formData.footerLogo}
                        alt="Footer Logo"
                        className="h-16 w-full object-contain"
                      />
                    </a>
                  ) : (
                    <div className="h-20 flex items-center justify-center rounded-lg border border-dashed border-zinc-700 text-sm text-zinc-500">
                      No Footer Logo
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const footerSocialLink = {
    id: "",
    title: "",
    description: "",
    actionText: "",
    actionColor: "#6366F1",
    href: "",
    text: "",
    showIcon: true,
    icon: "",
    iconColor: "#ffffff",
    boxClass: "",
  };

  const emptyFooterLink = {
    id: "",
    label: "",
    type: "scroll", // "scroll" | "projects" | "route"
    target: "",
    path: "",
    filter: "",
    showFooter: true,
  };

  const emptySection = {
    id: "",
    heading: "",
    links: [],
  };

  const footerForm = {
    footername: "",
    footerdes: "",
    socialLinks: [],
    footerSections: [],
    copyright: "",
    developerDetail: "",
    footerLink: "",
    isActive: true,
  };

  const genId = (prefix) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  function FooterAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [modalMode, setModalMode] = useState(null); // null | "create" | "edit"
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(footerForm);
    const [formLoading, setFormLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [previewRecord, setPreviewRecord] = useState(null);
    // ---------------------------------------------------------------------
    // List loading
    // ---------------------------------------------------------------------
    const fetchList = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getFooter();
        const raw = response?.data?.data ?? response?.data ?? [];
        setItems(Array.isArray(raw) ? raw : raw ? [raw] : []);
      } catch (err) {
        console.error("Failed to load Footer records:", err);
        setError("Failed to load Footer configuration");
        notify("Failed to load Footer configuration");
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchList();
    }, [fetchList]);

    // ---------------------------------------------------------------------
    // Map DB record -> form state
    // ---------------------------------------------------------------------
    const recordToForm = (footer) => ({
      footername: footer.footername || "",
      footerdes: footer.footerdes || "",
      socialLinks: Array.isArray(footer.socialLinks) ? footer.socialLinks : [],
      footerSections: Array.isArray(footer.footerSections)
        ? footer.footerSections.map((s) => ({ ...s, links: Array.isArray(s.links) ? s.links : [] }))
        : [],
      copyright: footer.copyright || "",
      developerDetail: footer.developerDetail || "",
      footerLink: footer.footerLink || "",
      isActive: footer.isActive ?? true,
    });

    // ---------------------------------------------------------------------
    // Open modals
    // ---------------------------------------------------------------------
    const openCreateModal = () => {
      setFormData(emptyForm);
      setEditingId(null);
      setModalMode("create");
    };

    const openEditModal = async (id) => {
      setModalMode("edit");
      setEditingId(id);
      setFormLoading(true);
      try {
        const response = await getFooterById(id);
        const footer = response?.data?.data ?? response?.data ?? {};
        setFormData(recordToForm(footer));
      } catch (err) {
        console.error("Failed to load Footer record:", err);
        notify("Failed to load record for editing");
        setModalMode(null);
      } finally {
        setFormLoading(false);
      }
    };

    const closeEditModal = () => {
      setModalMode(null);
      setEditingId(null);
      setFormData(emptyForm);
    };

    const openPreview = (record) => setPreviewRecord(record);
    const closePreview = () => setPreviewRecord(null);

    // ---------------------------------------------------------------------
    // Basic field helpers
    // ---------------------------------------------------------------------
    const setField = (field, value) =>
      setFormData((prev) => ({ ...prev, [field]: value }));

    // Social links
    const addSocialLink = () =>
      setFormData((prev) => ({
        ...prev,
        socialLinks: [...prev.socialLinks, { ...emptySocialLink, id: genId("social") }],
      }));

    const updateSocialLink = (index, field, value) =>
      setFormData((prev) => ({
        ...prev,
        socialLinks: prev.socialLinks.map((link, i) =>
          i === index ? { ...link, [field]: value } : link
        ),
      }));

    const removeSocialLink = (index) =>
      setFormData((prev) => ({
        ...prev,
        socialLinks: prev.socialLinks.filter((_, i) => i !== index),
      }));

    // Footer sections
    const addSection = () =>
      setFormData((prev) => ({
        ...prev,
        footerSections: [
          ...prev.footerSections,
          { ...emptySection, id: genId("section"), links: [] },
        ],
      }));

    const updateSectionField = (index, field, value) =>
      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.map((s, i) =>
          i === index ? { ...s, [field]: value } : s
        ),
      }));

    const removeSection = (index) =>
      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.filter((_, i) => i !== index),
      }));
    const moveSectionUp = (index) => {
      if (index === 0) return;

      setFormData((prev) => {
        const sections = [...prev.footerSections];

        [sections[index - 1], sections[index]] = [
          sections[index],
          sections[index - 1],
        ];

        return {
          ...prev,
          footerSections: sections,
        };
      });
    };

    const moveSectionDown = (index) => {
      setFormData((prev) => {
        const sections = [...prev.footerSections];

        if (index === sections.length - 1) return prev;

        [sections[index], sections[index + 1]] = [
          sections[index + 1],
          sections[index],
        ];

        return {
          ...prev,
          footerSections: sections,
        };
      });
    };
    const moveSectionLinkUp = (sectionIndex, linkIndex) => {
      if (linkIndex === 0) return;

      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.map((section, sIndex) => {
          if (sIndex !== sectionIndex) return section;

          const links = [...section.links];

          [links[linkIndex - 1], links[linkIndex]] = [
            links[linkIndex],
            links[linkIndex - 1],
          ];

          return {
            ...section,
            links,
          };
        }),
      }));
    };

    const moveSectionLinkDown = (sectionIndex, linkIndex) => {
      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.map((section, sIndex) => {
          if (sIndex !== sectionIndex) return section;

          const links = [...section.links];

          if (linkIndex === links.length - 1) {
            return section;
          }

          [links[linkIndex], links[linkIndex + 1]] = [
            links[linkIndex + 1],
            links[linkIndex],
          ];

          return {
            ...section,
            links,
          };
        }),
      }));
    };
    const addSectionLink = (sectionIndex) =>
      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.map((s, i) =>
          i === sectionIndex
            ? { ...s, links: [...s.links, { ...emptyFooterLink, id: genId("link") }] }
            : s
        ),
      }));

    const updateSectionLink = (sectionIndex, linkIndex, field, value) =>
      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.map((s, i) =>
          i === sectionIndex
            ? {
              ...s,
              links: s.links.map((l, j) => (j === linkIndex ? { ...l, [field]: value } : l)),
            }
            : s
        ),
      }));

    const removeSectionLink = (sectionIndex, linkIndex) =>
      setFormData((prev) => ({
        ...prev,
        footerSections: prev.footerSections.map((s, i) =>
          i === sectionIndex ? { ...s, links: s.links.filter((_, j) => j !== linkIndex) } : s
        ),
      }));

    // ---------------------------------------------------------------------
    // Save (create / update)
    // ---------------------------------------------------------------------
    const handleSave = async () => {
      if (saving) return; // prevent duplicate submissions

      if (!formData.footername.trim()) {
        notify("Footer name is required");
        return;
      }

      setSaving(true);
      try {
        const payload = {
          footername: formData.footername,
          footerdes: formData.footerdes,
          socialLinks: formData.socialLinks,
          footerSections: formData.footerSections,
          copyright: formData.copyright,
          developerDetail: formData.developerDetail,
          footerLink: formData.footerLink,
          isActive: formData.isActive,
        };

        if (modalMode === "edit") {
          await updateFooter(editingId, payload);
          notify("Footer updated");
        } else {
          await createFooter(payload);
          notify("Footer created");
        }

        closeEditModal();
        await fetchList();
      } catch (err) {
        console.error("Failed to save Footer:", err);
        notify(err?.response?.data?.message || "Failed to save Footer");
      } finally {
        setSaving(false);
      }
    };

    // ---------------------------------------------------------------------
    // Delete
    // ---------------------------------------------------------------------
    const handleDelete = async (id) => {
      if (!window.confirm("Delete this Footer configuration? This action cannot be undone.")) {
        return;
      }
      setDeletingId(id);
      try {
        await deleteFooter(id);
        notify("Footer deleted");
        await fetchList();
      } catch (err) {
        console.error("Failed to delete Footer:", err);
        notify("Failed to delete Footer");
      } finally {
        setDeletingId(null);
      }
    };

    // ---------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            Footer Manager
          </h1>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 transition-colors text-white px-4 py-2 rounded-lg font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Footer
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading configuration…
          </div>
        ) : error ? (
          <div className="text-center py-24 text-red-400 border border-dashed border-red-900 rounded-xl">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
            No Footer configurations yet. Click "Add Footer" to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col"
              >
                <div className="p-5 flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-lg truncate">{item.footername}</p>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${item.isActive
                        ? "bg-emerald-500/90 text-black"
                        : "bg-zinc-600 text-zinc-200"
                        }`}
                    >
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-400 line-clamp-2">{item.footerdes}</p>
                  <p className="text-xs text-zinc-500">
                    {(item.socialLinks || []).length} social link(s) ·{" "}
                    {(item.footerSections || []).length} section(s)
                  </p>
                </div>

                <div className="flex border-t border-zinc-800">
                  <button
                    onClick={() => openPreview(item)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-indigo-400 hover:bg-indigo-500/10 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Preview
                  </button>
                  <button
                    onClick={() => openEditModal(item.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors border-l border-zinc-800"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-l border-zinc-800 disabled:opacity-50"
                  >
                    {deletingId === item.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {modalMode && (
          <FooterEditModal
            mode={modalMode}
            formData={formData}
            formLoading={formLoading}
            saving={saving}
            setField={setField}
            addSocialLink={addSocialLink}
            updateSocialLink={updateSocialLink}
            removeSocialLink={removeSocialLink}
            addSection={addSection}
            updateSectionField={updateSectionField}
            removeSection={removeSection}
            addSectionLink={addSectionLink}
            updateSectionLink={updateSectionLink}
            removeSectionLink={removeSectionLink}
            moveSectionUp={moveSectionUp}
            moveSectionDown={moveSectionDown}
            moveSectionLinkUp={moveSectionLinkUp}
            moveSectionLinkDown={moveSectionLinkDown}
            onSave={handleSave}
            onClose={closeEditModal}
          />
        )}

        {previewRecord && (
          <FooterPreviewModal record={previewRecord} onClose={closePreview} />
        )}
      </div>
    );
  }

  // ===========================================================================
  // Edit / Create modal
  // ===========================================================================
  function FooterEditModal({
    mode,
    formData,
    formLoading,
    saving,
    setField,
    addSocialLink,
    updateSocialLink,
    removeSocialLink,
    addSection,
    updateSectionField,
    removeSection,
    addSectionLink,
    updateSectionLink,
    removeSectionLink,
    onSave,
    onClose,
    moveSectionUp,
    moveSectionDown,
    moveSectionLinkUp,
    moveSectionLinkDown,
  }) {
    const [openSocial, setOpenSocial] = useState({});
    const [openSections, setOpenSections] = useState({});
    const [iconSearch, setIconSearch] = useState({});
    const [iconSuggestions, setIconSuggestions] = useState(null);
    const toggleSocial = (i) => setOpenSocial((prev) => ({ ...prev, [i]: !prev[i] }));
    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget && !saving) onClose();
    };
    const toggleSection = (index) => {
      setOpenSections((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    };
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleOverlayClick}
      >
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-[1000px] max-h-[90vh] flex flex-col overflow-hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
            <h2 className="text-lg font-semibold">
              {mode === "edit" ? "Edit Footer" : "Add Footer"}
            </h2>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {formLoading ? (
              <div className="flex items-center justify-center py-24 text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading record…
              </div>
            ) : (
              <>
                <Section title="Basic Details">
                  <Field label="Footer Name">
                    <input
                      type="text"
                      value={formData.footername}
                      onChange={(e) => setField("footername", e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Footer Description">
                    <textarea
                      rows={3}
                      value={formData.footerdes}
                      onChange={(e) => setField("footerdes", e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Developer Detail">
                    <input
                      type="text"
                      value={formData.developerDetail}
                      onChange={(e) => setField("developerDetail", e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Copyright">
                    <input
                      type="text"
                      value={formData.copyright}
                      onChange={(e) => setField("copyright", e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Footer Link">
                    <input
                      type="text"
                      value={formData.footerLink}
                      onChange={(e) => setField("footerLink", e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <ToggleRow
                    label="Active"
                    checked={formData.isActive}
                    onChange={(v) => setField("isActive", v)}
                  />
                </Section>

                <Section
                  title="Social Links"
                  action={
                    <button
                      onClick={addSocialLink}
                      className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Social Link
                    </button>
                  }
                >
                  {formData.socialLinks.length === 0 ? (
                    <p className="text-sm text-zinc-500">No social links yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.socialLinks.map((social, index) => {
                        const Icon = icons[social.icon];
                        const isOpen = !!openSocial[index];
                        return (
                          <div
                            key={social.id || index}
                            className="bg-zinc-800/60 border border-zinc-700 rounded-lg overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() => toggleSocial(index)}
                              className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span
                                  className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-zinc-900 border border-zinc-700"
                                  style={{ color: social.iconColor || "#fff" }}
                                >
                                  {Icon ? <Icon className="w-4 h-4" /> : (
                                    <span className="text-[9px] text-zinc-500">N/A</span>
                                  )}
                                </span>
                                <span className="text-sm font-medium truncate">
                                  {social.title || "Untitled social link"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span
                                  className="text-red-400 hover:text-red-300 p-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSocialLink(index);
                                  }}
                                  role="button"
                                  aria-label="Delete social link"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </span>
                                {isOpen ? (
                                  <ChevronUp className="w-4 h-4 text-zinc-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-zinc-400" />
                                )}
                              </div>
                            </button>

                            {isOpen && (
                              <div className="px-3 pb-3 pt-1 border-t border-zinc-700 space-y-2">
                                {!Icon && social.icon && (
                                  <p className="text-xs text-amber-400">Icon not found</p>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                  <LabeledInput
                                    label="ID"
                                    value={social.id}
                                    onChange={(v) => updateSocialLink(index, "id", v)}
                                  />
                                  <LabeledInput
                                    label="Title"
                                    value={social.title}
                                    onChange={(v) => updateSocialLink(index, "title", v)}
                                  />
                                  <LabeledInput
                                    label="Description"
                                    value={social.description}
                                    onChange={(v) => updateSocialLink(index, "description", v)}
                                    fullWidth
                                  />
                                  <LabeledInput
                                    label="Action Text"
                                    value={social.actionText}
                                    onChange={(v) => updateSocialLink(index, "actionText", v)}
                                  />
                                  <LabeledInput
                                    label="Action Color"
                                    value={social.actionColor}
                                    onChange={(v) => updateSocialLink(index, "actionColor", v)}
                                  />
                                  <LabeledInput
                                    label="Href"
                                    value={social.href}
                                    onChange={(v) => updateSocialLink(index, "href", v)}
                                    fullWidth
                                  />
                                  <LabeledInput
                                    label="Text"
                                    value={social.text}
                                    onChange={(v) => updateSocialLink(index, "text", v)}
                                  />
                                  <div className="relative">
                                    <label className="block text-xs font-medium text-zinc-400 mb-1">
                                      Icon
                                    </label>

                                    <div className="relative">
                                      <input
                                        type="text"
                                        value={social.icon || ""}
                                        placeholder="Search icon..."
                                        onFocus={() => {
                                          setIconSuggestions(index);
                                          setIconSearch((prev) => ({
                                            ...prev,
                                            [index]: social.icon || "",
                                          }));
                                        }}
                                        onChange={(e) => {
                                          const value = e.target.value;

                                          updateSocialLink(index, "icon", value);

                                          setIconSearch((prev) => ({
                                            ...prev,
                                            [index]: value,
                                          }));

                                          setIconSuggestions(index);
                                        }}
                                        className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 pr-10 text-sm text-white outline-none focus:border-indigo-500"
                                      />

                                      {/* Selected icon preview */}
                                      {social.icon && icons[social.icon] && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                          {React.createElement(icons[social.icon], {
                                            size: 18,
                                            color: social.iconColor || "#ffffff",
                                          })}
                                        </div>
                                      )}
                                    </div>

                                    {/* Icon suggestions */}
                                    {iconSuggestions === index && (
                                      <div className="absolute left-0 right-0 top-full z-[99999] mt-1 max-h-60 overflow-y-auto rounded-lg border border-zinc-700 bg-[#111827] shadow-2xl">
                                        {Object.keys(icons)
                                          .filter((key) =>
                                            key
                                              .toLowerCase()
                                              .includes((iconSearch[index] || "").toLowerCase())
                                          )
                                          .slice(0, 20)
                                          .map((key) => {
                                            const Icon = icons[key];

                                            if (!Icon) return null;

                                            return (
                                              <button
                                                key={key}
                                                type="button"
                                                onMouseDown={(e) => {
                                                  e.preventDefault();

                                                  updateSocialLink(index, "icon", key);

                                                  setIconSearch((prev) => ({
                                                    ...prev,
                                                    [index]: key,
                                                  }));

                                                  setIconSuggestions(null);
                                                }}
                                                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white"
                                              >
                                                <span
                                                  className="flex h-7 w-7 items-center justify-center rounded-md bg-zinc-800"
                                                  style={{
                                                    color: social.iconColor || "#ffffff",
                                                  }}
                                                >
                                                  <Icon size={18} />
                                                </span>

                                                <span>{key}</span>
                                              </button>
                                            );
                                          })}

                                        {Object.keys(icons).filter((key) =>
                                          key
                                            .toLowerCase()
                                            .includes((iconSearch[index] || "").toLowerCase())
                                        ).length === 0 && (
                                            <div className="px-3 py-3 text-xs text-zinc-500">
                                              No icons found
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-zinc-400 mb-1">
                                      Icon Color
                                    </label>

                                    <div className="flex items-center gap-2">
                                      {/* Color Picker */}
                                      <input
                                        type="color"
                                        value={social.iconColor || "#ffffff"}
                                        onChange={(e) =>
                                          updateSocialLink(index, "iconColor", e.target.value)
                                        }
                                        className="w-10 h-10 p-1 rounded-lg border border-zinc-700 bg-zinc-900 cursor-pointer"
                                        title="Choose icon color"
                                      />

                                      {/* Hex Value */}
                                      <input
                                        type="text"
                                        value={social.iconColor || "#ffffff"}
                                        onChange={(e) =>
                                          updateSocialLink(index, "iconColor", e.target.value)
                                        }
                                        placeholder="#ffffff"
                                        className="flex-1 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white outline-none focus:border-indigo-500"
                                      />
                                    </div>

                                    {/* Live Preview */}
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-xs text-zinc-500">Preview:</span>

                                      {social.icon && icons[social.icon] && (
                                        <>
                                          {React.createElement(icons[social.icon], {
                                            size: 20,
                                            color: social.iconColor || "#ffffff",
                                          })}

                                          <span className="text-xs text-zinc-400">
                                            {social.iconColor || "#ffffff"}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <LabeledInput
                                    label="Box Class"
                                    value={social.boxClass}
                                    onChange={(v) => updateSocialLink(index, "boxClass", v)}
                                  />
                                </div>
                                <ToggleRow
                                  label="Show Icon"
                                  checked={!!social.showIcon}
                                  onChange={(v) => updateSocialLink(index, "showIcon", v)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Section>

                <Section
                  title="Footer Sections"
                  action={
                    <button
                      onClick={addSection}
                      className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Section
                    </button>
                  }
                >
                  {formData.footerSections.length === 0 ? (
                    <p className="text-sm text-zinc-500">No footer sections yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.footerSections.map((section, sIndex) => {
                        const isOpen = !!openSections[sIndex];
                        return (
                          <div
                            key={section.id || sIndex}
                            className="bg-zinc-800/60 border border-zinc-700 rounded-lg overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() => toggleSection(sIndex)}
                              className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                            >
                              <span className="text-sm font-medium truncate">
                                {section.heading || "Untitled section"}
                              </span>
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  type="button"
                                  disabled={sIndex === 0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveSectionUp(sIndex);
                                  }}
                                  className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                                  aria-label="Move section up"
                                >
                                  ↑
                                </button>

                                {/* Move Down */}
                                <button
                                  type="button"
                                  disabled={sIndex === formData.footerSections.length - 1}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveSectionDown(sIndex);
                                  }}
                                  className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                                  aria-label="Move section down"
                                >
                                  ↓
                                </button>
                                <span
                                  className="text-red-400 hover:text-red-300 p-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSection(sIndex);
                                  }}
                                  role="button"
                                  aria-label="Delete section"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </span>
                                {isOpen ? (
                                  <ChevronUp className="w-4 h-4 text-zinc-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-zinc-400" />
                                )}
                              </div>
                            </button>

                            {isOpen && (
                              <div className="px-3 pb-3 pt-1 border-t border-zinc-700 space-y-3">
                                <LabeledInput
                                  label="Heading"
                                  value={section.heading}
                                  onChange={(v) => updateSectionField(sIndex, "heading", v)}
                                  fullWidth
                                />

                                <div className="flex items-center justify-between pt-1">
                                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                                    Links
                                  </p>
                                  <button
                                    onClick={() => addSectionLink(sIndex)}
                                    className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Add Link
                                  </button>
                                </div>

                                {section.links.length === 0 ? (
                                  <p className="text-xs text-zinc-500">No links yet.</p>
                                ) : (
                                  <div className="space-y-2">
                                    {section.links.map((link, lIndex) => (
                                      <div
                                        key={lIndex}
                                        className="grid grid-cols-2 gap-2 bg-zinc-900 border border-zinc-700 rounded-md p-2.5"
                                      >
                                        <LabeledInput
                                          label="ID"
                                          value={link.id}
                                          onChange={(v) =>
                                            updateSectionLink(sIndex, lIndex, "id", v)
                                          }
                                          sm
                                        />
                                        <LabeledInput
                                          label="Label"
                                          value={link.label}
                                          onChange={(v) =>
                                            updateSectionLink(sIndex, lIndex, "label", v)
                                          }
                                          sm
                                        />
                                        <div>
                                          <label className="block text-[11px] text-zinc-500 mb-1">
                                            Type
                                          </label>
                                          <select
                                            value={link.type}
                                            onChange={(e) =>
                                              updateSectionLink(
                                                sIndex,
                                                lIndex,
                                                "type",
                                                e.target.value
                                              )
                                            }
                                            className={inputClassSm}
                                          >
                                            <option value="scroll">scroll</option>
                                            <option value="projects">projects</option>
                                            <option value="route">route</option>
                                            <option value="filter">filter</option>
                                          </select>
                                        </div>
                                        <LabeledInput
                                          label="Target"
                                          value={link.target}
                                          onChange={(v) =>
                                            updateSectionLink(sIndex, lIndex, "target", v)
                                          }
                                          sm
                                        />
                                        <LabeledInput
                                          label="Path"
                                          value={link.path}
                                          onChange={(v) =>
                                            updateSectionLink(sIndex, lIndex, "path", v)
                                          }
                                          sm
                                          fullWidth
                                        />
                                        <LabeledInput
                                          label="Filter"
                                          value={link.filter || ""}
                                          onChange={(v) =>
                                            updateSectionLink(sIndex, lIndex, "filter", v)
                                          }
                                          placeholder="project / publication / community"
                                          sm
                                          fullWidth
                                        />
                                        <div className="col-span-2 flex items-center justify-between pt-1">
                                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                                            <input
                                              type="checkbox"
                                              checked={!!link.showFooter}
                                              onChange={(e) =>
                                                updateSectionLink(
                                                  sIndex,
                                                  lIndex,
                                                  "showFooter",
                                                  e.target.checked
                                                )
                                              }
                                            />
                                            Show in Footer
                                          </label>
                                          <button
                                            type="button"
                                            disabled={lIndex === 0}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveSectionLinkUp(sIndex, lIndex);
                                            }}
                                            className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Move link up"
                                          >
                                            ↑
                                          </button>

                                          {/* Move Link Down */}
                                          <button
                                            type="button"
                                            disabled={lIndex === section.links.length - 1}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveSectionLinkDown(sIndex, lIndex);
                                            }}
                                            className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Move link down"
                                          >
                                            ↓
                                          </button>
                                          <button
                                            onClick={() => removeSectionLink(sIndex, lIndex)}
                                            className="text-red-400 hover:text-red-300 flex items-center gap-1 text-xs"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Section>
              </>
            )}
          </div>

          <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-6 py-4 border-t border-zinc-800 bg-zinc-900">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || formLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // Preview modal — read only, mirrors the public Footer design
  // ===========================================================================
  function FooterPreviewModal({ record, onClose }) {
    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget) onClose();
    };

    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleOverlayClick}
      >
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-[1000px] max-h-[90vh] flex flex-col overflow-hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Eye className="w-4 h-4 text-indigo-400" />
              Footer Preview
            </h2>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <PublicFooterPreview record={record} />
          </div>
        </div>
      </div>
    );
  }

  // Reconstruction of the public Footer's visual structure for preview
  // purposes only. Replace this markup with your actual public Footer
  // component's JSX/classes to make Preview pixel-identical — the data
  // filtering/lookup logic below is what matters and is already correct.
  function PublicFooterPreview({ record }) {
    const visibleSocialLinks = (record.socialLinks || []).filter((l) => l.showIcon === true);
    const sections = record.footerSections || [];

    return (
      <footer className="bg-zinc-950 border border-zinc-800 rounded-xl p-8 text-zinc-300">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{record.footername}</h3>
            <p className="text-sm text-zinc-400">{record.footerdes}</p>

            {visibleSocialLinks.length > 0 && (
              <div className="flex gap-3 mt-4">
                {visibleSocialLinks.map((link) => {
                  const Icon = icons[link.icon];
                  return (
                    <a
                      key={link.id}
                      href={link.href}
                      title={link.title}
                      className={`w-9 h-9 rounded-full flex items-center justify-center border border-zinc-700 pointer-events-none ${link.boxClass || ""
                        }`}
                      style={{ color: link.iconColor || "#fff" }}
                    >
                      {Icon ? (
                        <Icon className="w-4 h-4" />
                      ) : (
                        <span className="text-[9px] text-zinc-500">N/A</span>
                      )}
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {sections.map((section) => {
            const visibleLinks = (section.links || []).filter((l) => l.showFooter === true);
            if (visibleLinks.length === 0) return null;
            return (
              <div key={section.id}>
                <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-3">
                  {section.heading}
                </h4>
                <ul className="space-y-2">
                  {visibleLinks.map((link) => (
                    <li key={link.id}>
                      <span className="text-sm text-zinc-400">{link.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="border-t border-zinc-800 mt-8 pt-4 flex flex-col md:flex-row justify-between gap-2 text-xs text-zinc-500">
          <p>{record.copyright}</p>
          <p>{record.developerDetail}</p>
          {record.footerLink && (
            <span className="text-indigo-400">{record.footerLink}</span>
          )}
        </div>
      </footer>
    );
  }

  const emptyHeaderForm = {
    label: "",
    type: "scroll", // "scroll" | "projects" | "route"

    target: "",
    path: "",
    filter: "",

    // Desktop / normal navbar
    icon: "",
    iconColor: "#6366F1",

    // Hierarchy
    parentId: "",
    hasDropdown: false,

    // Normal navbar ordering
    sortOrder: 0,

    // Normal navbar visibility
    show: true,

    // Global active state
    isActive: true,

    // ============================================================
    // MOBILE / TABLET BOTTOM NAVIGATION
    // ============================================================
    showInBottom: false,

    // Name shown below bottom icon.
    // If empty, normal label is used.
    bottomLabel: "",

    // Icon used in bottom navigation.
    // If empty, normal icon is used.
    bottomIcon: "",

    // Color of bottom icon
    bottomIconColor: "#FFFFFF",

    // Position in bottom navigation
    bottomOrder: 0,

    // Raised center button
    bottomFeatured: false,
  };

  // Same resolver contract the public Navbar uses.
  function resolveIcon(iconName) {
    if (!iconName) return null;
    return icons[iconName] || null;
  }

  // Reference implementation of the public filter-dispatch handler, provided
  // for the real Navbar to import/reuse. Not wired to clicks inside the
  // admin Preview modal, which stays inert (it's a representation, not the
  // live site).
  function handlePortfolioNavigation(e, button) {
    const filter = button?.filter;
    if (!filter) return;

    e?.preventDefault?.();
    document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });

    window.dispatchEvent(new CustomEvent("portfolioFilterChange", { detail: { filter } }));
    window.dispatchEvent(new CustomEvent("navbarProjectsClick", { detail: { section: filter } }));
  }

  function buildHeaderHierarchy(items) {
    const roots = items
      .filter((i) => i.parentId === null || i.parentId === undefined)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    return roots.map((root) => ({
      ...root,
      children: items
        .filter((i) => i.parentId === root.id)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    }));
  }

  function validateHeaderForm(formData) {
    if (!formData.label?.trim() && !formData.icon?.trim()) {
      return "Please provide a label or select an icon";
    }
    if (!formData.type) return "Type is required";

    if ((formData.type === "scroll" || formData.type === "projects") && !formData.target.trim()) {
      return "Target is required for this type";
    }
    if (formData.type === "route" && !formData.path.trim()) {
      return "Path is required for route type";
    }
    if (formData.type === "projects" && !formData.filter) {
      return "Filter is required for projects type";
    }
    if (
      formData.parentId !== "" &&
      formData.parentId !== null &&
      Number.isNaN(Number(formData.parentId))
    ) {
      return "Parent must be numeric or empty";
    }
    if (Number.isNaN(Number(formData.sortOrder))) {
      return "Sort Order must be a number";
    }
    return null;
  }

  const headerRecordToForm = (item) => ({
    label: item.label || "",

    type: item.type || "scroll",

    target: item.target || "",
    path: item.path || "",
    filter: item.filter || "",

    icon: item.icon || "",
    iconColor:
      item.iconColor || "#6366F1",

    parentId:
      item.parentId === null ||
        item.parentId === undefined
        ? ""
        : item.parentId,

    hasDropdown:
      item.hasDropdown ?? false,

    sortOrder:
      item.sortOrder ?? 0,

    show:
      item.show ?? true,

    isActive:
      item.isActive ?? true,

    // ============================================================
    // BOTTOM NAVIGATION
    // ============================================================
    showInBottom:
      item.showInBottom ?? false,

    bottomLabel:
      item.bottomLabel || "",

    bottomIcon:
      item.bottomIcon || "",

    bottomIconColor:
      item.bottomIconColor ||
      item.iconColor ||
      "#FFFFFF",

    bottomOrder:
      item.bottomOrder ?? 0,

    bottomFeatured:
      item.bottomFeatured ?? false,
  });

  function HeaderAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [modalMode, setModalMode] = useState(null); // null | "add" | "edit"
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(emptyHeaderForm);
    const [saving, setSaving] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewTree, setPreviewTree] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    // -----------------------------------------------------------------------
    // Load flat list — source of truth for the table + parent dropdown
    // -----------------------------------------------------------------------
    const fetchItems = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getHeader();
        const raw = response?.data?.data ?? response?.data ?? [];
        setItems(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error("Failed to load header items:", err);
        setError("Failed to load header items");
        notify("Failed to load header items");
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchItems();
    }, [fetchItems]);

    const parentOptions = useMemo(
      () => items.filter((i) => i.parentId === null || i.parentId === undefined),
      [items]
    );

    const tree = useMemo(() => buildHeaderHierarchy(items), [items]);

    const hasChildren = (id) => items.some((i) => i.parentId === id);

    // -----------------------------------------------------------------------
    // Modal helpers
    // -----------------------------------------------------------------------
    const openAddModal = () => {
      setFormData(emptyHeaderForm);
      setEditingItem(null);
      setModalMode("add");
    };

    const openEditModal = (item) => {
      setFormData(headerRecordToForm(item));
      setEditingItem(item);
      setModalMode("edit");
    };

    const closeModal = () => {
      setModalMode(null);
      setEditingItem(null);
      setFormData(emptyHeaderForm);
    };

    // -----------------------------------------------------------------------
    // Save
    // -----------------------------------------------------------------------
    const handleSave = async () => {
      if (saving) return;

      const validationError = validateHeaderForm(formData);
      if (validationError) {
        notify(validationError);
        return;
      }

      setSaving(true);
      try {
        const payload = {
          label: formData.label,
          type: formData.type,
          target: formData.type !== "route" ? formData.target : "",
          path: formData.type === "route" ? formData.path : "",
          filter: formData.type === "projects" ? formData.filter : "",
          icon: formData.icon,
          iconColor: formData.iconColor,
          parentId: formData.parentId === "" ? null : Number(formData.parentId),
          hasDropdown: formData.hasDropdown,
          sortOrder: Number(formData.sortOrder),
          show: formData.show,
          isActive: formData.isActive,

          showInBottom: Boolean(formData.showInBottom),
          bottomLabel: formData.bottomLabel?.trim() || formData.label,
          bottomIcon: formData.bottomIcon?.trim() || formData.icon,
          bottomIconColor: formData.bottomIconColor || formData.iconColor || "#FFFFFF",
          bottomOrder: Number(formData.bottomOrder) || 0,
          bottomFeatured: Boolean(formData.bottomFeatured),
        };

        if (modalMode === "edit") {
          await updateHeader(editingItem.id, payload);
          notify("Header item updated");
        } else {
          await createHeader(payload);
          notify("Header item created");
        }

        closeModal();
        await fetchItems();
      } catch (err) {
        console.error("Failed to save header item:", err);
        notify(err?.response?.data?.message || "Failed to save header item");
      } finally {
        setSaving(false);
      }
    };

    // -----------------------------------------------------------------------
    // Delete
    // -----------------------------------------------------------------------
    const handleDelete = async () => {
      if (!deleteTarget || deleting) return;
      setDeleting(true);
      try {
        await deleteHeader(deleteTarget.id);
        notify("Header item deleted");
        setDeleteTarget(null);
        await fetchItems();
      } catch (err) {
        console.error("Failed to delete header item:", err);
        notify("Failed to delete header item");
      } finally {
        setDeleting(false);
      }
    };

    // -----------------------------------------------------------------------
    // Preview
    // -----------------------------------------------------------------------
    const openPreview = async () => {
      setPreviewOpen(true);
      setPreviewLoading(true);
      try {
        const response = await getHeaderTree();
        const raw = response?.data?.data ?? response?.data ?? [];
        setPreviewTree(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error("Failed to load header tree:", err);
        notify("Failed to load navbar preview");
      } finally {
        setPreviewLoading(false);
      }
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6">

        {/* ===========================================================
          TOOLBAR — stacks on mobile, wraps on tablet, one row on desktop
      ============================================================ */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
            <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-500 shrink-0" />
            Header Manager
          </h1>

          <div className="flex items-center gap-2">
            <button
              onClick={openPreview}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 transition-colors text-zinc-200 px-4 py-2.5 sm:py-2 rounded-lg font-medium border border-zinc-700 text-sm"
            >
              <Eye className="w-4 h-4" />
              <span className="sm:inline">Preview</span>
            </button>
            <button
              onClick={openAddModal}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 transition-colors text-white px-4 py-2.5 sm:py-2 rounded-lg font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Header</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">
            <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
            Loading header items…
          </div>
        ) : error ? (
          <div className="text-center py-16 sm:py-24 px-4 text-red-400 border border-dashed border-red-900 rounded-xl text-sm">
            {error}
          </div>
        ) : tree.length === 0 ? (
          <div className="text-center py-16 sm:py-24 px-4 text-zinc-500 border border-dashed border-zinc-800 rounded-xl text-sm">
            No header items yet. Tap "Add Header" to create one.
          </div>
        ) : (
          <>
            {/* =========================================================
              MOBILE — card list (below `lg`, ~<1024px)
              Full 16-column table doesn't fit a phone; cards surface
              the essentials and push the rest into the edit modal.
          ========================================================== */}
            <div className="lg:hidden flex flex-col gap-3">
              {tree.map((root) => (
                <HeaderCardGroup
                  key={root.id}
                  root={root}
                  onEdit={openEditModal}
                  onDelete={(item) => setDeleteTarget(item)}
                  hasChildren={hasChildren}
                />
              ))}
            </div>

            {/* =========================================================
              DESKTOP / LARGE TABLET — full table, horizontal scroll
              as a fallback rather than the primary interaction
          ========================================================== */}
            <div className="hidden lg:block bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[1000px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-800">
                    <th className="px-4 py-3">Label</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Parent</th>
                    <th className="px-4 py-3">Filter</th>
                    <th className="px-4 py-3">Icon</th>
                    <th className="px-4 py-3">Icon Color</th>
                    <th className="px-4 py-3">Dropdown</th>
                    <th className="px-4 py-3">Sort</th>
                    <th className="px-4 py-3">Show</th>
                    <th className="px-4 py-3">Active</th>
                    <th className="px-4 py-3 text-indigo-400">Bottom</th>
                    <th className="px-4 py-3 text-indigo-400">Bottom Icon</th>
                    <th className="px-4 py-3 text-indigo-400">Bottom Color</th>
                    <th className="px-4 py-3 text-indigo-400">Bottom Label</th>
                    <th className="px-4 py-3 text-indigo-400">Bottom Order</th>
                    <th className="px-4 py-3 text-indigo-400">Center</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tree.map((root) => (
                    <HeaderRowGroup
                      key={root.id}
                      root={root}
                      onEdit={openEditModal}
                      onDelete={(item) => setDeleteTarget(item)}
                      hasChildren={hasChildren}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {modalMode && (
          <HeaderFormModal
            mode={modalMode}
            formData={formData}
            setFormData={setFormData}
            parentOptions={parentOptions}
            editingId={editingItem?.id}
            saving={saving}
            onSave={handleSave}
            onClose={closeModal}
          />
        )}

        {deleteTarget && (
          <HeaderDeleteConfirmModal
            item={deleteTarget}
            hasChildren={hasChildren(deleteTarget.id)}
            deleting={deleting}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}

        {previewOpen && (
          <HeaderNavbarPreviewModal
            tree={previewTree}
            loading={previewLoading}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </div>
    );
  }

  // ===========================================================
  // MOBILE/TABLET CARD — one root item + its children, collapsed
  // to the fields that matter for a quick glance. Full detail
  // (filter, colors, bottom-nav config) is edited in the modal.
  // ===========================================================
  function HeaderCardGroup({ root, onEdit, onDelete, hasChildren }) {
    const [expanded, setExpanded] = useState(true);
    const children = root.children ?? [];
    const childless = children.length === 0;

    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <HeaderCard
          item={root}
          onEdit={onEdit}
          onDelete={onDelete}
          canExpand={!childless}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
        />

        {!childless && expanded && (
          <div className="border-t border-zinc-800 divide-y divide-zinc-800/60 bg-zinc-950/40">
            {children.map((child) => (
              <HeaderCard
                key={child.id}
                item={child}
                onEdit={onEdit}
                onDelete={onDelete}
                nested
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  function HeaderCard({ item, onEdit, onDelete, nested, canExpand, expanded, onToggle }) {
    return (
      <div className={cx("flex items-start gap-3 p-4", nested && "pl-8")}>
        <button
          type="button"
          onClick={canExpand ? onToggle : undefined}
          className={cx(
            "mt-0.5 shrink-0",
            canExpand ? "text-zinc-500" : "text-zinc-700 pointer-events-none"
          )}
        >
          {canExpand ? (
            expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          ) : (
            <span className="block w-4 h-4" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-zinc-100 truncate">{item.label}</span>
            <span className="text-[11px] uppercase tracking-wide bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">
              {item.type}
            </span>
            {!item.isActive && (
              <span className="text-[11px] uppercase tracking-wide bg-red-900/40 text-red-400 px-1.5 py-0.5 rounded">
                Inactive
              </span>
            )}
            {!item.show && (
              <span className="text-[11px] uppercase tracking-wide bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded">
                Hidden
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              Sort {item.sortOrder}
            </span>
            {item.showInBottom && (
              <span className="flex items-center gap-1 text-indigo-400">
                <Smartphone className="w-3 h-3" />
                Bottom nav
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onEdit(item)}
            className="p-2 rounded-lg text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800 transition-colors"
            aria-label="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-zinc-800 transition-colors"
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }
  function HeaderRowGroup({ root, onEdit, onDelete, hasChildren }) {
    const [expanded, setExpanded] = useState(true);
    const children = root.children || [];

    return (
      <>
        <HeaderRow
          item={root}
          parentLabel={null}
          indent={false}
          canExpand={children.length > 0}
          expanded={expanded}
          onToggleExpand={() => setExpanded((e) => !e)}
          onEdit={onEdit}
          onDelete={onDelete}
          isParent={hasChildren(root.id)}
        />
        {expanded &&
          children.map((child) => (
            <HeaderRow
              key={child.id}
              item={child}
              parentLabel={root.label}
              indent
              canExpand={false}
              onEdit={onEdit}
              onDelete={onDelete}
              isParent={false}
            />
          ))}
      </>
    );
  }

  function HeaderRow({
    item,
    parentLabel,
    indent,
    canExpand,
    expanded,
    onToggleExpand,
    onEdit,
    onDelete,
    isParent,
  }) {
    // ============================================================
    // NORMAL HEADER ICON
    // ============================================================
    const Icon = resolveIcon(item.icon);

    // ============================================================
    // BOTTOM NAV ICON
    //
    // If bottomIcon is not configured,
    // fall back to normal header icon.
    // ============================================================
    const BottomIcon = resolveIcon(
      item.bottomIcon || item.icon
    );

    return (
      <tr
        className="
        border-b
        border-zinc-800/60
        hover:bg-zinc-800/30
        transition-colors
      "
      >
        {/* ======================================================
          LABEL
      ======================================================= */}
        <td className="px-4 py-3">
          <div
            className={`
            flex
            items-center
            gap-2
            ${indent ? "pl-6" : ""}
          `}
          >
            {canExpand && (
              <button
                onClick={onToggleExpand}
                className="
                text-zinc-400
                hover:text-white
                transition-colors
              "
                aria-label={
                  expanded
                    ? "Collapse"
                    : "Expand"
                }
              >
                {expanded ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            )}

            <span className="font-medium">
              {item.label}
            </span>

            {isParent &&
              item.hasDropdown && (
                <span
                  className="
                  text-[10px]
                  px-1.5
                  py-0.5
                  rounded-full
                  bg-indigo-500/20
                  text-indigo-300
                  border
                  border-indigo-500/40
                "
                >
                  parent
                </span>
              )}
          </div>
        </td>

        {/* ======================================================
          TYPE
      ======================================================= */}
        <td className="px-4 py-3 text-zinc-400">
          {item.type}
        </td>

        {/* ======================================================
          PARENT
      ======================================================= */}
        <td className="px-4 py-3 text-zinc-400">
          {parentLabel || "—"}
        </td>

        {/* ======================================================
          FILTER
      ======================================================= */}
        <td className="px-4 py-3 text-zinc-400">
          {item.filter || "—"}
        </td>

        {/* ======================================================
          NORMAL HEADER ICON
      ======================================================= */}
        <td className="px-4 py-3">
          {Icon ? (
            <Icon
              size={16}
              color={
                item.iconColor ||
                "currentColor"
              }
            />
          ) : item.icon ? (
            <span className="
            text-xs
            text-amber-400
          ">
              Icon not found
            </span>
          ) : (
            <span className="text-zinc-600">
              —
            </span>
          )}
        </td>

        {/* ======================================================
          NORMAL ICON COLOR
      ======================================================= */}
        <td className="px-4 py-3">
          <div className="
          flex
          items-center
          gap-1.5
        ">
            <span
              className="
              w-4
              h-4
              rounded-full
              border
              border-zinc-700
              inline-block
            "
              style={{
                backgroundColor:
                  item.iconColor ||
                  "#666",
              }}
            />

            <span className="
            text-xs
            text-zinc-400
          ">
              {item.iconColor || "—"}
            </span>
          </div>
        </td>

        {/* ======================================================
          HAS DROPDOWN
      ======================================================= */}
        <td className="px-4 py-3">
          <HeaderStatusBadge
            value={item.hasDropdown}
          />
        </td>

        {/* ======================================================
          SORT ORDER
      ======================================================= */}
        <td className="
        px-4
        py-3
        text-zinc-400
      ">
          {item.sortOrder ?? 0}
        </td>

        {/* ======================================================
          SHOW IN NAVBAR
      ======================================================= */}
        <td className="px-4 py-3">
          <HeaderStatusBadge
            value={item.show}
          />
        </td>

        {/* ======================================================
          ACTIVE
      ======================================================= */}
        <td className="px-4 py-3">
          <HeaderStatusBadge
            value={item.isActive}
          />
        </td>

        {/* ======================================================
          BOTTOM NAV - SHOW
      ======================================================= */}
        <td className="px-4 py-3">
          <HeaderStatusBadge
            value={
              item.showInBottom
            }
          />
        </td>

        {/* ======================================================
          BOTTOM NAV - ICON
      ======================================================= */}
        <td className="px-4 py-3">
          {item.showInBottom ? (
            BottomIcon ? (
              <BottomIcon
                size={18}
                color={
                  item.bottomIconColor ||
                  item.iconColor ||
                  "#FFFFFF"
                }
              />
            ) : (
              <span className="
              text-xs
              text-amber-400
            ">
                Icon not found
              </span>
            )
          ) : (
            <span className="
            text-zinc-600
          ">
              —
            </span>
          )}
        </td>

        {/* ======================================================
          BOTTOM ICON COLOR
      ======================================================= */}
        <td className="px-4 py-3">
          {item.showInBottom ? (
            <div className="
            flex
            items-center
            gap-1.5
          ">
              <span
                className="
                w-4
                h-4
                rounded-full
                border
                border-zinc-700
                inline-block
              "
                style={{
                  backgroundColor:
                    item.bottomIconColor ||
                    item.iconColor ||
                    "#FFFFFF",
                }}
              />

              <span className="
              text-xs
              text-zinc-400
            ">
                {item.bottomIconColor ||
                  item.iconColor ||
                  "#FFFFFF"}
              </span>
            </div>
          ) : (
            <span className="
            text-zinc-600
          ">
              —
            </span>
          )}
        </td>

        {/* ======================================================
          BOTTOM LABEL
      ======================================================= */}
        <td className="
        px-4
        py-3
        text-zinc-400
      ">
          {item.showInBottom
            ? item.bottomLabel ||
            item.label ||
            "—"
            : "—"}
        </td>

        {/* ======================================================
          BOTTOM ORDER
      ======================================================= */}
        <td className="
        px-4
        py-3
        text-zinc-400
      ">
          {item.showInBottom
            ? item.bottomOrder ??
            "—"
            : "—"}
        </td>

        {/* ======================================================
          FEATURED / CENTER
      ======================================================= */}
        <td className="px-4 py-3">
          {item.showInBottom ? (
            item.bottomFeatured ? (
              <span className="
              inline-flex
              items-center
              gap-1

              text-[10px]
              px-2
              py-1

              rounded-full

              bg-amber-500/10
              text-amber-300

              border
              border-amber-500/30
            ">
                <span
                  className="
                  w-1.5
                  h-1.5
                  rounded-full
                  bg-amber-400
                "
                />

                Center
              </span>
            ) : (
              <span className="
              text-xs
              text-zinc-500
            ">
                Normal
              </span>
            )
          ) : (
            <span className="
            text-zinc-600
          ">
              —
            </span>
          )}
        </td>

        {/* ======================================================
          ACTIONS
      ======================================================= */}
        <td className="px-4 py-3">
          <div className="
          flex
          items-center
          justify-end
          gap-1
        ">
            {/* EDIT */}
            <button
              onClick={() =>
                onEdit(item)
              }
              className="
              p-1.5
              rounded-md
              text-zinc-300
              hover:bg-zinc-800
              hover:text-white
              transition-colors
            "
              aria-label="Edit"
            >
              <icons.Pencil className="
              w-3.5
              h-3.5
            " />
            </button>

            {/* DELETE */}
            <button
              onClick={() =>
                onDelete(item)
              }
              className="
              p-1.5
              rounded-md
              text-red-400
              hover:bg-red-500/10
              transition-colors
            "
              aria-label="Delete"
            >
              <icons.Trash2 className="
              w-3.5
              h-3.5
            " />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  function HeaderStatusBadge({ value }) {
    return (
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded-full ${value ? "bg-emerald-500/90 text-black" : "bg-zinc-700 text-zinc-300"
          }`}
      >
        {value ? "Yes" : "No"}
      </span>
    );
  }

  function HeaderFormModal({
    mode,
    formData,
    setFormData,
    parentOptions,
    editingId,
    saving,
    onSave,
    onClose,
  }) {
    const setField = (field, value) =>
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

    // ============================================================
    // NORMAL ICON SEARCH
    // ============================================================
    const [iconSearch, setIconSearch] =
      useState("");

    const [iconOpen, setIconOpen] =
      useState(false);

    // ============================================================
    // BOTTOM ICON SEARCH
    // ============================================================
    const [
      bottomIconSearch,
      setBottomIconSearch,
    ] = useState("");

    const [
      bottomIconOpen,
      setBottomIconOpen,
    ] = useState(false);

    // ============================================================
    // OVERLAY
    // ============================================================
    const handleOverlayClick = (e) => {
      if (
        e.target === e.currentTarget &&
        !saving
      ) {
        onClose();
      }
    };

    // ============================================================
    // NORMAL ICON
    // ============================================================
    useEffect(() => {
      setIconSearch(
        formData.icon || ""
      );
    }, [formData.icon]);

    // ============================================================
    // BOTTOM ICON
    // ============================================================
    useEffect(() => {
      setBottomIconSearch(
        formData.bottomIcon ||
        formData.icon ||
        ""
      );
    }, [
      formData.bottomIcon,
      formData.icon,
    ]);

    const availableParents =
      parentOptions.filter(
        (p) => p.id !== editingId
      );

    const SelectedIcon =
      resolveIcon(formData.icon);

    const SelectedBottomIcon =
      resolveIcon(
        formData.bottomIcon ||
        formData.icon
      );

    // ============================================================
    // NORMAL ICONS
    // ============================================================
    const filteredIcons =
      Object.keys(icons)
        .filter((name) =>
          name
            .toLowerCase()
            .includes(
              iconSearch.toLowerCase()
            )
        )
        .slice(0, 20);

    // ============================================================
    // BOTTOM ICONS
    // ============================================================
    const filteredBottomIcons =
      Object.keys(icons)
        .filter((name) =>
          name
            .toLowerCase()
            .includes(
              bottomIconSearch.toLowerCase()
            )
        )
        .slice(0, 20);

    return (
      <div
        className="
        fixed inset-0 z-50
        flex items-center justify-center
        p-4
        bg-black/70
        backdrop-blur-sm
      "
        onClick={handleOverlayClick}
      >
        <div
          className="
          bg-zinc-900
          border border-zinc-800
          rounded-2xl
          w-full max-w-2xl
          max-h-[90vh]
          flex flex-col
          overflow-hidden
        "
        >

          {/* HEADER */}
          <div
            className="
            sticky top-0 z-10
            flex items-center justify-between
            px-6 py-4
            border-b border-zinc-800
            bg-zinc-900
          "
          >
            <h2 className="text-lg font-semibold">
              {mode === "edit"
                ? "Edit Header Item"
                : "Add Header Item"}
            </h2>

            <button
              onClick={onClose}
              disabled={saving}
              className="
              text-zinc-400
              hover:text-white
              p-1 rounded-lg
              hover:bg-zinc-800
              transition-colors
            "
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* CONTENT */}
          <div
            className="
            flex-1
            overflow-y-auto
            p-6
            space-y-6
          "
          >

            {/* LABEL */}
            <Field label="Label">
              <input
                type="text"
                value={
                  formData.label || ""
                }
                onChange={(e) =>
                  setField(
                    "label",
                    e.target.value
                  )
                }
                className={inputClass}
              />
            </Field>

            {/* TYPE */}
            <Field label="Type">
              <select
                value={
                  formData.type ||
                  "scroll"
                }
                onChange={(e) =>
                  setField(
                    "type",
                    e.target.value
                  )
                }
                className={inputClass}
              >
                <option value="scroll">
                  scroll
                </option>

                <option value="projects">
                  projects
                </option>

                <option value="route">
                  route
                </option>
              </select>
            </Field>

            {/* TARGET */}
            {(
              formData.type ===
              "scroll" ||
              formData.type ===
              "projects"
            ) && (
                <Field label="Target">
                  <input
                    type="text"
                    value={
                      formData.target ||
                      ""
                    }
                    onChange={(e) =>
                      setField(
                        "target",
                        e.target.value
                      )
                    }
                    placeholder="e.g. projects"
                    className={inputClass}
                  />
                </Field>
              )}

            {/* FILTER */}
            {formData.type ===
              "projects" && (
                <Field label="Filter">
                  <select
                    value={
                      formData.filter ||
                      ""
                    }
                    onChange={(e) =>
                      setField(
                        "filter",
                        e.target.value
                      )
                    }
                    className={inputClass}
                  >
                    <option value="">
                      Select filter…
                    </option>

                    <option value="project">
                      project
                    </option>

                    <option value="publication">
                      publication
                    </option>

                    <option value="community">
                      community
                    </option>
                    <option value="services">services</option>
                  </select>
                </Field>
              )}

            {/* PATH */}
            {formData.type ===
              "route" && (
                <Field label="Path">
                  <input
                    type="text"
                    value={
                      formData.path || ""
                    }
                    onChange={(e) =>
                      setField(
                        "path",
                        e.target.value
                      )
                    }
                    placeholder="e.g. /blog"
                    className={inputClass}
                  />
                </Field>
              )}

            {/* NORMAL ICON */}
            <Field label="Icon">
              <div className="relative">
                <input
                  type="text"
                  value={iconSearch}
                  placeholder="Search icon…"
                  onFocus={() => {
                    setIconSearch(
                      formData.icon ||
                      ""
                    );

                    setIconOpen(true);
                  }}
                  onChange={(e) => {
                    const value =
                      e.target.value;

                    setIconSearch(
                      value
                    );

                    setField(
                      "icon",
                      value
                    );

                    setIconOpen(true);
                  }}
                  className="
                  w-full
                  rounded-lg
                  border border-zinc-700
                  bg-zinc-800
                  px-3 py-2 pr-10
                  text-sm text-white
                  outline-none
                  focus:border-indigo-500
                "
                />

                {SelectedIcon && (
                  <div
                    className="
                    absolute
                    right-3
                    top-1/2
                    -translate-y-1/2
                  "
                  >
                    <SelectedIcon
                      size={18}
                      color={
                        formData.iconColor ||
                        "currentColor"
                      }
                    />
                  </div>
                )}

                {iconOpen &&
                  iconSearch.trim() && (
                    <div
                      className="
                      absolute z-[100]
                      left-0 right-0
                      top-full mt-1
                      max-h-60
                      overflow-y-auto
                      rounded-lg
                      border border-zinc-700
                      bg-zinc-950
                      shadow-xl
                    "
                    >
                      {filteredIcons.length ===
                        0 ? (
                        <div className="
                        px-3 py-3
                        text-xs
                        text-zinc-500
                      ">
                          No icons found
                        </div>
                      ) : (
                        filteredIcons.map(
                          (key) => {
                            const IconComp =
                              icons[key];

                            return (
                              <button
                                key={key}
                                type="button"
                                onMouseDown={(
                                  e
                                ) => {
                                  e.preventDefault();

                                  setField(
                                    "icon",
                                    key
                                  );

                                  setIconSearch(
                                    key
                                  );

                                  setIconOpen(
                                    false
                                  );
                                }}
                                className="
                                flex
                                w-full
                                items-center
                                gap-3
                                px-3 py-2
                                text-left
                                text-sm
                                text-gray-300
                                hover:bg-white/10
                                hover:text-white
                              "
                              >
                                <IconComp
                                  size={18}
                                  color={
                                    formData.iconColor ||
                                    "currentColor"
                                  }
                                />

                                <span>
                                  {key}
                                </span>
                              </button>
                            );
                          }
                        )
                      )}
                    </div>
                  )}
              </div>
            </Field>

            {/* NORMAL ICON COLOR */}
            <Field label="Icon Color">
              <div className="
              flex items-center gap-3
            ">
                <input
                  type="color"
                  value={
                    formData.iconColor ||
                    "#6366F1"
                  }
                  onChange={(e) =>
                    setField(
                      "iconColor",
                      e.target.value
                    )
                  }
                  className="
                  w-10 h-10
                  rounded-md
                  border border-zinc-700
                  bg-zinc-800
                  cursor-pointer
                  p-0.5
                "
                />

                <input
                  type="text"
                  value={
                    formData.iconColor ||
                    ""
                  }
                  onChange={(e) =>
                    setField(
                      "iconColor",
                      e.target.value
                    )
                  }
                  placeholder="#6366F1"
                  className={`${inputClass} font-mono`}
                />
              </div>
            </Field>

            {/* ==================================================
              BOTTOM NAVIGATION
          ================================================== */}
            <div
              className="
              rounded-2xl
              border border-indigo-500/30
              bg-indigo-500/5
              p-5
              space-y-5
            "
            >
              <div>
                <h3 className="
                text-sm
                font-semibold
                text-white
              ">
                  Bottom Navigation
                </h3>

                <p className="
                text-xs
                text-zinc-500
                mt-1
              ">
                  Configure this item for
                  mobile/tablet bottom navigation.
                </p>
              </div>

              {/* SHOW */}
              <ToggleRow
                label="Show in Bottom Navigation"
                checked={
                  Boolean(
                    formData.showInBottom
                  )
                }
                onChange={(value) =>
                  setField(
                    "showInBottom",
                    value
                  )
                }
              />

              {formData.showInBottom && (
                <div className="space-y-5">

                  {/* BOTTOM LABEL */}
                  <Field label="Bottom Label">
                    <input
                      type="text"
                      value={
                        formData.bottomLabel ||
                        ""
                      }
                      onChange={(e) =>
                        setField(
                          "bottomLabel",
                          e.target.value
                        )
                      }
                      placeholder={
                        formData.label ||
                        "Home"
                      }
                      className={inputClass}
                    />
                  </Field>

                  {/* BOTTOM ICON */}
                  <Field label="Bottom Icon">
                    <div className="relative">

                      <input
                        type="text"
                        value={
                          bottomIconSearch
                        }
                        placeholder={
                          formData.icon ||
                          "Search icon…"
                        }
                        onFocus={() => {
                          setBottomIconSearch(
                            formData.bottomIcon ||
                            formData.icon ||
                            ""
                          );

                          setBottomIconOpen(
                            true
                          );
                        }}
                        onChange={(e) => {
                          const value =
                            e.target.value;

                          setBottomIconSearch(
                            value
                          );

                          setField(
                            "bottomIcon",
                            value
                          );

                          setBottomIconOpen(
                            true
                          );
                        }}
                        className="
                        w-full
                        rounded-lg
                        border border-zinc-700
                        bg-zinc-800
                        px-3 py-2 pr-10
                        text-sm text-white
                        outline-none
                        focus:border-indigo-500
                      "
                      />

                      {SelectedBottomIcon && (
                        <div className="
                        absolute
                        right-3
                        top-1/2
                        -translate-y-1/2
                      ">
                          <SelectedBottomIcon
                            size={18}
                            color={
                              formData.bottomIconColor ||
                              "#FFFFFF"
                            }
                          />
                        </div>
                      )}

                      {bottomIconOpen &&
                        bottomIconSearch.trim() && (
                          <div
                            className="
                            absolute z-[110]
                            left-0 right-0
                            top-full mt-1
                            max-h-60
                            overflow-y-auto
                            rounded-lg
                            border border-zinc-700
                            bg-zinc-950
                            shadow-xl
                          "
                          >
                            {filteredBottomIcons.length ===
                              0 ? (
                              <div className="
                              px-3 py-3
                              text-xs
                              text-zinc-500
                            ">
                                No icons found
                              </div>
                            ) : (
                              filteredBottomIcons.map(
                                (key) => {
                                  const IconComp =
                                    icons[key];

                                  return (
                                    <button
                                      key={key}
                                      type="button"
                                      onMouseDown={(
                                        e
                                      ) => {
                                        e.preventDefault();

                                        setField(
                                          "bottomIcon",
                                          key
                                        );

                                        setBottomIconSearch(
                                          key
                                        );

                                        setBottomIconOpen(
                                          false
                                        );
                                      }}
                                      className="
                                      flex
                                      w-full
                                      items-center
                                      gap-3
                                      px-3 py-2
                                      text-left
                                      text-sm
                                      text-gray-300
                                      hover:bg-white/10
                                      hover:text-white
                                    "
                                    >
                                      <IconComp
                                        size={18}
                                        color={
                                          formData.bottomIconColor ||
                                          "#FFFFFF"
                                        }
                                      />

                                      <span>
                                        {key}
                                      </span>
                                    </button>
                                  );
                                }
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </Field>

                  {/* BOTTOM COLOR */}
                  <Field label="Bottom Icon Color">
                    <div className="
                    flex
                    items-center
                    gap-3
                  ">
                      <input
                        type="color"
                        value={
                          formData.bottomIconColor ||
                          "#FFFFFF"
                        }
                        onChange={(e) =>
                          setField(
                            "bottomIconColor",
                            e.target.value
                          )
                        }
                        className="
                        w-10 h-10
                        rounded-md
                        border border-zinc-700
                        bg-zinc-800
                        cursor-pointer
                        p-0.5
                      "
                      />

                      <input
                        type="text"
                        value={
                          formData.bottomIconColor ||
                          ""
                        }
                        onChange={(e) =>
                          setField(
                            "bottomIconColor",
                            e.target.value
                          )
                        }
                        placeholder="#FFFFFF"
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                  </Field>

                  {/* ORDER */}
                  <Field label="Bottom Order">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={
                        formData.bottomOrder ??
                        0
                      }
                      onChange={(e) =>
                        setField(
                          "bottomOrder",
                          e.target.value
                        )
                      }
                      className={inputClass}
                    />

                    <p className="
                    text-[11px]
                    text-zinc-500
                    mt-1
                  ">
                      Example: Home = 1,
                      About = 2, YouTube = 3.
                    </p>
                  </Field>

                  {/* FEATURED */}
                  <ToggleRow
                    label="Featured / Center Button"
                    checked={
                      Boolean(
                        formData.bottomFeatured
                      )
                    }
                    onChange={(value) =>
                      setField(
                        "bottomFeatured",
                        value
                      )
                    }
                  />

                  {formData.bottomFeatured && (
                    <div className="
                    rounded-lg
                    border
                    border-amber-500/20
                    bg-amber-500/5
                    px-3 py-3
                    text-xs
                    text-amber-300
                  ">
                      This item will be displayed
                      as the raised center button.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PARENT */}
            <Field label="Parent">
              <select
                value={
                  formData.parentId ?? ""
                }
                onChange={(e) =>
                  setField(
                    "parentId",
                    e.target.value
                  )
                }
                className={inputClass}
              >
                <option value="">
                  No parent (top level)
                </option>

                {availableParents.map(
                  (p) => (
                    <option
                      key={p.id}
                      value={p.id}
                    >
                      {p.label}
                    </option>
                  )
                )}
              </select>
            </Field>

            {/* DROPDOWN */}
            <ToggleRow
              label="Has Dropdown"
              checked={
                Boolean(
                  formData.hasDropdown
                )
              }
              onChange={(value) =>
                setField(
                  "hasDropdown",
                  value
                )
              }
            />

            {/* SORT */}
            <Field label="Sort Order">
              <input
                type="number"
                value={
                  formData.sortOrder ?? 0
                }
                onChange={(e) =>
                  setField(
                    "sortOrder",
                    e.target.value
                  )
                }
                className={inputClass}
              />
            </Field>

            {/* SHOW */}
            <ToggleRow
              label="Show in Navbar"
              checked={
                Boolean(formData.show)
              }
              onChange={(value) =>
                setField(
                  "show",
                  value
                )
              }
            />

            {/* ACTIVE */}
            <ToggleRow
              label="Active"
              checked={
                Boolean(
                  formData.isActive
                )
              }
              onChange={(value) =>
                setField(
                  "isActive",
                  value
                )
              }
            />
          </div>

          {/* FOOTER */}
          <div className="
          sticky bottom-0 z-10
          flex justify-end gap-3
          px-6 py-4
          border-t border-zinc-800
          bg-zinc-900
        ">
            <button
              onClick={onClose}
              disabled={saving}
              className="
              px-4 py-2
              rounded-lg
              text-zinc-300
              hover:bg-zinc-800
              transition-colors
              disabled:opacity-50
            "
            >
              Cancel
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              className="
              flex items-center gap-2
              px-4 py-2
              rounded-lg
              bg-indigo-600
              hover:bg-indigo-500
              text-white
              font-medium
              transition-colors
              disabled:opacity-50
            "
            >
              {saving && (
                <Loader2 className="
                w-4 h-4
                animate-spin
              " />
              )}

              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }
  function HeaderDeleteConfirmModal({ item, hasChildren, deleting, onConfirm, onCancel }) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Are you sure you want to delete this header item?</p>
              <p className="text-sm text-zinc-400 mt-1">"{item.label}"</p>
              {hasChildren && (
                <p className="text-sm text-amber-400 mt-2">
                  This item has child navigation items. Deleting it will also remove its
                  children.
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onCancel}
              disabled={deleting}
              className="px-4 py-2 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  function HeaderNavbarPreviewModal({ tree, loading, onClose }) {
    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget) onClose();
    };

    const visibleRoots = (tree || [])
      .filter((i) => i.show !== false && i.isActive !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleOverlayClick}
      >
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Eye className="w-4 h-4 text-indigo-400" />
              Navbar Preview
            </h2>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-zinc-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading tree…
              </div>
            ) : visibleRoots.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-16">
                No visible header items to preview.
              </p>
            ) : (
              <nav className="bg-zinc-950 border border-zinc-800 rounded-xl p-4">
                <ul className="flex flex-wrap gap-1">
                  {visibleRoots.map((item) => (
                    <HeaderPreviewNavItem key={item.id} item={item} />
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>
    );
  }

  function HeaderPreviewNavItem({ item }) {
    const [expanded, setExpanded] = useState(true);
    const Icon = resolveIcon(item.icon);
    const children = (item.children || [])
      .filter((c) => c.show !== false && c.isActive !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    return (
      <li className="flex flex-col">
        <div className="flex items-center rounded-lg hover:bg-zinc-800/60">
          {/* Label is the clickable nav target — separate from the arrow toggle */}
          <span className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-200 pointer-events-none">
            {Icon && <Icon size={16} color={item.iconColor || "currentColor"} />}
            {item.label}
          </span>
          {children.length > 0 && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="pr-2 py-2 text-zinc-400 hover:text-white"
              aria-label="Toggle dropdown"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {children.length > 0 && expanded && (
          <ul className="ml-5 mt-1 border-l border-zinc-800 pl-3 space-y-1">
            {children.map((child) => {
              const ChildIcon = resolveIcon(child.icon);
              return (
                <li
                  key={child.id}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm text-zinc-400 rounded-md hover:bg-zinc-800/60"
                >
                  {ChildIcon && <ChildIcon size={14} color={child.iconColor || "currentColor"} />}
                  <span>{child.label}</span>
                  {child.type === "projects" && child.filter && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400">
                      {child.filter}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  }
  // ---------------------------------------------------------------------------
  // Small shared UI helpers
  // ---------------------------------------------------------------------------
  const inputClass =
    "w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/60";

  const inputClassSm =
    "w-full bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/60";

  function Section({ title, action, children }) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">
            {title}
          </h3>
          {action}
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    );
  }

  function Field({ label, children }) {
    return (
      <div>
        <label className="block text-xs text-zinc-400 mb-1">{label}</label>
        {children}
      </div>
    );
  }

  function LabeledInput({ label, value, onChange, placeholder, fullWidth, sm }) {
    return (
      <div className={fullWidth ? "col-span-2" : ""}>
        <label className="block text-[11px] text-zinc-500 mb-1">{label}</label>
        <input
          type="text"
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={sm ? inputClassSm : inputClassSm}
        />
      </div>
    );
  }

  function ToggleRow({ label, checked, onChange }) {
    return (
      <div className="flex items-center justify-between py-1">
        <span className="text-sm text-zinc-300">{label}</span>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`w-11 h-6 rounded-full transition-colors relative ${checked ? "bg-indigo-600" : "bg-zinc-700"
            }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0"
              }`}
          />
        </button>
      </div>
    );
  }
  const userRole = currentUser?.role || "User";

  const userPermissions = Array.isArray(currentUser?.permissions)
    ? currentUser.permissions
    : [];

  const canAccessPage =
    userRole === "Admin" ||
    adminPage === "dashboard" && userPermissions.includes("dashboard") ||
    userPermissions.includes(adminPage);
  // ─── Page content ──────────────────────────────────────────────
  let content;
  if (!canAccessPage) {
    content = (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <Lock
            size={40}
            className="mx-auto mb-3 text-gray-500"
          />

          <h2 className="text-lg font-semibold text-white">
            Access Denied
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to view this section.
          </p>
        </div>
      </div>
    );
  } else if (adminPage === "dashboard") content = <AdminDashboard about={about} projects={projects} publications={publications} community={community} experience={experience} contact={contact} setPage={setAdminPage} skillCount={skillCount} educationCount={educationCount} blog={blog} home={home} service={service} />;
  else if (adminPage === "about") content = <AdminAbout about={about} setAbout={setAbout} notify={notify} />;
  else if (adminPage === "projects")
    content = (
      <GenericManager
        config={projectsConfig}
        data={projects}
        setData={setProjects}
        notify={notify}
        createItem={createPortfolio}
        updateItem={updatePortfolio}
        deleteItem={deletePortfolio}
        loadData={getPortfolio}
        moveUp={movePortfolioUp}
        moveDown={movePortfolioDown}
      />
    );
  else if (adminPage === "publications")
    content = (
      <GenericManager
        config={publicationsConfig}
        data={publications}
        setData={setPublications}
        notify={notify}
        createItem={createPublication}
        updateItem={updatePublication}
        deleteItem={deletePublication}
        loadData={getPublications}
        moveUp={movePublicationUp}
        moveDown={movePublicationDown}
      />
    );
  else if (adminPage === "community")
    content = (
      <GenericManager
        config={communityConfig}
        data={community}
        setData={setCommunity}
        notify={notify}
        createItem={createCommunity}
        updateItem={updateCommunity}
        deleteItem={deleteCommunity}
        loadData={getCommunity}
        moveUp={moveCommunityUp}
        moveDown={moveCommunityDown}
      />
    );
  else if (adminPage === "experience") content = <ExperienceManager notify={notify} />;
  else if (adminPage === "contact") content = <AdminContact contact={contact} setContact={setContact} notify={notify} />;
  else if (adminPage === "youtube") content = <YoutubeAdmin notify={notify} />; else if (adminPage === "home") content = <HomeAdmin notify={notify} />;
  else if (adminPage === "footer") content = <FooterAdmin notify={notify} />; else if (adminPage === "header") content = <HeaderAdmin notify={notify} />;
  else if (adminPage === "users") content = <UserManagement profileEditRequest={profileEditRequest} />; else if (adminPage === "blog")
    content = (
      <BlogAdmin notify={notify} />
    );
  else if (adminPage === "service") content = <ServiceAdmin notify={notify} />;
  else if (adminPage === "settings") content = <SecuritySettings currentUser={currentUser} setCurrentUser={setCurrentUser} notify={notify} />;
  return (
    <div className={cx("min-h-screen font-body flex", theme.bg)}>
      <FontStyles />
      <Sidebar page={adminPage} setPage={setAdminPage} open={sidebarOpen} setOpen={setSidebarOpen} onLogout={handleLogout} home={home} currentUser={currentUser} />

      <div className="flex-1 min-w-0 flex flex-col lg:pl-64">
        <AdminHeader page={adminPage} setPage={setAdminPage} setOpen={setSidebarOpen} about={about} home={home} onProfileClick={handleProfileClick} onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 pt-20 pb-28">
          {content}
        </main>
        <AdminFooter home={home} />
      </div>
      <ToastStack toasts={toasts} onClose={closeToast} />
    </div>
  );
}